/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * English is the source catalogue. Every other language file mirrors this key
 * structure exactly, so a missing translation is visible as a missing key
 * rather than as a silently English string.
 *
 * Two conventions worth knowing before editing:
 *
 *   - Asset tags (ETCH-01, DEP-02) are stencilled on real hardware and are not
 *     translated. Neither are unit symbols. Both stay in the code.
 *   - A few values carry <kbd> or <em> markup. Keep the tags, translate around
 *     them; they are what makes a key cap look like a key cap.
 */

export const i18n_en = {
  translation: {
    mission: {
      whereThePhotonsWent: {
        Title: 'Where the photons went',
        Briefing: 'EUV-22 is a quarter slow and the night shift has a thirty-hour collector swap pencilled in for tomorrow. An EUV scanner can lose its light in three places, and only one of them is the collector. Walk the beam path before anyone opens the source vessel.',
        Debrief: 'The light was never made. The main laser pulse was landing late on the droplet, so the plasma gave about 60% of its qualified pulse energy, and power was down by the same fraction everywhere after it. A collector sits AFTER the plasma: it cannot lower the pulse energy measured at the plasma, and that one reading is what saved thirty hours and a collector that was doing its job.',
        objective: {
          timing: 'Run the droplet-to-pulse timing scan',
          call: 'Make the call, correctly',
        },
      },
      theRulerIsWide: {
        Title: 'The ruler is wide',
        Briefing: 'Every line SEM-23 has measured since the weekend reads about a nanometre wide, and lithography has already turned the dose up once to pull CD back. Before they do it again: find out whether the lines moved, or the instrument did.',
        Debrief: 'The instrument did. The cold field-emission tip had gone days without a flash, its energy spread had nearly tripled, and at 500 eV that is most of the spot - chromatic blur goes as ΔE over E. A wider spot reads every edge a little further out. The scanner was steered off target to correct a print that had been right, which is what a metrology fault costs: not a scrapped wafer, a wrong decision somewhere else.',
        objective: {
          spread: 'Measure the gun’s energy spread',
          call: 'Make the call, correctly',
        },
      },
      coldPlateWarmQubit: {
        Title: 'Cold plate, warm qubit',
        Briefing: 'QDT-24 reached 10 mK on schedule and every thermometer on it is normal. The chip in it measured well in another fridge last month and reads terribly here. Somebody will propose warming up to swap the chip. Find out what the qubit can hear first.',
        Debrief: 'A 20 dB attenuator was missing from the drive line, so the line carried a hundred times the room-temperature noise it should: an eighth of a photon in the qubit’s mode, a qubit at 109 mK on a plate at 10. The chip was fine and so was the fridge. A qubit is not at the temperature of the metal it is bolted to - it is at the temperature of its wiring - and the only thermometer that reads that is the qubit itself.',
        objective: {
          line: 'Measure the drive line’s insertion loss',
          call: 'Make the call, correctly',
        },
      },
      fiftyDegreesHot: {
        Title: 'Fifty degrees nobody set',
        Briefing: 'Sheet resistance out of THRM-04 is running low and junctions are coming out deep. The implanter group has been asked to check their dose, and they are not pleased about it. Before IMP-10 loses a shift to a dose audit: a low dose does not do this.',
        Debrief: 'The RTP pyrometer was reading low, so the lamps were driven fifty degrees past the setpoint the screen showed. Diffusivity is Arrhenius: fifty degrees nearly doubles the Dt, the dopant spreads, the junction deepens and the sheet resistance FALLS. A short dose moves sheet resistance the other way and leaves the junction where it was. The direction of the error named the tool before anybody measured anything.',
        objective: {
          pyrometer: 'Check the pyrometer against a calibration wafer',
          call: 'Make the call, correctly',
        },
      },
      oxygenClimb: {
        Title: 'The oxygen climb',
        Briefing: 'Device engineering is rejecting wafers from the last three ingots for interstitial oxygen, and the puller has not had a recipe change in a month. Same pull rate, same rotation, same argon setpoint. Find out what is different.',
        Debrief: 'The silica crucible was near the end of its life. A crucible dissolves into the melt the whole time it is hot, and a tired one gives up oxygen faster than a fresh one at exactly the recipe that worked last month. The argon flow was nominal, which is what rules out the sweep — the other way oxygen gets high — and turning the argon up would have masked it for one more ingot.',
        objective: {
          oxygen: 'Measure interstitial oxygen in the last boule',
          call: 'Make the call, correctly',
        },
      },
      flatAndSlow: {
        Title: 'Flat, and slow',
        Briefing: 'Polish is missing its cycle time on every lot and the operators have been adding minutes to hit thickness. Flatness still passes. Nobody has changed the slurry or the down-force.',
        Debrief: 'The pad was glazed — its surface loaded and stopped cutting, so removal rate fell and the fix everyone reached for, more time, was buying the number by spending the tool. Slurry size was in spec, which rules out the abrasive; a glazed pad and a spent slurry both slow the rate, and only one of them is fixed by a conditioning cut.',
        objective: {
          rate: 'Measure the removal rate on a monitor',
          call: 'Make the call, correctly',
        },
      },
      sharpEnoughToPass: {
        Title: 'Sharp enough to pass',
        Briefing: 'CD is drifting at LITH-03 and the obvious answer is dose. Before you touch the source: a scanner that has lost focus prints the same CD error a dose drift does, and the two call for opposite corrections.',
        Debrief: 'The stage had lost focus, not the source. Defocus costs CD and it costs CONTRAST and NILS with it — a dose error moves the size and leaves the edge alone, which is what the aerial-image numbers separate. Correcting this with dose would have held the number while the process window kept closing underneath it.',
        objective: {
          focus: 'Run the focus probe',
          call: 'Make the call, correctly',
        },
      },
      theSlowLeak: {
        Title: 'The slow leak',
        Briefing: 'ETCH-01 is not reaching base pressure and the shift before you logged it as a tired pump. Pumps are a four-hour swap and there is one spare in the building, so be sure before you spend it.',
        Debrief: 'A real leak, not a tired pump. Both raise base pressure and both look identical on the gauge; what separates them is where the gas is coming from — a leak admits atmosphere at a constant rate, and pump speed was fine. The spare pump is still on the shelf, which is the entire value of having checked.',
        objective: {
          seal: 'Run the seal check',
          call: 'Make the call, correctly',
        },
      },
      theCupThatLies: {
        Title: 'The cup that lies',
        Briefing: 'Sheet resistance is coming back low on implanted lots and the implanter says every dose was on target. One of those two statements is wrong, and the tool is the one with an opinion about itself.',
        Debrief: 'The Faraday cup had drifted, so the tool was counting fewer ions than it delivered and over-implanting to make its number. A dose monitor that measures itself cannot catch this — only a wafer can, which is why sheet resistance is the reading that settles it. Every other bench number on the tool was nominal, and every one of them was innocent.',
        objective: {
          rho: 'Implant a monitor and measure sheet resistance',
          call: 'Make the call, correctly',
        },
      },
      thePitchThatWalks: {
        Title: 'The pitch that walks',
        Briefing: 'PAT-21 is passing mean CD and failing at electrical test, with the failures alternating line to line. One exposure made these lines, so overlay cannot be the answer.',
        Debrief: 'Pitch walking. The spacer film came in thin, and in a self-aligned quadruple cell the spacer thickness IS the half pitch — so the four lines the module makes from one mandrel are no longer evenly spaced. The mean is untouched, which is why CD passed, and no amount of scanner work would have helped: this is a deposition problem wearing a lithography complaint.',
        objective: {
          walk: 'Measure the pitch, line by line',
          call: 'Make the call, correctly',
        },
      },
      dishingOut: {
        Title: 'Dishing out',
        Briefing: 'CMP-11 is over-polishing the wide copper features and the pad was changed on Tuesday. The obvious suspect has an alibi.',
        Debrief: 'The slurry had aged and its corrosion inhibitor was spent. The inhibitor is what stops the chemistry attacking copper where the pad is not pressing, so without it the wide features dish while the narrow ones look fine — a profile signature no amount of down-force correction reaches. A fresh pad on old slurry would have polished faster into the same defect.',
        objective: {
          inhibitor: 'Assay the slurry inhibitor',
          call: 'Make the call, correctly',
        },
      },
      voidsInTheVia: {
        Title: 'Voids in the via',
        Briefing: 'ECD-12 is plating to thickness and the vias are coming back with seams down the middle. Current density, bath temperature and contact resistance all read nominal.',
        Debrief: 'The organic additives were exhausted. Bottom-up fill is not a current setting — it is the accelerator and suppressor arranging where copper goes, and a bath that has lost them plates conformally and pinches the via shut before it fills. The thickness was right the whole time, which is exactly why only a cross-section could show it.',
        objective: {
          xsection: 'Cross-section a via',
          call: 'Make the call, correctly',
        },
      },
      theToolThatCriesWolf: {
        Title: 'The tool that cries wolf',
        Briefing: 'INSP-13 is flagging so many defects that engineering has started ignoring its maps. Either the line got dramatically worse this week, or the inspector did.',
        Debrief: 'The collection optics had hazed. Scattered light from a clouded lens raises the background the detector works against, so the tool reports defects that are not on the wafer — and a nuisance rate that high does more damage than a blind tool, because people stop reading real maps. Incoming roughness was nominal, which rules out the wafers themselves.',
        objective: {
          nuisance: 'Verify the nuisance rate against a known wafer',
          call: 'Make the call, correctly',
        },
      },
      goodDieCalledBad: {
        Title: 'Good die, called bad',
        Briefing: 'Yield at PRB-14 has dropped four points and the loss is all at the wafer edge. The tips were changed last week and are well inside their life.',
        Debrief: 'The probe card was out of plane. A card that is not parallel to the chuck touches down harder on one side than the other, so the far edge never makes proper contact and good die are binned as opens. Tip wear was nominal — the lookalike everyone reaches for first — and replacing tips would have cost a card change and fixed nothing.',
        objective: {
          contact: 'Measure contact resistance across the wafer',
          call: 'Make the call, correctly',
        },
      },
      wideKerf: {
        Title: 'The kerf that grew',
        Briefing: 'Assembly is losing die to edge chipping and dicing reports every lot in spec. Street width has not been changed and the tape lot is current.',
        Debrief: 'The blade was past its cut limit. A worn blade cuts a wider kerf and chips more at the edges, and the die that survive to the mould press are the ones that were not already cracked. Tape peel was nominal, which rules out die movement during the cut — the other way chipping climbs, and the one that would have sent you to the wrong supplier.',
        objective: {
          kerf: 'Measure the kerf width',
          call: 'Make the call, correctly',
        },
      },
      theQuietDispenser: {
        Title: 'The quiet dispenser',
        Briefing: 'Packages from BOND-16 are failing thermal cycling and the wire bonds all pull to spec. The failures are under the die, not at the loops.',
        Debrief: 'The epoxy dispense was starved. Too little adhesive leaves voids under the die, and a void is a place heat cannot leave and stress has nowhere to go — so the part passes every bond test and fails the first time it is cycled. Pull strength and ball shear were both nominal, which is what says the wire bonder is innocent.',
        objective: {
          void: 'Image the die-attach voids',
          call: 'Make the call, correctly',
        },
      },
      popcorn: {
        Title: 'Popcorn',
        Briefing: 'PKG-17 parts are cracking during solder reflow at the customer, not here. Transfer pressure is nominal and the compound is in date.',
        Debrief: 'The parts had taken up moisture in storage. Water trapped in a mould compound flashes to steam at reflow temperature and splits the package from the inside — which is why this fails at the customer and passes every test in this building. Mould void was normal, ruling out both the press and the compound, and neither of those repairs would have touched the dry-pack process that actually failed.',
        objective: {
          popcorn: 'Run the moisture-sensitivity reflow',
          call: 'Make the call, correctly',
        },
      },
      theEarlyFailures: {
        Title: 'The early failures',
        Briefing: 'FT-18 is failing more parts after burn-in than before it, and the tester passed its gauge study this morning. Handler temperature is on setpoint.',
        Debrief: 'The population itself carried infant mortality — a defect distribution from upstream, not a test problem. Burn-in is supposed to find these; a fallout that climbs means the lot arrived with them, and the answer is upstream rather than at the tester. Gauge sigma and handler temperature were both nominal, which is what turns "our test is broken" into "our wafers are", and those two go to different people.',
        objective: {
          fallout: 'Analyse the burn-in fallout',
          call: 'Make the call, correctly',
        },
      },
      theValveThatWont: {
        Title: 'The valve that will not',
        Briefing: 'Routine safety verification at GAS-19. The bunker is running normally and nothing is alarming — which is exactly when a protective device is worth testing, because it is the only time you can.',
        Debrief: 'The excess-flow valve had seized open. Nothing was wrong with the gas, the scrubber or the extract, and nothing would have been until a line broke — at which point the one device meant to close would not have. A protective device that is never exercised is a device you are assuming, and the release model is what turns that assumption into a number.',
        objective: {
          release: 'Run the release model against the valve test',
          call: 'Make the call, correctly',
        },
      },
      leakOrOutgas: {
        Title: 'Leak or outgas',
        Briefing: 'FAC-06 will not hold base and the log says the chamber was opened to atmosphere on Friday. Water on the walls and a real leak both do this. One of them bakes out and the other does not, and you cannot tell from the gauge reading.',
        Debrief: 'A real leak. The rate-of-rise SHAPE is what separates them: atmosphere entering through a seal climbs linearly and never stops, while water coming off the walls climbs and then rolls over as the surface depletes. Same base pressure, two different curves, and a bake-out would have cost a shift and changed nothing.',
        objective: {
          rise: 'Take the rate-of-rise curve, not just the number',
          call: 'Make the call, correctly',
        },
      },
      innocentInstrument: {
        Title: 'The innocent instrument',
        Briefing: 'MET-05 reads the oxide 4% high and half the fab wants it recalibrated. Before you take a working gauge down: a repeatable measurement of the wrong assumption looks exactly like a broken gauge, and only one reading can tell them apart.',
        Debrief: 'The reflectometer was innocent — the FILM changed. The deposition tool drifted and the measurement recipe went on assuming the old material, so a perfect fit of the wrong model reported a thicker film, repeatably. The ellipsometer, which solves for the index instead of assuming it, is what separated \'gauge broken\' from \'world changed\' — and a recalibration would have buried the real drift under a corrected lie.',
        objective: {
          ellipsometer: 'Cross-check on the ellipsometer',
          call: 'Make the call, correctly',
        },
      },
      atomsToFabs: {
        Title: 'Atoms to fabs',
        Briefing: 'One lot, the whole substrate line. Pull a usable crystal at CZ-07 and hand the ingot on; slice it at WPR-08; run the incoming clean at CLN-09; grow the device layer at EPI-20, in spec, and pass the wafer to the front end. Every decision you make rides the travel card to the next tool — including the ones you get wrong.',
        Debrief: 'Melt to device layer: the title of this fab, walked. The wafer leaving EPI-20 carries your melt doping, your slice, your clean and your epitaxy on its travel card, and every tool downstream will live inside the budgets you just set. That is what \'from atoms to fabs\' means — not a slogan, a lot history.',
        objective: {
          grow: 'Grow a usable ingot at CZ-07',
          handCz: 'Hand the ingot to wafer prep',
          slice: 'Slice, lap and polish at WPR-08',
          clean: 'Run the incoming clean at CLN-09',
          epi: 'Grow the device layer in spec at EPI-20',
          handEpi: 'Pass the finished substrate onward',
        },
      },
      holdTheClass: {
        Title: 'Hold the class',
        Briefing: 'Routine certification. Show the bay holds its ISO class where it matters — at the product, not at the wall monitor. The portable counter costs an hour; that is what it is for.',
        Debrief: 'The wall monitor is bolted where the wall is, and what matters is the air a metre above open product. You paid an hour to know the difference. That habit is the room.',
        objective: {
          enter: 'Enter the bay and open GOWN-00',
          workzone: 'Run the portable counter at the work zone',
        },
      },
      theWallLies: {
        Title: 'The wall lies',
        Briefing: 'Defect density is creeping on lots that pass through this bay, and the wall monitor reads comfortably in class. Someone is wrong. Find out who, and say so.',
        Debrief: 'The garments were past their laundering life — a source the room ventilation never gets a chance to dilute before it reaches the wafer. The wall reads the room; the wafer reads the person standing over it.',
        objective: {
          workzone: 'Measure where the product is',
          call: 'Make the call, correctly',
        },
      },
      twoRoomsAway: {
        Title: 'Two rooms from the symptom',
        Briefing: 'It is 02:00. ETCH-01 is out of spec on selectivity and non-uniformity, its chuck temperature is running warm, and the same alarm is showing on every etcher in the building. Lots are stacking up at etch. Nothing on the etcher is broken. Find out what is, decide what the line does while you wait, and get the building healthy - in that order, or the line will pay for every hour you spend on the wrong room.',
        Debrief: 'The chiller in the sub-fab was the fault; the etcher was the symptom, two rooms away. The chambers came back within hours of the call - and the queue did not. Every lot that waited at etch arrived late at its next tool, and the line spent its spare capacity working that off for the rest of the day. Open the Line to see what your decision cost against the two you did not make: by date loses the fewest dates at this load; holding starts did nothing for the queue and took slack from the lots it held. WIP, throughput and cycle time are tied by Little\'s law, and a line at 95% would still be draining tomorrow.',
        objective: {
          etch: 'Go to the etcher and see the alarm',
          call: 'Call the escalation, correctly - it is not this tool',
          decide: 'Decide what the line does while you wait: open the Line and choose',
          descend: 'Take the stairs to the sub-fab',
          repair: 'Restore the chilled water supply',
          green: 'Leave the building healthy',
        },
      },
      theResistClock: {
        Title: 'The resist clock',
        Briefing: 'The same night, a different route: this product\'s resist has an eight-hour shelf life between the scanner and the etcher, and two of every etcher\'s three chambers have just dropped out on temperature control. Every lot the scanner exposes from now on is a lot with a clock running toward a chamber that is not there. Decide what the scanner does, then get the building healthy.',
        Debrief: 'The window was never the trip\'s problem. The lots the trip sent back were exposed before the chambers dropped; nothing could save them. What a window does is turn every hour of etch queue into rework, all day, every day - and at 95% load that is most of the line. The gate is what makes a window workable: hold the exposure until the etcher can eat it in time, at the one step whose queue costs nothing but time. It is the only hold on this floor that is right, and only because it holds the lot before the clock starts.',
        objective: {
          scanner: 'Go to the scanner and see what is waiting to be exposed',
          decide: 'Decide what the scanner does: open the Line, the queue-time window, and choose',
          call: 'Call the escalation at the etcher, correctly',
          descend: 'Take the stairs to the sub-fab',
          repair: 'Restore the chilled water supply',
          green: 'Leave the building healthy',
        },
      },
      theSilentTool: {
        Title: 'The silent tool',
        Briefing: 'DEP-02 has been depositing thick for hours and its own screen says everything is fine, because the thermocouple it trusts is the thing that is wrong. Nothing on the floor knows yet. Metrology samples a fraction of its visits; one of those will be the lot that tells you. Decide how hard to sample, find the tool, and call it.',
        Debrief: 'Every lot through that tool until the inspection caught it is at risk, and that number is throughput through the tool times the hours it ran unseen - Little\'s law, from the yield side. Sampling harder buys hours off that delay and pays for them at the metrology tool, whose queue every lot in the building stands in. The rate is a decision about which of those two the fab would rather carry, and the Line shows both prices.',
        objective: {
          metrology: 'Go to metrology - it is the only tool that can see this',
          decide: 'Decide the sampling rate: open the Line, the excursion, and choose',
          call: 'Call the deposition tool, correctly',
        },
      },
      theDueChamber: {
        Title: 'The due chamber',
        Briefing: 'Chamber C on ETCH-01 is due its service tonight: four planned hours, on the bottleneck, in a shift that is already behind. The shift lead would rather run it through the week. Look at the tool, then decide - and be ready to say what running on would cost.',
        Debrief: 'A planned service costs its hours and nothing else. A deferred one is a bet on a worn chamber, and the Line says the bet loses every week: the chamber fails, the unplanned repair takes twice as long, and it lands at an hour nobody chose, on the tool whose hour is the fab\'s hour. The lead was not wrong that four hours tonight hurts. He was wrong about what eight hours on Thursday would cost.',
        objective: {
          etch: 'Go to the etcher',
          decide: 'Decide the service: open the Line, the maintenance, and choose',
        },
      },
      theStopwatch: {
        Title: 'The stopwatch',
        Briefing: 'The bay reads dirtier at the same headcount and takes visibly longer to settle after a door event. The ceiling filters were changed last month. Diagnose it — and be ready to defend the reading you trusted.',
        Debrief: 'Recovery time is set by air changes and nothing else, so a slow recovery with a clean filter bank is the building, not the room. The instrument that caught it was a stopwatch. The repair is not on this floor — but that is another work order.',
        objective: {
          recovery: 'Time the recovery',
          call: 'Call the escalation, correctly',
        },
      },
      firstShift: {
        Title: 'First shift at the bench',
        Briefing: 'Defect density at inspection has doubled on everything through CLN-09. The bench has not alarmed and looks exactly as it always does. Your first diagnosis: earn a reading before you call it.',
        Debrief: 'One chemistry out, one fine — the signature of a spent bath, and nothing about a transparent liquid ever looks spent. You paid for the monitor wafer and it paid you back.',
        objective: {
          visit: 'Take the shift at CLN-09',
          reading: 'Earn at least one reading',
          call: 'Make the call, correctly',
        },
      },
      invisibleFault: {
        Title: 'The invisible fault',
        Briefing: 'Gate oxide integrity is failing on one product and nobody can find a defect. Particle counts are perfect. Whatever this is, the cheap measurements cannot see it.',
        Debrief: 'A monolayer of copper is invisible to every optical inspector in the building, and it rides into the furnace on wafers that look perfect. The expensive, destructive reading exists precisely because this fault does.',
        objective: {
          metals: 'Order the surface-metals measurement',
          call: 'Make the call, correctly',
        },
      },
      shipsAnyway: {
        Title: 'Ships anyway',
        Briefing: 'Nothing has been reported against EPI-20. Every visit is in specification. The order exists because the qualification data looks a shade off trend, and someone upstairs trusts your judgement more than the spec limits.',
        Debrief: 'The hardest shift on the floor: a broken tool shipping passing wafers. Nothing forced the call you just made — which is exactly why it was worth making.',
        objective: {
          reading: 'Earn a reading at EPI-20',
          call: 'Call it anyway, correctly',
        },
      },
      notThisTool: {
        Title: 'Not this tool',
        Briefing: 'CLN-09 is failing at particles AND metals in the same visit, and both baths were remade this morning. Before you order eight hours of bench repairs: is this bench even the problem?',
        Debrief: 'Failing both chemistries at once is a signature no single bath can produce — the last water the wafer sees is the house’s water. The repair you refused to order would have cost eight hours and fixed nothing.',
        objective: {
          phone: 'Call facilities for the utility panel',
          call: 'Call the escalation, correctly',
        },
      },
      takeTheStairs: {
        Title: 'Take the stairs',
        Briefing: 'The escalation was made: the rinse loop reads 11 MΩ·cm and the polishing mixed bed is the suspect. You are the facilities side of the story now. Go down and make the building healthy.',
        Debrief: 'The bed held 18.2 for almost its whole life and then failed across the last few percent — which is why the plant regenerates on throughput, never on the meter. The board is green; the bench upstairs recovers on its next lot.',
        objective: {
          descend: 'Take the stairs behind FAC-06',
          repair: 'Regenerate the polishing mixed bed',
          green: 'Leave the building healthy',
        },
      },
      oneEventTwoSymptoms: {
        Title: 'One event, two symptoms',
        Briefing: 'Two complaints an hour apart: ALD growth is running fast and non-uniform, and the furnace oxide is coming out thick. Two tools, two process groups, two tickets. Visit both before you believe either.',
        Debrief: 'Water in the dry-gas header ruins ALD because water is its co-reactant, and thickens the furnace oxide by a completely different mechanism. The symptoms had nothing in common except a cause — and the two groups had no reason to compare notes until you walked between them.',
        objective: {
          ald: 'Visit the ALD tool',
          furnace: 'Visit the furnace',
          call: 'Call the escalation at either tool',
        },
      },
      wrongRepair: {
        Title: 'Make the building healthy',
        Briefing: 'Something in the sub-fab is sick. The order says only that. Every repair down there charges its hours whether or not it was the right one — the gauges are how you avoid paying twice.',
        Debrief: 'The gauges cleared the innocent plants before you spent a single hour on them: most of the sub-fab being fine is most of the diagnosis. If you repaired a healthy plant first, the debrief shows exactly what that certainty would have cost.',
        objective: {
          descend: 'Go down and read the gauges',
          repair: 'Repair the plant that is actually sick',
          green: 'Leave the building healthy',
        },
      },
    },
    orders: {
      Button: 'Work orders',
      Title: 'Work orders',
      BoardNote:
        'A briefing, a building in some state, and the expectation of evidence. Accepting ' +
        'an order arms the world; everything you then do lands in the journal, and the ' +
        'order completes when the evidence is in it. No score — the debrief just shows ' +
        'what your certainty cost.',
      TrackRoom: 'The room',
      TrackTool: 'The tool in front of you',
      TrackBuilding: 'The building',
      TrackCapstone: 'The whole line',
      ClassLink: 'Class link',
      LinkCopied: 'Link copied',
      Accept: 'Accept',
      Again: 'Run again',
      Abandon: 'Abandon',
      Active: 'active',
      Completed: 'completed',
      Done: 'Work order complete',
      Cost: 'What it cost',
      TotalHours: 'Total tool time',
    },
    subfab: {
      mau: {
        Title: 'Make-up air unit MAU-1',
        Intro:
          'Outside air in, cleanroom air out. Dehumidifying means cooling to the dew point and ' +
          'reheating; winter means steam; pressure is what is left after the exhaust.',
        Readings: 'On the unit',
        WeatherSummer: 'Summer design day',
        WeatherWinter: 'Winter design day',
        WeatherMild: 'Mild day',
        Reset: 'Reset',
        KnobOutsideC: 'Outside air temperature',
        KnobOutsideRh: 'Outside air humidity',
        KnobSupplyC: 'Supply air setpoint',
        KnobSupplyRh: 'Supply humidity setpoint',
        KnobFanPct: 'Supply fan, % of design',
        KnobExhaustPct: 'Process exhaust, % of design',
        Flow: 'Supply / exhaust',
        Moisture: 'Moisture in / out',
        DewPoint: 'Supply dew point',
        CoilLeaving: 'Cooling coil leaves at',
        Dehumidifying: 'dehumidifying',
        Cooling: 'Cooling coil',
        Heating: 'Preheat + reheat coils',
        Steam: 'Humidifier steam',
        FanPower: 'Fan power',
        Power: 'Power, chiller at its COP',
        Pressure: 'Cleanroom pressure',
        WarnFreeze: 'The chilled-water coil is being asked for a dew point near freezing: frost risk.',
        WarnCooling: 'The cooling coil is past its rated capacity; the supply will run warm and wet.',
        WarnHeating: 'The hot-water coils are past their rated capacity; the supply will run cold.',
        WarnSteam: 'The humidifier is past its rated capacity; the cleanroom will dry out.',
        WarnPressureLow: 'Cleanroom pressurisation lost: the corridor is breathing in through the cracks.',
        WarnPressureHigh: 'Over-pressurised: doors will fight their closers and the FFU plenum will lift.',
        Note:
          'Rating plate: 12 000 m³/h, 240 kW cooling, 130 kW heating, 110 kg/h steam. Nothing upstairs ' +
          'feels this unit yet; the numbers are the psychrometrics, not a fault model.',
      },
      BoardTitle: 'SUB-FAB UTILITIES',
      SkidNote:
        'The building’s side of the story. Repairs performed here are real: the hours are ' +
        'charged either way, and only the plant that is actually sick recovers anything.',
      RepairCleared:
        'Repair complete and the loop re-qualified. The utility board is green again — walk ' +
        'upstairs and watch the tools recover.',
      RepairWasted:
        'Repaired, re-certified… and the symptom upstairs has not moved. This plant was ' +
        'healthy; the hours are spent.',
      RepairIdle: 'Nothing was wrong with this plant. Preventive work, logged and charged.',
      BeltsSlipping: 'slipping under load',
      BeltsTight: 'tight',
      reading: {
        headerLeakUp: 'Header leak-up rate',
        headerAdder: 'Pressure added at each chamber',
        pcwSupply: 'Supply temperature vs nominal',
        gasMoisture: 'Header moisture at the dryer outlet',
        upwLoop: 'Loop resistivity, 25 °C compensated',
        ahuFlow: 'Delivered airflow vs design',
        ahuBelts: 'Drive belt inspection',
        forelineMET05: 'Foreline pressure, MET-05',
        forelineINSP13: 'Foreline pressure, INSP-13',
        forelinePRB14: 'Foreline pressure, PRB-14',
        trayCMP11: 'Containment tray, CMP-11 line',
        trayECD12: 'Containment tray, ECD-12 line',
        chemInterlock: 'Delivery interlock',
        mauFlow: 'Make-up air delivered vs design',
        mauFilterDp: 'Pre-filter pressure drop',
        roomPressure: 'Cleanroom pressure vs ambient',
      },
      PumpsPulling: 'all pulling',
      TrayDry: 'dry',
      TraysDry: 'trays dry',
      InterlockShut: 'SHUT — {{tag}} delivery stopped',
      InterlockOpen: 'open, all lines delivering',
      Tight: 'tight',
    },
    name: {
      SimFab: 'SimFab',
      Institute: 'Institute for Future Intelligence',
      Tagline: 'SimFab — From Atoms to Fabs',
    },

    language: {
      Language: 'Language',
      Change: 'Change language',
    },

    splash: {
      Eyebrow: 'From Atoms to Fabs',
      Headline: 'You are at the <em>gowning airlock</em>.',
      Sub:
        'Welcome! Your job is to run the tools in the aisle: open a tool, set up a ' +
        'process, ask the technician at the station to explain what is going on, analyze the ' +
        'data, and generate reports. You may also follow the work orders.',
      KeyWalk: '<span>walk · <kbd>Shift</kbd> to run</span>',
      KeyLook: '<span>look around · or <kbd>←</kbd><kbd>→</kbd> to turn</span>',
      KeyOpen: '<span>open a tool</span>',
      KeyAsk: '<span>the assistant runs the experiment for you</span>',
      CapLook: 'right-drag',
      CapOpen: 'click',
      CapAsk: 'ask',
      KeyFind: '<span>find any tool by name and jump to it</span>',
      EnterFab: 'Enter {{fab}}',
      CapTap: 'tap',
      CapDrag: 'drag',
      KeyWalkTouch: '<span>a tool — you walk over to it</span>',
      KeyLookTouch: '<span>look around</span>',
      KeyOpenTouch: '<span>open the tool in front of you</span>',
    },

    vehicle: {
      Hoist: 'Overhead hoist vehicle',
      Robot: 'Mobile robot',
      Carrying: 'Carrying lot {{lot}} in carrier {{carrier}}',
      BoundFor: 'Bound for {{tool}}, load port {{port}}',
      BoundEnd: 'Returning along the lane',
      Cruising: 'cruising at {{speed}} m/s',
      Holding: 'holding behind the vehicle ahead',
      Transferring: 'transferring the carrier at the port',
      Moving: 'moving at {{speed}} m/s',
      Delivering: 'delivering at the load port',
      Yielding: 'stopped — a person is in its safety field',
      Dismiss: 'Press <kbd>Esc</kbd>, or click the floor, to dismiss',
      DismissTouch: 'Tap the floor to dismiss',
    },
    talk: {
      Title: 'Talking to {{name}}',
      Placeholder: 'Ask {{name}} something…',
      Send: 'Send',
      Close: 'End the conversation',
      Stop: 'Stop',
      Stopped: 'stopped',
      Thinking: '…',
      Local: 'on-device reply — hosted model unreachable',
      VoiceOn: 'Read replies aloud',
      VoiceOff: 'Stop reading replies aloud',
      MicStart: 'Speak your question',
      MicStop: 'Stop listening',
      Listening: 'Listening…',
      MicWhere: 'Dictation is handled by your browser, which may send audio to its speech service.',
      MicDenied: 'No microphone access. Allow it in the browser to speak your question.',
      /*
       * The one thing a technician says that is not an answer: she asks the
       * student to call a result before the tool reports it (src/talk/predict.ts).
       * Her question is her own words, from the model; these are only the
       * furniture around the wait.
       */
      predict: {
        Waiting: 'Before it comes back:',
        Placeholder: 'Say which way you think it goes…',
        Skip: 'Just show me',
        SkipTitle: 'Show the result without calling it',
      },
      /*
       * The scenes a technician can put up mid-conversation (src/talk/demos.ts).
       *
       * Title is the card header. About is the line that goes into the brief,
       * and it is what the model chooses by, so it says what the scene SHOWS
       * rather than what it is built on. The ids are stable and English; only
       * these strings translate.
       */
      /*
       * Running an experiment on the tool, from the conversation
       * (src/talk/experiments.ts). The findings themselves are the report's,
       * authored in the intents; these are only the words around the wait.
       */
      knock: {
        Wants: '{{name}} wants a word',
        Listen: 'Listen',
        Dismiss: 'Not now',
        OutOfSpec: 'about the run at {{tag}} that just missed spec',
        WrongCall: 'about the call at {{tag}}',
        Stalled: 'about the work order you have open',
      },
      experiment: {
        Running: 'Running it now…',
        Progress: 'condition {{done}} of {{total}}',
        Unavailable: "I can't get at the tool from here — press E at it and ask there.",
        Failed: 'That run did not finish. The tool is untouched.',
        LockedOut: '{{tag}} is tagged out for PM — nothing runs on it until the checks are signed off',
      },
      line: {
        ChambersDown: 'the process cooling water is running warm, and two of the three chambers on every etcher in the building are down on it - one chamber per etcher is carrying all of etch. Lots are stacking up at etch across the floor.',
        Scenario: {
          chiller: 'the chiller trip',
          qtime: 'the queue-time window',
          excursion: 'the excursion',
          pm: 'the due chamber',
        },
        Decided: {
          chiller: {
            reroute: 'let everything queue for the surviving chambers, first come first served',
            hold: 'hold starts until the chambers are back',
            'by-date': 'run etch by due date',
          },
          qtime: {
            run: 'let the scanner run',
            gate: 'gate the scanner until the etcher can etch in time',
          },
          excursion: {
            '0.05': 'inspect 5% of metrology visits',
            '0.1': 'inspect 10% of metrology visits',
            '0.25': 'inspect 25% of metrology visits',
            '0.5': 'inspect 50% of metrology visits',
          },
          pm: {
            now: 'service the chamber now',
            defer: 'run the chamber on through the week',
          },
        },
      },
      demo: {
        // The controls a scene puts under itself in a conversation, where
        // its panel would have wired them to a recipe (app/talk/demoScenes.tsx).
        knob: {
          RingWear: 'Focus ring wear',
          IonEnergy: 'Mean ion energy',
          Downflow: 'Downflow velocity',
          Tilt: 'Beam tilt off the crystal axis',
        },
        Show: 'Show it again',
        Loading: 'Bringing it up…',
        Failed: 'That one will not come up here.',
        'crystal-lattice': {
          Title: 'What a silicon crystal is',
          About: 'Silicon atoms held in the diamond lattice, vibrating under real molecular dynamics.',
          Watch: 'Every atom is vibrating and none of them leaves. That is what a solid is — held in place by nothing but its neighbours.',
          Try: 'Drag "Crystal temperature" up towards 1600 K and watch the vibration grow — then take it back down. Nothing ever leaves its site.',
        },
        bands: {
          Title: 'Why silicon is a semiconductor',
          About: 'The band structure of silicon, and the gap across it that decides everything else.',
          Watch: 'Look at the gap between the full band and the empty one. Too wide and nothing conducts; too narrow and nothing switches off.',
          Try: 'Nothing to set here — the bands are solved once. Read the vertical gap at each point along the bottom axis and find where it is narrowest.',
        },
        carriers: {
          Title: 'Where the carriers in doped silicon come from',
          About: 'Freeze-out, extrinsic and intrinsic: how many carriers a doping level actually gives you.',
          Watch: 'Move the doping slider and watch the carrier count refuse to follow it. At the cold end most dopants are still holding on to their electron.',
          Try: 'Drag the doping slider from one end to the other and watch the three regions go past: frozen out, extrinsic, then intrinsic where the silicon itself takes over.',
        },
        'si-surface': {
          Title: 'A silicon surface, and why it reacts',
          About: 'The (100) surface reconstructing, and the dangling bonds every process on this floor works on.',
          Watch: 'Watch the top layer pair up. Those tilted dimers are the dangling bonds every process on this floor is reacting with.',
          Try: 'Let it settle, then raise the temperature slider and watch the dimers keep their tilt anyway — reconstruction is not something heat undoes.',
        },
        'diffusion-hop': {
          Title: 'How an atom moves through a solid',
          About: 'One interstitial hopping through silicon, hotter and hotter, and the Arrhenius line it draws.',
          Watch: 'Watch for the marked atom to swap places with a neighbour: one jump is one diffusion event. Turn the furnace up and they come faster — and the perfect crystal never hops at all.',
          Try: 'Drag "Furnace temperature" up to about 1600 K and press Run again — the jumps come several times faster. Then switch to "Perfect crystal (control)" and watch it sit at zero however hot you make it.',
        },
        'film-growth': {
          Title: 'How a film grows, atom by atom',
          About: 'Vapour atoms landing on a hot wafer and sticking, with the growth curve they build.',
          Watch: 'Atoms arrive, some stick and some leave again. The curve beside it is the film thickening, and where it bends over is where arriving and leaving balance.',
          Try: 'Drag "Wafer temperature" down and run it again: a colder wafer keeps more of what lands, so the curve climbs closer to the whole vapour charge.',
        },
        tunnelling: {
          Title: 'Electrons tunnelling through a gate oxide a few atoms thick',
          About: 'An electron wave packet meeting a gate oxide built from a few layers of atoms: most of it bounces back in fringes, and a faint copy tunnels through, magnified so you can see it.',
          Watch: 'Watch the packet spread as it travels, then pile up against the atoms in bright stripes — the incoming and reflected waves crossing. On the far side, magnified, a faint copy carries on through a wall it did not have the energy to climb. That is tunnelling.',
          Try: 'Drag "Oxide thickness" down to 0.6 nm — two layers of atoms — and watch the part past the wall brighten; the magnification label tells you by how much. Then press "Oxide as a slab" to swap the atoms for the smooth barrier they average to, and see the same fraction get through. Press "Volume (3D)" for the same wall as layers of spheres, solved on a worker in a few seconds.',
        },
        'plasma-emission': {
          Title: 'Why the glow tells you the film is gone',
          About: 'The etcher\'s endpoint is a spectrometer on the plasma. Each line is an atom falling between two levels, lit by electrons from the tail of the distribution.',
          Watch: 'Watch the two traces: the SiF band goes out as the silicon clears, and the fluorine line rises at the same moment because nothing is consuming fluorine any more.',
          Try: 'Drag T_e from 3 to 2 eV and watch argon\'s 750 nm line collapse while the 440 nm band hardly moves: a 13.5 eV threshold against a 2.9 eV one.',
        },
        'dopant-atom': {
          Title: 'A dopant is a hydrogen atom, until there are too many',
          About: 'The extra electron on a phosphorus atom orbits it like hydrogen\'s, scaled by the crystal: a 2.4 nm orbit and tens of meV. Solved from the Schrödinger equation, not drawn.',
          Watch: 'Watch the shaded orbit on the left with three rules through it - the Bohr radius, the mean radius, and halfway to the next dopant. On the right, the binding energy falls with doping and reaches zero at the Mott transition.',
          Try: 'Drag "Dopant concentration" up past 10¹⁸ and watch the halfway rule march in through the orbit while screening swells it - then the state is gone and the electron is free. Switch to GaAs to see the model at its best.',
        },
        'photon-counting': {
          Title: 'What an image is made of',
          About: 'The aerial image delivered as counted photons, a Poisson draw per pixel, then blurred by the resist and developed. The printed edge\'s roughness is measured, not assumed.',
          Watch: 'Watch the middle picture: at 20 mJ/cm² of EUV the image is grain, about 100 photons per pixel, and the developed edge on the right wanders. The dashed curve is the same image made of ArF photons, √14 smoother.',
          Try: 'Drag "Dose" down to 2 mJ/cm² and watch the roughness climb like 1/√dose; switch to "The EUV image, ArF photons" and the same edge goes smooth. Shrink the resist reach to 2 nm at 0.5 mJ/cm² and defect rows appear.',
        },
        'channel-quantisation': {
          Title: 'Why the threshold is not where the formula says',
          About: 'The gate bends the band into a well a nanometre wide, and a well that narrow allows only discrete levels. The channel charge stands off the oxide, and the probe card measures the difference.',
          Watch: 'Watch the shaded shapes on the left: every electron in the channel is zero at the oxide and peaks a nanometre in. On the right, both capacitances stop short of the oxide\'s; the solid quantum one sits lower, because each volt\'s charge lands a few ångströms further off.',
          Try: 'Push "Gate voltage" up and watch the levels sink and the shapes squeeze toward the oxide — closer, but never onto it. Thin "Oxide thickness" to 1 nm and the gap between the two capacitance curves grows: a few ångströms of standoff matter more when the oxide is only ten of them.',
        },
        'raman-strain': {
          Title: 'The line a Raman tool watches move',
          About: 'Silicon\'s phonons from the real Stillinger–Weber force constants, and the zone-centre optical mode that a Raman spectrometer reads strain from.',
          Watch: 'Watch the six branches, three acoustic from zero and three optical near 593 cm⁻¹, and the Raman line beside them with the measured 520 ruled in — the model overshoots, and says so.',
          Try: 'Drag "Biaxial strain" to −1 % and the line climbs and splits into a singlet and a doublet; push it to +1 % and it falls. Switch to Ge and everything drops, heavier atoms on softer bonds.',
        },
        'quantum-well': {
          Title: 'What a few nanometres of an alloy does to a hole',
          About: 'A thin silicon-germanium layer between silicon is a box for a hole, and the thickness grown is the box width. The allowed levels are solved from the grown profile, not drawn in.',
          Watch: 'Watch the ladder of dashed levels inside the well, and the shaded curve on the lowest one showing where that hole actually is. Every level is an energy the layer permits; between them there is nothing a hole can have.',
          Try: 'Drag "Thickness grown" down from 8 nm and watch every level climb, then watch the top one reach the wall and vanish — the well that held four holds three, then two. Push "Germanium fraction" up to deepen the well and they come back.',
        },
        'resist-absorption': {
          Title: 'Why a resist answers one colour of light and not another',
          About: 'The absorption band of a conjugated molecule, computed, with the wavelengths a fab exposes at ruled across it. Stretch the chain and the band marches down the spectrum, past each line in turn.',
          Watch: 'Watch which rules turn amber. A molecule only answers a line its band sits under — and the band moves because the π electron is in a box whose length is the conjugation, so a longer chain has closer levels and absorbs redder light.',
          Try: 'Drag "Conjugation length" from one double bond up to eleven. At four the band sits on the i line at 365 nm; by six it has walked past it onto the g line, and the i line goes dark again. Nothing you can set here lands on ArF at 193 nm, which is the reason 193 nm needed a different family of molecule.',
        },
        chemisorption: {
          Title: 'A precursor reacting with the wafer, bond by bond',
          About: 'Silane over a silicon wafer under a reactive force field: a Si-H bond breaks against a dangling bond, a Si-Si bond forms, and that silicon joins the film. The sticks are the bond order, not a script.',
          Watch: 'Watch a molecule drift down and sit against the surface, then lose a hydrogen to it — the stick between them vanishes and a new one to the wafer appears. The green curve counts silicon that has joined the film; the blue counts hydrogen now parked on the surface, which is what blocks the next molecule.',
          Try: 'Press "Silyl radicals (SiH₃, plasma CVD)" and drag "Wafer temperature" down to 600 K: a radical sticks where a whole silane will not, which is why plasma CVD runs hundreds of kelvin cooler. Then press "Hydrogen-terminated" and watch growth wait for the hydrogen to leave.',
        },
        'electron-diffraction': {
          Title: 'An electron wave going through two slits',
          About: 'A Gaussian electron packet meets two slits, or a row of atoms, and the edge of the box counts what arrives by angle. The beams land where sin θ = mλ/p says, and vanish once the wavelength outgrows the spacing.',
          Watch: 'Watch the packet spread as it travels, split at the slits, and leave as separate beams. The chart is the screen: each peak sits on a dashed rule, and those rules are the orders the spacing predicts. Nothing put the peaks there — the wave did.',
          Try: 'Drag "Electron energy" down toward 4 eV and watch the outer orders swing wider and then disappear as the wavelength passes the spacing. Press "A row of atoms, 0.5 nm apart" for the tighter target, where it takes a much shorter wavelength to see any structure at all.',
        },
        'light-absorption': {
          Title: 'Why a silicon wafer is transparent to red light',
          About: 'The substrate absorbing light, computed from its tight-binding bands: every jump from a filled band to an empty one, over the whole zone. It is where the refractive index a film measurement takes for granted comes from.',
          Watch: 'Watch where the curve leaves the floor. For silicon that is near 3 eV, its direct gap, far above the 1.1 eV gap usually quoted — a photon on its own cannot supply the momentum an indirect jump needs. The amber rules are the reflectometer\'s sweep, and nearly all of it sits in the transparent part.',
          Try: 'Press "Ge", then "GaAs". Both start absorbing far lower and swallow the whole visible sweep, which is why neither makes a window, and why the wafer under a film is the transparent thing a thickness fit needs it to be.',
        },
        'doped-lattice': {
          Title: 'The dopant atom, in the crystal it landed in',
          About: 'Boron sitting on a silicon site, vibrating under real molecular dynamics — what "p-type" actually is.',
          Watch: 'The haloed atoms are boron on silicon sites. That substitution IS the doping — not a property of the wafer, one atom in the wrong seat, repeated. Watch the chart: the light boron cycles visibly faster than the silicon around it.',
          Try: 'Drag "Dopant concentration (exaggerated)" up and down, then press "Highlight dopants" to drop the halo and see the true proportions. Real doping is parts per million — invisible in 216 atoms, which is why the view cheats.',
        },
        channelling: {
          Title: 'Why an implant is tilted seven degrees',
          About: 'The depth an implant actually reaches, and the tilt that decides it.',
          AxisDepth: 'depth into the wafer (nm)',
          AxisIons: 'ions stopped',
          LabelQualified: 'tilted 7° (qualified)',
          LabelLive: 'this tilt',
          Status: 'Critical angle ψ₁ = {{a}}° · channelled {{b}}% of the beam · deepest 1% reaches {{c}} nm',
          Tracing: 'Tracing 3,000 ions…',
          Watch: 'The dashed curve is the qualified 7° recipe. At zero tilt a quarter of the beam finds an open row and sails past it — same dose, same energy, twice the depth.',
          Try: 'Drag "Beam tilt off the crystal axis" from 0 up to 12° and watch where the deep tail dies. It survives 2° and 4° almost untouched, then collapses — and the angle it collapses at is the ψ₁ printed under the chart.',
        },
        sheath: {
          Title: 'The sheath over the wafer edge',
          About: 'The voltage drop that turns drifting ions into a vertical beam, and how a worn focus ring tilts it.',
          Watch: 'The bright band over the wafer is the voltage drop. The contour lines are the path an ion takes — flat over the wafer, bent where the focus ring has worn.',
          Try: 'Drag "Focus ring wear" from 0 to 100% and watch the contour lines over the wafer edge go from flat to bent. That bend is where the ions stop arriving straight down.',
        },
        'trench-arde': {
          Title: 'Why a narrow trench etches slower',
          About: 'Four trenches of different width under one plasma, and the narrow ones starving of etchant.',
          Watch: 'Four trenches, one plasma. Watch the etchant reach the bottom of the wide one and never quite reach the bottom of the narrow one.',
          Try: 'Drag "Mean ion energy" up from 93 eV and watch the narrow trench get worse, not better: harder ions make the surface consume etchant faster than it can be delivered down a narrow hole.',
        },
        'thermal-budget': {
          Title: 'What heat does to a dopant profile',
          About: 'How far dopant moves in a flow, the integral of D(T) over time: two identical implants, one given a ten-minute 1200 °C spike inside a 900 °C flow.',
          Watch: 'Two wafers with the same boron implant. The left one is heated to 1200 °C for ten minutes in the middle of the run; the right one stays at 900 °C the whole time. Watch how much further the boron spreads on the left.',
          Try: '',
        },
        'drive-in': {
          Title: 'A drive-in anneal, end to end',
          About: 'Boron driven into silicon in a furnace, with the temperature and the profile as it runs.',
          Watch: 'Boron going into silicon. Watch the front move down, and the curves on the right climb one after another as each sensor sees it arrive.',
          Try: 'Use the furnace setpoint under the canvas: a hundred degrees is worth far more than doubling the time, and you can see that in how fast the sensor curves lift.',
        },
        'line-chiller': {
          Title: 'The line: what the chiller trip did to the floor',
          About: 'The building as a queue: two of three etch chambers down for six hours, three ways the shift can respond, each measured lot by lot against the same week with no trip.',
          Watch: 'The blue line is what happens if nobody decides. Watch the lots at etch shoot up when the chambers drop, and watch how long after the chambers are back the line is still paying for it. Then look at the table: the same fault, three decisions, and what each one cost in dates.',
          Try: 'Switch the load to 95% and watch the same six-hour trip take days to drain. Then switch the call to 08:00 and see what six hours on the wrong tool cost.',
        },
        'bay-airflow': {
          Title: 'The downflow through a bay',
          About: 'The laminar air that carries particles down and away, and what a person standing in it does to it.',
          Watch: 'Watch the air come straight down and turn out along the floor. Whatever it carries goes with it — unless somebody is standing in the way.',
          Try: 'Drag "Downflow velocity" down towards 0.10 m/s and watch the straight column break up. That is the difference between air that sweeps particles away and air that lets them wander.',
        },
      },
      cast: {
        sarah: { Name: 'Sarah Mitchell', Short: 'Sarah', Role: 'Etch technician', Post: 'the ETCH-01 HMI in bay 04' },
        jake: { Name: 'Jake Miller', Short: 'Jake', Role: 'Lithography technician', Post: 'the LITH-03 track HMI in bay 05' },
        emily: { Name: 'Emily Carter', Short: 'Emily', Role: 'CMP technician', Post: 'the CMP-11 HMI in bay 08' },
        nicole: { Name: 'Nicole Brennan', Short: 'Nicole', Role: 'Implant technician', Post: 'the IMP-10 HMI in bay 03' },
        tom: { Name: 'Tom Reynolds', Short: 'Tom', Role: 'Maintenance technician', Post: 'the open panel of THRM-04 in bay 02, up on the stool' },
        owen: { Name: 'Owen Marsh', Short: 'Owen', Role: 'Epitaxy technician', Post: 'the EPI-20 HMI in bay 02' },
        grace: { Name: 'Grace Porter', Short: 'Grace', Role: 'Wafer sort technician', Post: 'the PRB-14 HMI in bay 09' },
        laura: { Name: 'Laura Bennett', Short: 'Laura', Role: 'Metrology technician', Post: 'the MET-05 HMI in bay 07' },
        ryan: { Name: 'Ryan Cooper', Short: 'Ryan', Role: 'Deposition technician', Post: 'the DEP-02 HMI in bay 04' },
        hannah: { Name: 'Hannah Whitaker', Short: 'Hannah', Role: 'EUV scanner technician', Post: 'the EUV-22 HMI in bay 06' },
        brian: { Name: 'Brian Keller', Short: 'Brian', Role: 'CD-SEM metrology technician', Post: 'the SEM-23 HMI in bay 06' },
        claire: { Name: 'Claire Donovan', Short: 'Claire', Role: 'Cryogenics technician', Post: 'the QDT-24 HMI in bay 12' },
        kevin: { Name: 'Kevin Brooks', Short: 'Kevin', Role: 'Shift supervisor', Post: 'the aisle, walking the floor' },
        marcus: { Name: 'Marcus Hale', Short: 'Marcus', Role: 'Etch equipment technician', Post: 'the back of ETCH-01 in the chase behind bay 04, with a rear panel off' },
        rachel: { Name: 'Rachel Dawson', Short: 'Rachel', Role: 'Deposition equipment technician', Post: 'the support rack behind DEP-02, in the chase behind bay 04' },
        dave: { Name: 'Dave Sullivan', Short: 'Dave', Role: 'Facilities electrician', Post: 'the MCC line-up in the chase behind bay 08' },
        megan: { Name: 'Megan Foster', Short: 'Megan', Role: 'Facilities technician', Post: 'the chase walkway behind the ETCH-01 row, on rounds' },
      },
      floor: {
        Walking: 'in the aisle, level with bay {{bay}} ({{tags}}), heading {{toward}}',
        TowardGas: 'east toward the gas farm',
        TowardGowning: 'west toward the gowning airlock',
        AtTool: '{{tag}} in bay {{bay}}',
        Down: '{{tag}} is down for preventive maintenance',
        LayoutBallroom:
          'A ballroom hall: the same tools on one open floor, no walls. Two rows face the main aisle; behind each stands a second row of copies (ETCH-02 behind ETCH-01) facing an outer aisle, unstaffed and never opened, so questions go to the first row. Pumps, chillers, generators and gas cabinets are in the sub-fab. Gowning is at the west end, the gas farm at the east; bays are numbered from 01 at gowning, the same from any aisle.',
        Layout:
          'This is Fab A, a bay-and-chase hall: tool faces set in a bulkhead wall either side of one aisle, the chase behind each wall, an overhead hoist loop and floor robots carrying carriers, the gowning station at the west end and the gas farm at the east end. Bays are numbered along the aisle, 01 nearest gowning.',
      },
      local: {
        Greeting: "Hi, I'm {{name}}. I'm at {{post}}. What do you want to know?",
        Doing: "I'm at {{tag}}, {{state}}. The screen says {{headline}} ({{caption}}).",
        DoingNoTool: "I'm between bays right now, heading down the aisle.",
        About: '{{tag}} is a {{name}} — it does {{process}}. {{blurb}}',
        Screen: 'Right now it reads {{headline}} — {{caption}}. {{rows}}',
        Order: 'The open work order is "{{title}}": {{done}} of {{total}} objectives met.',
        NoOrder: 'No work order is open right now.',
        Down: '{{tag}} is down for PM: panel off, lockout tag on, boat out on the elevator. It goes back up when the checks are signed off.',
        Showing: "I can't run that from here, but I can put it up for you: {{title}}.",
        CantSay: "I can't tell you that from here. Press E at {{tag}} and ask the tool's own assistant — it can run it.",
        CantSayNoTool: "I can't tell you that from here. The tool's own panel can run it — press E at the tool.",
        NotHuman: "I'm not a person — you're talking to the simulator. And no AI model is answering right now either; these lines are canned, straight out of the sim.",
        States: { idle: 'idle', running: 'running', alarm: 'in alarm' },
      },
    },
    hud: {
      OpenLive: 'Click the tool, or press <kbd>E</kbd>, to open it',
      OpenPending: 'Click, or press <kbd>E</kbd>, for station scope — not yet simulated',
      OpenLiveTouch: 'Tap the tool to open it',
      OpenPendingTouch: 'Tap for station scope — not yet simulated',
      Idle: 'idle',
      Open: 'Open',
      Running: 'running {{speed}}x',
      Rate: 'RATE',
      NonUniformity: 'NU',
      Cleared: 'CLEARED',
      Time: 't',
    },

    panel: {
      Close: 'Esc — back to the floor',
      Failed: 'This panel failed to draw ({{message}}). Press Esc to go back to the floor.',
    },

    hmi: {
      Status: 'Status',
      Idle: 'Idle',
      LastStep: 'Last step',
      Down: 'Down',
      PmInProgress: 'PM in progress',
    },

    xr: {
      EnterVr: 'Enter VR',
      ExitVr: 'Exit VR',
      Unavailable: 'VR unavailable',
      ViewInVr: 'VR',
      ViewInVrTitle: 'Look at this in a phone holder',
      HolderHint: 'Tap to recentre · press and hold to leave',
      NoMotion: 'This phone is not reporting which way it is pointed.',
      MotionDenied: 'Motion access was refused. Allow it in the browser’s settings for this site.',
    },

    routes: {
      Button: 'Routes',
      ButtonTitle: 'The order a wafer visits the floor in',
    },

    line: {
      DecideLabelQtime: 'Your call, at the scanner',
      DecideLabelExcursion: 'Your call: how many metrology visits get inspected',
      DecideLabelPm: 'Your call, on the due chamber',
      ProductsLabel: 'Products',
      ProductsOne: 'one',
      ProductsTwo: 'two — 30% double-patterned',
      ChartCtTitleMix: 'Cycle time by product, under reroute (dashed: no trip)',
      ProductsThree: 'three — 25% EUV, 3% qubit chips',
      ProductEuv: 'EUV part',
      ProductQuantum: 'qubit chip',
      NewToolsTitle: 'The scanner, the CD-SEM and the fridge farm, beside the bottleneck',
      ColTool: 'Tool',
      ColInBuilding: 'In the building',
      ColUtilisation: 'Busy',
      ColDown: 'Down',
      ColWait: 'Mean wait for it',
      NewToolsNote: 'A quarter of the starts are a leading-edge part: EUV-22 prints its gate and four lowest metals, and SEM-23 measures every one of those exposures before ETCH-01 is allowed to etch it. Three per cent are qubit chips, which leave the front end after eight operations and then spend two days in a fridge. Read the last column against the third. The fridge farm runs at about half its capacity, far below the etcher, and its lots wait the longest by an order of magnitude: what a lot waits for is a fridge to come free, and a fridge is busy for two days at a time. Waiting scales with how long the job in front of you takes, not only with how busy the tool is. The scanner is the other lesson. The building has one, it is down one hour in eight, and there is nowhere else to send the lot. The trip stops all three water-cooled families at once: two etch chambers in three, the scanner, and every fridge.',
      ProductA: 'single-patterned',
      ProductB: 'double-patterned',
      ChartCtTitleProducts: 'Cycle time by product, under reroute',
      TabQtime: 'The queue-time window',
      TabExcursion: 'The excursion',
      TabPm: 'The maintenance',
      QtimeRun: 'Let it run',
      QtimeGate: 'Gate the scanner',
      QtimeNote: 'The same trip, on a route where the resist has a shelf life: a lot not etched within eight hours of leaving the scanner goes back to be stripped and exposed again. Every lot exposed and then stuck behind a chamber that is down is a lot whose clock is running. Two moves: let it run, or gate the scanner - do not expose a lot that the etcher it is bound for could not etch within the window, given the chambers it has up, so it waits before the clock starts.',
      QtimeChartTitle: 'Lots sent back to the scanner, since the trip',
      ColReworks: 'Lots sent back',
      ColReworksControl: 'With no trip',
      ColReworkHours: 'Scanner-hours lost',
      QtimeFinding: 'Look at the no-trip column first: a queue-time window is a rework machine on its own wherever the etch queue waits past it, which at 85% is a few lots a day and at 95% is most of them. The gate is what makes the window workable - it holds exposures at the scanner, whose queue costs nothing but time, until the etcher can eat them in time - and it costs a few dates at 85% and nothing at 95%. The trip itself adds little: the lots it sends back are the ones already exposed when the chambers dropped, which no policy can save. This is the one case where holding lots is right, and only because it holds them before the clock starts.',
      ExcursionNote: 'No trip this time. At two in the morning one deposition tool goes silently out of control and keeps running. Nothing on the floor says so. Metrology inspects a fraction of its visits; an inspected lot that went through the bad tool is what catches it. Every lot through the tool until then is exposed. Averaged over six weeks with different luck, because one detection is one draw.',
      ExcursionChartTitle: 'What the sampling rate buys, and costs',
      ExcursionChartX: 'fraction of metrology visits inspected',
      ExcursionChartY: 'lots · wait × 10 h',
      ExcursionSeriesExposed: 'lots exposed, mean',
      ExcursionSeriesWait: 'wait at MET-05, × 10 h',
      ColRate: 'Visits inspected',
      ColDetect: 'Caught after',
      ColExposed: 'Lots exposed, mean',
      ColExposedWorst: 'Worst week',
      ColInspections: 'Inspections a day',
      ColMetWait: 'Wait at MET-05',
      ColMetUtil: 'MET-05 busy',
      ExcursionFinding: 'Lots exposed is throughput through the bad tool times the hours until it is caught - Little\'s law from the yield side. Sampling ten times harder catches it in half the time and halves the lots at risk, and it fills the metrology tool: its queue grows with the rate and every lot in the building pays in cycle time. The sampling rate is a decision about which of those two costs the fab would rather carry.',
      PmNow: 'Service it now',
      PmDefer: 'Run on',
      PmNote: 'One chamber of one etcher is due its maintenance at two in the morning. Do it now: four planned hours, tonight. Or run on: a worn chamber that is going to fail, and when it does, the unplanned repair takes twice as long, at an hour nobody chose. A deferral is a bet, so both are judged over eight weeks with different luck, against the same weeks with neither.',
      ColChamberDown: 'Chamber down, mean week',
      ColBeyondControl: 'Beyond a week with neither',
      ColWorstWeek: 'Worst week',
      ColFailed: 'Failed before it was serviced',
      ColDelayLotHours: 'Net lot-hours of delay',
      PmFinding: 'The planned service costs its {{pm}} hours and nothing else. The worn chamber, with its life cut to {{worn}} hours, failed within the week every time, and its unplanned repair took {{repair}} hours against the table\'s {{mttr}} - twice the planned service, at an hour nobody chose, on the bottleneck. The bottleneck\'s hour is the fab\'s hour: every lot-hour of delay in the last two columns is that difference, felt down the line.',
      DecideLabel: 'Your call, while the chambers are down',
      Decided: 'You chose: {{policy}}. It is in the journal.',
      Undecided: 'Not yet decided. The order needs one; the numbers below are the same either way.',
      Button: 'Line',
      ButtonTitle: 'The fab as a line: lots, queues, and what a fault in the sub-fab does to them',
      Title: 'The line — a chiller trip at 02:00',
      Intro: 'Ten bays, one route, a queue at every tool. At two in the morning a chiller in the sub-fab trips: the process cooling water runs warm and two of the three chambers on every etcher lose temperature control. The third keeps running. Lots keep arriving. You are on shift.',
      Bottleneck: 'Bottleneck: {{tag}} — {{n}} etchers of three chambers each, about {{starts}} wafer starts a day',
      LoadLabel: 'Line load',
      Load85: '85% — a healthy line',
      Load95: '95% — a full one',
      CallLabel: 'The call to facilities',
      CallPrompt: 'made at once — chambers back in 6 h',
      CallLate: 'made at 08:00 — chambers back in 12 h',
      ViewLabel: 'View',
      TabScenario: 'The chiller trip',
      TabCurve: 'The operating curve',
      Running: 'Running the line…',
      PolicyReroute: 'Reroute',
      PolicyRerouteNote: 'Everything queues for the surviving chamber, first come first served. What happens if nobody decides.',
      PolicyHold: 'Hold starts',
      PolicyHoldNote: 'Stop starting lots until the chambers are back.',
      PolicyByDate: 'By date',
      PolicyByDateNote: 'Etch runs whoever is nearest their due date next, hot or not.',
      Control: 'No trip',
      ControlNote: 'the same week, with the chiller running.',
      ChartWipTitle: 'Lots at etch beyond the no-trip line',
      ChartWipY: 'lots',
      ChartX: 'hours after the trip',
      ChartCtTitle: 'Cycle time of the lots finishing, day by day',
      ChartCtY: 'hours',
      MarkTrip: 'trip',
      MarkRestore: 'back',
      CostTitle: 'What the fault cost, against the same week with no trip',
      ColPolicy: 'Policy',
      ColPeak: 'Lots at etch, peak',
      ColDrain: 'Queue back to normal',
      ColDelayed: 'Lots delayed',
      ColMeanDelay: 'Mean delay',
      ColDates: 'Dates lost',
      ColHot: 'Hot lots lost',
      ColChamberHours: 'Chamber-hours lost',
      DrainNever: 'not within two weeks',
      HoursAfter: '{{h}} h after restore',
      Hours: '{{h}} h',
      CostNote: 'Measured over the two weeks after the trip, lot by lot against the control: a lot is delayed if it finished later than it did with no trip, and a date is lost if the control met it. The chambers were down {{h}} h.',
      LittleTitle: 'Little\'s law on the steady line',
      LittleLine: 'Before the trip: {{wip}} lots in the line = {{thr}} lots/h × {{ct}} h cycle time (ratio {{ratio}}). WIP, throughput and cycle time cannot move independently.',
      FindingsTitle: 'What the line shows',
      FindingDrain: 'The chambers are back in hours; the queue is not. Every lot that waited at etch is now late for its next tool, and the ones behind it are late for theirs, and the line spends its spare capacity - what is left after the arrivals it already has - working that off. At 85% there is spare capacity. At 95% there is almost none, and the same six hours take days to clear.',
      FindingByDate: 'Running etch by due date loses the fewest dates at either load, because nearest-date is close to oldest-first and oldest-first pushes lots out of a re-entrant line instead of round it again. Its price shows only at 95%: hot lots stop jumping a queue that is now long, and most of them miss. The rule that is right at 85% is the rule that loses the hot lot at 95%.',
      FindingHold: 'Holding starts changes nothing at etch. The lots that will pile up there over the next six hours are already in the building; the lots you did not start were eight days from etch. All a hold does is take six hours of slack from the lots it held.',
      CurveNote: 'Cycle time against load on the healthy line, with no trip. Flat and near the raw process time when the line is half empty, bending through the eighties, steep in the nineties. The chiller trip at 85% and at 95% is two points on this curve seen from inside: the slope is how long a queue takes to clear once it has formed.',
      CurveTitle: 'Cycle time against load',
      CurveX: 'load on the bottleneck',
      CurveY: '× raw',
      CurveSeries: 'healthy line',
      CurveHere: 'this line',
      ColLoad: 'Load',
      ColCt: 'Cycle time',
      ColCtRaw: '× raw process time',
      ColWip: 'Lots in the line',
    },
    finder: {
      Button: 'Find a tool',
      Placeholder: 'Type a tag, a machine, or a process…',
      Hint: '<kbd>↑</kbd><kbd>↓</kbd> choose · <kbd>Enter</kbd> go · <kbd>Esc</kbd> close',
      Empty: 'No tool matches “{{q}}”.',
    },
    place: {
      subfab: {
        Name: 'Sub-fab (plant level)',
        Where: 'Plant room, stair behind FAC-06',
      },
    },
    zone: {
      entry: 'Entry and protocol',
      substrate: 'Substrate — silicon into wafers',
      frontend: 'Front end — building the device',
      metrology: 'Metrology and utilities',
      interconnect: 'Planarisation and interconnect',
      sort: 'Inspection and sort',
      assembly: 'Assembly, packaging and test',
      quantum: 'Quantum devices — the same fab, a different product',
      support: 'Sub-fab and chemical supply',
    },

    /**
     * One entry per station on the floor, each carrying three different things.
     *
     *   Name     the hardware, as it would be written on a purchase order:
     *            "Inductively Coupled Plasma Etcher"
     *   Process  the unit process that tool runs, which is what happens to a
     *            wafer rather than what the machine is called: "Plasma Etch"
     *   Blurb    one line on what it does, for the approach card
     *
     * Name and Process are easy to conflate and must not be. The approach card
     * shows the Process first, because that is what a wafer comes here for.
     */
    station: {
      bonding: {
        Name: 'Die Attach and Wire Bonder',
        Process: 'Assembly',
        Blurb:
          'Force, ultrasonic power and time — a window with a floor and a ceiling. Fully ' +
          'simulated: oxide clearing, cratering, and intermetallic growth.',
      },

      etch: {
        Name: 'Inductively Coupled Plasma Etcher',
        Process: 'Plasma Etch',
        Blurb:
          'Polysilicon gate etch. Fully simulated: reactor transport, sheath physics, surface ' +
          'kinetics.',
      },
      deposition: {
        Name: 'ALD / CVD Deposition Cluster',
        Process: 'Deposition',
        Blurb:
          'Al2O3 by ALD. Fully simulated: precursor transport, site-limited surface chemistry, ' +
          'saturation and the temperature window.',
      },
      litho: {
        Name: 'Immersion Scanner and Coat/Develop Track',
        Process: 'Lithography',
        Blurb:
          'Dose and focus set the critical dimension. Fully simulated: aerial image from ' +
          'diffraction orders, resist chemistry, process window and photon statistics.',
      },
      thermal: {
        Name: 'Vertical Furnace and RTP',
        Process: 'Thermal Processing',
        Blurb:
          'Oxidation, dopant diffusion and activation. Fully simulated: Deal-Grove growth, ' +
          'Fickian diffusion, and thermal budget as a shared account.',
      },
      metrology: {
        Name: 'Ellipsometer and Reflectometer',
        Process: 'Metrology and SPC',
        Blurb:
          'Drift detection and control charts. Fully simulated: reflectance fitting, gauge ' +
          'capability, and the decision to hold a lot or stop a tool.',
      },
      facilities: {
        Name: 'Gas Delivery and Abatement',
        Process: 'Facilities and Safety',
        Blurb:
          'Vacuum, gas delivery and abatement. Fully simulated: conductance, pump-down, and the ' +
          'rate-of-rise test that separates a leak from a wet chamber.',
      },
      gowning: {
        Name: 'Gowning Airlock and Protocol Bay',
        Process: 'Cleanroom Protocol',
        Blurb:
          'You are the largest particle source in the building until you are gowned. Fully ' +
          'simulated: garment penetration as a product, ISO 14644-1 classes, and the work-zone ' +
          'concentration the room monitor cannot see.',
      },
      crystal: {
        Name: 'Czochralski Crystal Puller',
        Process: 'Crystal Growth',
        Blurb:
          'Polysilicon and a seed become a single-crystal ingot. Fully simulated: Scheil ' +
          'segregation down the length, Voronkov’s defect ratio, and the latent-heat ceiling ' +
          'on how fast you can pull.',
      },
      waferprep: {
        Name: 'Slice, Lap and Polish Line',
        Process: 'Wafer Preparation',
        Blurb:
          'An ingot becomes wafers, flat to a micron. Fully simulated: kerf loss, the abrasive ' +
          'damage law shared with DICE-15, Preston polishing, and why bow is a front-to-back ' +
          'imbalance rather than a damage measurement.',
      },
      patterning: {
        Name: 'Self-Aligned Quadruple Patterning Cell',
        Process: 'Multipatterning',
        Blurb:
          'Print coarse, divide twice, and the final pitch is a film thickness rather than ' +
          'anything the scanner resolved. Fully simulated: spacer geometry, pitch walking, and ' +
          'why a perfect mean hides a broken pattern.',
      },
      epi: {
        Name: 'Reduced-Pressure Epitaxy Reactor',
        Process: 'Silicon Epitaxy',
        Blurb:
          'Grows the layer the devices actually live in. Fully simulated: Grove\'s two growth resistances in series, autodoping from the substrate the crystal puller doped, and the slip and haze that police the recipe.',
      },
      clean: {
        Name: 'RCA Wet Clean Bench',
        Process: 'Surface Preparation',
        Blurb:
          'The most-run process in the fab and the least glamorous. Fully simulated: first-order ' +
          'particle removal, native oxide regrowth against the queue clock, and the particle count ' +
          'that becomes the line\'s defect density.',
      },
      implant: {
        Name: 'High-Current Ion Implanter',
        Process: 'Ion Implantation',
        Blurb:
          'The only process in the fab that counts its own atoms. Fully simulated: LSS ranges ' +
          'and straggle, channelling against stage tilt, and the lattice damage the furnace has ' +
          'to anneal out.',
      },
      cmp: {
        Name: 'Chemical Mechanical Planariser',
        Process: 'Planarisation',
        Blurb:
          'Flatness is what makes multilevel metal possible at all. Fully simulated: Preston ' +
          'removal, pattern density, dishing and erosion, and the focus budget it hands to ' +
          'lithography.',
      },
      plating: {
        Name: 'Copper Electroplating Cell',
        Process: 'Interconnect',
        Blurb:
          'Copper is plated rather than etched, and three additives make it fill. Fully ' +
          'simulated: Faraday’s law, curvature-enhanced accelerator coverage, the terminal ' +
          'effect, and an overburden handed to CMP-11.',
      },
      inspection: {
        Name: 'Optical Patterned-Wafer Inspection and Review',
        Process: 'Defect Inspection',
        Blurb:
          'Sensitivity against nuisance rate. Fully simulated: Rayleigh scattering, a power-law ' +
          'size distribution, and why every inspection number is an underestimate.',
      },
      probe: {
        Name: 'Wafer Probe and Sort',
        Process: 'Wafer Test',
        Blurb:
          'Where the wafer becomes die, and yield becomes a number. Fully simulated: Poisson ' +
          'and negative-binomial yield, map signatures, and the losses that belong to the probe ' +
          'card rather than to the wafer.',
      },
      dicing: {
        Name: 'Backgrind and Singulation',
        Process: 'Wafer Thinning and Dicing',
        Blurb:
          'Thin enough to stack, fragile enough to matter. Fully simulated: Griffith fracture, grinding damage, Weibull die strength, and chipping against the street budget.',
      },
      packaging: {
        Name: 'Mould, Underfill and Advanced Packaging',
        Process: 'Packaging',
        Blurb:
          'Where a good die becomes a bad product, mechanically. Fully simulated: Timoshenko ' +
          'warpage, moisture uptake and popcorning at reflow.',
      },
      finaltest: {
        Name: 'Final Test and Burn-In',
        Process: 'Final Test',
        Blurb:
          'How much test is enough is a calculation, not a preference. Fully simulated: Arrhenius ' +
          'acceleration, the bathtub, and the guard band trade.',
      },
      euv: {
        Name: 'EUV Scanner with Tin Source',
        Process: 'EUV Lithography',
        Blurb:
          'The most important tool in a modern fab, and quantum from end to end. Fully simulated: tin ionization solved from the Schrödinger equation, the plasma that makes 13.5 nm light, a Mo/Si mirror by transfer matrix, and photoelectron cascades printing rough edges.',
      },
      ebeam: {
        Name: 'CD-SEM and E-Beam Inspection',
        Process: 'Electron-Beam Metrology',
        Blurb:
          'Measuring what light cannot resolve. Fully simulated: Fowler-Nordheim emission from the gun on the THRM-04 tunnelling engine, the probe diffraction and aberrations allow, and a Monte Carlo linescan whose edges the tool has to decide.',
      },
      quantum: {
        Name: 'Cryogenic Quantum Device Test',
        Process: 'Qubit Characterisation',
        Blurb:
          'Same fab, same tools, different product. Fully simulated: the Josephson junction’s oxide by transfer matrix, the transmon Hamiltonian solved exactly, frequency-collision yield, and valley splitting in the silicon well EPI-20 grows.',
      },
      gas: {
        Name: 'Gas Bunker and Chemical Distribution',
        Process: 'Gas and Chemical Handling',
        Blurb:
          'Silane, arsine, HF. The station where being wrong is not a scrap issue. Fully ' +
          'simulated: purge dilution as a geometric series, release dilution against published ' +
          'exposure limits, and how much warning a detector actually buys.',
      },

    },

    assistant: {
      // Suggestion chips, keyed by intent id. The English example stays
      // in the intent as the source of record and is what actually routes.
      topic: {
        'ion-enhanced etching': 'ion-enhanced etching',
        'sheath and ion energy': 'sheath and ion energy',
        'ion energy distribution': 'ion energy distribution',
      },
      example: {
        'etch-qual': 'Is the tool qualified to run product?',
        'cmp-qual': 'Is the polisher qualified to run product?',
        'litho-qual': 'Is the scanner qualified to print product?',
        'thermal-qual': 'Is the furnace qualified to run product?',
        selectivity: 'Can I just turn the bias up to hit my rate target?',
        'cmp-overpolish': 'How much over-polish should I run?',
        'cmp-speeds': 'Why do the platen and the carrier turn at the same speed?',
        'cmp-density': 'Why does the bond pad dish when the dense array does not?',
        'dep-saturation': 'Is my dose actually saturated?',
        'dep-window': 'What temperature should this run at?',
        'dep-purge': 'Can I shorten the purge to get throughput?',
        'dep-conformality': 'Why is the bottom of my trench thinner?',
        'dep-uniformity': 'Why is my film thicker at the edge?',
        'fac-conductance': 'Pump-down is slow — do we need a bigger pump?',
        'fac-leak': 'Base pressure is high — leak or outgassing?',
        'edge-fast': 'Why is my wafer etching faster at the edge?',
        loading: 'Why does the same recipe run faster on a different product?',
        sheath: 'What actually sets the ion energy at the wafer?',
        endpoint: 'How do I know when the etch is done?',
        'what-if': 'What happens if I raise the pressure?',
        diagnose: 'Nothing was changed — why is the etch slow?',
        'litho-dose-focus': 'My CD is off target — is it dose or focus?',
        'litho-window': 'Does this feature have a usable process window?',
        'litho-stochastics': 'Why do identical exposures print differently?',
        'metro-trust': 'Can I trust this thickness reading?',
        'metro-capability': 'Is this gauge good enough for the job?',
        'metro-signal': 'The chart alarmed — is it real?',
        'thermal-budget': 'Nobody changed the implant — why did the junction move?',
        'thermal-oxide': 'Why does the oxide stop growing?',
        'thermal-junction': 'How do I get a shallower junction without losing sheet resistance?',
        'thermal-lateral': 'Does the drive-in only push dopant down?',
        'bond-window': 'What is the bonding window, and what happens at each end of it?',
        'bond-wires': 'Why does the same bond recipe yield worse on a part with more wires?',
        'bond-imc': 'What is the intermetallic doing to my bonds over time?',
        'clean-longer': 'Should I just run SC-1 longer to get the wafer cleaner?',
        'clean-queue': 'Does it matter how long the wafer waits after the HF dip?',
        'clean-metals': 'What is the SC-2 bath actually for?',
        'gown-monitor': 'The room monitor says we are in class — is my wafer clean?',
        'gown-garments': 'Does gowning properly actually make a measurable difference?',
        'gown-recovery': 'How long does the bay take to recover after an upset?',
        'cz-segregation': 'Why is the resistivity different at the two ends of the ingot?',
        'cz-voronkov': 'How fast can I pull this crystal?',
        'cz-oxygen': 'Where does the oxygen in the wafer come from?',
        'dice-thin': 'How thin can I grind these wafers before they start breaking?',
        'dice-chipping': 'Can I cut faster without chipping into the die?',
        'dice-wheel': 'Does a finer grinding wheel pay for itself?',
        'euv-dose': 'What does raising the dose cost in wafers per hour?',
        'euv-power': 'How much faster would the scanner run with twice the source power?',
        'sem-landing': 'Why not run the CD-SEM at a higher landing energy for a sharper image?',
        'sem-current': 'Would more beam current make the CD measurement repeat better?',
        'cryo-attenuation': 'How much attenuation does the drive line need?',
        'cryo-frequency': 'Why are qubits built at 5 GHz rather than 1 GHz?',
        'epi-hotter': 'Why does raising the temperature stop speeding up the growth?',
        'epi-bake': 'Can I shorten the H2 bake to save cycle time?',
        'epi-faster': 'Can I just raise the TCS and grow the layer faster?',
        'ft-guardband': 'What does a guard band cost me?',
        'ft-burnin': 'Is burn-in worth the time it costs?',
        'ft-coverage': 'How much test time is enough?',
        'gas-purge': 'How many purge cycles before it is safe to open the panel?',
        'gas-detector': 'Will the detector warn me in time to get out?',
        'gas-abatement': 'What comes out of the stack?',
        'imp-channelling': 'Why is my junction deeper than the tables say?',
        'imp-damage': 'At what dose does the implant turn the silicon amorphous?',
        'imp-profile': 'How do I make the junction shallower?',
        'insp-threshold': 'Should I lower the threshold to catch more defects?',
        'insp-limit': 'Why did we not see the defect that killed the die?',
        'insp-coverage': 'Do I need to scan the whole wafer?',
        'pkg-popcorn': 'Why did a good die fail at reflow?',
        'pkg-warpage': 'Why does the package warp, and when does it matter?',
        'pkg-mould': 'What does the post-mould cure actually do?',
        'saqp-walk': 'Why do my spaces alternate wide and narrow?',
        'saqp-pitch': 'What actually sets the final pitch?',
        'saqp-collapse': 'Why did this recipe collapse?',
        'ecd-fill': 'Why is there a void in my trench?',
        'ecd-terminal': 'Why is the copper thicker at the wafer edge?',
        'ecd-rate': 'How long do I plate to get the thickness I need?',
        'prb-area': 'Why does a bigger die yield so much worse?',
        'prb-clustering': 'Why does the Poisson yield model under-predict my line?',
        'prb-signature': 'Is this yield loss the wafer or the probe card?',
        'wpr-kerf': 'How much of the ingot ends up as wafers?',
        'wpr-damage': 'How much silicon do I have to remove to get the saw damage out?',
        'wpr-flatness': 'What makes a wafer bow, and why does it matter?',
      },
      Who: 'Inquiry assistant',
      OffThread: 'Experiments run off the main thread, so the fab stays walkable.',
      InlineThread: 'Experiments run inline; the view may hitch briefly while one runs.',
      Placeholder: 'Ask about what you are seeing…  (Enter to send)',
      Run: 'Run experiment',
      Designing: 'Designing experiment…',
      RunningCondition: 'Running condition {{done}} of {{total}} — {{label}}',
      Promise:
        'Every answer I give is assembled from numbers a run actually produced. If I cannot turn ' +
        'your question into an experiment, I will say so rather than invent one.',
      Hypothesis: 'Hypothesis',
      Design: 'Design',
      Measured: 'Measured',
      Findings: 'Findings',
      CanInvestigate: 'What I can investigate',
      ConditionsRun: 'Conditions run',
      Condition: 'Condition',
      Descend: '↓ Descend to the microscopic scale — {{topic}}',
      NextQuestion: 'Your next question',
      NoMatch: 'No matching experiment',
      CannotRun:
        'I can\'t turn "{{question}}" into an experiment I know how to run yet, so I am not going ' +
        'to guess at an answer. Everything I say comes from running the simulation, and I have ' +
        'nothing to run for that one. Here is what I can investigate on this tool:',
      NoMatchFollowUp: 'Try one of those, or rephrase in terms of what you saw on the wafer.',

      // Nothing matched exactly, but one experiment is close. Offered rather
      // than run: the student decides whether it is the question they meant.
      NearestMatch: 'No exact match — nearest experiment',
      NearestLead:
        'I can\'t run "{{question}}" exactly as you asked it. The nearest thing I know how to run ' +
        'on this tool is "{{example}}" — {{summary}} That is not the same question as yours, so ' +
        'decide whether it answers you before you take the result.',
      RunNearest: 'Run that instead',
      NearestFollowUp:
        'If that is not what you meant, say it in terms of what you saw on the wafer and I will ' +
        'look again.',
    },

    auth: {
      Title: 'Sign in to use the assistant',
      Why:
        'Signing in is what puts a name on the session — the questions you ask, the experiments ' +
        'they turn into, and the work you will be able to pick up again next time. The rest of ' +
        'the fab needs no account: the tools, the wafer and the solver are open to anyone ' +
        'standing on the floor.',
      Google: 'Continue with Google',
      Guest: 'Continue as a guest',
      SignIn: 'Sign in',
      AsMe: 'Sign in as me',
      Anonymously: 'Sign in anonymously',
      MenuWhy: 'An account is what will let your work follow you to the next session.',
      GuestScope: 'Anonymous — this browser only',
      ConfirmGuestTitle: 'Sign in anonymously?',
      ConfirmGuestBody:
        'An anonymous account is gone the moment you sign out, and it cannot be recovered — ' +
        'anything saved under it goes with it. It does not exist on any other browser or ' +
        'machine either. Use it for a single sitting, on this computer.',
      ConfirmSignOutTitle: 'Sign out of this anonymous account?',
      ConfirmSignOutBody:
        'This account cannot be recovered once you sign out, and any work kept under it will ' +
        'be lost. Signing in as yourself instead is what makes work last.',
      Yes: 'Yes',
      No: 'No',
      GuestNote:
        'A guest account carries no name and no email. It works exactly like a signed-in one and ' +
        'lasts as long as this browser keeps it.',
      Checking: 'Checking who is signed in…',
      Retry: 'Try again',
      SignedInAs: 'Signed in as {{name}}',
      SignedInAsGuest: 'Signed in as a guest',
      SignOut: 'Sign out',
      GuestName: 'Guest',
      SignedIn: 'Signed in',
      ErrNetwork: 'No network. Signing in needs a connection — the fab and the solver do not.',
      ErrNotEnabled: 'That sign-in method is not enabled for this project yet.',
      ErrDomain: 'Sign-in is not authorised for {{host}}. Add it to the Firebase authorised domains.',
      ErrTooMany: 'Too many attempts from this machine. Wait a minute and try again.',
      ErrPopupBlocked:
        'The browser blocked the sign-in window. Allow popups for this site, or use the guest door.',
      ErrUnavailable: 'Sign-in is unavailable — the app could not reach Firebase.',
    },

    /**
     * Recipe knobs and stored recipes.
     *
     * Preset names (BASELINE-SI-01 and the rest) are recipe codes as they would
     * appear in a real tool's recipe book, so they stay in the data untranslated.
     * Only the note explaining what the recipe does is localised.
     */
    recipe: {
      // Preset display labels. Code-like ones keep their code everywhere.
      presetLabel: {
        // Etch (ETCH-01) recipe codes: rendered without a namespace.
        baseline: 'BASELINE-SI-01',
        lowPressure: 'LO-P STARVED',
        highPressure: 'HI-P DIFFUSION-LIMITED',
        highBias: 'HI-BIAS ANISOTROPIC',
        smallOpenArea: 'LOW-LOAD PRODUCT',
        starvedFlow: 'LO-FLOW EXCURSION',
        facilities: {
          vacNominal: 'HEALTHY CHAMBER',
          vacThrottled: 'CONDUCTANCE LIMITED',
          vacLeak: 'REAL LEAK',
          vacDirty: 'WET CHAMBER',
          vacBiggerPump: 'BIGGER PUMP, SAME PIPE',
        },
        ale: {
          baseline: 'BASELINE-ALE-SI',
          tooEnergetic: 'ION ENERGY TOO HIGH',
          tooGentle: 'ION ENERGY TOO LOW',
          starvedDose: 'DOSE BELOW SATURATION',
          noPurge: 'NO PURGE',
          highAspect: 'HIGH ASPECT RATIO',
          nitride: 'NITRIDE',
        },
        bonding: {
          baseline: 'BASELINE-AUAL',
          coldBond: 'UNDER-BONDED',
          overDriven: 'OVER-DRIVEN',
          longCure: 'EXTENDED CURE',
          highPinCount: 'HIGH PIN COUNT',
        },
        dicing: {
          baseline: 'BASELINE-200UM',
          thinStack: 'THIN FOR STACKING',
          noRelief: 'NO STRESS RELIEF',
          fastFeed: 'FAST FEED',
          narrowStreet: 'NARROW STREETS',
        },
        packaging: {
          baseline: 'BASELINE-BGA',
          thinMould: 'THIN MOULD CAP',
          bigBody: 'LARGE BODY',
          floorLifeBlown: 'LEFT OUT OF THE DRY PACK',
          hotReflow: 'HOT REFLOW',
        },
        finaltest: {
          baseline: 'BASELINE-COMMERCIAL',
          noBurnIn: 'NO BURN-IN',
          automotive: 'AUTOMOTIVE',
          noGuardBand: 'NO GUARD BAND',
          shortTest: 'SHORT TEST',
        },
        clean: {
          baseline: 'BASELINE-RCA',
          noHf: 'NO HF-LAST',
          queueBlown: 'QUEUE TIME BLOWN',
          aggressive: 'AGGRESSIVE SC-1',
          shortClean: 'SHORT CLEAN',
        },
        gowning: {
          baseline: 'BASELINE-ISO5-BAY',
          noHood: 'HOOD LEFT OFF',
          wrongOrder: 'GOWNED OUT OF ORDER',
          crowded: 'CROWDED BAY',
          straightThrough: 'STRAIGHT THROUGH THE AIRLOCK',
          lowFlow: 'REDUCED AIR CHANGES',
          longExposure: 'WAFER LEFT OUT',
        },
        cmp: {
          baseline: 'BASELINE-CU-DAMASCENE',
          underPolished: 'NO OVER-POLISH',
          overPolished: 'HEAVY OVER-POLISH',
          mismatchedSpeeds: 'SPEEDS MISMATCHED',
          hardPress: 'HIGH DOWN FORCE',
        },
        crystal: {
          baseline: 'BASELINE-CZ300-B',
          perfectSilicon: 'PERFECT SILICON',
          antimony: 'ANTIMONY SUBSTRATE',
          pulledTooFast: 'PULLED TOO FAST',
          tightSpec: 'TIGHT RESISTIVITY SPEC',
          magneticCz: 'MAGNETIC CZ',
        },
        epi: {
          epiDevice: 'DEVICE LAYER',
          epiSlow: 'KINETIC PROBE',
          epiThin: 'THIN FAST LAYER',
        },
        gas: {
          baseline: 'BASELINE-ARSINE-PANEL',
          lazyPurge: 'LAZY PURGE',
          silaneRelease: 'SILANE RELEASE',
          chlorinePool: 'CHLORINE, CEILING DETECTOR',
          dilutedArsine: 'DILUTED ARSINE',
          slowDetector: 'SLOW DETECTOR',
          ammoniaHabit: 'AMMONIA — THE BAD HABIT',
        },
        implant: {
          baseline: 'BASELINE-BORON-SD',
          zeroTilt: 'ZERO TILT',
          shallow: 'SHALLOW EXTENSION',
          deepWell: 'DEEP WELL',
          hotImplant: 'HOT IMPLANT',
        },
        patterning: {
          baseline: 'BASELINE-SAQP-40',
          pitchWalk: 'PITCH WALK',
          overTrimmed: 'MANDREL OVER-TRIMMED',
          thickSpacer: 'SPACER TOO THICK',
          doubleOnly: 'SADP (ONE PASS)',
          aggressive: 'AGGRESSIVE — 32 nm PITCH',
        },
        plating: {
          baseline: 'BASELINE-CU-DAMASCENE',
          noAccelerator: 'NO ACCELERATOR',
          noSuppressor: 'NO SUPPRESSOR',
          thinSeed: 'THIN SEED',
          hotPlate: 'HIGH CURRENT',
          overPlated: 'OVER-PLATED',
          highAspect: 'HIGH ASPECT RATIO',
        },
        inspection: {
          baseline: 'BASELINE-DARKFIELD',
          hotScan: 'HOT SCAN',
          coldScan: 'COLD SCAN',
          shortWave: 'SHORTER WAVELENGTH',
          sampled: 'SAMPLED SCAN',
        },
        probe: {
          baseline: 'BASELINE-SORT',
          bigDie: 'LARGE DIE',
          poissonForecast: 'POISSON FORECAST',
          heavyCluster: 'HEAVILY CLUSTERED',
          wideEdge: 'WIDE EDGE EXCLUSION',
        },
        thermalBaseline: 'GATE OXIDE + RTA',
        thermalWetField: 'WET FIELD OXIDE',
        thermalFurnaceAnneal: 'FURNACE ANNEAL',
        thermalShallowJunction: 'SHALLOW JUNCTION',
        thermalArsenicSd: 'ARSENIC SOURCE/DRAIN',
        waferprep: {
          baseline: 'BASELINE-300MM',
          thinWire: 'THIN WIRE',
          singleSided: 'SINGLE-SIDED POLISH',
          shortEtch: 'SHORT ETCH',
          shortPolish: 'SHORT POLISH',
          coarseSaw: 'COARSE SAW',
          thinWafer: 'THIN WAFER',
        },
      },
      knob: {
        // Labels for the tools added since the first catalogue pass. The
        // limits objects carry the same English as the source of record;
        // these entries exist so the other languages have a key to fill.
        bedLoadFraction: 'Mixed-bed life used',
        uvIntensity: 'UV oxidiser output',
        pouTempC: 'Sample temperature',
        inletTocPpb: 'TOC from RO',
        modifySeconds: 'Chlorine dose',
        modifyPressureMtorr: 'Modify pressure',
        purgeSeconds: 'Purge',
        ionEnergyEv: 'Ion energy',
        removeSeconds: 'Removal step',
        ionFlux: 'Ion flux',
        forceGf: 'Bond force',
        ultrasonicMw: 'Ultrasonic power',
        bondMs: 'Bond time',
        stageTempC: 'Stage temperature',
        cureMinutes: 'Epoxy cure time',
        cureTempC: 'Cure temperature',
        wiresPerDie: 'Wires per die',
        targetThicknessUm: 'Wafer thickness',
        finalGrit: 'Final wheel grit',
        feedRateMmS: 'Dicing feed rate',
        streetWidthUm: 'Street width',
        diePerWafer: 'Die per wafer',
        mouldThicknessUm: 'Mould cap',
        substrateThicknessUm: 'Substrate',
        mouldTempC: 'Mould temperature',
        postCureMinutes: 'Post-mould cure',
        floorLifeHours: 'Floor life before reflow',
        humidityPct: 'Ambient humidity',
        reflowPeakC: 'Reflow peak',
        bodySizeMm: 'Body size',
        useTempC: 'Specified use temperature',
        burnInTempC: 'Burn-in temperature',
        burnInHours: 'Burn-in duration',
        hotTestC: 'Hot test',
        coldTestC: 'Cold test',
        guardBandFraction: 'Guard band',
        testSeconds: 'Test time per device',
        lotSize: 'Lot size',
        sc1Minutes: 'SC-1 time',
        sc1TempC: 'SC-1 temperature',
        megasonicW: 'Megasonic power',
        sc2Minutes: 'SC-2 time',
        hfSeconds: 'HF dip',
        hfDilution: 'HF dilution',
        queueMinutes: 'Queue after HF',
        incomingParticlesCm2: 'Incoming particles',
        incomingMetal: 'Incoming metal',
        occupants: 'People in the bay',
        activity: 'Activity level',
        airChangesPerHour: 'Bay air changes',
        filterCoverage: 'Filter coverage',
        filterEfficiency: 'Filter efficiency',
        bayVolumeM3: 'Bay volume',
        downflowMs: 'Downflow velocity',
        airlockMinutes: 'Airlock dwell',
        waferExposureMinutes: 'Wafer exposure',
        particleSizeUm: 'Specified particle size',
        downForceKPa: 'Down force',
        platenRpm: 'Platen speed',
        carrierRpm: 'Carrier speed',
        slurryFlowMlMin: 'Slurry flow',
        overpolishPct: 'Over-polish',
        overburdenNm: 'Incoming overburden',
        incomingStepNm: 'Incoming step height',
        meltDopingCm3: 'Melt doping',
        chargeKg: 'Charge mass',
        diameterMm: 'Ingot diameter',
        pullRateMmMin: 'Pull rate',
        gradientKCm: 'Axial gradient',
        crucibleRpm: 'Crucible rotation',
        crystalRpm: 'Crystal rotation',
        magneticFieldT: 'Magnetic field',
        resistivityTolerance: 'Resistivity tolerance',
        targetResistivity: 'Target resistivity',
        cylinderPercent: 'Cylinder concentration',
        purgeCycles: 'Purge cycles',
        purgeRatio: 'Ratio per cycle',
        leakRateSccm: 'Leak rate',
        detectorThreshold: 'Alarm threshold',
        detectorResponseS: 'Detector response',
        energyKeV: 'Energy',
        dose: 'Dose',
        tiltDeg: 'Tilt',
        twistDeg: 'Twist',
        beamCurrentMa: 'Beam current',
        waferTempC: 'Wafer temperature',
        mandrelPitchNm: 'Mandrel pitch',
        mandrelCdNm: 'Mandrel CD, printed',
        mandrelTrimNm: 'Mandrel trim',
        spacer1Nm: 'Spacer 1 thickness',
        spacer2Nm: 'Spacer 2 thickness',
        spacerOveretch: 'Spacer over-etch',
        currentDensityMaCm2: 'Current density',
        seconds: 'Plating time',
        seedThicknessNm: 'Seed thickness',
        trenchWidthNm: 'Trench width',
        trenchDepthNm: 'Trench depth',
        suppressor: 'Suppressor',
        accelerator: 'Accelerator',
        leveler: 'Leveler',
        copperGL: 'Copper sulphate',
        acidGL: 'Acid',
        wavelengthNm: 'Wavelength',
        thresholdSigma: 'Detection threshold',
        pixelNm: 'Pixel size',
        scanCoverage: 'Wafer coverage',
        reviewSample: 'SEM review sample',
        designRuleNm: 'Design rule',
        incomingDensityCm2: 'Incoming defects',
        dieAreaCm2: 'Die area',
        edgeExclusionMm: 'Edge exclusion',
        clusterAlpha: 'Clustering',
        touchdownsPerClean: 'Touchdowns per clean',
        parametricGuardBand: 'Parametric guard band',
        wireDiameterUm: 'Wire diameter',
        sawGrit: 'Saw grit',
        lapGrit: 'Lap grit',
        lapRemovalUm: 'Lap removal per side',
        etchRemovalUm: 'Etch removal per side',
        polishPressureKPa: 'Polish pressure',
        polishRpm: 'Polish speed',
        polishMinutes: 'Polish time',
        ingotLengthMm: 'Usable ingot length',
        bakeMinutes: 'H2 bake time',
        epiTempC: 'Growth temperature',
        tcsTorr: 'TCS partial pressure',
        epiMinutes: 'Growth time',
        epiDopingCm3: 'Layer doping',
        h2Slm: 'Hydrogen carrier',
        pressureMtorr: 'Chamber pressure',
        sourcePowerW: 'Source RF power',
        biasPowerW: 'Bias RF power',
        flowSccm: 'Total gas flow',
        cf4Fraction: 'CF4 fraction',
        wafertempC: 'Wafer temperature',
        openAreaFraction: 'Open area',

        // Lithography.
        doseMjCm2: 'Exposure dose',
        focusNm: 'Focus offset',
        pitchNm: 'Pitch',
        pebBlurNm: 'PEB diffusion length',
        resistContrast: 'Resist contrast',
        developThreshold: 'Develop threshold',

        // Thermal processing.
        oxideTempC: 'Oxidation temperature',
        oxideMinutes: 'Oxidation time',
        implantDose: 'Implant dose',
        implantKeV: 'Implant energy',
        annealTempC: 'Anneal temperature',
        annealSeconds: 'Anneal time',

        // Facilities: the vacuum system rather than a recipe, but the same bank.
        volumeL: 'Chamber volume',
        pumpSpeedLs: 'Pump speed',
        lineDiameterCm: 'Line diameter',
        lineLengthCm: 'Line length',
        leakThroughput: 'Leak throughput',
        outgassingInitial: 'Outgassing at t=0',
        outgassingTau: 'Outgassing decay time',
      },
      preset: {
        facilities: {
          vacNominal: 'Short fat line, tight seals. Conductance is not the limit and there is no leak.',
          vacThrottled: 'Same pump behind a 5 cm line two metres long. The chamber never sees the pump speed.',
          vacLeak: 'A pinched door seal on a chamber that has already pumped overnight, so the walls are dry and only the leak is left. Base pressure sticks and the rate-of-rise never bends. Note the low outgassing: you cannot leak-check a chamber you opened this morning.',
          vacDirty: 'Opened to atmosphere and reassembled damp. Heavy outgassing that pumps away eventually.',
          vacBiggerPump: 'Three times the pump on the throttled line — the expensive wrong answer.',
        },
        ale: {
          baseline:
            'Qualified ALE: saturated chlorine dose, 28 eV argon — the middle of the ' +
            'window. About 0.3 nm a cycle, and two orders of magnitude slower than the ' +
            'RIE recipe in the same chamber.',
          tooEnergetic:
            'Sixty electron-volts to speed it up. Above the substrate sputter threshold, ' +
            'so unmodified silicon is now being removed continuously. Faster, and no ' +
            'longer ALE — the synergy number says so before the profile does.',
          tooGentle:
            'Twelve electron-volts. Below the threshold for the modified layer, so the ' +
            'chlorine goes on and nothing comes off. The chamber looks like it is ' +
            'working.',
          starvedDose:
            'Half a second of chlorine. Coverage is incomplete, so etch per cycle now ' +
            'depends on how much dose reached each part of the wafer — which is exactly ' +
            'the uniformity problem ALE was adopted to escape.',
          noPurge:
            'The two half-cycles overlap, so part of the etching is continuous. It still ' +
            'etches, and it is no longer layer by layer.',
          highAspect:
            'A 50:1 feature on the same recipe. The removal half is fine — ions are ' +
            'directional — but the neutral dose has to reach the bottom, and at this ' +
            'aspect ratio it does not.',
          nitride:
            'Same recipe, different film. Chlorine chemistry is not universal — nitride ' +
            'modifies less readily, so the etch per cycle drops and the recipe needs ' +
            're-qualifying.',
        },
        bonding: {
          baseline:
            'Qualified thermosonic gold ball bond on aluminium pads.',
          coldBond:
            'Ultrasonic power cut by half. The oxide survives, so most of the interface ' +
            'never welds.',
          overDriven:
            'Power and force both raised to force the bond through. It welds, and then it ' +
            'keeps going into the silicon.',
          longCure:
            'Four hours at 200 C to be sure the epoxy is through. The intermetallic grows ' +
            'the whole time, and it does not stop being thick afterwards.',
          highPinCount:
            '480 wires on one die. A per-wire yield that looked excellent stops looking ' +
            'excellent when you raise it to the power of 480.',
        },
        dicing: {
          baseline:
            'Qualified flow: fine wheel, stress relief, blade dicing at a modest feed.',
          thinStack:
            '50 um for a stacked package. Everything downstream gets more fragile for it.',
          noRelief:
            'Grind and go. Saves 45 seconds a wafer and leaves the grinding damage in, ' +
            'which is a strength problem nobody sees until the mould press.',
          fastFeed:
            'Feed doubled for throughput. Chipping goes as the square root of it.',
          narrowStreet:
            'Streets cut to 40 um to fit more die on the wafer. The kerf and the chipping ' +
            'have not got any smaller.',
        },
        packaging: {
          baseline:
            'Qualified wirebond BGA: balanced mould and substrate, dry-packed, standard ' +
            'reflow.',
          thinMould:
            'Cap cut to 250 um for a height-limited slot. The stack is now badly ' +
            'unbalanced and it warps for it.',
          bigBody:
            'A 40 mm body. Warpage goes as the SQUARE of the span, so this is not a small ' +
            'change.',
          floorLifeBlown:
            'Three weeks on the floor at 70% humidity before reflow. The clock printed on ' +
            'the bag was not decoration.',
          hotReflow:
            'Peak pushed to 268 C to guarantee wetting. Everything moisture-related gets ' +
            'worse.',
        },
        finaltest: {
          baseline:
            '48 h at 125 C, hot and cold test, a 10% guard band.',
          noBurnIn:
            'Skip it entirely for throughput. Cheap, fast, and every infant-mortality ' +
            'part ships to a customer.',
          automotive:
            '168 h at 150 C with a wide guard band. Escapes are recalls here, so overkill ' +
            'is cheap.',
          noGuardBand:
            'Ship everything that measures inside the limit. Maximum yield, and every ' +
            'escape is a return.',
          shortTest:
            'Test time cut to a second for capacity. Coverage falls with it.',
        },
        clean: {
          baseline:
            'Full RCA with a short HF-last and a 20 minute queue to the furnace.',
          noHf:
            'Leaves the chemical oxide on. Right before a deposition that wants one; ' +
            'wrong before a gate oxide.',
          queueBlown:
            'HF-last, then the wafer sat for four hours. The native oxide is back and the ' +
            'clean was for nothing.',
          aggressive:
            'Thirty minutes at 80 C with the megasonics wide open. Almost no particles ' +
            'left, and a surface you can measure the roughness of.',
          shortClean:
            'Two minutes of SC-1 for throughput. Most of the particles are still there.',
        },
        gowning: {
          baseline:
            'Four people, fully gowned, 500 air changes an hour. The protocol as written, ' +
            'and the number every downstream tool assumes.',
          noHood:
            'One garment missing. Not one fifth of the protection — the whole factor the ' +
            'hood was contributing, because the garments multiply.',
          wrongOrder:
            'Every garment on, none of them in the right sequence. The suit is complete ' +
            'and it has already been contaminated by the process of putting it on.',
          crowded:
            'Twelve people in for a tool install. Generation is linear in occupancy and ' +
            'nothing about the ventilation changed.',
          straightThrough:
            'No dwell. The airlock exists to sweep what the fresh gown is shedding; ' +
            'walking through it without stopping skips the only thing it does.',
          lowFlow:
            'Fan speed cut to 150 ACH to save energy. Concentration is generation over ' +
            'ventilation, so this does exactly what tripling the occupancy would.',
          longExposure:
            'A lot left open on a bench for half an hour. The concentration is unchanged ' +
            'and perfectly in class; the wafer collects for thirty minutes instead of ' +
            'two.',
        },
        cmp: {
          baseline:
            'Qualified copper polish: matched platen and carrier speed, 20% over-polish.',
          underPolished:
            'Stops the instant the endpoint trace moves. On a perfectly uniform tool that ' +
            'is just about survivable; on any real one, wherever the wafer polished slow ' +
            'still has metal bridging the lines, which is a short, which is a dead die.',
          overPolished:
            'Double the over-polish to be safe about clearing. Every wide line dishes for ' +
            'it.',
          mismatchedSpeeds:
            'Carrier at half the platen speed. Relative velocity stops being uniform ' +
            'across the wafer and a radial signature appears that pressure trimming ' +
            'cannot remove.',
          hardPress:
            'Force up to 40 kPa for throughput. Faster, and every defect mechanism scales ' +
            'with it.',
        },
        crystal: {
          baseline:
            'Boron, 150 kg, 300 mm. Pulled at 1.0 mm/min against a 30 K/cm gradient — ' +
            'vacancy side of the Voronkov window, which is where most production silicon ' +
            'lives.',
          perfectSilicon:
            'Pull rate cut to sit on Voronkov’s ratio: neither vacancies nor ' +
            'interstitials dominate. Costs growth rate and therefore money, and it is ' +
            'what a gate-oxide customer pays for.',
          antimony:
            'Heavily doped n+ substrate. k = 0.023, so the melt enriches almost ' +
            'forty-fold and most of the ingot misses any tight resistivity window. ' +
            'Segregation, at its most brutal.',
          pulledTooFast:
            'Two and a half millimetres a minute on an unchanged gradient. Past the ' +
            'latent-heat ceiling: the interface cannot stay planar and the crystal loses ' +
            'structure.',
          tightSpec:
            'Same crystal, tolerance narrowed to five per cent. Nothing about the growth ' +
            'changed and most of the ingot is now unsellable — the specification is the ' +
            'yield.',
          magneticCz:
            'A 0.3 T field damps melt convection, so far less crucible oxygen reaches the ' +
            'interface. Low-oxygen silicon — and with it, no internal gettering to fall ' +
            'back on.',
        },
        epi: {
          epiDevice:
            'The production point: deep in the transport plateau, where the bubbler sets ' +
            'the rate and the lamps are forgiven. Four microns of lightly doped silicon ' +
            'over whatever CZ-07 grew.',
          epiSlow:
            'Down at 950 °C the surface reaction is the bottleneck and the rate is a ' +
            'thermometer — this is the recipe that separates a gas problem from a ' +
            'temperature problem.',
          epiThin:
            'A micron in under a minute at maximum TCS. Sits close to the spitting limit ' +
            'on purpose — watch the particle count.',
        },
        gas: {
          baseline:
            'Arsine panel, ten cycles at a ten-to-one ratio before opening. The qualified ' +
            'procedure, and the arithmetic behind it.',
          lazyPurge:
            'Same ten cycles, but only pumping to half pressure each time. Ten cycles at ' +
            '0.5 is a factor of a thousand; ten at 0.1 is a factor of ten billion. Same ' +
            'procedure, same time, seven orders of magnitude apart.',
          silaneRelease:
            'Pure silane leaking into the bay. Pyrophoric, so the fire is the immediate ' +
            'problem and the exposure limit is the one that matters if it does not light.',
          chlorinePool:
            'Chlorine is twice the density of air and pools at floor level. The ' +
            'well-mixed number below is optimistic, and a ceiling detector is measuring ' +
            'the wrong place.',
          dilutedArsine:
            'One per cent arsine in hydrogen, which is how it is usually bought. Two ' +
            'fewer orders of magnitude to purge away, and the same exposure limit at the ' +
            'end of it.',
          slowDetector:
            'A detector set at twice the exposure limit with a two minute response. It ' +
            'will alarm. The question this tool asks is whether it alarms in time to ' +
            'matter.',
          ammoniaHabit:
            'You smell ammonia far below its exposure limit, so it teaches you that your ' +
            'nose is a detector. Arsine has no odour warning at any dangerous ' +
            'concentration.',
        },
        implant: {
          baseline:
            'Source/drain boron: 30 keV, 3 × 10¹⁵, seven degrees of tilt to break the channel.',
          zeroTilt:
            'Straight down the channel. The peak lands where it should and a large part ' +
            'of the dose goes three times deeper than anyone intended.',
          shallow:
            'BF2 at 5 keV. Heavy molecule, low energy, very shallow — and far more damage ' +
            'per ion.',
          deepWell:
            'Phosphorus at 200 keV. Deep, and unavoidably smeared — straggle is 38% of ' +
            'range.',
          hotImplant:
            'Wafer held at 300 C so the lattice repairs itself as the beam goes in. Less ' +
            'damage to anneal out later, which means less thermal budget spent.',
        },
        patterning: {
          baseline:
            'A 160 nm mandrel pitch divided twice to 40 nm — a pitch no 193 nm scanner ' +
            'can resolve. The trim is set so both spacings come out equal.',
          pitchWalk:
            'The mandrel trim is 4 nm off — 14 instead of 10. The MEAN pitch is still ' +
            'exactly 40 nm, the mean spacing is still exactly 20 nm, and every summary ' +
            'number on the CD-SEM is on target. Every other line is in the wrong place.',
          overTrimmed:
            'Trimmed to nothing. The mandrel is gone before the spacer is deposited, so ' +
            'there is no sidewall to stand on.',
          thickSpacer:
            'Thirty nanometres of ALD on a 160 nm pitch. The spacers meet and the pattern ' +
            'is a solid block — geometry, not process.',
          doubleOnly:
            'Stop after the first pass: 80 nm pitch, one way to walk instead of two. Half ' +
            'the density and considerably less that can go wrong.',
          aggressive:
            'A 128 nm mandrel pitch and 16 nm spacers, for a 32 nm final. Everything is ' +
            'tighter and the trim window narrows with it.',
        },
        plating: {
          baseline:
            'Qualified bath, 20 mA/cm² for a minute into a 60 nm trench. Fills bottom-up ' +
            'and leaves an overburden for CMP-11 to take off.',
          noAccelerator:
            'Take the accelerator out and there is no CEAC feedback, so the trench plates ' +
            'conformally, the top closes first, and the feature is voided. The additive ' +
            'is not an optimisation.',
          noSuppressor:
            'Nothing holding the field and the trench mouth back, so the gain collapses ' +
            'toward one and the sidewalls win the race.',
          thinSeed:
            'A 10 nm seed leaves more room in the trench — and quadruples the sheet ' +
            'resistance, so the terminal effect starves the wafer centre.',
          hotPlate:
            'Fifty milliamps per square centimetre to halve the cycle time. Past the ' +
            'limiting current the bath cannot supply copper fast enough and the deposit ' +
            'burns.',
          overPlated:
            'Three minutes instead of one. The trench filled long ago; everything after ' +
            'that is overburden, and every nanometre of it is CMP-11 dishing time.',
          highAspect:
            'A 20 nm trench, 300 deep. Superfill gets STRONGER here — the bottom area ' +
            'contracts harder — but the sidewalls have far less distance to close.',
        },
        inspection: {
          baseline:
            'Qualified production scan: darkfield at 355 nm, 4 sigma, full wafer.',
          hotScan:
            'Threshold dropped to 2 sigma to find everything. It does find more — and ' +
            'buries the review queue in surface roughness.',
          coldScan:
            'Threshold raised to 9 sigma for a clean report. The report is clean; the ' +
            'wafer is not.',
          shortWave:
            '266 nm instead of 355 nm. Sensitivity goes as the two-thirds power of ' +
            'wavelength, so this is a 20% gain, not a 25% one.',
          sampled:
            'A fifth of the wafer, for throughput. Everything scales, including the ' +
            'confidence.',
        },
        probe: {
          baseline:
            'Qualified sort: 0.59 cm² die, 3 mm edge exclusion, negative binomial at ' +
            'alpha 3.',
          bigDie:
            'A 2 cm² die on the same line. Yield falls exponentially in area, which is ' +
            'why die size is the most expensive decision a designer makes.',
          poissonForecast:
            'The same wafer predicted with Poisson instead. Systematically pessimistic, ' +
            'because real defects cluster and clustered defects waste themselves on die ' +
            'that were already dead.',
          heavyCluster:
            'A scratch or a bad handling event: alpha down to 0.8. Same defect count, ' +
            'better yield.',
          wideEdge:
            '8 mm excluded to avoid edge failures. It also throws away good die on the way.',
        },
        waferprep: {
          baseline:
            'Qualified line: 120 µm wire, double-sided lap and polish, 775 µm finished. A ' +
            'fifth of the ingot still leaves as slurry.',
          thinWire:
            'A 70 µm wire to save kerf. It does — and it also wanders, because the same ' +
            'tension on a thinner wire is a slacker cut. More wafers, worse flatness.',
          singleSided:
            'Work the front face only. Half the process time and a wafer that bows, ' +
            'because the damage stress on the back is no longer balanced by anything.',
          shortEtch:
            'The chemical etch cut to a micron. It is the only step that removes damage ' +
            'without making any, so cutting it leaves lapping damage in the wafer — and ' +
            'the polish that follows is not deep enough to reach it.',
          shortPolish:
            'Polish cut to two minutes. The damage was already gone — what suffers is the ' +
            'finish, and a hazy wafer is a wafer the scanner cannot focus on.',
          coarseSaw:
            'A #250 wire cuts faster and leaves damage far deeper than the lap step can ' +
            'reach. The downstream steps were sized for a finer saw and no longer are.',
          thinWafer:
            '400 µm finished, for a stacked part. More wafers per ingot and far less ' +
            'silicon to carry the same bow.',
        },
        baseline:
          'Qualified centre-of-window recipe: 204 nm/min at 4.3% non-uniformity, 3.7:1. Start here.',
        lowPressure:
          'Pressure down to 6 mTorr: 116 nm/min at 10.9%. Too little feed in the chamber to make ' +
          'fluorine, so demand outruns supply (Da 1.15) and the edge, which sees the gas first, ' +
          'runs 19% fast.',
        highPressure:
          'Pressure up to 90 mTorr: 201 nm/min at 8.6%. Plenty of fluorine (Da 0.20), but ' +
          'diffusion scales as 1/p so it cannot reach the wafer centre fast enough. Fast edge ' +
          'again, 13% - opposite cause, same signature.',
        highBias:
          'Bias to 350 W: 227 nm/min but 20% non-uniform and selectivity down to 1.3:1. The extra ' +
          'demand drives the etch transport-limited (Da 1.22). The wrong way to chase rate.',
        smallOpenArea:
          'Only 4% open area: 308 nm/min, half again as fast on an unchanged recipe, because far ' +
          'less silicon is competing for the same fluorine. Uniformity improves to 1.8% for the ' +
          'same reason. This is the loading effect, and it is why a recipe qualified on one ' +
          'product drifts on another.',
        starvedFlow:
          'Mass flow controller down to 20 sccm from 200: collapses to 72 nm/min at 28% ' +
          'non-uniformity, with 78% of the feed dissociated and Da above 2. The fluorine budget ' +
          'is set by what the MFC lets in, and here it has become the binding constraint.',

        // Lithography.
        lithoBaseline:
          'Dosed to size at 180 nm pitch, best focus. Contrast 0.97, NILS 7.1, LER 2.4 nm.',
        lithoDefocused:
          '90 nm of defocus. Contrast falls to 0.42 and the line goes badly off size.',
        lithoUnderdosed: '20 mJ/cm². The line prints far too wide — less light removes less resist.',
        lithoTightPitch:
          'Past what conventional illumination handles well: contrast 0.57, and the window shrinks.',
        lithoEuvFine: 'Switch the source to EUV as well. Prints, but on a fraction of the photons.',

        // Thermal processing.
        thermalBaseline:
          'Dry gate oxide at 1000 °C, then a 30 s rapid anneal to activate a shallow boron implant.',
        thermalWetField:
          'Steam at 1050 °C for two hours. Far thicker, far faster — and it spends a lot of budget.',
        thermalFurnaceAnneal:
          'Same implant, but annealed 30 minutes in a furnace instead of 30 seconds in an RTP.',
        thermalShallowJunction:
          'Low-energy implant with a short, hot spike anneal — the modern recipe for a shallow ' +
          'junction.',
        thermalArsenicSd:
          'Arsenic instead of boron. A far heavier atom, so it barely moves — which is the point.',

        // Facilities: vacuum system configurations.
        vacNominal: 'Short fat line, tight seals. Conductance is not the limit and there is no leak.',
        vacThrottled:
          'Same pump behind a 5 cm line two metres long. The chamber never sees the pump speed.',
        vacLeak:
          'A pinched door seal on a chamber that has already pumped overnight, so the walls are ' +
          'dry and only the leak is left. Base pressure sticks and the rate-of-rise never bends. ' +
          'Note the low outgassing: you cannot leak-check a chamber you opened this morning.',
        vacDirty:
          'Opened to atmosphere and reassembled damp. Heavy outgassing that pumps away eventually.',
        vacBiggerPump: 'Three times the pump on the throttled line — the expensive wrong answer.',
      },
    },

    chamber: {
      maint: {
        chamberClean: {
          Label: 'Chamber clean and season',
          Detail:
            'Wet clean, reassemble, then run season wafers. Resets wall condition, and with it ' +
            'the match detune the deposits were causing. Costs several hours of tool availability.',
        },
        pumpService: {
          Label: 'Service the pump stack',
          Detail: 'Restores base pressure and leak-up rate. The tool is down for a shift.',
        },
        replaceFocusRing: {
          Label: 'Replace the focus ring',
          Detail: 'Restores edge field shaping. Usually done inside a scheduled clean.',
        },
        matchTune: {
          Label: 'Re-tune the RF match',
          Detail:
            'Recovers delivered power without opening the chamber - but if deposits are the cause ' +
            'it will drift straight back. Treating the symptom, not the fault.',
        },
      },
    },

    tool: {
      hmi: {
        Qubit: 'Qubit',
        WafersPerHour: 'wafers/h',
        Yield: 'Yield',
        WorkZone: 'Work zone',
        Wires: 'Wires per die',
        Welded: 'Welded',
        WarpUm: 'µm warpage',
        Usable: 'Usable length',
        Uniformity: 'Non-uniformity',
        Thickness: 'Thickness',
        Surface: 'Surface',
        Stack: 'At the stack',
        Species: 'Species',
        Screened: 'Screened',
        Repeatability: 'Repeatability',
        Removal: 'Removal',
        Regime: 'Regime',
        Recovery: 'Recovery',
        Purge: 'Purge',
        PumpSpeed: 'Pump speed',
        PitchWalk: 'Pitch walk',
        PctYield: '% yield',
        OxideCapped: 'oxide-capped',
        Overkill: 'Overkill',
        Nuisance: 'Nuisance',
        NmProbe: 'nm probe',
        NmPitch: 'nm pitch',
        NmOxide: 'nm oxide',
        Moisture: 'Moisture',
        LotHere: 'Lot in',
        Landing: 'Landing',
        HfLast: 'HF-last',
        Growth: 'Growth',
        GoodWafers: 'Good wafers',
        GoodDie: 'Good die',
        Gain: 'Bottom-up gain',
        EscapesPpm: 'ppm escapes',
        Dose: 'Dose',
        Cycles: 'Cycles',
        Contrast: 'Contrast',
        Channelled: 'Channelled',
        Capture: 'Capture',
        Budget: 'Budget',
        Bow: 'Bow',
        AcrossDie: 'Across the die',
      },
      history: {
        word: {
          map_clustered: 'map reads clustered',
          map_radial: 'map reads radial',
          map_centre: 'map reads centre',
          map_edge: 'map reads edge',
          map_uniform: 'map reads uniform',
          attach_void: 'attach void',
          intermetallic: 'intermetallic',
          cratered_pad: 'cratered pad',
          lifted_ball: 'lifted ball',
          trench_filled: 'trench filled',
          trench_seam: 'trench seam',
          trench_void: 'trench void',
          lattice_damaged_but_crystalline: 'lattice damaged but crystalline',
          layer_amorphised: 'layer amorphised',
          transport_limited: 'transport-limited',
          transition_limited: 'transition-limited',
          reaction_limited: 'reaction-limited',
          interstitial_rich: 'interstitial-rich',
          perfect: 'perfect',
          vacancy_rich: 'vacancy-rich',
          arsenic: 'arsenic',
          antimony: 'antimony',
          phosphorus: 'phosphorus',
          boron: 'boron',
        },
        EtchedCleared: ', {{pct}}% of the wafer cleared to the stop layer',
        Etched: 'Etched {{s}} s at {{rate}} nm/min, {{nu}}% non-uniform, {{angle}}° ' +
          'profile: CD {{cd}} nm in the film',
        LithoFailed: ' — PART OF THE FIELD DID NOT PRINT',
        LithoExposedEuv: 'Exposed at 13.5 nm, {{dose}} mJ/cm², {{pitch}} nm pitch: CD {{cd}} nm, LER ' +
          '{{ler}} nm 3σ, {{range}} nm across the field',
        LithoExposed: 'Exposed at 193 nm, {{dose}} mJ/cm², {{pitch}} nm pitch: CD {{cd}} nm, LER ' +
          '{{ler}} nm 3σ, {{range}} nm across the field',
        Cooled: 'Cooled to 10 mK: junction R·A {{ra}} Ω·µm², E_J {{ej}} GHz on {{cap}} fF, ' +
          'transmon f₀₁ {{f01}} GHz',
        SemMeasured: 'After-develop CD {{cd}} nm at {{pitch}} nm pitch, measured at {{ev}} eV ' +
          'landing with a {{probe}} nm probe',
        EuvExposedPlain: 'Exposed at 13.5 nm',
        EuvExposed: 'Exposed at {{dose}} mJ/cm², {{pitch}} nm pitch: CD {{cd}} nm, LER {{ler}} ' +
          'nm 3σ, {{failures}} stochastic failures',
        Tested: 'Burned in {{hours}} h at {{temp}} °C and tested: {{shipped}} shipped, ' +
          '{{overkill}} good parts scrapped, {{escaped}} bad parts escaped',
        MouldedCracked: ' ({{pct}}% cracked from grinding damage)',
        MouldedPopcorned: ', POPCORNED',
        Moulded: 'Moulded and reflowed: {{um}} µm warpage, {{die}} die in, {{packages}} ' +
          'packages out',
        BondedFailure: ' ({{mode}})',
        Bonded: '{{wires}} wires per die, {{gf}} gf shear, {{welded}}% welded — {{before}} ' +
          'die in, {{after}} out',
        DicedChipping: ', CHIPPING PAST THE STREET',
        Diced: 'Ground to {{um}} µm and singulated: {{die}} die, {{mpa}} MPa at m = {{m}}',
        SaqpWalk: ' — mean spacing {{nm}} nm, and every other line is wrong',
        Saqp: 'SAQP {{mult}}×: {{from}} → {{to}} nm pitch, {{cd}} nm lines, pitch walk ' +
          '{{walk}} nm',
        SaqpCollapsed: 'SAQP collapsed: {{reason}}',
        Sorted: 'Sorted: {{yield}}% yield, {{good}} good die of {{probed}} probed, ' +
          '{{signature}}',
        Scanned: 'Scanned at {{nm}} nm: reported {{density}}/cm² ({{nuisance}}% nuisance), ' +
          'captured {{capture}}% of what is there',
        RcaOxideCapped: ', oxide-capped',
        RcaHfLast: ', HF-last with a {{min}} min queue ({{pct}}% of the benefit already back)',
        Rca: 'RCA clean: {{pct}}% of particles removed, {{left}}/cm² left',
        Exposure: '{{min}} min open in an ISO {{iso}} work zone ({{zone}}/m³ against a room ' +
          'reading of {{room}}/m³): +{{adders}} particles/cm²',
        PlatedOverburden: ', {{over}} nm of overburden and a {{step}} nm step for CMP-11, {{nu}}% ' +
          'non-uniform',
        PlatedVoid: ' ({{pct}}% void)',
        Plated: 'Plated {{nm}} nm at gain {{gain}}×: {{fill}}',
        PolishedWorst: ', worst at {{region}} ({{dishing}} nm dishing, {{erosion}} nm erosion)',
        PolishedBridging: ', METAL STILL BRIDGING',
        Polished: 'Polished {{s}} s, {{nm}} nm across the die',
        AldParasitic: ', {{pct}}% of it parasitic CVD',
        Ald: '{{cycles}} ALD cycles, {{nm}} nm at {{nu}}% non-uniformity',
        AnnealOxide: ', {{nm}} nm oxide grown',
        AnnealFresh: ' (a fresh wafer would sit at {{xj}} nm)',
        Anneal: 'Anneal cycle: xj {{xj}} nm',
        ImplantChannelled: ', {{pct}}% CHANNELLED to {{nm}} nm',
        Implant: '{{species}} at {{kev}} keV, {{dose}}/nm²: Rp {{rp}} nm, {{lattice}}',
        EpiSpitting: ', gas-phase particles added',
        EpiSlip: ', SLIP AT THE EDGE',
        EpiSeam: ', {{um}} µm autodoped seam',
        Epi: 'Epi layer: {{um}} µm at {{doping}}/nm³, {{regime}}',
        Sliced: 'Sliced, lapped, etched and polished: {{um}} µm, TTV {{ttv}} µm, bow ' +
          '{{bow}} µm, {{damage}} µm residual damage ({{mpa}} MPa). {{good}} good ' +
          'wafers from the ingot.',
        IngotStructureLost: ' — STRUCTURE LOST',
        Ingot: 'Ingot grown: {{mm}} mm of {{dopant}} silicon, {{rho}} Ω·cm at the seed, ' +
          'oxygen {{oxygen}}/nm³, {{regime}}',
        StockBlank: 'Stock blank — no ingot from CZ-07 on the saw. ',
        BareWafer: 'Bare polished wafer, no films',
      },
      quantum: {
        Dd3Note: 'Every number on this panel above here treats the two dots as lying on a ' +
          'line. They do not: the electron crosses through a gap between gates, and ' +
          'the gap has a width. So this lets one electron go in the left dot of a ' +
          'three-dimensional potential and integrates the Schrödinger equation for ' +
          'the whole wave, with the same propagator that runs the tunnelling volume ' +
          'at THRM-04 and the diffraction at MET-05. Nothing tells it how fast to ' +
          'cross. The chart counts how much of the wave is on the left, and the ' +
          'coupling is read off the first turn: h/4t. Leave the squeeze at zero and ' +
          'the dashed grey curve, two levels with the 1D solver’s t, lies under the ' +
          'blue one - two calculations that share nothing but the potential, ' +
          'agreeing. Now squeeze the gap. A narrower gap holds the electron tighter ' +
          'across it, that costs zero-point energy, and the electron has to find it ' +
          'on the way through: it is barrier height by another name, half of ħω₀ for ' +
          'every unit of squeeze. The orange curve adds exactly that to the 1D ' +
          'barrier, and it lands within a tenth of what the wave actually does. A ' +
          'barrier gate a few nanometres narrower than drawn changes the qubit’s ' +
          'speed by more than its voltage does, which makes t a lithography number. ' +
          'The well is drawn at its real ten nanometres; on the grid it is 2.2 times ' +
          'thicker, because silicon’s electrons are five times heavier along the ' +
          'growth direction and a change of variable puts both masses on one grid. ' +
          'One electron, one valley, no spin, no noise, no relaxation.',
        Dd3KnobDetuning: 'Detuning',
        Dd3KnobPinchValue: 'ħω in the gap {{hw}} meV',
        Dd3KnobPinch: 'How much the gate squeezes the gap',
        Dd3KnobBarrier: 'Barrier gate',
        Dd3Idle: 'Press Run: the electron is let go in the left dot and propagated on a ' +
          'worker thread, ten to twenty seconds a run.',
        Dd3Status: '{{ps}} of {{run}} ps · {{left}} % in the left dot · Σ|ψ|² = {{norm}}',
        Dd3TileNoPinch: 'the gap is as wide as the dots: nothing to add',
        Dd3TileAdiabaticNote: '+{{mev}} meV of barrier: the gap holds the electron at ħω = {{hw}} meV',
        Dd3TileAdiabatic: 'With the gap’s zero-point energy',
        Dd3Tile1dNote: 'would cross in {{ps}} ps',
        Dd3Tile1d: 'The 1D solver, same gate',
        Dd3TileWaiting: 'read at the electron’s first turn',
        Dd3TileMeasuredNote: 'first turn at {{ps}} ps, {{pct}} % across',
        Dd3TileMeasured: 'Tunnel coupling, measured off the run',
        Dd3Crossed: 'first turn',
        Dd3ChartY: 'P(left)',
        Dd3ChartX: 'time (ps)',
        Dd3ChartTitle: 'Probability the electron is in the left dot',
        Dd3LineAdiabatic: 'Two levels, the gap’s zero-point energy added to the barrier',
        Dd3Line1d: 'Two levels, the 1D solver’s t',
        Dd3Measured: 'Propagated in 3D',
        Dd3About: 'What this shows, and what the model leaves out',
        SectionDd3: 'The crossing, propagated in three dimensions — and what a narrower gap ' +
          'costs (Schrödinger, split Cayley step)',
        NextFrequency: 'A spin qubit in EPI-20’s well is split by a magnetic field instead of a ' +
          'junction, at about 28 GHz per tesla. What field would make it as cold as a ' +
          '5 GHz transmon in this fridge, and what would you expect to go wrong ' +
          'first?',
        TitleFrequency: 'Population in |1⟩ against qubit frequency',
        LabelP1High: 'In |1⟩, highest frequency',
        LabelP1Low: 'In |1⟩, lowest frequency',
        HypFrequency: 'A two-level system is in its ground state when the gap between its levels ' +
          'is large against the thermal energy around it: what matters is hf over kT, ' +
          'not T. If so, the same fridge and the same wiring should leave a ' +
          'low-frequency qubit badly mixed and a high-frequency one clean, with ' +
          'nothing on the fridge changing between them.',
        AskedFrequency: 'How does the qubit’s frequency decide whether the fridge is cold enough?',
        SumFrequency: 'Qubit frequency against thermal population: cold is hf against kT, not a ' +
          'number of millikelvin.',
        NextAttenuation: 'A chip that measured well last month reads ten percent in |1⟩ today, and ' +
          'every thermometer on the fridge is normal. What would you measure with the ' +
          'fridge still cold, before anyone proposes warming it up?',
        TitleAttenuation: 'Qubit temperature against line attenuation',
        LabelPlate: 'Plate throughout',
        LabelQubitCold: 'Qubit, most attenuation',
        LabelQubitWarm: 'Qubit, least attenuation',
        HypAttenuation: 'The coax that carries the drive pulse also carries room-temperature noise, ' +
          'about a thousand photons in the qubit’s mode. Each 10 dB of cold ' +
          'attenuation removes nine tenths of it. If the qubit is thermalised to what ' +
          'it hears rather than to the plate it sits on, its temperature should fall ' +
          'with every attenuator until the plate’s own emission is all that is left - ' +
          'and the plate thermometer should not move at all.',
        AskedAttenuation: 'How much attenuation does a qubit’s drive line need?',
        SumAttenuation: 'Drive-line attenuation against qubit temperature: room-temperature noise, ' +
          'ten times less per 10 dB.',
        knob: {
          circulation: 'Helium-3 circulation',
          attenuationDb: 'Drive-line attenuation',
        },
        AssistantBlurb: 'Ask how much attenuation a drive line needs, or why qubits are built at 5 ' +
          'GHz. Every answer is the fridge run against this recipe.',
        AssistantLead: 'QDT-24 inquiry assistant',
        TileExcitedNote: 'for the {{f}} GHz qubit designed above',
        TileExcited: 'Found in |1⟩ with no drive',
        TileQubitNote: '{{n}} thermal photons in its mode',
        TileQubit: 'Qubit temperature',
        TilePlateNote: '{{h}} h from 4 K to base',
        TilePlate: 'Mixing chamber plate',
        SectionTool: 'The fridge this shift: how cold the plate is, and how cold the qubit is',
        fault: {
          missingAttenuator: {
            Evidence: [
              'The qubit reads ten percent in |1⟩ with no drive, against a one percent ' +
                'limit, while the plate reads 10 mK.',
              'Flow, 4 K stage and can pressure are all nominal: the fridge is healthy.',
              'The network analyser finds 40 dB on a line specified at 60.',
            ],
            Truth: 'The 20 dB attenuator on the mixing-chamber end of the drive line was left ' +
              'out when the lines were re-made. The line now passes a hundred times the ' +
              'room-temperature noise it should: an eighth of a photon in the qubit’s ' +
              'mode instead of a thousandth, which is a qubit at 109 mK on a plate at 10. ' +
              'One shot in ten starts in the wrong state. The thermometers are all ' +
              'correct. A qubit is not at the temperature of the metal it is bolted to; ' +
              'it is at the temperature of what it can hear.',
            Complaint: 'The fridge is perfect: 10 mK, on schedule. The qubit is not. Readout ' +
              'fidelity is terrible and T1 looks fine, and the chip measured well in the ' +
              'other fridge last month.',
            Label: 'Attenuator missing from the drive line',
          },
          canLeak: {
            Evidence: [
              'Can pressure is eight times its qualified value.',
              'Still flow is nominal, so cooling power is intact; the 4 K stage is ' +
                'nominal, so the extra heat is not coming down the supports.',
              'The leak detector shows helium at the can flange.',
            ],
            Truth: 'An indium seal on the vacuum can is leaking and there is helium gas in the ' +
              'space that is supposed to be empty. Gas conducts: it carries heat from the ' +
              '4 K wall straight to the plate, eight times the qualified leak, and the ' +
              'plate sits at root eight times base. This is the expensive one - the ' +
              'fridge has to come up to room temperature to be opened - which is exactly ' +
              'why the two cheap lookalikes have to be ruled out first.',
            Complaint: 'The plate stalled at 28 mK on this cooldown and will not move. Flow is ' +
              'normal and the 4 K stage is normal.',
            Label: 'Helium in the vacuum can',
          },
          warmCompressor: {
            Evidence: [
              'The 4 K stage reads 5.2 K. Neither a plugged trap nor a leaking can warms ' +
                'the 4 K stage.',
              'Still flow and can pressure are nominal.',
              'Water at the compressor inlet is 31 °C against a 25 °C limit.',
            ],
            Truth: 'The compressor that drives the pulse tube is getting cooling water at 31 ' +
              '°C and is derating, so the 4 K stage is sitting at 5.2 K. Everything ' +
              'colder hangs from that stage, and the heat conducted down the supports and ' +
              'the wiring goes roughly as its temperature squared: 2.6 times the heat ' +
              'leak, root 2.6 times the base temperature. The afternoons are the ' +
              'building’s cooling water, not the fridge.',
            Complaint: 'Base temperature has crept from 10 to 16 mK over a week, and it is worse ' +
              'in the afternoons. Circulation is exactly where it was set.',
            Label: 'Pulse-tube compressor running warm',
          },
          pluggedTrap: {
            Evidence: [
              'Flow at the still is 20% of set. A leak in the can adds heat; it does not ' +
                'take flow away.',
              'The can pressure and the 4 K stage are both nominal.',
              'Pressure drop across the cold trap is five times nominal.',
            ],
            Truth: 'The nitrogen cold trap that scrubs the returning helium-3 has loaded with ' +
              'frozen air and is choking the circulation to a fifth of its set rate. ' +
              'Cooling power goes as circulation times T squared, so a fifth of the flow ' +
              'holds root five times the temperature against the same heat leak: 22 mK. ' +
              'Nothing is wrong inside the fridge. The fix is on the gas handling ' +
              'cabinet, takes an afternoon, and does not need a warm-up.',
            Complaint: 'The plate will not go below 22 mK. It reached 10 on the last cooldown. ' +
              'Somebody has suggested the can has a leak and wants to warm up and re-make ' +
              'every seal.',
            Label: 'Cold trap plugging',
          },
          cryoHealthy: {
            Evidence: [
              'The plate is at 10 mK and the qubit reads an eighth of a percent in |1⟩.',
              'Every bench reading is nominal.',
            ],
            Truth: 'Nothing is wrong. The correct call is to start measuring.',
            Complaint: 'Routine cooldown. The plate reached base overnight.',
            Label: 'Nothing wrong',
          },
        },
        reading: {
          lineInsertionLoss: {
            Label: 'Drive-line insertion loss with a network analyser (2.0 h)',
          },
          leakCheck: {
            Limit: 'no signal',
            Label: 'Helium leak check on the vacuum can, fridge cold (3.0 h)',
          },
          compressorWater: {
            Limit: '< 25 °C in',
            Label: 'Cooling water at the pulse-tube compressor (30 min)',
          },
          trapPressure: {
            Label: 'Pressure drop across the nitrogen cold trap (30 min)',
          },
          qubitPopulation: {
            Label: 'Qubit population in |1⟩ with no drive',
          },
          canPressure: {
            Label: 'Vacuum can pressure',
          },
          fourKelvin: {
            Label: '4 K stage temperature',
          },
          stillFlow: {
            Label: 'Helium-3 flow at the still',
          },
        },
        aspect: {
          wiring: {
            Action: 'Warm up, open the fridge and re-fit the attenuator missing from the drive ' +
              'line',
            Label: 'Microwave lines',
          },
          vacuum: {
            Action: 'Warm up, find the leak, re-make the indium seal and cool down again',
            Label: 'Vacuum can',
          },
          pulseTube: {
            Action: 'Restore cooling water to the compressor and let the 4 K stage recover',
            Label: 'Pulse tube and its compressor',
          },
          circulation: {
            Action: 'Warm and pump out the nitrogen cold trap, then bring the circulation back up',
            Label: 'Helium-3 circulation',
          },
        },
        SecondaryLabel: 'Qubit population in |1⟩',
        ToolLabel: 'QDT-24 Cryogenic Quantum Device Test',
        GuardWafer: 'This lot is still a whole wafer. Dice it at DICE-15 first: the fridge ' +
          'takes a chip on a sample holder, and its cold plate is smaller than the ' +
          'wafer.',
        GuardPackaged: 'These parts are already moulded into packages. A qubit chip goes into the ' +
          'fridge bare, wire-bonded to a sample holder.',
        CoolLot: 'Cool to 10 mK and characterise the qubits',
        NoteNoLot: 'No chip in the fridge. Bring a finished quantum chip here to cool it down ' +
          'and measure it.',
        SectionSpin: 'The spin qubit in EPI-20’s well: valley splitting (tight binding)',
        SectionTransmon: 'The transmon: the Hamiltonian, solved (Sturm bisection)',
        DriveRabiTitle: 'A resonant drive: the populations swing',
        DriveRabiX: 'time (ns)',
        DriveRabiY: 'population',
        DriveP0: '|0⟩',
        DriveP1: '|1⟩',
        DriveP2: '|2⟩ (leaked)',
        DriveLeakTitle: 'A π pulse too short leaks out of the qubit',
        DriveLeakX: 'gate time (ns)',
        DriveLeakY: 'log₁₀ population left in |2⟩ and above',
        DriveLeakSeries: 'leakage after a calibrated π pulse',
        DriveSpeedLimit: '1/|α| = {{nsec}} ns',
        DriveThisGate: 'this gate',
        DriveRamseyTitle: 'Ramsey: the phase the charge noise takes away',
        DriveRamseyX: 'wait between the two π/2 pulses ({{unit}})',
        DriveRamseyY: 'probability of |0⟩ after the second pulse',
        DriveFringe: 'fringe',
        DriveEnvelope: 'envelope',
        DriveT2: 'T₂*',
        DriveStatF01: 'Qubit frequency f₀₁',
        DriveStatRatio: 'E_J/E_C = {{ratio}}',
        DriveStatAlpha: 'Anharmonicity α',
        DriveStatLimit: 'speed limit 1/|α| = {{nsec}} ns',
        DriveStatRabi: 'Rabi frequency',
        DriveStatPi: 'a square π pulse takes {{nsec}} ns',
        DriveStatLeak: 'Leakage after a {{nsec}} ns π pulse',
        DriveStatP1: 'P(|1⟩) = {{p}}',
        DriveFrameRwa: 'rotating-wave approximation',
        DriveFrameLab: 'lab frame, counter-rotating terms kept',
        DriveStatT2: 'T₂* from charge noise',
        DriveStatAsymptotic: 'E_J/E_C is past what the charge basis resolves; the swing of f₀₁ is Koch’s asymptotic form here, and charge noise no longer limits this qubit',
        DriveStatSigma: 'offset-charge noise σ = {{sigma}} e',
        DriveTransmon: 'Transmon (E_J from the junction)',
        DriveBox: 'Cooper-pair box (E_J = E_C)',
        DriveShowLeak: 'Leakage against gate time',
        DriveShowRamsey: 'Ramsey fringe',
        DriveKnobCap: 'Shunt capacitance',
        DriveKnobCapValue: '{{ff}} fF · E_C = {{ec}} MHz',
        DriveKnobAmp: 'Drive amplitude',
        DriveKnobGate: 'π-pulse gate time',
        DriveKnobSigma: 'Offset-charge noise σ',
        DriveKnobSigmaValue: '{{sigma}} e',
        SectionDrive: 'The transmon, driven: Rabi, leakage and dephasing (Schrödinger equation, RK4)',
        DriveNote: 'The spectrum above, made to do something. A microwave tone couples to the island’s charge, and in the ladder’s own basis that charge links only neighbouring levels, so a tone at f₀₁ swings the qubit between |0⟩ and |1⟩ at the Rabi frequency on the left. The catch is |2⟩. It sits only the anharmonicity α away from where the next even step would be, and a pulse shorter than a few 1/|α| carries enough bandwidth to reach it. The leakage curve is a calibrated π pulse at every length: the speed limit every superconducting processor lives under. The Ramsey toggle is the other clock. Stray charge in the substrate wanders, the ladder’s dependence on it moves f₀₁, and an ensemble of shots loses its phase in T₂*. Switch to the Cooper-pair box and the phase is gone in nanoseconds; the transmon, at the panel’s defaults, keeps it for tens to hundreds of microseconds across the noise slider, some twenty thousand times longer, which is the reason it exists. Four solved levels are driven in the rotating-wave approximation where it holds, and in the lab frame, with the counter-rotating terms kept, where it does not: the Cooper-pair box, or a pulse strong against the qubit frequency. The noise is quasi-static, and energy relaxation, 1/f noise spectra and pulse shaping such as DRAG are left out.',
        SectionChargeQubit: 'One electron, two dots: the charge qubit in motion (Schrödinger, unitary steps)',
        ChargeQubitNote: 'The section above ends at a number: a tunnel coupling t of some tens of microelectronvolts. This is what that number is. Put the electron in the left dot and let go, and it does not stay - it crosses to the right dot in h/(4t), about fifty picoseconds here, and comes back, for ever, because nothing in this model can stop it.\n\nThe shape on the left is |c_L φ_L + c_R φ_R|², the two orbitals the exchange solver already found, with the amplitudes integrated step by step rather than taken from the textbook formula the harness checks them against. Tilt the dots and two things happen at once: less of the electron crosses, as 4t²/(4t² + ε²), and what does cross goes faster.\n\nThe ramp toggle is how a real gate is driven - sweep the detuning through zero and the electron follows the anticrossing into the other dot if the sweep is slow, or sails straight through it if the sweep is fast, at the Landau-Zener probability.\n\nThe last knob is the reason a charge qubit is a teaching device rather than a product: stray charge moves the detuning, the frequency moves with it, and the oscillation blurs out in nanoseconds - hundreds of round trips, against the hundreds of thousands a transmon manages. At zero detuning the frequency depends on the noise only in second order, which is what a sweet spot is. The model is two levels and quasi-static noise: no valleys, no phonons, no relaxation, and the electron is never measured.',
        CqDensityTitle: 'Where the electron is: |ψ|² across the two dots',
        CqDensityX: 'position along the dots (nm)',
        CqDensityY: 'energy (meV)',
        CqPotential: 'Gate potential',
        CqDensitySeries: 'Electron density |ψ|²',
        CqTraceTitle: 'The electron sloshing: P(left) against time',
        CqTraceX: 'time (ns)',
        CqTraceY: 'probability it is in the left dot',
        CqTraceSeries: 'P(left), integrated',
        CqEnvelopeSeries: 'Visibility left by charge noise',
        CqNow: 'now',
        CqRampTitle: 'A detuning ramp through the anticrossing',
        CqRampX: 'log₁₀ ramp rate (µeV/ns)',
        CqRampY: 'probability it stayed in its own dot',
        CqRampSeries: 'Integrated',
        CqRampFormula: 'Landau–Zener formula',
        CqRampHere: 'this ramp',
        CqCrossover: 'crossover',
        CqToggleOscillation: 'Let it slosh',
        CqToggleRamp: 'Ramp through the anticrossing',
        Cq3dCrossing: 'crossing',
        Cq3dSweep: 'the sweep takes',
        Dot3dInLeft: 'in the left dot',
        Dot3dInRight: 'in the right dot',
        CqAbout: 'What this shows, and what the model leaves out',
        CqBenchKnobs: 'Knobs',
        CqBenchReadouts: 'Readouts',
        Dot3dAbout: 'About this picture',
        Dot3dScaleTailsShort: 'Tails (log)',
        Dot3dScaleDensityShort: 'Density (linear)',
        Dot3dAnimCaption: 'One electron, in the device. The cloud is the two dot orbitals added together with the electron\'s changing amplitudes, |c_L φ_L + c_R φ_R|², interference included - so it drains from one dot into the other rather than two clouds swapping brightness. Along the dots the shape is solved; across them and through the 10 nm well it is an assumed ground state.',
        CqStatCrossover: 'Crossover ramp rate',
        CqStatCrossoverNote: '2πt²/ħ: slower ramps carry the electron across, faster ones leave it behind',
        CqStatStays: 'This ramp leaves behind',
        CqStatStaysNote: 'the Landau–Zener formula gives {{formula}}%',
        YieldShort: '{{y}}% of chips have no frequency collision · each qubit lands within about {{sigma}} MHz of target',
        CqRampAveraged: 'Away from the crossing the two branches beat faster than a frame can show, so there the picture is the average of that beat; near the crossing the beat itself is drawn.',
        CqStatT: 'Tunnel coupling t',
        CqStatTNote: 'the same {{freq}} the exchange section reads off this design',
        CqStatPeriod: 'Round trip',
        CqStatPeriodNote: '{{half}} to put the electron next door: half a period',
        CqStatAmplitude: 'How much of it crosses',
        CqStatAmplitudeNote: '4t²/(4t² + ε²) at ε = {{eps}} µeV',
        CqStatT2: 'Coherence T₂*',
        CqStatT2Note: '{{periods}} round trips before the oscillation blurs out',
        CqStatT2Coherent: 'no noise: coherent',
        CqStatT2CoherentNote: 'turn the charge noise up: this is the one thing a charge qubit cannot survive',
        CqSlowdown: 'slowed {{factor}}×: one second here is {{nsec}} ns in the fridge',
        CqRun: 'Run',
        CqPause: 'Pause',
        CqReset: 'Back to the left dot',
        CqKnobBarrier: 'Barrier gate',
        CqKnobDetuning: 'Detuning between the dots',
        CqKnobNoise: 'Charge noise on the detuning',
        CqNoiseNone: 'none',
        CqKnobRate: 'Ramp rate',
        CqKnobRateValue: '{{rate}} µeV/ns · {{stays}}% stays',
        ExchOff: 'off',
        ExchPotentialTitle: 'Two dots, one electron: the localised orbitals',
        ExchPotentialX: 'position along the dots (nm)',
        ExchPotentialY: 'energy (meV)',
        ExchPotentialSeries: 'Gate potential',
        ExchLeft: 'Left dot |φ_L|²',
        ExchRight: 'Right dot |φ_R|²',
        ExchJTitleBarrier: 'Exchange J against the barrier gate',
        ExchJTitleDetuning: 'Exchange J against detuning',
        ExchJXBarrier: 'barrier gate height (meV)',
        ExchJXDetuning: 'detuning ε (meV)',
        ExchJY: 'log₁₀ J (µeV)',
        ExchJSeries: 'J, Hubbard model',
        ExchJHere: 'this design',
        ExchJGate: '10 ns √SWAP',
        ExchStatT: 'Tunnel coupling t',
        ExchStatTNote: 'the off-diagonal element between the localised orbitals; the saddle stands {{saddle}} meV above the dot floor',
        ExchStatU: 'Cost of sharing a dot, U',
        ExchStatUNote: 'Coulomb energy of both electrons in one dot {{size}} nm across',
        ExchStatJ: 'Exchange J',
        ExchStatJNote: '{{mhz}} MHz; the weak-tunnelling formula 4t²U/(U² − ε²) gives {{formula}}',
        ExchStatGate: '√SWAP gate time',
        ExchStatGateNote: 'h/(4J): a quarter turn of the exchange phase',
        ExchStatMerged: 'the electron is no longer held in two dots - lower the ground level under the saddle or raise the barrier',
        ExchStatTilted: 'the tilt has pulled both orbitals into one dot - there is no electron next door to exchange with; bring the detuning back towards zero',
        ExchSweepBarrier: 'J against the barrier gate',
        ExchSweepDetuning: 'J against detuning',
        ExchKnobBarrier: 'Barrier gate',
        ExchKnobDetuning: 'Detuning between the dots',
        ExchKnobSep: 'Dot spacing (half the pitch)',
        ExchKnobHw: 'Dot confinement ħω₀',
        SectionExchange: 'Two spin qubits and the gate between them: exchange coupling (Schrödinger, Hubbard)',
        ExchangeNote: 'One dot holds one qubit, and a computer needs two of them to interact. In silicon that interaction is exchange, and nobody wires it in. Two electrons in neighbouring dots can lower their energy by briefly sharing one dot, but the Pauli principle allows that only when their spins are opposite, so the singlet sits an energy J below the triplet; hold J on for h/(4J) and the spins make a √SWAP gate. Each step here is solved: one electron in the double-dot potential gives the tunnel coupling t between the localised orbitals, the Coulomb integral over the left orbital gives U, the cost of sharing a dot, and the two-electron Hubbard model gives J, which for weak tunnelling is 4t²U/(U² − ε²). Raise the barrier gate and t falls exponentially, J with its square: ten millielectronvolts on one gate switches J over nearly five decades, which is how a chip turns a gate on and off. The model runs in one dimension along the dots, with the dot\'s size softening the Coulomb kernel, and has no valleys, spin-orbit coupling or charge noise.',
        Dot3dT: 'tunnel coupling t',
        Dot3dJ: 'exchange J',
        Dot3dGate: '√SWAP',
        Dot3dCaption: 'Along the dots: solved. Across the dots and through the 10 nm well: assumed ground states. Plunger gates over each dot, the barrier gate between - warmer means a higher barrier. White is where the two orbitals overlap.',
        Dot3dScaleTails: 'Show the tails (log, four decades)',
        Dot3dScaleDensity: 'True density (linear)',
        SectionJunction: 'The Josephson junction: a nanometre of oxide (transfer matrix, Landauer)',
        SpinNote: 'Silicon’s conduction band has its lowest energy at six points in momentum; ' +
          'a strained well leaves two of them, the valleys, almost level. The chain ' +
          'here is the smallest model that has two valleys at all - one site per ' +
          'atomic plane, hoppings chosen to put the minimum where silicon’s is - so a ' +
          'sharp Si/SiGe step splits them by hundreds of µeV. Smear that step over ' +
          'half a nanometre, as germanium does when the growth runs warm, and the ' +
          'smooth splitting collapses; but the alloy is random atom by atom, and the ' +
          'random germanium the electron touches splits the valleys again by chance. ' +
          'A fab’s spin-qubit yield is the share of dots that win that lottery by ' +
          'enough to keep the spin out of the upper valley at the field it is run at.',
        KnobB: 'Magnetic field',
        KnobDot: 'Dot radius',
        KnobField: 'Gate field',
        KnobGe: 'Germanium in the barriers',
        KnobWell: 'Silicon well thickness',
        KnobInterface: 'Interface width (EPI-20 growth)',
        DotNext: 'Look at another dot',
        DotSmooth: 'Smooth alloy, no disorder',
        DotAlloy: 'A real dot: random alloy',
        StatThermalNote: 'the valley splitting has to be many times this, or the upper valley fills',
        StatThermal: 'Thermal energy at 100 mK',
        StatHotSpotNote: 'where spin splitting equals valley splitting and the spin relaxes fastest ' +
          '- stay well below it',
        StatHotSpot: 'Hot spot',
        StatQubitNote: 'at {{b}} T: a Zeeman splitting of {{ez}} µeV',
        StatQubit: 'Spin qubit frequency',
        StatWaferNote: 'of dots above 100 µeV; median {{median}}, 10-90%: {{lo}}-{{hi}} µeV',
        StatWafer: 'Across fifty dots',
        StatThisDotNote: 'valley splitting of the dot drawn; {{smooth}} µeV with no disorder',
        StatThisDot: 'This dot',
        LevelsHot: 'hot spot',
        LevelsHere: 'operating field',
        LevelsY: 'energy (µeV)',
        LevelsX: 'magnetic field (T)',
        LevelsTitle: 'Spin and valley levels against magnetic field',
        LevelValley1Up: 'upper valley, spin up',
        LevelValley1Down: 'upper valley, spin down',
        LevelValley0Up: 'lower valley, spin up',
        LevelValley0Down: 'lower valley, spin down',
        ValleyTarget: '100 µeV',
        ValleyHere: 'this growth',
        ValleyBand: '10% and 90% of dots',
        ValleyMedian: 'Real alloy, median dot',
        ValleySmooth: 'Smooth alloy (no disorder)',
        ValleyY: 'log₁₀ valley splitting (µeV)',
        ValleyX: 'Si/SiGe interface width (nm)',
        ValleyTitle: 'Valley splitting against interface width',
        WellDensity: 'The electron',
        WellSmooth: 'Smooth profile',
        WellAlloy: 'This dot’s alloy',
        WellY: 'conduction band edge (meV)',
        WellX: 'height through the stack (nm)',
        WellTitle: 'The well, plane by plane, and where the electron is',
        YieldNote: 'At this scatter the qubit above lands within about {{sigma}} MHz of its ' +
          'target, one sigma. Two neighbours collide when either lands within ' +
          '{{window}} MHz of the other’s 0-1 or 1-2 line, where a two-qubit gate ' +
          'would drive the wrong transition - and {{y}}% of chips have no collision ' +
          'anywhere. Measuring every junction warm and nudging it with a laser anneal ' +
          'is one way fabs pull the scatter down.',
        KnobSpread: 'Junction resistance scatter across the wafer',
        TransmonNote: 'The circuit’s Hamiltonian is 4E_C(n − n_g)² − E_J cos φ, where n counts ' +
          'Cooper pairs across the junction and φ is the phase difference, conjugate ' +
          'like position and momentum. Written in the pair-number basis it is a ' +
          'tridiagonal matrix, and its eigenvalues are the ladder. Shrink the ' +
          'capacitor until E_J is close to E_C and the bands swing with every stray ' +
          'charge; make it large and they flatten exponentially while the ' +
          'anharmonicity - the gap between the first and second steps that lets a ' +
          'pulse drive only the qubit - shrinks only as a power. That trade is the ' +
          'transmon.',
        KnobNg: 'Offset charge',
        KnobEj: 'Josephson energy E_J / h',
        KnobCapValue: '{{ff}} fF · E_C/h {{ec}} MHz',
        KnobCap: 'Shunt capacitance',
        StatShiftNote: 'how far 0.01 nm more AlOx moves this qubit',
        StatShift: 'One tenth of an ångström of oxide',
        StatDispersionNote: 'Koch et al.’s asymptotic formula: {{koch}}',
        StatDispersion: 'Charge dispersion of f₀₁',
        StatRatioNote: 'above about 20 it is a transmon; near 1, a Cooper-pair box',
        StatRatio: 'E_J / E_C',
        StatAnharmNote: 'about −E_C, which is −{{ec}} MHz: what lets a pulse drive only 0→1',
        StatAnharm: 'Anharmonicity',
        StatF01Note: 'the textbook √(8E_J E_C) − E_C says {{asym}} GHz',
        StatF01: 'Qubit frequency f₀₁',
        YieldHere: 'this fab',
        YieldSeries: 'Monte Carlo over chips',
        YieldY: 'chips with no frequency collision (%)',
        YieldX: 'junction resistance scatter, one sigma (%)',
        YieldTitle: 'Collision-free chips, {{n}} qubits in a chain',
        BandsHere: 'n_g',
        BandsY: 'energy / h above the ground mean (GHz)',
        BandsX: 'offset charge n_g (Cooper pairs)',
        BandsTitle: 'The ladder against stray charge on the island',
        PotentialExcited: '|1⟩',
        PotentialGround: '|0⟩',
        PotentialSeries: 'Josephson potential',
        PotentialY: 'energy / h (GHz)',
        PotentialX: 'phase across the junction φ (rad)',
        PotentialTitle: 'The quantum pendulum: −E_J cos φ and its two lowest states',
        JunctionNote: 'The junction is grown by letting oxygen into the evaporator for a few ' +
          'minutes between two aluminium depositions, and the oxide it leaves is a ' +
          'nanometre or so of amorphous AlOx. Electrons cross it by tunnelling, so ' +
          'its resistance grows exponentially with thickness - the THRM-04 gate-oxide ' +
          'lesson, with the goal reversed: here the fab needs a precise amount of ' +
          'tunnelling, not none. The resistance is measured warm, junction by ' +
          'junction, because Ambegaokar and Baratoff showed it fixes the critical ' +
          'current once the chip is cold. The barrier height and mass are fitted to ' +
          'real junctions; the steepness of the curve does not depend on them.',
        KnobMass: 'Effective mass in the oxide',
        KnobBarrier: 'Barrier height',
        KnobArea: 'Junction area',
        KnobOxide: 'Oxide thickness',
        StatEjNote: 'E_J/h - what the transmon below is built on',
        StatEj: 'Josephson energy',
        StatIcNote: 'Ambegaokar-Baratoff: I_c R_n = πΔ/2e, once the aluminium superconducts',
        StatIc: 'Critical current',
        StatRnNote: 'for a {{a}} µm² junction, measured warm',
        StatRn: 'Normal-state resistance',
        StatRaNote: '×{{f}} for every ångström more oxide',
        StatRa: 'R·A',
        RaBand: 'qubit junctions',
        RaHere: 'this oxide',
        RaSeries: 'Landauer, through the transfer matrix',
        RaY: 'log₁₀ R·A (Ω·µm²)',
        RaX: 'AlOx thickness (nm)',
        RaTitle: 'Resistance against oxide thickness',
        JunctionWave: 'the electron’s wave, through the oxide: {{f}} of it survives',
        JunctionAl: 'Al',
      },
      ebeam: {
        NextCurrent: 'The gauge study failed and single frames look sharp. Which of current, ' +
          'frames and the floor under the tool would you look at first, and what ' +
          'reading would tell you it is not the other two?',
        TitlePrecisionCurrent: 'Repeatability and spot against probe current',
        LabelSpotThere: 'Spot at that current',
        LabelAtCurrent: 'At a current of',
        LabelBestPrecision: 'Best repeatability',
        HypCurrent: 'An edge is located in a profile made of counted electrons, so its scatter ' +
          'should fall as one over the square root of the current. But the current ' +
          'comes from a source of fixed brightness: asking for more means imaging a ' +
          'larger patch of the tip onto the wafer. If both are true, repeatability ' +
          'should improve quickly at first and then stall - on the growing spot, or ' +
          'on whatever noise current cannot buy down.',
        AskedCurrent: 'Does more beam current give a better-repeating CD?',
        SumCurrent: 'Probe current against repeatability: counting noise falls until the noise ' +
          'current cannot touch is all that is left.',
        NextLanding: 'The yield section above shows where a resist stops charging. If the ' +
          'sharpest spot is at 2000 eV and the charge-neutral point is near 500, ' +
          'which would you give up, and what would the image look like if you chose ' +
          'wrong?',
        TitleSpotEnergy: 'Spot size against landing energy',
        LabelChromaticShare: 'Chromatic blur at the low end',
        LabelSpotHigh: 'Spot, highest energy',
        LabelSpotLow: 'Spot, lowest energy',
        HypLanding: 'Two blurs depend on the beam energy. Diffraction goes as the electron’s ' +
          'wavelength, which falls as one over the square root of the energy. ' +
          'Chromatic blur goes as the gun’s energy spread divided by the energy, ' +
          'which falls faster. If both are right, the spot should shrink steadily as ' +
          'the beam gets harder, with the chromatic share collapsing first.',
        AskedLanding: 'What does landing energy do to the spot?',
        SumLanding: 'Landing energy against spot size: diffraction and chromatic blur both ' +
          'shrink as the beam gets harder.',
        knob: {
          alphaMrad: 'Convergence half-angle',
          frames: 'Frames averaged',
          currentPa: 'Probe current',
          landingEv: 'Landing energy',
        },
        AssistantBlurb: 'Ask what landing energy does to the spot, or whether more beam current ' +
          'makes a CD repeat better. Every answer is the column run against this ' +
          'recipe.',
        AssistantLead: 'SEM-23 inquiry assistant',
        TileSignalNote: 'current × frames × detector, against qualified',
        TileSignal: 'Signal per measurement',
        TilePrecisionNote: 'the same line, measured again and again',
        TilePrecision: 'Repeatability',
        TileSpotNote: 'diffraction {{d}} nm, chromatic {{c}} nm',
        TileSpot: 'Spot on the wafer',
        SectionTool: 'The CD-SEM this shift: how small it sees, how well it repeats',
        fault: {
          tiredScintillator: {
            Evidence: [
              'Detector signal on the reference is 25% of qualified.',
              'The noise is INSIDE each frame, not between frames: registration scatter ' +
                'is nominal.',
              'The spot size and the image power spectrum are nominal.',
            ],
            Truth: 'The scintillator has been burned by years of electrons and gives a quarter ' +
              'of its original light. Fewer counted electrons per pixel is more noise per ' +
              'pixel, and an edge found in a noisy profile wanders: precision goes as one ' +
              'over the square root of the signal, so a quarter of the signal is twice ' +
              'the scatter. The spot is untouched. More frames does fix it, at four times ' +
              'the dose to a resist that shrinks under the beam.',
            Complaint: 'Repeatability has crept out of limit over a month. Images look grainy, and ' +
              'operators have started averaging more frames to get a clean one, which has ' +
              'halved throughput.',
            Label: 'Detector scintillator worn out',
          },
          groundedMount: {
            Evidence: [
              'Frame-to-frame registration scatter is three times its limit while the ' +
                'single-frame spot is nominal.',
              'Detector signal is nominal, so it is not counting noise.',
              'The accelerometer shows the frame moving with the floor.',
            ],
            Truth: 'One air mount has lost pressure and the frame is resting on its stop, so ' +
              'the column now rides the floor: 1.2 nm rms of beam-to-wafer motion. It ' +
              'barely shows in one frame because a frame is fast. It shows between ' +
              'frames, as each lands somewhere slightly different, and the averaged edge ' +
              'wanders with them. The SEM-23 console desk standing off the plinth is ' +
              'there to prevent exactly this.',
            Complaint: 'The gauge study failed: the same line measured ten times scatters three ' +
              'times what it should. Single images look perfectly sharp.',
            Label: 'Isolation mount not floating',
          },
          stigmatorDrift: {
            Evidence: [
              'The image power spectrum is elliptical. A chromatic blur is round.',
              'Emission noise is nominal, and flashing the tip changed nothing.',
              'Through focus, the blur stretches and turns through ninety degrees.',
            ],
            Truth: 'The stigmator has drifted and there are 3.5 nm of astigmatism in the spot. ' +
              'It looks like the gun because both make a soft image and a wide line, and ' +
              'it is not the gun because the blur has a DIRECTION: the spot is stretched ' +
              'one way below focus and the other way above it. Lines running along the ' +
              'stretch still look sharp, which is why one operator says the tool is fine ' +
              'and the next says it is not.',
            Complaint: 'Images are soft and lines read wide. The gun was flashed this morning and ' +
              'it made no difference at all.',
            Label: 'Astigmatism uncorrected',
          },
          gassyTip: {
            Evidence: [
              'Emission current noise is up with the blur. Astigmatism does not make a ' +
                'gun flicker.',
              'The blur is round: the power spectrum is not stretched.',
              'The defocus series reads 0.7 eV of spread.',
            ],
            Truth: 'The cold field-emission tip has not been flashed in days. Gas adsorbed on ' +
              'it changes the work function patch by patch, and the energy spread has ' +
              'gone from 0.25 to 0.7 eV. At a landing energy of 500 eV that spread is ' +
              'most of the spot: chromatic blur goes as ΔE/E, so a low-voltage column is ' +
              'the kind that feels it. The lines are not wide. The ruler is.',
            Complaint: 'Images have gone soft since the weekend and every line is reading about a ' +
              'nanometre wide. Litho has already turned the dose up once to bring CD back ' +
              'on target.',
            Label: 'Gun tip gathering gas',
          },
          semHealthy: {
            Evidence: [
              'The spot and the repeatability are both nominal.',
              'Every bench reading is inside its limit.',
            ],
            Truth: 'Nothing is wrong. The correct call is to keep measuring.',
            Complaint: 'Routine shift. The daily standard measured on target.',
            Label: 'Nothing wrong',
          },
        },
        reading: {
          grainCheck: {
            Label: 'Noise inside a single frame on a flat sample (30 min)',
          },
          accelerometer: {
            Limit: 'isolating',
            Label: 'Accelerometer on the column frame against the floor (1.5 h)',
          },
          throughFocus: {
            Limit: 'round',
            Label: 'Through-focus series on the gold-on-carbon standard (1.0 h)',
          },
          energySpread: {
            Label: 'Energy spread from the chromatic defocus series (2.0 h)',
          },
          detectorSignal: {
            Label: 'Detector signal on the reference sample',
          },
          frameJitter: {
            Label: 'Frame-to-frame registration scatter',
          },
          fftEllipticity: {
            Label: 'Ellipticity of the image power spectrum',
          },
          emissionNoise: {
            Label: 'Emission current noise',
          },
        },
        aspect: {
          detector: {
            Action: 'Replace the scintillator and re-set the photomultiplier gain',
            Label: 'Secondary-electron detector',
          },
          isolation: {
            Action: 'Re-float and re-level the air mounts, and find what is touching the frame',
            Label: 'Vibration isolation',
          },
          column: {
            Action: 'Re-align the column and null the astigmatism on the gold-on-carbon standard',
            Label: 'Column alignment and stigmators',
          },
          gun: {
            Action: 'Flash the tip to drive off adsorbed gas, and let the emission settle',
            Label: 'Field-emission gun',
          },
        },
        SecondaryLabel: 'Repeatability',
        ToolLabel: 'SEM-23 CD-SEM and E-Beam Inspection',
        GuardEtched: 'This pattern has already been etched into the film. After-develop ' +
          'inspection is the look BEFORE the etch, while a bad print can still be ' +
          'stripped and reworked.',
        GuardNoPattern: 'Nothing is printed on this lot. Expose it at EUV-22 or LITH-03 first: a ' +
          'CD-SEM measures a pattern, and this wafer does not carry one.',
        MeasureLot: 'Measure the pattern and release it to etch',
        NoteNoLot: 'No lot on the stage. Bring an exposed wafer here to measure it.',
        SectionInspection: 'E-beam inspection: seeing more, covering less',
        SectionLinescan: 'Measuring a line (Monte Carlo)',
        WaveNote: 'MET-05’s box sends electrons of a few eV through slits a nanometre apart, ' +
          'and the beams fan out at the angle their wavelength sets. The column above ' +
          'works at {{e}} eV, where the wavelength is {{pm}} pm, and its lens accepts ' +
          'only a few milliradians - so the focused spot spreads by 0.54 λ/α, a few ' +
          'nanometres, for exactly the reason the slit spreads the beam. The box ' +
          'below is MET-05’s own solver.',
        SectionWave: 'The wave behind the diffraction term (MET-05’s box)',
        SectionProbe: 'The spot: diffraction against aberration',
        YieldBalance: 'σ = 1: as many leave as arrive',
        YieldLandingMark: 'landing {{e}}',
        YieldChartTitle: 'What leaves the sample for each electron that lands',
        YieldChartX: 'landing energy, log₁₀ eV (2 = 100 eV, 3 = 1 keV)',
        YieldChartY: 'electrons out per electron in',
        YieldSeriesDelta: 'δ secondaries (< 50 eV)',
        YieldSeriesEta: 'η backscattered (≥ 50 eV)',
        YieldSeriesTotal: 'σ = δ + η',
        YieldChargeBalanced: 'balanced - an insulator sits still here',
        YieldChargePositive: 'charges positive - more electrons leave than land',
        YieldChargePending: 'sweeping… the beam has not reached this energy yet',
        YieldChargeNegative: 'charges negative - electrons pile up in the surface',
        YieldStatMax: 'Most secondaries',
        YieldStatMaxNote: 'at {{e}} landing energy',
        YieldStatCross: 'Where σ crosses one',
        YieldStatCrossNote: 'E₁ and E₂: the landing energies an insulator can be imaged at without charging',
        YieldStatLanding: 'At {{e}}',
        YieldProgress: 'Sweeping… {{pct}}% ({{n}} beam electrons per energy)',
        YieldDone: '{{n}} beam electrons in {{s}} s',
        YieldIdle: 'Press Run to fire the beam at the film.',
        YieldMaterial: 'Under the beam',
        YieldCar: 'Chemically amplified resist',
        YieldMor: 'Metal-oxide resist',
        YieldSi: 'Silicon',
        YieldPause: 'Pause',
        YieldResume: 'Resume',
        YieldRun: 'Run the sweep',
        YieldKnobLanding: 'Landing energy to check',
        SectionYield: 'Why the image has contrast — secondary-electron yield against landing energy (Monte Carlo)',
        YieldNote: 'Every picture an SEM takes is made of electrons that left the sample, and they come in two kinds. Backscattered electrons are beam electrons that turned round inside the solid and came back out fast; secondaries are the slow electrons the beam knocked loose, and only those born within a few nanometres of the surface get out. This sweep fires beam electrons at a thick flat film with the same Monte Carlo cascade the linescan above uses, and counts both. δ and η are drawn from 100 eV up: below that a beam electron bouncing straight back already falls under the 50 eV cut, so only their sum is drawn there. Watch δ rise, peak at a few hundred eV, and fall as a faster beam spends its energy too deep for any secondary to reach the surface. Where the total σ = δ + η is above one the surface loses charge; below one it gains it. An insulating resist sits still only at the two crossovers, and the upper one, E₂, is why a CD-SEM lands at a few hundred volts. Omitted: the charge is not fed back into the beam, the film is flat, and the model\'s silicon peaks lower and backscatters more than measured silicon.',
        SectionGun: 'The gun: electrons tunnelling out of a tip (THRM-04’s transfer matrix)',
        KnobSnr: 'Signal to noise required',
        KnobContrast: 'Defect contrast',
        KnobPixel: 'Pixel size',
        InspNote: 'An electron probe sees an open contact or a buried void that no optical ' +
          'tool will ever flag, because it does not depend on light scattering off ' +
          'something smaller than its wavelength. But a defect is only seen when it ' +
          'stands out of the counting noise, and that fixes the electrons every pixel ' +
          'needs. A smaller pixel needs a smaller probe, a smaller probe carries less ' +
          'current, and past a hundred nanoamps the beam blurs itself. So an e-beam ' +
          'inspector goes to the few places a design or an optical scan says are ' +
          'worth the time.',
        StatWaferTimeNote: 'INSP-13 scans one in {{min}} min, blind below {{nm}} nm',
        Hours: '{{h}} hours',
        Years: '{{y}} years',
        StatWaferTime: 'A whole wafer',
        StatRate: 'Area an hour',
        StatDwellNote: 'never below {{min}} ns - the detector has to settle',
        StatDwell: 'Dwell per pixel',
        StatInspCurrentNote: 'what a {{probe}} nm probe can carry, capped at {{cap}} nA',
        StatInspCurrent: 'Beam current',
        StatElectronsPxNote: 'to see a {{c}}% contrast defect at signal-to-noise {{snr}}',
        StatElectronsPx: 'Electrons each pixel needs',
        InspWafer: 'a whole wafer an hour',
        InspHere: 'this pixel',
        InspOptical: 'INSP-13 optical scan (sees down to {{nm}} nm)',
        InspEbeam: 'Electron beam',
        InspY: 'log₁₀ mm² an hour',
        InspX: 'pixel size (nm)',
        InspTitle: 'Area inspected an hour, against pixel size',
        ScanNote: 'A CD-SEM never sees a width. It counts slow electrons escaping the sample ' +
          'while the beam steps across, and near a sidewall they escape through the ' +
          'wall as well as the top, so the edges glow. The number it reports is where ' +
          'that signal crosses a chosen fraction of the way from the space to the ' +
          'peak - move the threshold and the same line measures wider or narrower, ' +
          'tilt the sidewall and the answer moves between the top and the bottom ' +
          'width. That offset is calibrated against a cross-section. What cannot be ' +
          'calibrated away is the counting: average more frames and the spread of ' +
          'repeated measurements shrinks as their square root, while every extra ' +
          'electron shrinks the resist being measured.',
        KnobSidewall: 'Sidewall angle from vertical',
        KnobHeight: 'Resist height',
        KnobLineCd: 'Line width at the bottom',
        KnobFrames: 'Frames averaged',
        KnobThreshold: 'Edge threshold',
        ScanRepeat: 'Repeat forty times',
        ScanAgain: 'Measure again',
        ScanRun: 'Scan the line',
        ScanStale: 'The sample or the beam changed - press Scan to measure it.',
        ScanStatus: 'Scanned with a {{probe}} nm probe at {{e}} eV, from the column above.',
        ScanProgress: 'Scanning the line… {{pct}}%',
        StatPrecisionNote: 'mean {{mean}} nm',
        StatPrecision: 'Precision, 40 repeats',
        StatDoseNote: '{{pa}} pA, {{dwell}} ns a pixel, {{frames}} frames, {{lines}} lines averaged',
        StatDose: 'Electrons per pixel',
        StatBiasNote: 'what the tool is calibrated out against a cross-section',
        StatBias: 'Bias against the mid-height width',
        StatCdNote: 'at a {{threshold}}% threshold; true width {{top}} nm at the top, ' +
          '{{bottom}} nm at the bottom',
        StatCd: 'Measured width',
        SignalCd: 'edges · {{cd}} nm',
        SignalMeasured: 'Measured, {{frames}} frames',
        SignalExpected: 'Expected (Monte Carlo)',
        SignalY: 'secondaries per beam electron',
        SignalX: 'beam position (nm)',
        SignalTitle: 'Secondary electrons across the line',
        ScanSilicon: 'silicon',
        ScanResist: 'resist',
        KnobAlpha: 'Convergence half-angle',
        KnobCurrent: 'Probe current',
        KnobLanding: 'Landing energy',
        ProbeNote: 'Open the lens up and the diffraction blur shrinks - the electron is a ' +
          'wave, and a wider cone of it focuses tighter - but rays far from the axis ' +
          'focus short and electrons of different energies focus at different depths, ' +
          'and both of those grow with the angle. The best spot is where they cross. ' +
          'At a few hundred eV the chromatic term usually wins, so the gun’s energy ' +
          'spread from the section above decides the resolution; asking for more ' +
          'current grows the source’s image and trades resolution for signal.',
        StatGunUsedNote: 'energy spread {{spread}} eV, from the gun above',
        GunSchottky: 'Schottky',
        GunCold: 'Cold field emission',
        StatGunUsed: 'Gun',
        StatBestNote: 'at {{a}} mrad',
        StatBest: 'Best possible',
        StatProbeNote: 'diffraction {{d}}, chromatic {{c}}, spherical {{s}}, source {{i}} nm',
        StatProbe: 'Spot at this angle',
        StatLambdaNote: 'at {{e}} eV - far smaller than an atom, and it still limits the spot',
        StatLambda: 'Electron wavelength',
        ProbeHere: 'set',
        ProbeBest: 'best angle',
        ProbeSpherical: 'Spherical aberration',
        ProbeChromatic: 'Chromatic, from the gun’s spread',
        ProbeDiffraction: 'Diffraction, 0.54 λ/α',
        ProbeTotal: 'The spot',
        ProbeY: 'diameter holding half the current (nm)',
        ProbeX: 'convergence half-angle (mrad)',
        ProbeTitle: 'Spot size against convergence angle',
        GunNote: 'At a few volts per nanometre the vacuum outside a tungsten point is a ' +
          'barrier under a nanometre wide, and electrons tunnel through it - ' +
          'Fowler-Nordheim tunnelling, the same regime THRM-04 reaches when a gate ' +
          'oxide is driven past its band offset. The current comes from the same ' +
          'transfer matrix, handed vacuum instead of oxide, integrated over the ' +
          'metal’s electrons; it lands on the Murphy-Good equation without being told ' +
          'it. Heat the tip and coat it to lower the work function and the same ' +
          'calculation becomes a Schottky emitter: electrons climbing over a barrier ' +
          'the field has pulled down. It is steadier, and its energy spread is wider ' +
          '- which the next section turns into a bigger spot.',
        KnobField: 'Extraction field at the tip',
        EmitterSchottky: 'Schottky, ZrO/W, 1800 K',
        EmitterCold: 'Cold field emission, W, 300 K',
        KnobEmitter: 'Emitter',
        StatBrightnessNote: 'A/(m²·sr·V) - how much current a small, well-collimated probe can carry',
        StatBrightness: 'Reduced brightness',
        StatTopNote: 'above the Fermi level, after the field and the image charge have pulled it ' +
          'down',
        StatTop: 'Top of the barrier',
        StatSpreadNote: 'FWHM of the emitted electrons - sets the chromatic blur below',
        StatSpread: 'Energy spread',
        StatJNote: '{{i}} µA from a {{r}} nm tip',
        StatJ: 'Current density',
        FnSeries: 'Straight where it tunnels',
        FnY: 'ln(J / F²)',
        FnX: '1 / field (nm/V)',
        FnTitle: 'The Fowler-Nordheim plot',
        JfHere: 'this field',
        JfMg: 'Murphy-Good equation',
        JfTmm: 'Transfer matrix (THRM-04 engine)',
        JfY: 'log₁₀ current density (A/m²)',
        JfX: 'field at the tip (V/nm)',
        JfTitle: 'Current density against field',
        GunWidth: '{{w}} nm to tunnel',
        GunX: 'distance into vacuum (nm)',
        GunOut: 'out',
        GunMetal: 'tip',
      },
      euv: {
        NextPower: 'The tool is a quarter slow this week and the source is at full power. ' +
          'Where between the plasma and the wafer would you put a power meter first, ' +
          'and what would each reading rule out?',
        TitlePowerWph: 'Wafers per hour against source power',
        LabelMirrorHeat: 'Heat into the mirrors',
        LabelWphHigh: 'Throughput, highest power',
        LabelWphLow: 'Throughput, lowest power',
        HypPower: 'Exposure time is dose over power at the wafer, so doubling the source ' +
          'should halve it. But a wafer also spends time being loaded, aligned and ' +
          'stepped with no light on it at all. If that overhead is real, throughput ' +
          'should rise more slowly than power and flatten toward a ceiling no source ' +
          'can lift.',
        AskedPower: 'How much throughput does source power buy?',
        SumPower: 'Source power against throughput: diminishing, and paid for in mirror heat.',
        NextDose: 'A metal-oxide resist absorbs three times as many of the photons that reach ' +
          'it. Would you spend that on a lower dose or on a smoother edge, and who in ' +
          'the fab would argue for each?',
        AxisWph: 'wafers per hour',
        TitleDoseWph: 'Wafers per hour against dose',
        AxisLer: 'LER 3σ (nm)',
        TitleDoseTrade: 'Roughness and throughput against dose',
        LabelWphLost: 'Throughput given up',
        LabelLerHigh: 'Roughness, highest dose',
        LabelLerLow: 'Roughness, lowest dose',
        HypDose: 'An edge is rough because it is drawn by a countable number of photons, and ' +
          'counting noise falls as one over the square root of the count. If that is ' +
          'the whole story, quadrupling the dose should about halve the roughness - ' +
          'while the exposure, which has to deliver that dose from a fixed beam, ' +
          'takes four times as long.',
        AskedDose: 'What does dose buy, and what does it cost?',
        SumDose: 'Dose buys smooth edges with throughput: shot noise against exposure time.',
        knob: {
          sourceW: 'Source power at the intermediate focus',
          doseMjCm2: 'Exposure dose',
        },
        AssistantBlurb: 'Ask what dose costs in wafers per hour, or what more source power really ' +
          'buys. Every answer is the whole chain, plasma to wafer, run against this ' +
          'recipe.',
        AssistantLead: 'EUV-22 inquiry assistant',
        TileIfNote: '{{heat}} W of it ends up heating mirrors',
        TileIf: 'Power at the intermediate focus',
        TileLerNote: 'at {{dose}} mJ/cm² actually delivered',
        TileLer: 'Line-edge roughness',
        TileWphNote: '{{w}} W reaches the wafer',
        TileWph: 'Throughput',
        SectionTool: 'The scanner this shift: photons in, wafers out',
        fault: {
          doseSensorOverReads: {
            Evidence: [
              'Roughness on the monitor is above its limit while power is nominal ' +
                'everywhere on the beam path. No loss of light does that.',
              'Throughput is ABOVE rated. Every other fault on this tool makes it slower.',
              'The reference photodiode on the stage reads 80% of what the dose sensor ' +
                'reports.',
            ],
            Truth: 'The slit energy sensor has drifted and reports a fifth more dose than it ' +
              'delivers, so the tool ends each exposure early. The wafer gets 32 mJ/cm² ' +
              'against a recipe of 40. Roughness is photon shot noise - it goes as one ' +
              'over the square root of dose - so the edges are about a tenth rougher, and ' +
              'the tool is faster for exactly the same reason. The good news and the bad ' +
              'news are one fault.',
            Complaint: 'Nobody has complained about this tool. It is running ten percent over its ' +
              'rated throughput, which the shift lead has put in the weekly report as ' +
              'good news. SEM-23 has flagged the last three lots for edge roughness.',
            Label: 'Dose sensor over-reading',
          },
          carbonOnOptics: {
            Evidence: [
              'Power at the intermediate focus is on target; wafer-level power is a ' +
                'quarter low against it. The loss is inside the scanner.',
              'The mirror-by-mirror check reads 68% where it should read 70%.',
              'Pulse energy at the plasma is nominal.',
            ],
            Truth: 'Each projection mirror has lost two points of reflectance to carbon ' +
              'cracked out of residual hydrocarbons by the beam itself. Two points sounds ' +
              'like nothing. There are eleven reflections between the intermediate focus ' +
              'and the wafer, and 0.68 to the eleventh is three quarters of 0.70 to the ' +
              'eleventh. A multilayer stack has no tolerance to give.',
            Complaint: 'Throughput is down about a sixth. Source power at the intermediate focus ' +
              'is exactly on target, so the source group has closed their ticket.',
            Label: 'Carbon growth on the projection mirrors',
          },
          tinOnCollector: {
            Evidence: [
              'Pulse energy at the plasma is nominal, and power at the intermediate focus ' +
                'is 40% low. The loss is between the two, and only the collector is between ' +
                'the two.',
              'Wafer-level power is in proportion to intermediate-focus power: the ' +
                'projection optics are passing what they are given.',
              'The far-field image shows the collector at 60% of clean.',
            ],
            Truth: 'Tin debris has built up on the collector and it now reflects about 60% of ' +
              'what a clean one does. The source is right: the plasma is making all of ' +
              'its light. It is being lost on the first mirror it meets, which is the one ' +
              'mirror in the tool that sits in line of sight of a tin explosion fifty ' +
              'thousand times a second.',
            Complaint: 'Throughput has been sliding for three weeks and is now about a quarter ' +
              'down. Source says their pulse energy is rock steady.',
            Label: 'Tin on the collector',
          },
          dropletTimingDrift: {
            Evidence: [
              'Pulse energy at the plasma is down to 60% of qualified. A dirty collector ' +
                'cannot do that: it sits after the plasma.',
              'Power at the intermediate focus is down by the same fraction, so nothing ' +
                'further is lost on the collector.',
              'The timing scan finds the main pulse late.',
            ],
            Truth: 'The main pulse is arriving late, after the pre-pulse has already let the ' +
              'flattened droplet drift out of focus, so each shot makes about 60% of the ' +
              'in-band light it should. The light is missing AT THE PLASMA - it was never ' +
              'made - and the collector is reflecting what it is given perfectly well. ' +
              'Swapping it would cost thirty hours and change nothing.',
            Complaint: 'Throughput is down about a quarter since the droplet generator nozzle was ' +
              'changed. The night shift thinks the collector has finally had enough tin ' +
              'and wants to book the swap.',
            Label: 'Laser pulse missing the droplet',
          },
          euvHealthy: {
            Evidence: [
              'Power is where it should be at the plasma, the intermediate focus and the ' +
                'wafer.',
              'Roughness on the monitor is nominal.',
            ],
            Truth: 'Nothing is wrong. The correct call is to keep exposing.',
            Complaint: 'Routine shift. Throughput on plan, monitor wafers in spec.',
            Label: 'Nothing wrong',
          },
        },
        reading: {
          doseCrossCheck: {
            Label: 'Dose cross-check with the reference photodiode on the stage (2.0 h)',
          },
          opticsTransmission: {
            Label: 'Mirror-by-mirror transmission check with the tool open to service (6.0 h)',
          },
          collectorImage: {
            Label: 'Far-field image of the collector (2.0 h)',
          },
          dropletTiming: {
            Limit: 'on target',
            Label: 'Droplet-to-pulse timing scan on the source (1.5 h)',
          },
          lerMonitor: {
            Label: 'Line-edge roughness on the monitor wafer',
          },
          waferPower: {
            Label: 'Wafer-level power against intermediate-focus power',
          },
          ifPower: {
            Label: 'Power at the intermediate focus',
          },
          plasmaEnergy: {
            Label: 'In-band pulse energy at the plasma',
          },
        },
        aspect: {
          doseControl: {
            Action: 'Re-calibrate the slit energy sensor against the reference photodiode',
            Label: 'Dose sensor',
          },
          optics: {
            Action: 'Run the in-situ hydrogen-radical clean on the projection mirrors',
            Label: 'Projection optics',
          },
          collector: {
            Action: 'Swap the collector for a clean one and send this one out for tin removal',
            Label: 'Collector mirror',
          },
          source: {
            Action: 'Re-time the pre-pulse and main pulse to the droplet, and re-centre the ' +
              'droplet stream',
            Label: 'Droplet generator and laser timing',
          },
        },
        SecondaryLabel: 'Line-edge roughness',
        ToolLabel: 'EUV-22 EUV Scanner with Tin Source',
        GuardNoPrint: 'Nothing has been printed yet. Let the print section above finish, so the ' +
          'lot is exposed with a recipe somebody has looked at.',
        NoteWouldExpose: 'This lot would be exposed at {{dose}} mJ/cm², with edges rough to {{ler}} ' +
          'nm 3σ.',
        ExposeLot: 'Expose the wafer and send it to the CD-SEM',
        NoteNoLot: 'No lot on the chuck. Bring a coated wafer here to expose it.',
        SectionPrint: 'Printing with countable light',
        SectionCascade: 'One photon, a spray of electrons (Monte Carlo)',
        SectionThroughput: 'From watts at the source to wafers an hour',
        AbsorbCar: 'Polymer CAR',
        AbsorbCarF: 'Fluorinated CAR',
        AbsorbMorSn: 'Tin-oxo MOR',
        AbsorbMorHf: 'Hafnium-oxo MOR',
        AbsorbSi: 'Silicon (substrate)',
        AbsorbBar: '{{el}}: {{share}}% of μ from {{mass}}% of the mass',
        AbsorbElementsTitle: 'Who does the absorbing: μ by element',
        AbsorbElementsX: 'element in the film',
        AbsorbElementsY: 'part of μ (per µm)',
        AbsorbWithout: 'without the added atom',
        AbsorbCurveTitle: 'How much of the light the film keeps',
        AbsorbCurveX: 'film thickness (nm)',
        AbsorbCurveY: 'absorbed (%)',
        AbsorbShort: 'μ = {{mu}} per µm · {{pct}}% of the photons absorbed',
        AbsorbHelp: 'The photon budget in full',
        AbsorbStatus: 'μ = {{mu}} per µm · attenuation length {{length}} nm · {{absorbed}} of {{incident}} photons per nm² absorbed ({{pct}}%) · the top 5 nm gets {{ratio}}× the bottom 5 nm',
        AbsorbGain: '{{pct}}% {{el}} by mass raises μ {{factor}}× at the same density. A metal-oxide resist also brings the density.',
        AbsorbResist: 'Resist',
        AbsorbThickness: 'Film thickness',
        AbsorbDose: 'Dose',
        AbsorbAdd: 'Add an atom',
        AbsorbAddNone: 'Nothing',
        AbsorbAddFraction: 'Its share of the mass',
        SectionAbsorb: 'How many photons the resist keeps — absorption atom by atom (photoelectric cross-sections)',
        AbsorbNote: 'The cascade above starts from one absorbed photon and the print below counts them, but a dose delivers photons to the surface, not into the film. At 92 eV absorption is the photoelectric effect and nothing else, so the film\'s attenuation is a sum over its atoms: μ = ρ Σ wᵢ(μ/ρ)ᵢ, each element\'s cross-section set by its inner shells. Per atom, tin absorbs about thirty times what carbon does, oxygen three and a half times, hydrogen almost nothing. Per gram the gap is much smaller, which is why adding a tenth of a polymer\'s mass as tin buys only about a sixth more absorption; a metal-oxide resist wins on loading and density together. Watch the bars: in a polymer oxygen out-absorbs its mass share, and in a tin cluster tin takes most of μ. A 35 nm polymer film keeps about one photon in seven, and a darker, thicker film gives its top more dose than its bottom, which prints a sloped wall. The cross-sections are the Henke tables at 91.84 eV, least certain for tin and hafnium on their 4d and 4f resonances; what an absorbed photon then does is the cascade\'s business.',
        SectionMirror: 'The mirror: forty pairs of molybdenum and silicon (transfer matrix)',
        SectionSource: 'The source: tin ions that glow at 13.5 nm',
        PrintNote: 'Each square nanometre gets a Poisson number of photons around what the ' +
          'aerial image asks for, each photon throws its acids a few nanometres, the ' +
          'bake blurs them, and the resist clears where enough acid arrived. Nothing ' +
          'here is told that roughness goes as one over the square root of the dose - ' +
          'the counting does that. Raise the dose and the edges smooth while the line ' +
          'thins; lower it to save throughput and the edges roughen until, below a ' +
          'cliff, a space bridges or a line breaks somewhere along it. More acid ' +
          'diffusion hides the noise and blurs the image with it. The rated dose is ' +
          'the resist chemist’s knob: it sets where half-pitch lines print.',
        PrintSweep: 'Sweep the dose',
        PrintAgain: 'Expose another wafer',
        Na055: 'High-NA 0.55',
        Na033: 'NA 0.33',
        KnobSizeDose: 'Resist rated dose',
        KnobDiffusion: 'Acid diffusion in the bake',
        KnobPitch: 'Line pitch',
        KnobPrintDose: 'Dose',
        PrintScale: '{{w}} nm along the lines · {{h}} nm across',
        PrintWorking: 'Exposing, photon by photon…',
        StatNilsNote: 'aerial image contrast {{contrast}}%',
        StatNils: 'Image log slope',
        StatFailuresNote: '{{breaks}} broken and {{bridges}} bridged, of {{rows}} line rows',
        StatFailures: 'Stochastic failures',
        StatLerNote: '3σ along the edge; width roughness {{lwr}} nm',
        StatLer: 'Line-edge roughness',
        StatCdNote: 'target {{half}} nm, half the pitch',
        StatCd: 'Printed line width',
        StatPhotonsNote: '{{abs}} of them absorbed in the resist',
        StatPhotons: 'Photons per nm²',
        SweepHere: 'this exposure',
        SweepFail: 'Rows broken or bridged ÷ 20',
        SweepLer: 'Line-edge roughness',
        SweepY: 'LER 3σ (nm) · failed rows ÷ 20',
        SweepX: 'dose (mJ/cm²)',
        SweepTitle: 'Roughness and failures against dose, resist re-rated at each',
        CascadeNote: 'A 92 eV photon is absorbed by one atom, which throws out a photoelectron ' +
          'carrying most of its energy. That electron knocks loose others, each ' +
          'ionization costing about ten electronvolts, until none is fast enough to ' +
          'ionize anything more; where the slow ones stop, a photoacid generator can ' +
          'catch them and release an acid. So the image is not written where the ' +
          'light landed but a few nanometres around it, and the count per photon is ' +
          'itself random. A metal-oxide resist absorbs four times harder - tin’s 4d ' +
          'electrons again, the same ones glowing in the source - and its denser film ' +
          'stops the electrons sooner.',
        CascadeFire: 'Absorb another seven photons',
        ResistMor: 'Metal oxide (tin)',
        ResistCar: 'Chemically amplified (polymer)',
        KnobResist: 'Resist',
        StatAbsorbedNote: 'of the photons reaching {{t}} nm of resist',
        StatAbsorbed: 'Film absorbs',
        StatBlurNote: 'RMS sideways from where the photon landed',
        StatBlur: 'Electron blur',
        StatElectronsNote: 'the photoelectron and every secondary it freed',
        StatElectrons: 'Electrons per photon',
        StatEventsNote: '{{blank}}% of photons make none at all',
        StatEvents: 'Acids per photon',
        HistSeries: '1500 photons',
        HistY: 'share of photons',
        HistX: 'chemical events from one photon',
        HistTitle: 'Acids per absorbed photon',
        CascadeEvent: 'chemical event',
        CascadeAcid: 'acid made',
        CascadeSecondary: 'secondaries',
        CascadePhotoelectron: 'photoelectron',
        CascadePhoton: 'photon absorbed',
        CascadeSubstrate: 'underlayer',
        KnobMirrors: 'Mirrors after the source',
        KnobMirrorR: 'Reflectance per mirror',
        KnobDose: 'Resist dose',
        KnobSource: 'Source power at intermediate focus',
        WphNote: 'Every link multiplies. A dozen reflections at 70% keep under 2%, and ' +
          'shaping a uniform slit keeps half of that, so 250 W at the source is about ' +
          'two and a half watts at the wafer. Halve the dose the resist needs and the ' +
          'tool nearly doubles its output - which is why a resist chemist and a ' +
          'source engineer are having the same argument, and why the next section ' +
          'shows what a lower dose costs.',
        StatWph: 'Throughput',
        StatExposureNote: 'plus {{overhead}} s a wafer that is not light: exchange, alignment, stepping',
        StatExposure: 'Light on per wafer',
        StatWaferNote: 'while the mirrors absorb {{heat}} W on the way',
        StatWafer: 'Power at the wafer',
        StatTransmissionNote: '{{n}} reflections, and the illuminator keeps {{keep}}% shaping the slit',
        StatTransmission: 'Optics transmission',
        WphHere: 'this recipe',
        WphSeries: 'Source at {{w}} W',
        WphY: 'wafers per hour',
        WphX: 'dose (mJ/cm²)',
        WphTitle: 'Wafers an hour, against the dose the resist needs',
        MirrorNote: 'Every material bends 13.5 nm light by only a few per cent, so no single ' +
          'surface reflects much. Stack a heavy metal and a light spacer, each pair ' +
          'half a wavelength thick, and the weak reflections from eighty interfaces ' +
          'arrive in step. The standing wave inside puts its dark nodes in the ' +
          'molybdenum, where light would be absorbed. Rough interfaces soften the ' +
          'steps the mirror reflects from; carbon cracked out of the vacuum by the ' +
          'light itself absorbs on top. A scanner has a dozen of these reflections in ' +
          'a row, so a point lost on one mirror is lost twelve times.',
        KnobAngle: 'Angle of incidence',
        KnobCarbon: 'Carbon grown on the mirror',
        KnobRough: 'Interface roughness',
        KnobPairs: 'Pairs',
        KnobGamma: 'Molybdenum share of the period',
        KnobPeriod: 'Period',
        MirrorSolving: 'Solving the stack…',
        StatBestNote: 'set now: {{now}} nm',
        StatBest: 'Best period for 13.5 nm',
        StatChainNote: '{{n}} reflections in a row, at 13.5 nm',
        StatChain: 'Through the whole scanner',
        StatFwhmNote: 'full width at half the peak',
        StatFwhm: 'Band width',
        StatRNote: 'peak {{peak}}% at {{at}} nm',
        StatR: 'Reflectance at 13.5 nm',
        PairsHere: 'this coating',
        PairsSeries: 'Reflectance',
        PairsY: 'reflectance at 13.5 nm (%)',
        PairsX: 'Mo/Si pairs',
        PairsTitle: 'More pairs stop helping',
        MirrorEdge: 'Si L-edge',
        MirrorChain: 'After the whole chain of mirrors, ×20',
        MirrorSeries: 'This coating',
        MirrorY: 'reflectance (%)',
        MirrorX: 'wavelength (nm)',
        MirrorTitle: 'One mirror, against wavelength',
        FieldX: 'depth from the surface (nm)',
        FieldTitle: '|E|² inside the top of the mirror',
        SourceNote: 'The ladder is computed: every rung is a self-consistent solution of the ' +
          'Schrödinger equation for that ion, and it lands on the NIST values where ' +
          'NIST has them. The charge states are computed on it, from electron-impact ' +
          'ionization against recombination. Where the ions emit is measured, not ' +
          'computed: a one-electron calculation puts the 4d→4f jump near 17 nm, and ' +
          'it is the 4d and 4f electrons exchanging with each other - a many-electron ' +
          'effect - that pulls the light of Sn⁸⁺ to Sn¹³⁺ onto 13.5 nm together. That ' +
          'pile-up is why a tin plasma beats a black body by fifty times in the band ' +
          'that matters.',
        LaserSolid: 'Solid state, 1 µm',
        LaserCo2: 'CO₂, 10.6 µm',
        KnobLaser: 'Drive laser',
        KnobTeValue: '{{ev}} eV · {{kk}} thousand K',
        KnobTe: 'Electron temperature',
        StatCeNote: 'of the laser energy, in band, measured on real sources',
        StatCe: 'Production conversion',
        StatPhotonNote: 'the next electron off Sn¹⁰⁺ alone costs {{ev}} eV - more than twice the photon an ion gives back',
        StatPhoton: 'One photon',
        StatBlackNote: 'in the same band - why a line emitter, and why tin',
        StatBlack: 'A black body would put',
        StatInBandNote: 'of what these ions emit, inside 13.5 nm ± 1%',
        StatInBand: 'In band',
        StatMeanNote: 'the most common ion is Sn{{q}}+',
        StatMean: 'Mean charge',
        ScanHere: 'this plasma',
        ScanSolid: '1 µm laser, 10²¹ electrons/cm³',
        ScanCo2: 'CO₂ laser, 10¹⁹ electrons/cm³',
        ScanY: 'share of the light in band (%)',
        ScanX: 'electron temperature (eV)',
        ScanTitle: 'Where the laser should put the plasma',
        SpectrumBand: '2% band',
        SpectrumBlack: 'A black body at the same temperature',
        SpectrumTin: 'Tin ions at this temperature',
        SpectrumY: 'emitted power, peak = 1',
        SpectrumX: 'wavelength (nm)',
        SpectrumTitle: 'The light that comes back out',
        ChargeBand: 'these emit in band',
        ChargeSeries: 'Ions',
        ChargeY: 'share of the tin',
        ChargeX: 'charge state q, for tin ions Sn q+',
        ChargeTitle: 'Which ions a plasma at {{te}} eV holds',
        LadderPhoton: 'one 13.5 nm photon',
        LadderNist: 'Measured (NIST)',
        LadderSolved: 'Solved (Hartree-Fock-Slater)',
        LadderY: 'ionization energy (eV)',
        LadderX: 'charge state q of the ion being ionized',
        LadderTitle: 'The ladder: energy to take the next electron',
      },
      panel: {
        HelpMore: 'More about this',
        AboutSim: 'What this shows, and what the model leaves out',
        BenchKnobs: 'Knobs',
        BenchReadouts: 'Readouts',
        BenchRun: 'Run',
        DownForPm: 'On the floor this tool is locked out for preventive maintenance: its skin ' +
          'is off and a technician is inside it, which is why its screen and the ' +
          'bay’s board read DOWN. What you operate here is its qualified process, the ' +
          'one it is released back to. A lot run from this panel is run on that ' +
          'process, as it will be when the tag comes off.',
        ValueOnTarget: 'on target',
        SolverHeadMeasured: 'measured',
        SolverHeadClosedForm: 'closed form',
        SolverHeadSolved: 'solved',
        LabelClosed: 'closed',
        LabelSolved: 'solved',
        TravelCard: 'Travel card',
        ThermalBudgetLine: 'Thermal budget: {{min}} min at 1000 °C across {{steps}} hot step(s) — enough to move boron ≈ {{nm}} nm',
        SectionStoredRecipes: 'Stored recipes',
        SectionWafer: 'Wafer',
        SectionLot: 'Lot',
        SectionProcessState: 'Process state',
        SectionRecipe: 'Recipe',
        SectionShift: 'Shift',
      },
      cleanroom: {
        NextThoseAddersBecomeCln09: 'Those adders become CLN-09\'s incoming particle count, and then INSP-13\'s ' +
          'defect density, and then a yield number at PRB-14. Follow one of them down ' +
          'the line and see what a minute of open carrier is worth in die.',
        AxisXAirChangesPerHour: 'air changes per hour',
        TitleRecoveryTimeAgainstAirChanges: 'Recovery time against air changes',
        LabelDepositionVelocity: 'Deposition velocity',
        NoteAfterFilterLoading: 'after filter loading',
        LabelEffectiveAch: 'Effective ACH',
        LabelRecovery: 'Recovery',
        HypSteadyStateConcentrationShouldGo: 'Steady-state concentration should go as generation over ventilation, so ' +
          'doubling the air changes should roughly halve the concentration - ' +
          'diminishing in absolute terms as it goes. Recovery time should fall the ' +
          'same way. If so, ventilation is a lever with a sharply rising price and a ' +
          'floor it cannot get under while people are in the room.',
        AskedWhatDoesVentilationBuyAnd: 'What does ventilation buy, and how long is an upset felt?',
        SumAirChangesRecoveryTimeAnd: 'Air changes, recovery time, and the exposure clock on an open carrier.',
        NextEveryNumberHereIsPer: 'Every number here is per minute of exposure. Ask me what the exposure time ' +
          'itself is worth - it is the one variable in this list that a tool schedule ' +
          'controls rather than a person.',
        LabelWrongOrder: 'Wrong order',
        LabelStreetClothes: 'Street clothes',
        LabelPenetrationGowned: 'Penetration, gowned',
        HypEachGarmentIsAFilter: 'Each garment is a filter, and filters in series multiply rather than add - ' +
          'so the combination should be dramatically better than any single piece, ' +
          'and REMOVING one piece should cost far more than that piece appears to ' +
          'contribute on its own. Order should matter for the same reason: a garment ' +
          'donned after a dirtier one is contaminated before it starts working.',
        AskedHowMuchDoesTheGowning: 'How much does the gowning protocol actually buy?',
        SumGarmentPenetrationAsAProduct: 'Garment penetration as a product, and why the order of donning is a rule.',
        NextTheDownflowCarriesParticlesFrom: 'The downflow carries particles from wherever a person is standing toward ' +
          'whatever is below and downstream of them. Given that, where in the aisle ' +
          'should you stand while a load port is open - and does the monitor on the ' +
          'wall tell you if you got it wrong?',
        LabelRoomAverageTheMonitor: 'room average (the monitor)',
        LabelWorkZone: 'work zone',
        AxisYParticlesM: 'particles/m³',
        AxisXPeopleInTheBay: 'people in the bay',
        TitleRoomAverageAndWorkZone: 'Room average and work zone, against occupancy',
        LabelZoneRoom: 'Zone / room',
        LabelWaferSees: 'Wafer sees',
        LabelMonitorReads: 'Monitor reads',
        HypACeilingMonitorMeasuresA: 'A ceiling monitor measures a room average, and an average assumes the ' +
          'source is spread evenly through the room. A person standing at a tool is ' +
          'not spread evenly - they are a point source a metre from open product. If ' +
          'that matters, the work-zone concentration should be substantially higher ' +
          'than the room average, and the gap should grow with every person added.',
        AskedIsTheNumberOnThe: 'Is the number on the wall the number the wafer experiences?',
        SumRoomAverageAgainstWorkZone: 'Room average against work zone, and why a monitor on the wall can be right ' +
          'and useless.',
      },
      intents: {
        NextHoldingIsTheEasyHalf: 'Holding is the easy half. What does the lot already at the load port do ' +
          'now, and what would you need to know about it before you called that a ' +
          'scrap rather than a wait?',
        NextAQualPassesOnTheDay: 'A qual passes on the day it is run. What would you have to see on the next ' +
          'lot to decide this one was luck rather than a healthy tool - and how many ' +
          'wafers would you want before you believed either answer?',
        LabelBenchReadings: 'Bench readings',
        LabelNonUniformity: 'Non-uniformity',
        LabelMonitorRate: 'Monitor rate',
        VerdictWithAWatchItem: 'released, with a watch item',
        VerdictHeld: 'Held',
        VerdictQualified: 'Qualified',
        LabelVerdict: 'Verdict',
        HypAQualificationIsADecision: 'A qualification is a decision, not an explanation. If the monitor wafer ' +
          'lands inside the spec window and every bench reading is inside its own ' +
          'limit, the tool releases. If either fails it is held - and one run on a ' +
          'nominal chamber and one on the qualified recipe should say which of the ' +
          'two is responsible, without a full diagnosis.',
        AskedIsThisToolFitToRun: 'Is this tool fit to run product right now, and what do I do about it?',
        SumRunTheMonitorWafer: 'Run the monitor wafer, read the bench against its limits, and make the call.',
        LabelAsFound: 'As found',
        LabelNoEffect: 'no effect',
        AxisYRecovered: '% recovered',
        AxisXCandidateRanked: 'candidate (ranked)',
        TitleHowMuchEachSingleRepair: 'How much each single repair recovers',
        LabelDeviationAsFound: 'Deviation as found',
        LabelSeparation: 'Separation',
        LabelNextBest: 'Next best',
        LabelMostLikelyCause: 'Most likely cause',
        HypIfASingleChamberAspect: 'If a single chamber aspect is responsible, then restoring exactly that one ' +
          'to nominal - and nothing else - should recover the result, while restoring ' +
          'any other should not. If no single restoration helps, the chamber is not ' +
          'the cause.',
        AskedTheRecipeHasNotChanged: 'The recipe has not changed but the result has. What in the chamber moved?',
        SumIsolateAChamberFaultBy: 'Isolate a chamber fault by restoring one suspect at a time.',
        AxisYNonUniformity: 'non-uniformity %',
        LabelBestUniformity: 'Best uniformity',
        LabelRateChange: 'Rate change',
        LabelAtYourSetting: 'At your setting',
        SumSweepAnySingleRecipeKnob: 'Sweep any single recipe knob and see every consequence at once.',
        LabelHeavyLoad: 'Heavy load',
        TitleRadialRateTheSpreadThat: 'Radial rate - the spread that sets the overetch window',
        LabelMeanRate: 'Mean rate',
        LabelNuHeavyLoad: 'NU heavy load',
        LabelNuAsRunning: 'NU as running',
        HypOpticalEmissionFromTheEtch: 'Optical emission from the etch product falls when the film clears, so the ' +
          'trace marks endpoint. But the wafer does not clear all at once - it clears ' +
          'over a window set by uniformity - so the required overetch, and the oxide ' +
          'lost during it, should scale with non-uniformity.',
        AskedHowIsTheEndOf: 'How is the end of the etch detected, and how much overetch is needed?',
        SumEndpointDetectionOveretchAndWhat: 'Endpoint detection, overetch, and what non-uniformity costs downstream.',
        NextTheSheathIsThinnerThan: 'The sheath is thinner than the ion mean free path at this pressure, so ' +
          'ions cross it without colliding and arrive in a narrow energy band. Raise ' +
          'the pressure enough and that stops being true. What happens to the profile ' +
          'when ions start arriving at every angle?',
        AxisXSourcePowerW: 'source power (W)',
        TitleEtchRateVersusSourcePower: 'Etch rate versus source power at fixed bias',
        NoteImprovesWithSofterIons: 'improves with softer ions',
        LabelRate2001600W: 'Rate, 200 -> 1600 W',
        NoteScale: 'Scale',
        DesignSourcePowerSwept200To: 'Source power swept 200 to 1600 W at constant bias power. Solved the ' +
          'discharge at each point for plasma density, Bohm flux, sheath voltage from ' +
          'power balance, and Child-law sheath thickness.',
        HypTheBiasSupplyDeliversA: 'The bias supply delivers a fixed number of watts to the ions crossing the ' +
          'sheath. Energy per ion is therefore watts divided by ion current. If that ' +
          'is right, then raising SOURCE power - which makes more ions - should make ' +
          'each one LESS energetic, even though the bias setting never moved.',
        AskedWhereDoesIonBombardmentEnergy: 'Where does ion bombardment energy come from, and which knob controls it?',
        NextBiasRaisesIonEnergySource: 'Bias raises ion ENERGY. Source power raises ion COUNT. If you need more ' +
          'rate without losing selectivity, which one should you reach for - and what ' +
          'does the sheath do to ion energy when you turn the source up?',
        AxisXBiasPowerW: 'bias power (W)',
        TitleRateAndSelectivityBothRespond: 'Rate and selectivity both respond to bias - in opposite directions',
        LabelProfileAt200W: 'Profile at 200 W',
        LabelProfileAt0W: 'Profile at 0 W',
        LabelSelectivityAt500W: 'Selectivity at 500 W',
        LabelRateAt500W: 'Rate at 500 W',
        LabelOutOfReach: 'out of reach',
        DesignBiasSweptFrom0To: 'Bias swept from 0 to 500 W at fixed source power, pressure, and flow. ' +
          'Measured etch rate, Si:SiO2 selectivity, sidewall angle, and the resulting ' +
          'ion energy.',
        HypBiasPowerSetsIonEnergy: 'Bias power sets ion energy, not ion count. Silicon has a spontaneous ' +
          'fluorine channel and a low ion threshold; oxide has neither. So raising ' +
          'bias should speed both up, but oxide faster in relative terms - meaning ' +
          'rate is bought with selectivity.',
        SumTradeRateAgainstSelectivityAnd: 'Trade rate against selectivity and oxide punch-through.',
        LabelLoadTheLowLoadProduct: 'Load the low-load product',
        NextARecipeQualifiedOnA: 'A recipe qualified on a 15% open product now has to run a 65% open one. Do ' +
          'you re-time the etch, or re-qualify the recipe? Ask me what each choice ' +
          'costs in oxide loss at the wafer edge.',
        TitleRadialProfileAtEachLoad: 'Radial profile at each load',
        LabelMeanRateL305: 'mean rate',
        AxisXOpenAreaFraction: 'open area fraction',
        TitleEtchRateVersusOpenArea: 'Etch rate versus open area',
        LabelNuAtHeavyLoad: 'NU at heavy load',
        LabelLoadingRatio: 'Loading ratio',
        LabelRateAt90Open: 'Rate at 90% open',
        LabelRateAt4Open: 'Rate at 4% open',
        HypEveryExposedSquareMillimetreOf: 'Every exposed square millimetre of silicon competes for the same finite ' +
          'supply of fluorine. If that is what is happening, rate should fall as open ' +
          'area rises, and the wafer should get less uniform at the same time - ' +
          'because a heavier load pushes the etch toward transport control.',
        AskedWhyDoesAnIdenticalRecipe: 'Why does an identical recipe give a different etch rate on a different ' +
          'product?',
        LabelLoadARecipeThatRuns: 'Load a recipe that runs edge-fast (90% open area)',
        LabelAsRunning: 'As running',
        AxisYFAtomsM3: 'F atoms m⁻³',
        TitleEtchantDensityAtTheSurface: 'Etchant density at the surface - the supply behind the rate',
        AxisXRR0Centre: 'r / R (0 = centre)',
        TitleEtchRateAcrossTheWafer: 'Etch rate across the wafer radius',
        LabelBestCondition: 'Best condition',
        LabelSignature: 'Signature',
        HypAFastEdgeMeansThe: 'A fast edge means the wafer edge is better supplied with etchant than the ' +
          'centre. If that is true, anything that raises supply relative to demand - ' +
          'more gas, less bias, faster diffusion - should flatten the map. If none of ' +
          'them do, the cause is not supply at all.',
        AskedWhyDoesTheEtchRate: 'Why does the etch rate lean from centre to edge, and what is causing it ' +
          'here?',
        SumDiagnoseACentreToEdge: 'Diagnose a centre-to-edge signature and find which mechanism is causing it.',
      },
      metro: {
        NextYouHaveOneAlarmAnd: 'You have one alarm and a lot on hold. Holding costs money and so does ' +
          'shipping bad product. What would you measure next to decide, and how many ' +
          'extra readings would it take before the answer stopped being a coin flip?',
        LabelNoisyGauge: 'noisy gauge',
        LabelStepAt30: 'step at 30',
        TitleTheDriftingRunMeasuredAnd: 'The drifting run — measured, and the truth behind it',
        LabelCpkNoisyGauge: 'Cpk, noisy gauge',
        LabelStepCaughtAfter: 'Step caught after',
        LabelDriftCaughtAtLot: 'Drift caught at lot',
        NoteIn60LotsOfA: 'in 60 lots of a process that never moved',
        LabelFalseAlarmsStable: 'False alarms, stable',
        HypControlRulesFireOnWhatever: 'Control rules fire on whatever is in front of them, and what is in front ' +
          'of them is the process plus the gauge. So a perfectly stable process ' +
          'should still produce alarms, a genuine drift should trip the run rules ' +
          'before the three-sigma rule, and a noisier gauge should destroy capability ' +
          'without the process changing at all.',
        AskedTheChartFiredIsThe: 'The chart fired. Is the process moving, or is this noise?',
        SumSeparateARealProcessDrift: 'Separate a real process drift from measurement noise and from luck.',
        NextIfTheGaugeIsNot: 'If the gauge is not capable, you can average several readings to shrink ' +
          'its noise. What does that cost you, and at what point is it cheaper to buy ' +
          'a better tool?',
        LabelTightProcess: 'Tight process',
        LabelLooseProcess: 'Loose process',
        LabelThisProcess: 'This process',
        NoteRepeatability: 'Repeatability',
        DesignTwentyFiveRepeatMeasurementsOf: 'Twenty-five repeat measurements of a single unchanging wafer, then that ' +
          'same repeatability compared against three different process variations.',
        HypAGaugeHasOneRepeatability: 'A gauge has one repeatability, but capability is not a property of the ' +
          'gauge. It is the ratio of measurement spread to total spread. So the same ' +
          'tool should come out capable for a loose process and incapable for a tight ' +
          'one, with nothing about the tool changing.',
        AskedIsThisMeasurementToolCapable: 'Is this measurement tool capable of seeing the process it is watching?',
        SumCompareMeasurementSpreadWithProcess: 'Compare measurement spread with process spread — capability is a ratio.',
        NextIfTheResidualCannotTell: 'If the residual cannot tell you the model is wrong, what can? Think about ' +
          'what a second, independent measurement would have to be sensitive to in ' +
          'order to catch this.',
        LabelRecipeIndex: 'recipe index',
        LabelCorrectIndex: 'correct index',
        TitleMeasuredSpectrumAndTheFitted: 'Measured spectrum and the fitted model',
        LabelResidualRecipe: 'Residual, recipe',
        LabelResidualCorrect: 'Residual, correct',
        LabelRecipeModel: 'Recipe model',
        LabelCorrectModel: 'Correct model',
        HypTheToolMeasuresReflectedLight: 'The tool measures reflected light, not thickness. It finds the film ' +
          'thickness whose MODEL spectrum best matches what it saw. If the model is ' +
          'right the answer should be right; if the model has the wrong refractive ' +
          'index the fit should still converge, still report a confident number, and ' +
          'be wrong - and the residual should barely complain.',
        AskedTheToolReportedAThickness: 'The tool reported a thickness. Is it the right one?',
        SumShowThatAMeasurementIs: 'Show that a measurement is a model fit, and can be precisely wrong.',
      },
      dep: {
        TitleThicknessAcrossTheRadiusUnder: 'Thickness across the radius under each condition',
        LabelBestCondition: 'Best condition',
        LabelParasitic: 'Parasitic',
        LabelAsRunning: 'As running',
        HypAProperlySaturatedProperlyPurged: 'A properly saturated, properly purged ALD film should be almost perfectly ' +
          'uniform, because every site takes exactly one molecule regardless of how ' +
          'much gas passed over it. So non-uniformity means growth is tracking gas ' +
          'delivery somewhere - either the dose never saturated, or parasitic CVD is ' +
          'adding film on top of the ALD. Those have different fixes, and testing ' +
          'each separately tells them apart.',
        AskedIsThisThicknessMapA: 'Is this thickness map a dose problem or a purge problem?',
        SumDecideWhetherAThicknessMap: 'Decide whether a thickness map is a dose problem or a purge problem.',
        TitleFlatWaferGrowthOverThe: 'Flat-wafer growth over the same sweep — it does not move',
        LabelStepCoverage: 'step coverage',
        AxisYCoverageAtTheBottom: '% coverage at the bottom',
        TitleStepCoverageVersusDoseExposure: 'Step coverage versus dose — exposure is the lever',
        NoteAlreadySaturatedUpTop: 'already saturated up top',
        LabelFlatGrowthUnchanged: 'Flat growth unchanged',
        LabelOnAFlatWafer: 'On a flat wafer',
        HypInsideADeepFeatureThe: 'Inside a deep feature the precursor moves by bouncing off the walls, and ' +
          'every bounce is a chance to react before it reaches the bottom. If that is ' +
          'what limits conformality, then the fix is exposure - a longer dose - and ' +
          'it should improve the bottom without changing growth on the flat wafer at ' +
          'all, because the flat surface is already saturated.',
        SumShowWhyConformalityIsA: 'Show why conformality is a dose question, not a chemistry question.',
        NextThePurgeHasToClear: 'The purge has to clear the chamber, and the chamber empties on its ' +
          'residence time. If you raised the carrier flow, would you be able to ' +
          'shorten the purge further - and what else would that change?',
        LabelCycleTime: 'cycle time',
        AxisYSecondsPerCycle: 'seconds per cycle',
        TitleCycleTimeVersusPurgeTime: 'Cycle time versus purge time — the throughput you are buying',
        AxisXPurgeMs: 'purge (ms)',
        TitleParasiticCvdVersusPurgeTime: 'Parasitic CVD versus purge time',
        LabelCycleTimeSaved: 'Cycle time saved',
        LabelShortestSafePurge: 'Shortest safe purge',
        LabelResidenceTime: 'Residence time',
        HypPurgingEmptiesTheChamberExponentially: 'Purging empties the chamber exponentially, on the gas residence time. ' +
          'Whatever precursor is still there when the co-reactant arrives reacts in ' +
          'the gas phase instead of waiting for a free site - that is CVD. So a short ' +
          'purge should buy cycle time and pay for it in parasitic growth and ' +
          'uniformity, not in growth rate.',
        AskedHowShortCanThePurge: 'How short can the purge be before the process stops being ALD?',
        SumTradeCycleTimeAgainstParasitic: 'Trade cycle time against parasitic CVD and uniformity.',
        NextBothEndsOfTheWindow: 'Both ends of the window grow FASTER than the middle. If someone showed you ' +
          'only a thickness number and told you the tool was running hot, how would ' +
          'you tell that apart from a tool running cold?',
        LabelGrowthHere: 'Growth here',
        LabelParasiticHere: 'Parasitic here',
        LabelYourSetpoint: 'Your setpoint',
        HypAdsorptionNeedsHeatToComplete: 'Adsorption needs heat to complete, but too much heat lets the precursor ' +
          'desorb or decompose on its own. If that is right there will be a band in ' +
          'the middle where growth is flat and essentially all of it is ' +
          'self-limiting, with parasitic growth rising at both ends for two different ' +
          'reasons.',
        AskedWhereIsTheTemperatureBand: 'Where is the temperature band in which this process stays ALD?',
        SumMapTheAldWindowAnd: 'Map the ALD window and find where growth stops being self-limiting.',
        NextASaturatedDoseOnA: 'A saturated dose on a flat wafer can still starve the bottom of a deep ' +
          'feature, because the precursor has to get down there before it reacts. ' +
          'What aspect ratio is your product, and does this dose still saturate at ' +
          'the bottom of it?',
        AxisYNonUniformity: 'non-uniformity %',
        TitleNonUniformityFollowsSaturation: 'Non-uniformity follows saturation',
        AxisXDoseMs: 'dose (ms)',
        LabelUniformityAtYourDose: 'Uniformity at your dose',
        LabelDoseNeeded: 'Dose needed',
        LabelSaturatedGrowth: 'Saturated growth',
        LabelSelfLimiting: 'Self-limiting',
        HypIfTheSurfaceSaturatesGrowth: 'If the surface saturates, growth per cycle will rise with dose and then go ' +
          'flat, and everything past the knee will be identical. If it keeps rising, ' +
          'the surface is not the limiting factor and the process is CVD wearing an ' +
          'ALD recipe.',
        AskedDoesGrowthStopRespondingTo: 'Does growth stop responding to dose - is this process actually ' +
          'self-limiting?',
        NextDoseCost: 'A longer dose fixes the trench but lengthens every one of your {{cycles}} cycles. Work out what that costs in run time, and then decide whether raising the precursor pressure would buy the same exposure more cheaply.',
        SumSweepDoseTimeAndFind: 'Sweep dose time and find where growth stops responding.',
      },
      config: {
        LabelDeepseek: 'DeepSeek',
      },
      route: {
        step: { wellDriveIn: 'Well drive-in', gateOxidation: 'Gate oxidation', spacerDensify: 'Spacer densify', spikeAnneal: 'Spike anneal' },
        stepPurpose: {
          'a-day-per-cooldown-the-test-is-the-bottleneck': 'A day per cooldown: the test is the bottleneck',
          'aluminium-wire-which-goes-superconducting-with-the-chip': 'Aluminium wire, which goes superconducting with the chip',
          'a-fridge-takes-a-chip-not-a-wafer': 'A fridge takes a chip, not a wafer',
          'area-sets-the-frequency-and-ten-percent-wide-is-a-collision': 'Area sets the frequency, and ten percent wide is a collision',
          'a-nanometre-of-aluminium-oxide-counted-in-cycles': 'A nanometre of aluminium oxide, counted in cycles',
          'transfer-it-into-the-superconducting-film': 'Transfer it into the superconducting film',
          'capacitor-pads-resonators-and-the-junction-leads': 'Capacitor pads, resonators and the junction leads',
          'a-lossy-surface-is-a-lossy-qubit': 'A lossy surface is a lossy qubit',
          'bin-it-and-decide-what-escapes': 'Bin it, and decide what escapes',
          'and-the-die-is-under-183-mpa-for-the-rest-of-its-life': 'And the die is under 183 MPa for the rest of its life',
          'onto-a-substrate': 'Onto a substrate',
          'and-the-wafer-stops-being-a-wafer': 'And the wafer stops being a wafer',
          'the-first-honest-yield-number-anyone-has-had': 'The first honest yield number anyone has had',
          'last-look-before-the-wafer-is-cut-up': 'Last look before the wafer is cut up',
          'a-defect-free-doped-to-order-layer-over-the-bulk': 'A defect-free, doped-to-order layer over the bulk',
          'before-it-is-allowed-into-the-front-end': 'Before it is allowed into the front end',
          'one-crystal-becomes-a-thousand-wafers': 'One crystal becomes a thousand wafers',
          '72-hours-for-one-ingot-and-one-ingot-is-1000-wafers': '72 hours for one ingot, and one ingot is ~1000 wafers',
          'the-next-layer-prints-on-this-surface': 'The next layer prints on this surface',
          'slurry-is-a-defect-if-it-is-left-on': 'Slurry is a defect if it is left on',
          'take-off-everything-above-the-trench': 'Take off everything above the trench',
          'bottom-up-fill-and-an-overburden-for-cmp': 'Bottom-up fill, and an overburden for CMP',
          'copper-poisons-silicon-so-it-is-fenced-in': 'Copper poisons silicon, so it is fenced in',
          'polymer-off-before-anything-is-deposited-on-it': 'Polymer off, before anything is deposited on it',
          'ash-the-resist-off-before-anything-is-deposited': 'Ash the resist off before anything is deposited',
          'and-the-wire-that-sits-on-top-of-the-via': 'And the wire that sits on top of the via',
          'down-to-the-layer-below': 'Down to the layer below',
          'where-the-wires-go': 'Where the wires go',
          'the-insulator-the-trenches-go-into': 'The insulator the trenches go into',
          'because-everything-after-this-assumes-it-worked': 'Because everything after this assumes it worked',
          'activate-the-dopant-and-move-it-as-little-as-possible': 'Activate the dopant and move it as little as possible',
          'self-aligned-to-the-gate-that-was-just-etched': 'Self-aligned to the gate that was just etched',
          'the-gate-electrode': 'The gate electrode',
          'the-thinnest-most-consequential-film-in-the-device': 'The thinnest, most consequential film in the device',
          'deep-and-expensive-in-budget': 'Deep, and expensive in budget',
          'the-tub-the-device-sits-in': 'The tub the device sits in',
          'a-furnace-drives-contamination-in-it-does-not-remove-it': 'A furnace drives contamination in, it does not remove it',
          'print-one-line-in-four': 'Print one line in four',
          'four-lines-where-there-was-one': 'Four lines where there was one',
          'anisotropic-again': 'Anisotropic again',
          'the-first-spacers-are-now-the-mandrels': 'The first spacers are now the mandrels',
          'two-lines-where-there-was-one': 'Two lines where there was one',
          'anisotropic-clears-the-horizontals': 'Anisotropic: clears the horizontals',
          'ald-this-thickness-sets-the-half-pitch': 'ALD — this thickness sets the half pitch',
          'the-knob-that-centres-the-pattern': 'The knob that centres the pattern',
          'the-only-exposure-in-the-whole-module': 'The only exposure in the whole module',
          'transfer-that-too': 'Transfer that too',
          'print-the-lines-in-between': 'Print the lines in between',
          'transfer-it': 'Transfer it',
          'print-every-other-line': 'Print every other line',
          'transfer-resist-into-the-film': 'Transfer resist into the film',
          'print-the-pattern-directly-at-13-5-nm': 'Print the pattern directly at 13.5 nm',
          'print-the-pattern-directly': 'Print the pattern directly',
        },
        stepLabel: {
          'cool-down-and-characterise': 'Cool down and characterise',
          'wire-bond-to-the-sample-holder': 'Wire bond to the sample holder',
          'dice-into-chips': 'Dice into chips',
          'measure-every-junction': 'Measure every junction',
          'grow-the-tunnel-barrier': 'Grow the tunnel barrier',
          'etch-the-circuit': 'Etch the circuit',
          'expose-the-circuit': 'Expose the circuit',
          'substrate-clean': 'Substrate clean',
          'final-test-and-burn-in': 'Final test and burn-in',
          'mould-and-cure': 'Mould and cure',
          'die-attach-and-wire-bond': 'Die attach and wire bond',
          'grind-and-dice': 'Grind and dice',
          'wafer-sort': 'Wafer sort',
          'final-inspection': 'Final inspection',
          'epitaxial-device-layer': 'Epitaxial device layer',
          'incoming-clean': 'Incoming clean',
          'slice-lap-etch-polish': 'Slice, lap, etch, polish',
          'crystal-growth': 'Crystal growth',
          'thickness-and-dishing-check': 'Thickness and dishing check',
          'post-cmp-clean': 'Post-CMP clean',
          'copper-cmp': 'Copper CMP',
          'copper-plate': 'Copper plate',
          'barrier-and-seed': 'Barrier and seed',
          'post-etch-clean': 'Post-etch clean',
          'resist-strip': 'Resist strip',
          'trench-etch': 'Trench etch',
          'via-etch': 'Via etch',
          'trench-lithography': 'Trench lithography',
          'dielectric-deposition': 'Dielectric deposition',
          'post-gate-metrology': 'Post-gate metrology',
          'spike-anneal': 'Spike anneal',
          'source-drain-implant': 'Source/drain implant',
          'polysilicon-deposition': 'Polysilicon deposition',
          'gate-oxidation': 'Gate oxidation',
          'well-drive-in': 'Well drive-in',
          'well-implant': 'Well implant',
          'pre-gate-clean': 'Pre-gate clean',
          'pull-the-first-spacers': 'Pull the first spacers',
          'spacer-2-etch': 'Spacer 2 etch',
          'deposit-spacer-2': 'Deposit spacer 2',
          'pull-the-mandrel': 'Pull the mandrel',
          'spacer-1-etch': 'Spacer 1 etch',
          'deposit-spacer-1': 'Deposit spacer 1',
          'trim-the-mandrel': 'Trim the mandrel',
          'print-the-mandrel': 'Print the mandrel',
          'etch-population-4': 'Etch, population 4',
          'etch-population-3': 'Etch, population 3',
          'etch-population-2': 'Etch, population 2',
          'etch-population-1': 'Etch, population 1',
          'expose-population-4': 'Expose, population 4',
          'expose-population-3': 'Expose, population 3',
          'expose-population-2': 'Expose, population 2',
          'expose-population-1': 'Expose, population 1',
          'etch-second-population': 'Etch, second population',
          'expose-second-population': 'Expose, second population',
          'etch-first-population': 'Etch, first population',
          'expose-first-population': 'Expose, first population',
          'pattern-transfer-etch': 'Pattern transfer etch',
          expose: 'Expose',
        },
        NoteTakeTheCall: 'Four candidate causes, and one of them is that it is simply one tool. The ' +
          'station alarms are free and the layer plots are not, which is the ' +
          'decision: an hour and a half of tool time to see a trend, against calling ' +
          'it from a summary that looks green either way.',
        FaultIntro: 'Every fault elsewhere in this simulator is a tool fault: something is ' +
          'wrong with a chamber and the chamber says so. These three are not. In each ' +
          'one every single visit is inside its specification and the product is ' +
          'outside its own — so there is no bad reading to find, no alarm to answer ' +
          'and no tool to fix. The competency is to stop looking at the tool, which ' +
          'is the hardest habit to teach because every instinct a good technician has ' +
          'says to go and check the tool.',
        AxisChlorineDoseS: 'chlorine dose (s)',
        AxisEpcNm: 'EPC (nm)',
        NoteTheSameInstrumentDep02: 'The same instrument DEP-02 carries for ALD, and the same test: a true ' +
          'self-limiting half-cycle rises and then goes FLAT. A curve that keeps ' +
          'climbing means more precursor is still removing more material. Note that ' +
          'this curve alone does not prove ALE — an over-energetic recipe saturates ' +
          'too. The synergy number is the test that separates them.',
        NoteBelowSaturationDoseNow: 'BELOW saturation — dose now sets the rate',
        NoteSaturated: 'saturated',
        ValueSputtering: 'sputtering',
        ValueStarved: 'starved',
        ValueInWindow: 'in window',
        NotePartOfThisEtchIs: 'part of this etch is continuous',
        NoteNeitherHalfEtchesAlone: 'neither half etches alone — this is ALE',
        LabelEtchRate: 'Etch rate',
        LabelSelectivity: 'Selectivity',
        LabelRegime: 'Regime',
        LabelSynergy: 'Synergy',
        mat: {
          'silicon-oxide': 'Oxide',
          'silicon-nitride': 'Nitride',
          silicon: 'Silicon',
        },
        SectionFilm: 'Film',
        SecondaryLabel: 'CD spread at the last layer',
        ToolLabel: 'The route — every station, in order',
        fault: {
          justOneTool: {
            Evidence: [
              'A station is out of specification, which on a genuine route fault never ' +
                'happens.',
              'The layer plot is flat, the junction is on target, and no control rule has ' +
                'fired.',
              'The change was abrupt and dated — route faults arrive gradually.',
            ],
            Truth: 'It is not a route fault. CMP-11 is alarming on removal rate — an ordinary ' +
              'single-station problem with an ordinary diagnosis, and its own shift ' +
              'report will name it in twenty minutes. Most yield excursions ARE one tool, ' +
              'and a student who has learned to reflexively reach for the route machinery ' +
              'has learned the wrong half of the lesson. Read the station alarms first; ' +
              'they are free.',
            Complaint: 'Yield down five points since Tuesday. The yield engineer has opened the ' +
              'route review and wants to talk about accumulation.',
            Label: 'It is one tool, and the route is a distraction',
          },
          sequenceDrift: {
            Evidence: [
              'Control-chart rules have fired without a single point outside a limit.',
              'The golden wafer shows the accumulated shift directly.',
              'CMP layer plot is flat and the junction is on target.',
            ],
            Truth: 'A tool is drifting about 0.18 per layer. Over eleven layers that is 1.8 — ' +
              'sixty per cent of the one-sided specification — and not one reading has ' +
              'left its control limits, because the drift is small compared with the ' +
              'limits and large compared with the run-to-run scatter. Eight consecutive ' +
              'points on one side of the centreline is a one-in-128 event on a centred ' +
              'process, which is why the rule exists and why only a chart across visits ' +
              'has one.',
            Complaint: 'Parametric yield is sliding a little every week and nobody can point at a ' +
              'lot where it went wrong. Nothing has alarmed.',
            Label: 'Eleven measurements, all in spec, and something is moving',
          },
          budgetSpent: {
            Evidence: [
              'No individual anneal is over its limit.',
              'The junction cross-section is past 78 nm.',
              'The CMP layer plot is flat and no control-chart rule has fired.',
            ],
            Truth: 'The well drive-in was extended from 300 s to 880 s to fix a resistivity ' +
              'complaint. It is still a legal anneal — the per-step limit is 900 s — and ' +
              'it spent budget the spike anneal at the end of the flow was depending on. ' +
              'Junction depth goes as the square root of the TOTAL, so the last step in a ' +
              'flow is constrained by every step before it, and the account has no owner.',
            Complaint: 'Parametric bins have shifted across the whole product. Every furnace step ' +
              'is inside its own window and THRM-04 diagnoses clean at every visit.',
            Label: 'The junction is too deep and no anneal was too long',
          },
          stackAccumulation: {
            Evidence: [
              'Post-CMP step height passes on all ten layers, against the limit as written.',
              'Plotted against LAYER NUMBER it climbs and settles high instead of staying ' +
                'flat.',
              'No station is alarming and the junction is on target.',
            ],
            Truth: 'The CMP-11 pad conditioner is worn, so planarisation efficiency has fallen ' +
              'from 0.90 to 0.55. Every polish still leaves less than the 80 nm per-visit ' +
              'limit — and it passes more topography upward than it used to, and ten ' +
              'layers of that compounds to 70 nm. Both parties are telling the truth. The ' +
              'per-visit limit was never derived from the focus budget, and the gap ' +
              'between 80 nm and the 55 nm the scanner needed is the room the fault hid ' +
              'in.',
            Complaint: 'Litho is scrapping at metal 8 on every lot and blaming focus. CMP says it ' +
              'is in spec on all ten layers and has the data to prove it.',
            Label: 'Metal 8 will not print, and CMP-11 is in spec on every layer',
          },
          routeHealthy: {
            Evidence: [
              'Every per-visit check passes and no station is alarming.',
              'The layer plots are flat and the junction is on target.',
            ],
            Truth: 'Nothing is wrong. The correct call is to keep running.',
            Complaint: 'Yield review. The line is running to forecast.',
            Label: 'Nothing wrong',
          },
        },
        NoteSCycleTheRieRecipe: '{{a}} s/cycle — the RIE recipe next door runs at ~200',
        NoteEvAgainstAEvWindow: '{{a}} eV against a {{b}}–{{c}} eV window',
        NoteNmOverCycles: '{{a}} nm over {{b}} cycles',
        NoteSameChamberSameGasesSame: 'Same chamber, same gases, same power supplies. What makes this ALE is that ' +
          'the chemistry and the ions have been separated in time, and the ion energy ' +
          'has been turned down below the point where it can etch anything on its ' +
          'own. Two orders of magnitude slower than the recipe above, and it is how ' +
          'you remove the last two nanometres when being wrong is not an option.',
        SectionEnergyWindowIsTheRemoval: 'Energy window — is the removal half self-limiting?',
        NoteTheModifiedLayerHasA: 'The modified layer has a lower sputter threshold than the material under ' +
          'it, and between the two thresholds is a band where the modified layer ' +
          'comes off and the substrate does not. Below it nothing is removed; above ' +
          'it you are sputtering, and the process is no longer atomic anything. ' +
          'Running above the window is the commonest way to have a recipe that is ' +
          'called ALE and is not.',
        SectionSaturationCurveIsTheDose: 'Saturation curve — is the dose half self-limiting?',
        SectionStoredAleRecipes: 'Stored ALE recipes',
        KnobWafers: '{{n}}k wafers',
        flow: {
          'quantum-chip': {
            Purpose: 'Eight operations, one junction a nanometre thick, and a test that takes a ' +
              'day at 10 mK.',
            Name: 'Transmon test chip',
          },
          'product-saqp': {
            Purpose: 'Silicon to binned part, with the interconnect loop run once per metal layer.',
            Name: 'Full product route — SAQP',
          },
          'back-end': {
            Purpose: 'A finished wafer becomes binned parts.',
            Name: 'Assembly and test',
          },
          'gate-saqp': {
            Purpose:
              'Well, gate oxide, polysilicon, pattern, and the source/drain that follows ' +
              'it.',
            Name: 'Gate layer — SAQP',
          },
          substrate: {
            Purpose: 'Polysilicon and a seed become a polished wafer.',
            Name: 'Substrate',
          },
          'metal-layer': {
            Purpose: 'Etch the dielectric, fill it with copper, take the excess off.',
            Name: 'Damascene metal layer',
          },
          le4: {
            Purpose: 'Four masks to the same pitch SAQP reaches with one.',
            Name: 'LE⁴ quadruple patterning',
          },
          saqp: {
            step7: {
              Label: 'Pull the first spacers',
            },
            step6: {
              Label: 'Spacer 2 etch',
            },
            step5: {
              Label: 'Deposit spacer 2',
            },
            step4: {
              Label: 'Pull the mandrel',
            },
            step3: {
              Label: 'Spacer 1 etch',
            },
            step2: {
              Label: 'Deposit spacer 1',
            },
            step1: {
              Label: 'Trim the mandrel',
            },
            step0: {
              Label: 'Print the mandrel',
            },
            Purpose: 'One mask, eight operations, and a final pitch set by a film thickness.',
            Name: 'SAQP',
          },
          lele: {
            Purpose: 'Two masks, four operations. The overlay between them becomes a CD error.',
            Name: 'LELE double patterning',
          },
          'single-euv': {
            Purpose: 'One mask, two operations, and the pitch the others need eight steps for.',
            Name: 'Single exposure, EUV',
          },
          'single-duv': {
            Purpose: 'One mask, two operations, and a hard stop at 36 nm half-pitch.',
            Name: 'Single exposure, 193i',
          },
        },
        LabelGateOxide: 'Gate oxide',
        LabelWellDriveIn: 'Well drive-in',
        TextTheThreeMechanisms: 'The three mechanisms, worked through',
        TextTakeTheCall: 'Take the call',
        TextHowYouWouldSeeIt: 'How you would see it',
        TextTheFault: 'The fault',
        TextWhatTheProductSays: 'What the product says',
        TextWhatTheToolSays: 'What the tool says',
        TextMostExpensive: 'Most expensive',
        TextMostVisited: 'Most visited',
        TextShowWhatWasActuallyWrong: 'Show what was actually wrong',
        TextRouteFaults: 'Route faults',
        TextComparePatterning: 'Compare patterning',
        LabelEtchPerCycle: 'Etch per cycle',
        LabelSurfaceCoverage: 'Surface coverage',
        NoteTheMaskThresholdIsFar: 'the mask threshold is far above the modified layer',
        SectionAleRecipe: 'ALE recipe',
        case: {
          'slow-drift': {
            HowToSeeIt:
              'The Western Electric rules. Eight consecutive points on one side of the ' +
              'centreline is a one-in-128 event on a centred process, so it is not ' +
              'chance. Nothing is out of limits; the PATTERN is the signal, and only a ' +
              'chart across visits has one.',
            ProductSays:
              'Parametric yield is sliding and nobody can point at a lot where it went ' +
              'wrong.',
            ToolSays:
              'Every reading is inside spec and inside the control limits. No alarm has ' +
              'fired.',
            Truth:
              'A mass flow controller is drifting about 0.4 nm-equivalent per layer. Over ' +
              'eleven layers that is a 4 nm shift — inside the specification at every ' +
              'single visit, and a process that is no longer centred.',
            Label: 'Eleven measurements, all in spec, and the tool is broken',
          },
          'thermal-account': {
            HowToSeeIt:
              'Add the thermal budget up along the route instead of checking each step ' +
              'against its own limit. The last anneal in a flow is constrained by every ' +
              'anneal before it, and the account has no owner.',
            ProductSays: 'Junction depth out of specification at the end of the gate module.',
            ToolSays:
              'Every furnace step is inside its own time and temperature window. THRM-04 ' +
              'diagnoses clean at every visit.',
            Truth:
              'The well drive-in was extended to fix a resistivity complaint. It is still ' +
              'a legal anneal on its own. It spent budget the spike anneal at the end of ' +
              'the flow was depending on, and the junction that comes out is 30% too ' +
              'deep.',
            Label: 'The junction is too deep and no anneal was too long',
          },
          'planarisation-drift': {
            HowToSeeIt:
              'Plot the post-CMP range against LAYER NUMBER rather than against the ' +
              'specification. A healthy stack converges to a flat line; this one climbs ' +
              'and keeps climbing, because a geometric series with a smaller ratio ' +
              'converges somewhere higher.',
            ProductSays:
              'CD spread at the scanner is past the focus budget by metal 8, and ' +
              'lithography is blaming the scanner.',
            ToolSays:
              'Every CMP run on every layer is inside its own specification. Removal ' +
              'rate, uniformity and post-polish range all read normal.',
            Truth:
              'The pad conditioner is worn, so planarisation efficiency has fallen from ' +
              '0.90 to 0.55. Each polish still removes what the recipe asks for and still ' +
              'leaves less than the 45 nm per-visit limit — but it passes more topography ' +
              'upward than it used to, and ten layers of that compounds.',
            Label: 'Metal 8 will not print, and CMP-11 is in spec on every layer',
          },
        },
        AsideDrift:
          'Total drift across eleven layers is {{total}} — sixty per cent of the ' +
          'one-sided specification, spent without a single reading leaving the ' +
          'window. Eight consecutive points on one side of the centreline has about a ' +
          'one in 128 chance on a centred process, which is why it is a rule and not ' +
          'a hunch.',
        AsidePlanarisation:
          'A healthy stack settles at {{healthy}} nm and this one at {{worn}} nm. ' +
          'Both are geometric series — R(n+1) = (R(n)+F)(1−η) — and where they settle ' +
          'is F(1−η)/η, set by the efficiency and not by any single pass. The ' +
          'per-visit limit was written at {{visit}} nm because that is what the tool ' +
          'could hold; the focus budget needed {{route}} nm. The fault lives in the ' +
          'gap between those two numbers, and the gap is the real defect.',
        VerdictNoRule: 'no rule fired',
        VerdictNoReadingOut: 'no reading outside spec or control limits',
        VerdictJunction: 'junction {{junction}} nm against {{limit}} nm',
        VerdictAnnealsOk: 'every anneal inside its own window',
        VerdictCdSpread: 'CD spread {{spread}} nm against {{budget}} nm',
        VerdictInSpecAll: 'in spec on all 10 layers',
        LabelSpike: 'Spike',
        LabelDensify: 'Densify',
        LimitRouteNeeds: 'what the route needs {{limit}} {{unit}}',
        LimitPerVisit: 'per-visit limit {{limit}} {{unit}}',
        LimitControl: 'control limit',
        LimitSpec: 'specification',
        StepListMore: '… and {{more}} more, most of them the metal layer run again',
        StepListHead: 'Operations — 1 to {{shown}} of {{total}}',
        TileGoodDieNote: '{{good}} of {{gross}} die',
        TilePerGoodDie: 'Per good die',
        TileDieYieldNote: 'at {{area}} cm² die',
        TileDieYield: 'Die yield',
        TileLineYieldNote: 'whole wafers that survive',
        TileLineYield: 'Line yield',
        TileThermalNote: 'equivalent seconds at 1000 °C',
        TileThermal: 'Thermal budget',
        TileDefectsNote: 'every touch costs something',
        TileDefects: 'Defects added',
        TileLithoNote: 'of the cost, in {{masks}} of {{steps}} steps',
        TileLithoShare: 'Litho share',
        TileCostNote: 'of it reticle',
        PerWaferSuffix: '/wafer',
        TileCost: 'Cost',
        TileMasksNote: 'each one is a reticle, bought once',
        TileOperationsNote: 'the wafer stops this many times',
        TileOperations: 'Operations',
        ThPerGoodDie: '$/good die',
        ThYield: 'Composite yield',
        ThLitho: 'Litho',
        ThPerWafer: '$/wafer',
        ThMasks: 'Masks',
        ThSteps: 'Steps',
        ThRoute: 'Route',
        CompareIntro:
          'Five ways to put a pattern on a wafer. The last three reach a pitch no ' +
          'single 193 nm exposure can resolve, and they get there by trading masks ' +
          'against operations — a mask is bought once and a step is paid for on every ' +
          'wafer. Change the die size or the production volume below and the ranking ' +
          'moves, which is the real answer: there is no best route, only a best route ' +
          'for a product.',
        KnobVolume: 'Production volume',
        KnobDieArea: 'Die area',
        group: {
          quantum: 'Quantum devices',
          product: 'Product',
          layers: 'Layer modules',
          patterning: 'Patterning modules',
        },
        SideNote:
          'A fab is not a line of tools. It is a loop, run once per layer, and a ' +
          'product is the order in which a wafer goes round it.',
        reading: {
          goldenWafer: {
            Label: 'Re-measure the last 11 layers on a golden wafer (6.0 h)',
          },
          junction: {
            Label: 'Cross-section the junction — destructive (4.0 h)',
          },
          layerTrend: {
            Limit: 'flat, and under 55 nm',
            Label: 'Plot post-CMP range against layer number (1.5 h)',
          },
          stationAlarms: {
            ValueNone: 'none',
            ValueCmp: 'CMP-11 removal rate low',
            Limit: 'none',
            Label: 'Stations currently out of specification',
          },
          spcRules: {
            ValueNone: 'none',
            Limit: 'none',
            Label: 'Control-chart rules fired across the layer sequence',
          },
          thermalTotal: {
            Limit: 'no step over 900 s',
            Label: 'Thermal budget spent this flow',
          },
          cmpVisits: {
            Value: '{{a}} of {{b}} inside {{c}} nm',
            Limit: 'all 10',
            Label: 'Post-CMP step height, every layer',
          },
        },
        aspect: {
          singleStation: {
            Action:
              'Go to the tool that is alarming and run its own shift report — this is not ' +
              'a route problem',
            Label: 'One station, and the ordinary diagnosis applies',
          },
          toolDrift: {
            Action: 'Find the drifting tool from the layer chart and re-centre it',
            Label: 'A tool drifting across the layer sequence',
          },
          thermalBudget: {
            Action:
              'Put the well drive-in back to 300 s and find another way to meet the ' +
              'resistivity target',
            Label: 'Thermal budget spent before the last anneal',
          },
          planarisation: {
            Action:
              'Dress the CMP-11 pad conditioner, and re-derive the per-visit step-height ' +
              'limit from the focus budget instead of from what the tool can hold',
            Label: 'Accumulation across the metal stack',
          },
        },
      },
      etch: {
        GuardNotRun: 'The chamber has not run. Start the process above: the lot is given what ' +
          'the plasma actually did, not what the recipe asked for.',
        GuardEtched: 'This pattern is already in the film. It needs a new exposure before there ' +
          'is anything more to transfer.',
        GuardNoPattern: 'Nothing is printed on this lot. An etcher transfers a resist pattern into ' +
          'the film under it: expose the wafer at LITH-03 or EUV-22 first.',
        TransferLot: 'Transfer the pattern and send the lot to clean',
        NoteNoLot: 'No lot in the load lock. Bring a patterned wafer here to transfer its ' +
          'pattern into the film.',
        SecondaryLabel: 'Non-uniformity',
        ToolLabel: 'ETCH-01 · ICP polysilicon etch',
        SectionAtomicLayerMode: 'Atomic layer mode — the same chamber, run differently',
        SectionSheath: 'Where the ions come from — the sheath at the wafer edge (electric field)',
        SheathNote:
          'A slice through the edge of the wafer: the plasma as a flat boundary at the top, the ' +
          'wafer and its focus ring at the bottom, and between them the sheath — which is what ' +
          'the picture is. The brightness is the potential, bright at the plasma and dark at ' +
          'the wafer; the yellow lines are its equipotentials; and the arrows are where the ' +
          'ions go — straight down the field, which over a flat surface means straight down. ' +
          'That is the whole reason a plasma etch cuts a vertical wall: the ions did not aim, ' +
          'the field did. Now the ring. New, its top is flush with the wafer and the field runs ' +
          'flat past the edge, so the last die sees exactly what the centre sees. Let the ' +
          'chamber run and the ring erodes: the equipotentials dip over the lower surface, so at ' +
          'any height the potential over the ring is higher than over the wafer, and the field ' +
          'just above the wafer’s edge picks up a sideways pull from the ring toward the wafer. ' +
          'Two things happen at once — the ions at the edge lean inward onto the wafer’s exposed ' +
          'corner, so the outermost dies etch a leaning profile, and the field lines that used to ' +
          'spread over the ring converge on that corner the way they converge on a lightning rod, ' +
          'so the edge is hit harder and runs fast. That is the edge-fast map of the worn-ring ' +
          'fault, seen. Turn the bias up and every arrow grows and none of them turns: the field ' +
          'is Laplace’s, and Laplace is linear, so the tilt belongs to the geometry and only a ' +
          'new ring fixes it.',
        SheathCaveat:
          'Honest limits. A real sheath is a space-charge layer a few millimetres thick whose ' +
          'boundary is the plasma itself; this is a conducting gap under a fixed plate. The ' +
          'shape — flat over flat, bent over a step — is the same, and the bend is the lesson. ' +
          'There is no Run to press and no clock: this field has no time in it, so the solver ' +
          'settles it the moment a knob moves, and what varies here is the ring rather than the ' +
          'minute. The ring itself touches the wafer, where a real one sits a fraction of a ' +
          'millimetre away, and it is held at the wafer’s potential: a real ring is RF-coupled ' +
          'through its own capacitance, which changes as it thins and can push the sheath over ' +
          'the ring the other way — which is why modern chambers drive the ring with its own RF ' +
          'to steer the tilt. Here the geometry is the only thing moving.',
        SheathTilt: 'ions at the wafer edge lean inward',
        SheathTiltTitle: 'How the ions arrive, across the wafer',
        SheathTiltX: 'across the wafer (mm) — the edge is on the right',
        SheathTiltY: 'lean off vertical (°, inward positive)',
        SheathTiltSeries: 'Ion arrival angle',
        SheathTiltStatus:
          '{{a}}° at the edge, {{b}}° over the middle of the wafer · the ring bends this line; the bias does not move it',
        SheathEdgeFlux: 'edge / centre current',
        SheathWear: 'ring wear',
        SheathDirected: 'drift/diffusion',
        SectionTrench: 'Radicals into a trench — why narrow features lag (aspect-ratio-dependent etching)',
        TrenchNote:
          'The wafer map above is one starvation — every open site on the wafer drawing on one ' +
          'supply, which is the loading effect. This is the same starvation a thousand times ' +
          'smaller. Four trenches under one plasma, 90 to 720 nm wide and all 900 nm deep, so ' +
          'they stand at aspect ratios of 10, 5, 2.5 and 1.25. Radicals diffuse in from the ' +
          'top and are consumed wherever they meet silicon: the bottoms, and every sidewall on ' +
          'the way down. Press Run and read the four bottoms on the graph. The 10:1 trench ' +
          'bottom sees a fraction of what the 1.25:1 sees, under the same plasma at the same ' +
          'instant, so it etches slower — and a layout carrying both finishes the wide one ' +
          'first. That is aspect-ratio-dependent etching, or RIE lag, and it is why an etch is ' +
          'timed to its narrowest feature and over-etches everything else. Now make the surface ' +
          'consume harder: warm the wafer, or raise the bias so the ions arrive harder. The ' +
          'narrow trench starves further, and the lag gets WORSE. The aggressive recipe that ' +
          'wins the rate loses the uniformity between features.',
        TrenchCaveat:
          'What is calibrated here is the surface: the activation energy is the panel’s own ' +
          'fluorine-on-silicon value and the ion enhancement follows the panel’s ' +
          'square-root-above-threshold law, so every direction is the panel’s. The transport is ' +
          'continuum diffusion standing in for the molecular flow a 100 nm trench really sees ' +
          'at 20 mTorr, and the rate constant is chosen to put the narrowest trench in a ' +
          'visibly starved state at the qualified recipe. So the ordering of the four bottoms ' +
          'and their response to the knobs are earned; the percentages are not numbers to carry ' +
          'back to the tool, and neither is the clock — a real trench fills in microseconds.',
        TrenchWide: '1.25:1 bottom',
        TrenchNarrow: '10:1 bottom',
        TrenchLag: 'lag',
        reading: {
          houseutilities: {
            Limit: 'header tight, PCW at supply, dry gas < 50 ppm',
            Label: 'House utility check with facilities (2 h)',
          },
          seasoning: {
            Label: 'Wall coupon / OES seasoning check (1 h)',
          },
          sealcheck: {
            Label: 'Helium leak check on this chamber (6 h)',
          },
          ringgauge: {
            Label: 'Focus ring thickness gauge (30 min)',
          },
          base: {
            Label: 'Base pressure',
          },
          leak: {
            Label: 'Leak-up rate',
          },
          power: {
            Label: 'Delivered source power',
          },
          pump: {
            Label: 'Pump hours',
          },
          rf: {
            Label: 'RF hours on ring',
          },
          flowverify: {
            Label: 'Flow verification, rate-of-rise (45 min)',
          },
          tempverify: {
            Label: 'Instrumented wafer, chuck temperature (2 h)',
          },
        },
        aspect: {
          power: {
            Label: 'RF match / delivered power',
            Action: 'Re-tune the match',
          },
          flow: {
            Label: 'Gas delivery (MFC)',
            Action: 'Calibrate or replace the MFC',
          },
          vacuum: {
            Label: 'Vacuum integrity (leak / pump)',
            Action: 'Find the leak, service the pump',
          },
          walls: {
            Label: 'Wall condition',
            Action: 'Chamber clean and season',
          },
          chuck: {
            Label: 'Chuck temperature control',
            Action: 'Service the chiller',
          },
          ring: {
            Label: 'Focus ring',
            Action: 'Replace the focus ring',
          },
          house: {
            Label: 'Nothing on this tool — escalate to facilities',
            Action: 'Stop work on the tool and raise it with facilities',
          },
        },
        fault: {
          none: {
            Label: 'No fault — healthy chamber',
            Complaint: 'Tool is qualified and running to spec.',
            Truth: 'Nothing is wrong.',
            Evidence: [
              'All chamber readings sit at their nominal values.',
            ],
          },
          mfcDrift: {
            Label: 'Mass flow controller under-delivering',
            Complaint:
              'Etch rate is low and the wafer is going non-uniform. Nobody changed the ' +
              'recipe.',
            Truth:
              'The CF4 mass flow controller reads its setpoint but passes about 42% of ' +
              'it. The fluorine budget is set by what actually enters the chamber, not by ' +
              'what the display says.',
            Evidence: [
              'A flow verification (rate-of-rise) disagrees with the MFC readback.',
              'Delivered RF power is normal, which rules out the match.',
              'Leak-up rate is normal, which rules out a leak.',
              'A chamber clean does not fix it.',
            ],
          },
          chamberLeak: {
            Label: 'Vacuum leak',
            Complaint: 'Rate is down and selectivity has gone strange. Started after the last PM.',
            Truth:
              'A door seal was pinched at reassembly. Air is leaking in; the oxygen ' +
              'scavenges fluorine in the gas phase, so etchant never reaches the wafer ' +
              'even though flow and power are correct.',
            Evidence: [
              'Leak-up rate is far above the 1 mTorr/min limit — this is the decisive test.',
              'Base pressure is also elevated.',
              'Flow verification and delivered power both check out normal.',
            ],
          },
          matchDetuned: {
            Label: 'RF match network detuned',
            Complaint: 'Rate is low. Reflected power alarm cleared itself, so the shift let it run.',
            Truth:
              'The match network is passing only about 70% of the setpoint into the ' +
              'plasma. Less absorbed power means a thinner plasma, fewer ions - and, ' +
              'because bias watts are shared among fewer ions, each one arrives harder.',
            Evidence: [
              'Delivered power reads well below setpoint — the decisive reading.',
              'Ion energy is HIGHER than nominal, not lower, which is the giveaway that ' +
                'separates this from a gas-supply problem.',
              'Leak-up and flow verification are both normal.',
            ],
          },
          pumpDegraded: {
            Label: 'Pump stack degraded',
            Complaint: 'Base pressure alarm at start of shift. Process still runs.',
            Truth:
              'The pump stack can no longer reach base pressure. Residual gas raises the ' +
              'effective chamber pressure above setpoint, which slows diffusion to the ' +
              'wafer centre.',
            Evidence: [
              'Base pressure is several times its normal value.',
              'Leak-up rate is somewhat raised but well short of a true leak.',
              'Flow and delivered power are normal.',
            ],
          },
          heavySeasoning: {
            Label: 'Chamber overdue for a clean',
            Complaint:
              'Rate has been creeping down all week. Each lot is slightly slower than the ' +
              'last.',
            Truth:
              'Wall deposits have built up over many wafers. They recombine fluorine ' +
              'before it reaches the wafer, and they detune the match at the same time — ' +
              'one root cause producing two symptoms that look like two separate faults.',
            Evidence: [
              'Wafers since last clean is far past the PM limit.',
              'Delivered power is somewhat low AND radical supply is low — both at once.',
              'Leak-up and flow verification are normal.',
              'A chamber clean restores it; a match tune alone only helps briefly.',
            ],
          },
          chillerFault: {
            Label: 'Chuck chiller not holding setpoint',
            Complaint: 'Profiles look undercut on the last two lots. Rate is slightly high.',
            Truth:
              'The chuck is running about 85 C above setpoint. The spontaneous fluorine ' +
              'branch is Arrhenius in temperature, so it grows — and spontaneous ' +
              'chemistry has no direction, so the sidewall undercuts.',
            Evidence: [
              'Chuck temperature readback disagrees with an independent wafer measurement.',
              'Rate is UP, not down, which rules out every supply-side fault at once.',
              'Sidewall angle has fallen while ion energy is unchanged.',
            ],
          },
          wornFocusRing: {
            Label: 'Focus ring worn past limit',
            Complaint:
              'Edge of the wafer is clearing well before the centre. Rate on average ' +
              'looks fine.',
            Truth:
              'The focus ring has eroded and no longer shapes the field at the wafer ' +
              'edge, so edge ion flux runs high. Mean rate barely moves, which is why ' +
              'this hides from a rate chart and only shows on a uniformity map.',
            Evidence: [
              'RF hours on the ring are past the replacement limit.',
              'Mean etch rate is close to nominal — a rate control chart would not catch ' +
                'this.',
              'The uniformity map is strongly edge-fast, and the profile is a ring, not a ' +
                'tilt.',
            ],
          },
          houseVacuumLeak: {
            Label: 'Leak in the shared vacuum header',
            Complaint:
              'Rate is down and selectivity has gone strange - exactly like the pinched ' +
              'door seal after the last PM. We already changed that seal. DEP-02 is ' +
              'complaining too.',
            Truth:
              'A flange on the shared process vacuum header was disturbed during the ' +
              'sub-fab work last weekend and is leaking. Every chamber on that header ' +
              'sees it as its own leak - because from inside the chamber, it is one. The ' +
              'only thing that is not true is that it is this chamber\'s leak, and every ' +
              'hour spent opening this tool is an hour the header goes on leaking into ' +
              'every other tool on it.',
            Evidence: [
              'Leak-up rate is far above the limit and base pressure is up with it - the ' +
                'classic leak.',
              'A helium leak check on THIS chamber comes back clean. That is the reading ' +
                'that turns it from a repair into an escalation.',
              'Flow verification and delivered power are both normal.',
              'The other tools on the same header are reporting their own version of it, ' +
                'which is the clue that costs nothing and is almost always ignored.',
            ],
          },
          warmPcw: {
            Label: 'Cooling water running warm',
            Complaint:
              'Rate is low, about 15%. Delivered power reads low too. The shift already ' +
              're-tuned the match once and it came back the same.',
            Truth:
              'The chilled water plant is delivering supply water about 22 C above ' +
              'nominal. Nothing on this tool is broken. It arrives twice: the chuck ' +
              'floats up about 19 C because the chiller has nothing cold to reject into, ' +
              'and - much more importantly - the RF generator and match are on the same ' +
              'loop and are derating, so delivered power falls. That is why it reads as a ' +
              'detuned match and why tuning the match does not fix it.',
            Evidence: [
              'Delivered power is low, so it looks exactly like a detuned match network.',
              'Re-tuning the match recovers almost nothing. A real detune would have ' +
                'recovered.',
              'Every wafer is still in spec, which is the trap - nothing forces the ' +
                'issue, and the tool goes on quietly running 15% slow until somebody ' +
                'notices the throughput.',
              'The house utility check shows cooling water well above its supply ' +
                'temperature.',
            ],
          },
        },
        SectionProcessState: 'Process state',
        SectionWaferMap: 'Wafer map — {{sites}}-site pattern',
        SectionRadialProfile: 'Radial profile',
        OesGround: 'ground',
        OesElectron: 'electron in',
        OesPhoton: 'photon out',
        OesSpectrumTitle: 'What the spectrometer sees',
        OesSpectrumX: 'wavelength (nm)',
        OesSpectrumY: 'intensity per nm (a line’s area is its intensity; Ar 750 at 3 eV, 2.1×10¹⁷ m⁻³ = 1)',
        OesSpectrumSeries: 'emission',
        OesTraceTitle: 'Two lines through the run - the endpoint',
        OesTraceX: 'process time (s)',
        OesTraceY: 'line intensity (Ar 750 at 3 eV, 2.1×10¹⁷ m⁻³ = 1)',
        OesTraceSiF: 'SiF 440 nm (product)',
        OesTraceF: 'F 703.7 nm',
        OesClearing: 'film clearing',
        OesActinometry: 'Actinometry F 703.7 / Ar 750.4: {{ratio}}, ×{{fold}} since the start of the run',
        OesActinometryNow: 'Actinometry F 703.7 / Ar 750.4: {{ratio}}',
        OesActinometryWatching: 'Actinometry F 703.7 / Ar 750.4: {{ratio}}, ×{{fold}} since this panel began watching, part-way through the run',
        OesUnwatched: 'Part of this run happened while this panel was closed. The product line there is the chamber’s own record; the fluorine line is left blank, because nothing was reading the chamber’s fluorine then and it cannot be reconstructed.',
        OesActinometryNone: 'Actinometry needs argon in the feed, and this recipe has none.',
        OesFailed: 'The emission model failed to load.',
        OesStatus: 'T_e {{te}} eV · n_e {{ne}} m⁻³ · {{line}} nm: k = {{k}} m³/s, intensity {{rel}}',
        OesLoading: 'Loading the emission model…',
        OesTeSource: 'Electron temperature',
        OesFollow: 'Follow the chamber',
        OesManual: 'Set it by hand',
        OesTe: 'T_e',
        OesPause: 'Pause',
        OesRun: 'Run',
        OesReset: 'Reset',
        SectionEmission: 'Where the endpoint signal comes from — electrons exciting atoms, atoms emitting lines (levels and a Maxwellian)',
        EmissionNote: 'The endpoint trace above is a spectrometer watching the glow, and the glow is quantum mechanics used every shift. An atom in the chamber has discrete levels. An electron from the plasma can lift it to one only if it arrives with at least that level\'s energy, and when the atom falls back it emits a photon whose wavelength the two levels fix: λ = hc/(E_upper − E_lower). Argon\'s 750 nm line needs a 13.5 eV electron; the electrons are a Maxwellian at a few eV, so the light comes from the tail, and that tail goes as exp(−13.5/T_e) — which is why a small change in T_e moves the argon lines by a large factor and the product band, with a 2.9 eV threshold, barely moves. The rate of each line is computed: a Bethe cross-section from its threshold, averaged over the distribution. Fluorine is made by dissociation and consumed while there is silicon to eat; SiF is the product. So at endpoint two things happen at once: the SiF band goes out and the fluorine line rises, because nothing is consuming fluorine any more. Argon is the balance gas and nothing consumes it, so the endpoint a fab actually watches is fluorine over argon (actinometry): a drift in the plasma itself moves both lines and cancels. Levels are NIST values. Omitted: cascades, quenching, and the vibrational structure of the band, drawn as one broad feature.',
        SectionEndpoint: 'Optical emission endpoint trace',
        SectionIedf: 'Ion energy distribution — microscopic scale',
        IedfNote:
          'The distribution above is solved from the discharge, not tabulated: Bohm ' +
          'flux sets how many ions arrive, bias power divided by that current sets ' +
          'how hard they hit, and the low-energy tail is the fraction that ' +
          'charge-exchanged crossing the sheath.',
        SectionRunControl: 'Run control',
        RunControlNote:
          'A real etch of this film takes about two and a half minutes. The ' +
          'multiplier scales process time only — the physics is solved at every step ' +
          'regardless.',
        SectionChamber: 'Chamber condition — what the recipe does not control',
        SectionRecipe: 'Recipe — {{code}}',
        SectionStoredRecipes: 'Stored recipes',
        StoredRecipesNote: 'Loading a stored recipe also loads a fresh wafer.',
        ModeThickness: 'Remaining thickness',
        ModeRate: 'Etch rate',
        ModeDeviation: 'Deviation from mean',
        TileEtchRate: 'Etch rate',
        TileNonUniformity: 'Non-uniformity',
        TileSelectivity: 'Selectivity',
        TileRegime: 'Regime',
        TileIonEnergy: 'Ion energy',
        TileIonFlux: 'Ion flux',
        TileSidewall: 'Sidewall',
        TileProcessTime: 'Process time',
        TileCleared: 'Cleared',
        FastEdge: 'fast edge',
        FastCentre: 'fast centre',
        Flat: 'flat',
        RegimeTransport: 'transport',
        RegimeMixed: 'mixed',
        RegimeReaction: 'reaction',
        FromSheathSolve: 'from sheath solve',
        Anisotropic: 'anisotropic',
        Undercut: 'undercut',
        FilmIntact: 'film intact',
        OxideLost: 'oxide lost {{nm}} nm',
        UnitNmRemaining: 'nm remaining',
        CaptionDeviation: 'blue = slower than mean, red = faster',
        CaptionNonUniformity: '{{percent}}% non-uniformity',
        ChartProfileTitle: 'Etch rate and etchant supply across the radius',
        ChartProfileX: 'r / R  (0 = centre)',
        ChartProfileSeries: 'etch rate',
        ChartEndpointTitle: 'Emission from the etch product — falls when the film clears',
        ChartEndpointX: 'process time (s)',
        ChartEndpointY: 'signal',
        ChartIedfTitle: 'Ion energy distribution — mean {{ev}} eV',
        ChartIedfX: 'ion energy (eV)',
        ChartIedfY: 'fraction',
        ChartMean: 'mean',
        PreventiveMaintenance: 'Preventive maintenance',
        MaintButton: '{{label}} — {{hours}} h down',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'Start the process and watch the wafer map, then ask me why it looks like ' +
          'that.',
      },
      patterning: {
        NoteMeanIsStillOnTarget: 'mean is {{a}} nm and still on target',
        NoteCentred: 'centred',
        NoteSpacersMeet: 'Spacers meet: a spacing of {{a}} nm is not a spacing. The film is too ' +
          'thick for this pitch, or the mandrel was over-trimmed.',
        ResultFourLines: 'Four lines where there was one — pitch {{a}} nm',
        ResultAnisotropicAgain: 'Anisotropic again',
        ResultAldOverFirstPass: '{{a}} nm ALD over the first-pass spacers, which are now the mandrels',
        ResultTwoLines: 'Two lines where there was one — pitch {{a}} nm',
        ResultAnisotropicClears: 'Anisotropic: clears horizontal surfaces, leaves the sidewalls standing',
        ResultAldConformalSidewall: '{{a}} nm ALD, {{b}}% conformal → {{c}} nm on the sidewall',
        ResultTrimBothSides: '{{a}} → {{b}} nm (both sides)',
        ResultNmLinesOnPitch: '{{a}} nm lines on a {{b}} nm pitch',
        NoteMeanSpacingWalks: 'Mean spacing {{a}} nm — exactly on target — with a {{b}} nm spread across ' +
          'the four. The pitch walks. Set the mandrel trim to {{c}} nm to centre it, ' +
          'six steps upstream of where you measured the problem.',
        NoteAllFourSpacingsAre: 'All four spacings are {{a}} nm. The pattern is centred.',
        SectionPasses: 'Passes',
        SectionRoute: 'Route',
        SecondaryLabel: 'Line CD uniformity',
        ToolLabel: 'PAT-21 Self-Aligned Quadruple Patterning Cell',
        NextTheRouteSurvivedOnPaper: 'The route survived on paper. Now ask what a 2% ALD thickness variation and ' +
          'a non-vertical mandrel sidewall do to the same recipe - the condition on ' +
          'this tool carries both.',
        AxisXSpacer1ThicknessNm: 'spacer 1 thickness (nm)',
        TitleFinalPitchAgainstFirstSpacer: 'Final pitch against first spacer thickness',
        LabelTrimmedMandrel: 'Trimmed mandrel',
        HypPitchDivisionIsSubtractionEach: 'Pitch division is subtraction. Each spacer takes width from the gap it is ' +
          'deposited in, and there is a point where the remaining gap is not wide ' +
          'enough to hold the next film at all. If so, the failure should be abrupt ' +
          'rather than gradual - the recipe works, works, works and then has no room.',
        AskedWhereDoesThisRouteStop: 'Where does this route stop being geometrically possible?',
        SumTheGeometricLimitsOfPitch: 'The geometric limits of pitch division, and what runs out first.',
        LabelFinalCd: 'final CD',
        AxisXSpacer2ThicknessNm: 'spacer 2 thickness (nm)',
        TitleFinalCdFollowsTheSpacer: 'Final CD follows the spacer film',
        NoteToPrintThisDirectly: 'to print this directly',
        LabelScannerNeeded: 'Scanner needed',
        LabelMultiplication: 'Multiplication',
        HypIfSaqpWorksTheWay: 'If SAQP works the way it is supposed to, the scanner sets only the mandrel ' +
          'pitch and everything after that is division. The final pitch should then ' +
          'track the SPACER thickness - a number set by a deposition tool with ' +
          'sub-nanometre control - and be entirely insensitive to how well the ' +
          'scanner resolves.',
        AskedWhichKnobDecidesThePitch: 'Which knob decides the pitch that ships?',
        SumADepositedFilmThicknessNot: 'A deposited film thickness, not the scanner - and what that buys.',
        NextEveryOtherLineInThis: 'Every other line in this pattern came from a different place. If half of ' +
          'them are systematically narrow, what does that do to a yield map at PRB-14 ' +
          '- and would it look like a defect or like a parametric failure?',
        LabelMeanSpacing: 'mean spacing',
        AxisYMeanSpacingNm: 'mean spacing (nm)',
        TitleAndTheMeanWhichBarely: 'And the mean, which barely notices',
        LabelIdealTrim: 'ideal trim',
        AxisYWalkNm: 'walk (nm)',
        AxisXMandrelTrimNm: 'mandrel trim (nm)',
        TitlePitchWalkAgainstMandrelTrim: 'Pitch walk against mandrel trim',
        LabelIdealTrimL126: 'Ideal trim',
        NoteLooksFineEitherWay: 'looks fine either way',
        LabelMeanSpacingL122: 'Mean spacing',
        AskedWhyAreTheSpacesIn: 'Why are the spaces in the final pattern not all the same width?',
        SumPitchWalkTheTrimThat: 'Pitch walk, the trim that causes it, and the trim that would fix it.',
        fault: {
          slopedMandrel: {
            Evidence: [
              'Mandrel sidewall angle 4.5° against a 1° limit.',
              'Final CD uniformity out of limit, pitch still centred.',
              'ALD step coverage and thickness spread are both nominal.',
            ],
            Truth: 'The mandrel etch is leaving a 4.5° sidewall. A spacer deposited on a ' +
              'sloped wall is thicker at the bottom than the top, so the etched line is ' +
              'not the film thickness after all — the same CD uniformity loss a drifting ' +
              'ALD produces, from the etcher instead. The ALD readings are what separate ' +
              'them, and they are clean.',
            Complaint: 'Same degraded CD uniformity, and the ALD was re-qualified yesterday.',
            Label: 'Mandrel sidewall not vertical',
          },
          aldNonUniform: {
            Evidence: [
              'ALD thickness spread at 4.2% against a 1.5% limit.',
              'Final CD uniformity out of limit while the pitch walk is zero.',
              'Step coverage and mandrel profile are both nominal.',
            ],
            Truth: 'The ALD showerhead has drifted and the within-wafer thickness spread is ' +
              '4.2%. The final line IS the film, so the film’s uniformity is the line’s ' +
              'uniformity — and because it affects every spacer equally it does not walk ' +
              'the pitch at all. Uniformity out with pitch centred is the signature.',
            Complaint: 'Line CD uniformity has degraded across the wafer. The pitch is perfectly ' +
              'centred.',
            Label: 'Spacer thickness varying across the wafer',
          },
          thinSidewallFilm: {
            Evidence: [
              'Step coverage at 86% against a 97% floor.',
              'CD-SEM shows the spacings walking.',
              'The ALD thickness map and the scanner CD control are both nominal.',
            ],
            Truth: 'The ALD is at 86% step coverage, so the film on the SIDEWALL — which is ' +
              'the only part that becomes a line — is thinner than the film the thickness ' +
              'monitor measures on the field. The pitch is set by that sidewall ' +
              'thickness, so it walks, and the deposition looks perfectly in-spec on ' +
              'every flat surface anybody has measured. Litho is right to refuse.',
            Complaint: 'The pitch has started walking. Litho has been asked to tighten mandrel CD ' +
              'control and is refusing on the grounds that theirs is the best in the fab.',
            Label: 'Spacer thinner on the sidewall than on top',
          },
          patHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'The pitch is centred and the CD spread is small.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. Final pitch and CD both on target.',
            Label: 'Nothing wrong',
          },
        },
        NoteNmHalfPitchDuvLimit: '{{a}} nm half-pitch, DUV limit {{b}} nm',
        NoteSetToNmThisIs: 'set to {{a}} nm — this is the knob that centres the pattern',
        NoteTheScannerAloneWouldGive: 'the scanner alone would give {{a}} nm',
        NoteNmPrintedDivided: '{{a}} nm printed, divided {{b}}×',
        SectionThePitchStopsBeingThe: 'The pitch stops being the scanner’s business',
        NoteA193NmScannerCannot: 'A 193 nm scanner cannot print below about 36 nm half-pitch and has not ' +
          'been able to since 2007. Everything finer is made by printing something ' +
          'coarse and dividing it: deposit a conformal film over a printed line, etch ' +
          'it anisotropically so it survives only on the sidewalls, and pull the line ' +
          'out from between. Two lines where there was one. Do it twice for four. The ' +
          'final CD is a film thickness — which DEP-02 controls to a per cent, where ' +
          'LITH-03 manages several.',
        TextPatternCollapsed: 'Pattern collapsed',
        SectionCellState: 'Cell state',
        SectionThePatternThatComesOut: 'The pattern that comes out',
        TextRunTheCellOnThis: 'Run the cell on this lot',
        LabelSaqpTwoPasses: 'SAQP — two passes',
        LabelSadpOnePass: 'SADP — one pass',
        LabelFinalPitch: 'Final pitch',
        LabelPitchWalk: 'Pitch walk',
        LabelFinalLineCd: 'Final line CD',
        NoteThisIsAFilmThickness: 'this is a film thickness, not a printed feature',
        LabelCdUniformity: 'CD uniformity',
        LabelIdealMandrelTrim: 'Ideal mandrel trim',
        LabelAgainstAScannerAlone: 'Against a scanner alone',
        AssistantLead: 'PAT-21 inquiry assistant',
        AssistantBlurb:
          'Ask why the spaces alternate, what really sets the final pitch, or where ' +
          'the route runs out of room. Every answer is the eight-step route run ' +
          'against this recipe.',
        reading: {
          cdUniformity: {
            Label: 'Final line CD uniformity, 49 sites (2.0 h)',
          },
          pitchWalk: {
            Limit: '< 1 nm spread',
            Label: 'CD-SEM every space in a period, 200 sites (3.0 h)',
          },
          scannerCd: {
            Label: 'Scanner mandrel CD control',
          },
          sidewallAngle: {
            Label: 'Mandrel sidewall angle from vertical',
          },
          aldMap: {
            Label: 'ALD within-wafer thickness spread',
          },
          conformalityCheck: {
            Label: 'ALD step coverage on a monitor feature',
          },
        },
        aspect: {
          scanner: {
            Action:
              'Nothing to repair here — the mandrel is consumed and its CD does not reach ' +
              'the product',
            Label: 'Scanner CD control at LITH-03',
          },
          sidewall: {
            Action: 'Re-tune the mandrel etch for a vertical profile',
            Label: 'Mandrel sidewall angle at ETCH-01',
          },
          aldUniformity: {
            Action: 'Re-tune the ALD showerhead and re-map thickness across the wafer',
            Label: 'Spacer thickness uniformity at DEP-02',
          },
          conformality: {
            Action: 'Re-qualify the ALD saturation window — the sidewall is thinner than the top',
            Label: 'Spacer conformality at DEP-02',
          },
        },
      },
      cmp: {
        NextHoldingIsTheEasyHalf: 'Holding is the easy half. The wafers already polished this shift went out ' +
          'against the same sheet - what would you have to measure to decide whether ' +
          'any of them should come back?',
        NextTheSheetPassedToday: 'The sheet passed today. Which line on it is closest to its limit, and how ' +
          'many lots do you think it has left before it is the one that holds the ' +
          'tool?',
        BridgingClear: 'clear',
        BridgingShorted: 'SHORTED',
        LabelBridging: 'Bridging metal',
        LabelPlanarity: 'Planarity',
        VerdictWithAWatchItem: 'released, with a watch item',
        VerdictHeld: 'Held',
        VerdictQualified: 'Qualified',
        LabelVerdict: 'Verdict',
        HypAQualificationIsADecision: 'A qualification is a decision, not an explanation. If the monitor wafer ' +
          'comes out flat enough for the next layer, inside the cycle time the line ' +
          'can absorb, with nothing bridging, and every bench reading is inside its ' +
          'own limit, the tool releases. Otherwise it is held, and the qualified ' +
          'recipe on the same hardware should say whether the recipe or the tool is ' +
          'responsible.',
        AskedIsThisPolisherFit: 'Is this polisher fit to run product right now, and what do I do about it?',
        SumPolishTheMonitorWafer: 'Polish the monitor wafer, read the bench against its limits, and make the ' +
          'call.',
        UnitNmAcrossDie: 'nm across die',
        NoteTheRegionsAboveAre: 'The regions above are a LIST: each clears by its own formula, dishes by ' +
          'its own law, and none of them knows its neighbours exist. Below, the same ' +
          'die is a MAP, stepped through time — the pad is a millimetre-scale kernel ' +
          'that presses hardest where the surface stands proud, so planarisation is a ' +
          'feedback rather than a coefficient, and what a block of layout sits NEXT ' +
          'TO changes how it polishes. The cut line below runs across the middle of ' +
          'the die: bond pad, dense array, isolated line, field in between.',
        NoteTheTwoModelsDisagree: 'The two models disagree on the clock here, which usually means the recipe ' +
          'has left the regime the region list was fitted in - a heavy incoming step ' +
          'or an extreme overpolish. The grid is the one still doing physics: ' +
          'pressure follows the surface it is actually pressing on.',
        NoteOnThisRecipeTheTwo: 'On this recipe the two agree on the clock and on the failure ordering - ' +
          'the grid contains the region list\'s own laws, so where regions really are ' +
          'independent it reproduces them. What the list can never contain is the ' +
          'pad\'s LENGTH: a stiff pad planarises and a floppy one polishes ' +
          'conformally, erosion depends on the neighbourhood, and overpolish ' +
          'un-flattens the die - the step height falls to its minimum at endpoint and ' +
          'erosion buys it back afterwards.',
        ValueNoSuchTerm: 'no such term',
        ValueInTheKernel: 'in the kernel: layout polishes by its company',
        MetricNeighbourhood: 'Neighbourhood',
        ValuePerRegionTimes: 'per-region times — no field, no map',
        ValueSOfSequence: '{{a}} s of sequence, last on the open field',
        MetricClearing: 'Clearing',
        MetricDieSurfaceRange: 'Die surface range',
        MetricWorstErosion: 'Worst erosion',
        MetricWorstDishing: 'Worst dishing',
        MetricEndpoint: 'Endpoint',
        LegendTheRegionList: 'the region list',
        LegendTheDieStepped: 'the die, stepped through time ({{a}}×{{a}} cells)',
        ValueClears: 'clears',
        LabelRegion: 'Region',
        LabelRemoval: 'removal',
        LabelRemovalNonUniformity: 'Removal non-uniformity',
        LabelPolishTime: 'Polish time',
        SecondaryLabel: 'Polish time',
        ToolLabel: 'CMP-11 · Copper damascene polisher',
        NextIfTheLayoutBuildsThe: 'If the layout builds the failure in, the fix is not on this tool. What ' +
          'would you change about a design to make it polish flat - and who has to ' +
          'agree to it?',
        AxisYDishingNm: 'dishing (nm)',
        AxisXLineWidthUm: 'line width (um)',
        TitleDishingAgainstFeatureWidthAt: 'Dishing against feature width, at fixed pattern density',
        NoteSamePolishSameWafer: 'same polish, same wafer',
        LabelFineArrayDishes: 'Fine array dishes',
        LabelWidestFeatureDishes: 'Widest feature dishes',
        LabelClearsLast: 'Clears last',
        LabelClearsFirst: 'Clears first',
        HypTwoSeparateLayoutPropertiesAre: 'Two separate layout properties are doing it, and they are easy to confuse. ' +
          'How much of a region is RAISED decides when it clears, because only the ' +
          'raised part touches the pad and it carries the whole load. How WIDE a ' +
          'feature is decides whether it dishes, because the pad has to bend into a ' +
          'recess to reach the metal at the bottom and it can only bend so far. ' +
          'Density sets the timing; width sets the damage.',
        AskedYouWantToKnowWhy: 'You want to know why some features on the die come out fine and others are ' +
          'ruined by the same polish.',
        SumShowThatTheLayoutNot: 'Show that the layout, not the polisher, decides which features fail.',
        NextIfMatchedSpindlesMakeThe: 'If matched spindles make the speed uniform, then any radial signature left ' +
          'on this tool is a pressure problem. Two things on the carrier can cause ' +
          'one - and they push in opposite directions. Which is which?',
        LabelMatchedToPlaten: 'matched to platen',
        AxisYRemovalNonUniformity: 'removal non-uniformity (%)',
        AxisXCarrierSpeedRpm: 'carrier speed (rpm)',
        TitleRemovalUniformityAgainstCarrierSpeed: 'Removal uniformity against carrier speed, platen held fixed',
        LabelEdgeSpeedThen: 'Edge speed then',
        LabelCarrierAtHalf: 'Carrier at half',
        LabelNonUniformityMatched: 'Non-uniformity, matched',
        NoteIdenticalAtEveryRadius: 'identical at every radius',
        LabelMatchedCentreToEdge: 'Matched, centre to edge',
        HypAPointOnTheWafer: 'A point on the wafer moves relative to the pad at w_p x d + (w_p - w_c) x ' +
          'r, where d is the offset between the two centres and r is where the point ' +
          'sits on the wafer. When the two speeds are equal the second term is ' +
          'exactly zero, so every point sees the same speed no matter where it is. If ' +
          'that is right, matching the spindles should give perfectly uniform removal ' +
          'and any mismatch should put a radial signature straight onto the wafer.',
        AskedYouWantToKnowWhyL193: 'You want to know why both spindles run at the same speed, and what happens ' +
          'to the wafer when they do not.',
        SumShowThatMatchingTheTwo: 'Show that matching the two spindles makes pad speed uniform, exactly.',
        NextOverPolishBuysInsuranceAgainst: 'Over-polish buys insurance against a short. What decides how much ' +
          'insurance you need - and which knob on this tool changes that, rather than ' +
          'changing how long you polish for?',
        LabelYourRecipe: 'your recipe',
        LabelDishingWidestFeature: 'dishing, widest feature',
        LabelMetalStillBridging: 'metal still bridging',
        AxisXOverPolish: 'over-polish (%)',
        NoteOfTheScannerBudget: 'of the scanner budget',
        LabelCdCostAt20: 'CD cost at 20%',
        NoteSameRecipeMoreTime: 'same recipe, more time',
        LabelDishingAt120: 'Dishing at 120%',
        NoteWidestFeature: 'widest feature',
        LabelDishingAt20: 'Dishing at 20%',
        NoteMetalStillBridging: 'metal still bridging',
        LabelShortsBelow: 'Shorts below',
        HypTheseTwoFailuresPullIn: 'These two failures pull in opposite directions and there is no setting ' +
          'that avoids both, because the endpoint detector fires on a bulk friction ' +
          'change and cannot see the last few per cent of area still carrying metal. ' +
          'Over-polish is what pays for that blind spot, and every second of it is ' +
          'also spent dishing whatever cleared first.',
        SumFindTheTradeBetweenMetal: 'Find the trade between metal left bridging and metal dished away.',
        fault: {
          zoneDrift: {
            Evidence: [
              'Removal is centre-fast — the opposite sign to a worn retaining ring, and ' +
                'the only thing that separates the two.',
              'Both specs still pass. Nothing is scrapping and nothing will force a ' +
                'decision.',
              'A pressure-mapping wafer disagrees with what the zone regulators report.',
              'The ring gauges fine, which rules out the other pressure fault.',
            ],
            Truth: 'A regulator on the carrier centre zone has drifted and is delivering about ' +
              '40% more pressure than it reports. The centre of every wafer polishes ' +
              'fast, clears early and then over-polishes while the edge finishes. ' +
              'Planarity and cycle time both still pass, which is exactly the trap: the ' +
              'tool is 16% non-uniform and every wafer ships, so nothing forces the issue ' +
              'until the drift gets far enough to start scrapping.',
            Complaint: 'Wafer map has gone centre-fast. Everything is still passing, so the shift ' +
              'wants to keep running it.',
            Label: 'Carrier centre zone reading high',
          },
          wornRing: {
            Evidence: [
              'Removal is edge-fast — the edge-minus-centre reading is strongly positive.',
              'Planarity is out of spec, and it is out of spec worst at the edge.',
              'A drifted carrier zone gives the OPPOSITE radial signature. The direction ' +
                'is the whole diagnosis and a wafer map shows it at a glance.',
              'Ring thickness gauges below its replacement limit.',
            ],
            Truth: 'The retaining ring is sacrificial and this one is past its limit. It is no ' +
              'longer standing proud enough to hold the pad down ahead of the wafer, so ' +
              'the pad rebounds as it crosses the wafer edge and the last few millimetres ' +
              'see more pressure than the recipe asked for. The edge clears early, then ' +
              'over-polishes for the rest of the run while the centre catches up, which ' +
              'is why it is both non-uniform and dished rather than merely non-uniform.',
            Complaint: 'Edge sites are over-polished and dishing at the wafer edge is out of spec. ' +
              'The centre of the wafer is fine.',
            Label: 'Retaining ring worn past limit',
          },
          agedSlurry: {
            Evidence: [
              'Planarity is far out of spec while removal rate and uniformity are both ' +
                'close to nominal.',
              'The damage is UNIFORM across the wafer, which rules out both pressure ' +
                'faults at once - nothing about a carrier or a ring produces a defect with ' +
                'no radial signature.',
              'A slurry titration shows inhibitor concentration well below its limit.',
              'Profilometry on a monitor shows dishing on the wide features and erosion ' +
                'in the arrays.',
            ],
            Truth: 'The drum has been on the tool too long and the corrosion inhibitor has ' +
              'broken down. The abrasive is largely unaffected, which is why the removal ' +
              'rate barely moves - but the inhibitor was doing two jobs. It passivated ' +
              'the dielectric, which is where the process\'s selectivity came from, and it ' +
              'protected recessed copper between pad contacts. With it gone, every wide ' +
              'line dishes and every dense array erodes, uniformly across the whole ' +
              'wafer.',
            Complaint: 'Dishing on the bond pads has trebled. Rate looks normal, uniformity looks ' +
              'normal.',
            Label: 'Slurry past its shelf life',
          },
          glazedPad: {
            Evidence: [
              'Cycle time is far out while planarity and uniformity are exactly nominal. ' +
                'Nothing about the wafer has changed.',
              'The removal rate is down by more than half.',
              'A pad cut-rate check shows the conditioner is not removing pad material.',
              'This is the fault a quality system cannot see. Only the tool log catches it.',
            ],
            Truth: 'The diamond conditioner has stopped cutting, so the pad surface has closed ' +
              'over and no longer carries slurry into the contact. Removal drops for the ' +
              'same force and speed. What makes this so easy to ignore is that the tool ' +
              'is endpoint-controlled: it simply polishes for longer and produces an ' +
              'identical wafer. The defect is not on the wafer at all, it is in the ' +
              'capacity of the line, and it will go on being invisible until somebody ' +
              'looks at a throughput chart instead of a quality one.',
            Complaint: 'Polish times have crept up all week — we are at nearly three minutes on a ' +
              'recipe that used to run ninety seconds. Every wafer still measures ' +
              'perfectly. Nobody can see a problem.',
            Label: 'Pad glazed, conditioner not cutting',
          },
          healthy: {
            Evidence: [
              'Planarity, uniformity and cycle time are all at their qualified values.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Pad was changed on Monday and the tool passed its qualification. Nothing ' +
              'reported.',
            Label: 'Polisher qualified',
          },
        },
        TextArrivedWithNmOfSurface: 'Arrived with {{a}} nm of surface variation.',
        NoteNmTotal: '{{a}} nm total',
        TextNm: '{{a}} / {{b}} nm',
        region: { pad: 'Bond pad, 80 um', array: 'Dense array, 0.5 um', iso: 'Isolated line, 2 um' },
        TextMetal: '{{a}} — {{b}}% metal',
        NoteRecessedMetalAtOfBlanket: 'recessed metal at {{a}}% of blanket',
        NoteNm: '< {{a}} nm',
        NoteEndpointAtS: 'endpoint at {{a}} s',
        NoteMSPadSpeed: '{{a}} m/s pad speed',
        SectionRemovalAcrossTheWafer: 'Removal across the wafer',
        NoteTurnThePlatenAndThe: 'Turn the platen and the carrier at the same speed and every point on the ' +
          'wafer sees the same pad speed — exactly, not approximately. Set them apart ' +
          'and a radial signature appears that no amount of pressure trimming will ' +
          'remove.',
        SectionTheDieRegionByRegion: 'The die, region by region',
        NoteEndpointFiresWhenTheBulk: 'Endpoint fires when the bulk of the wafer transitions, not when the last ' +
          'region finishes. Whatever clears early waits — and over-polishes while it ' +
          'waits.',
        SectionWhatTheNextLayerInherits: 'What the next layer inherits',
        NoteAScannerHasOneFocal: 'A scanner has one focal plane and this surface has a range. Whatever is ' +
          'left here is spent out of the lithography focus budget two steps later, on ' +
          'a scanner that is working perfectly.',
        SectionSolvedBesideTheFormulaIt: 'Solved, beside the formula it replaces',
        TextDishingErosion: 'dishing / erosion',
        AxisXPositionOnTheDieMm: 'position on the die (mm)',
        AxisYNmAboveTheLowestPoint: 'nm above the lowest point',
        LabelRemovalRate: 'Removal rate',
        LabelPlanarityAcrossDie: 'Planarity across die',
        LabelMetalBridging: 'Metal bridging',
        LabelSlurrySelectivity: 'Slurry selectivity',
        AxisXNormalisedRadius: 'normalised radius',
        AxisYMetalRemovedNm: 'metal removed (nm)',
        LabelSurfaceHandedOn: 'Surface handed on',
        NoteAcrossTheDie: 'across the die',
        LabelCdSpreadItCauses: 'CD spread it causes',
        NoteAtLith03FromDefocus: 'at LITH-03, from defocus alone',
        LabelOfTheCdBudget: 'Of the CD budget',
        NoteNoLotOnThePlaten: 'No lot on the platen. Send a wafer here from an upstream tool to polish it.',
        NoteThisLotIsStillAn:
          'This lot is still an ingot. WPR-08 has to slice it before it can be ' +
          'polished.',
        TextPolishThisWaferAndPass: 'Polish this wafer and pass it on',
        reading: {
          profilometry: {
            Label: 'Profilometry on a monitor wafer (4 h)',
          },
          zonemap: {
            Label: 'Pressure-mapping wafer, all zones (3 h)',
          },
          ringgauge: {
            Label: 'Retaining ring thickness gauge (1 h)',
          },
          inhibitor: {
            Label: 'Slurry titration, inhibitor assay (1 h)',
          },
          padcut: {
            Label: 'Conditioner cut-rate check (2 h)',
          },
          short: {
            Limit: 'clear',
            Label: 'Metal bridging after polish',
          },
          edge: {
            Label: 'Edge minus centre removal',
          },
          nu: {
            Label: 'Removal non-uniformity',
          },
          rate: {
            Label: 'Blanket removal rate',
          },
          cycletime: {
            Label: 'Polish time to endpoint',
          },
        },
        aspect: {
          carrier: {
            Action: 'Re-calibrate the zone regulators against a pressure-mapping wafer',
            Label: 'Carrier zone pressures',
          },
          slurry: {
            Action: 'Drain the loop, fit a fresh drum, flush and re-certify',
            Label: 'Slurry supply',
          },
          ring: {
            Action: 'Replace the retaining ring and re-zero the carrier',
            Label: 'Retaining ring',
          },
          pad: {
            Action: 'Break in a new pad and re-qualify the conditioner sweep',
            Label: 'Polishing pad',
          },
        },
        AssistantBlurb:
          'Ask about over-polish, spindle speeds, or why one feature on a die fails ' +
          'while its neighbour survives the same polish. Every answer is an ' +
          'experiment run against this polisher in the condition it is actually in.',
        AssistantLead: 'CMP-11 inquiry assistant',
      },
      plating: {
        TransportSurface: 'Cu(II) at the wafer',
        TransportOfLimit: 'of the limit',
        TransportBurning: 'surface dry — burning',
        SectionTransport: 'Where the limiting current comes from (mass transport)',
        TransportNote: 'Current at the wafer consumes copper out of the bath, and the bath resupplies it by ' +
          'diffusion through a layer at the surface. The colour is how much Cu(II) is actually there. ' +
          'Wind the current up and the surface runs down; wind it far enough and it reaches zero, which ' +
          'is what "burnt deposit" means — copper arriving as fast as diffusion allows and no faster, ' +
          'landing as powder instead of film. The sharp part is what happens when you change the bath ' +
          'strength: the same current always leaves the same gap under the surface, whatever the bath ' +
          'started with, so the surface empties at a current exactly proportional to the copper in it. ' +
          'That is the straight line the panel above uses as its cap, with its slope explained rather ' +
          'than chosen. One honest limit: this solves an ohmic field, so the current here keeps ' +
          'climbing past the limit instead of levelling off the way a real cell does. Read the surface, ' +
          'not the current.',
        NoteBothModelsAgreeOn: 'Both models agree on this feature, and that is worth as much as the ' +
          'disagreements — a solver that never reproduced the formula where the ' +
          'formula works would just be wrong. Take the aspect ratio past about six, ' +
          'or starve the accelerator, and watch them separate.',
        NoteTheTwoModelsReturn: 'The two models return different VERDICTS on this feature, not merely ' +
          'different numbers. The race criterion maps a ratio onto a void fraction ' +
          'through a fitted line; the grid either has cells left over at the end or ' +
          'it does not. Only one of them can be checked against a cross-section, ' +
          'which is the point.',
        NoteAboveValidatedDomain: 'This accelerator concentration is above {{a}}× the qualified bath, which ' +
          'is where this solver stops being trustworthy. Its coverage balance admits ' +
          'a runaway up there that has not been checked against measurement, so read ' +
          'the picture as an illustration and not as a result.',
        ValueAtTheBottomByDefinition: '{{a}}, at the bottom by definition',
        ValueAtOfTheDepth: '{{a}} at {{b}} % of the depth',
        MetricPeakAcceleratorCoverage: 'Peak accelerator coverage',
        ValueXAssumed: '{{a}}× (assumed)',
        ValueXMeasured: '{{a}}× (measured)',
        MetricBottomUpGain: 'Bottom-up gain',
        ValueATimeNotAnEvent: 'a time, not an event',
        ValueNever: 'never',
        ValueSteps: '{{a}} steps',
        MetricMouthClosesAt: 'Mouth closes at',
        ValueNoSuchQuantity: 'no such quantity',
        ValueNone: 'none',
        ValueNmWideNmDown: '{{a}} nm wide, {{b}} nm down',
        MetricVoidAsAPlace: 'Void, as a place',
        MetricVoidAsAFraction: 'Void, as a fraction',
        ValueFilled: 'filled',
        MetricVerdict: 'Verdict',
        NoteTheCellAboveDecides: 'The cell above decides the feature by racing two numbers: how long the ' +
          'bottom takes to reach the top against how long the sidewalls take to meet. ' +
          'Below, the copper front is ADVANCED on a grid instead. Nothing in it ' +
          'decides whether the trench voids — the sidewalls either meet before the ' +
          'floor arrives or they do not, and what is left over is the void. So there ' +
          'is a cross-section to look at, which a race between two numbers can never ' +
          'produce.',
        LabelNonUniformity: 'Non-uniformity',
        ValueVoid: 'void',
        ValueSeam: 'seam',
        ValueClean: 'clean',
        LabelFill: 'Fill',
        LabelVoidLegend: 'void',
        LabelDielectric: 'dielectric',
        SecondaryLabel: 'Bottom-up gain',
        ToolLabel: 'ECD-12 Copper Electroplating Cell',
        NextEveryNanometreOfOverburdenHere: 'Every nanometre of overburden here is polish time at CMP-11, and polish ' +
          'time is dishing. What is the thinnest plate that still fills the trench ' +
          'completely?',
        AxisXCurrentDensityMaCm: 'current density (mA/cm²)',
        TitleFieldThicknessAgainstCurrentDensity: 'Field thickness against current density',
        LabelFieldThickness: 'Field thickness',
        AskedWhatSetsPlatedThicknessAnd: 'What sets plated thickness, and what does plating faster cost?',
        NextTryTheSameSweepAgainst: 'Try the same sweep against fill quality. Where is the seed thickness that ' +
          'is thick enough to plate flat and thin enough to still fill the trench - ' +
          'and does one exist here?',
        AxisYEdgeCentre: 'edge − centre (%)',
        AxisXSeedThicknessNm: 'seed thickness (nm)',
        TitleTerminalEffectAgainstSeedThickness: 'Terminal effect against seed thickness',
        NoteOverburden: 'Overburden',
        NoteEdgeOverCentre: 'edge over centre',
        LabelSeedSheetR: 'Seed sheet R',
        HypCurrentEntersTheWaferThrough: 'Current enters the wafer through contacts on its rim and has to travel ' +
          'through the seed layer to reach the centre. That seed is thin metal, so it ' +
          'is a resistor, and the centre sits at a lower potential than the edge. If ' +
          'that is the mechanism, a THICKER seed should flatten the profile - and it ' +
          'should do so without touching the bath at all.',
        AskedWhyDoesPlatedThicknessLean: 'Why does plated thickness lean from edge to centre?',
        NextTheAdditivesBreakDownAs: 'The additives break down as they work, so a bath that filled this morning ' +
          'may not fill this afternoon. What would that failure look like on an ' +
          'inspection scan at INSP-13 - and would it look like anything at all?',
        AxisYFractionOfTrench: 'fraction of trench',
        TitleVoidFraction: 'Void fraction',
        LabelConformalNoAdvantage: 'conformal — no advantage',
        AxisXAcceleratorQualifiedBath: 'accelerator (× qualified bath)',
        TitleBottomUpGainAgainstAccelerator: 'Bottom-up gain against accelerator',
        LabelAspectRatio: 'Aspect ratio',
        HypIfPlatingWereUniformA: 'If plating were uniform, a trench would coat at the same rate everywhere, ' +
          'the mouth would close before the bottom filled, and every feature would ' +
          'contain a void. That it usually does not means growth at the BOTTOM must ' +
          'be faster than at the mouth. If the accelerator is what does that, ' +
          'removing it should reintroduce the void the process exists to avoid.',
        AskedWhyDoesTheTrenchEnd: 'Why does the trench end up with a void instead of solid copper?',
        SumBottomUpSuperfillAndWhat: 'Bottom-up superfill, and what happens when the additive balance is wrong.',
        fault: {
          starvedBath: {
            Evidence: [
              'Bath copper 14 g/L against a 34 g/L floor.',
              'Thickness low with the burnt-deposit non-uniformity, on every wafer.',
              'Anode potential, contact resistance, bath age and temperature all nominal.',
              'The utility panel at facilities shows the ECD-12 chemistry line leaking and its ' +
                'delivery interlocked shut.',
            ],
            Truth:
              'The plating chemistry line in the sub-fab has leaked into its containment sleeve, ' +
              'the tray sensor under the cabinet tripped, and the leak interlock shut the delivery ' +
              'pump — which is exactly what it is for. Up here that means the bath has not been ' +
              'made up all shift: plating pulls copper out of it with every wafer, and it is down ' +
              'to a third of the recipe’s copper. The limiting current came down with it, so the ' +
              'same 20 mA/cm² is now past what the bath can feed and the deposit burns — thin, and ' +
              'rough at the edge where the current crowds. Nothing on this cell refills a bath ' +
              'whose supply is interlocked off two floors down.',
            Complaint:
              'Copper thin across the whole wafer and the deposit has gone dull and powdery at the ' +
              'edge. Anode, contact ring and bath temperature all check out; the bath was dosed on ' +
              'schedule.',
            Label: 'Bath starved of copper — delivery line leaking',
          },
          dirtyContact: {
            Evidence: [
              'Contact resistance at 70 mΩ against a 15 mΩ limit.',
              'Thickness map both low and out of uniformity.',
              'Anode potential is nominal.',
            ],
            Truth: 'The contact ring is dirty. It starves the wafer of current the way a ' +
              'passivated anode does, and it adds something the anode never could: the ' +
              'loss is not the same on every finger, so the wafer plates unevenly as well ' +
              'as thin. Thin plus non-uniform is the signature.',
            Complaint: 'Copper thin AND the map has gone edge-heavy on one sector.',
            Label: 'Contact ring resistance high',
          },
          passivatedAnode: {
            Evidence: [
              'Anode potential 1.4 V above nominal against a 0.4 V limit.',
              'Thickness map low.',
              'Contact ring resistance is nominal.',
            ],
            Truth: 'A film has grown on the anode and it is costing driving potential, so the ' +
              'wafer sees less current than the supply believes it is delivering. Less ' +
              'current is less copper, exactly as Faraday says. The contact ring is ' +
              'clean, which is what separates this from the other way to starve a wafer.',
            Complaint: 'Copper is coming out thin and CMP is over-polishing to compensate.',
            Label: 'Anode passivated',
          },
          exhaustedAdditives: {
            Evidence: [
              'Bath age at 32 Ah/L against a 12 Ah/L limit.',
              'Cross-section shows a voided fill.',
              'Thickness and uniformity are both perfectly on target, which is what makes ' +
                'this fault invisible without destroying a wafer.',
            ],
            Truth: 'The bath is at 32 Ah/L and the accelerator has broken down. Faraday does ' +
              'not care about additives — the right amount of copper went down — but the ' +
              'CEAC feedback that fills a trench from the bottom is gone, so every ' +
              'feature has a void down the middle. It passes every non-destructive ' +
              'measurement the fab owns, and it fails in the customer’s hands.',
            Complaint: 'Reliability has returned parts with open vias from a lot that passed every ' +
              'test at sort. Plating says the wafers were perfect: right thickness, right ' +
              'uniformity, right cycle time.',
            Label: 'Additive package exhausted',
          },
          ecdHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Cross-section shows a clean bottom-up fill.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. Thickness and uniformity both on target.',
            Label: 'Nothing wrong',
          },
        },
        NoteSPerWafer: '{{a}} s per wafer',
        NoteSeedSqTheCentreIs: 'seed {{a}} Ω/sq — the centre is furthest from the contact',
        NoteAcceleratorCoverageAtTheTrench: 'accelerator coverage {{a}} at the trench bottom',
        NoteGainAgainstNeededAtAr: 'gain {{a}}× against {{b}}× needed at AR {{c}}',
        SectionAPositiveFeedbackMadeOf: 'A positive feedback made of geometry',
        NoteTheAcceleratorIsConservedOn: 'The accelerator is conserved on the surface it sits on. As the bottom of a ' +
          'trench fills, that surface CONTRACTS — so the same molecules occupy less ' +
          'area, coverage rises, the bottom goes faster, and its area shrinks faster ' +
          'still. That is the whole mechanism behind bottom-up fill, and it is why ' +
          'superfill works hardest on exactly the features that need it most. The ' +
          'gain has to beat twice the aspect ratio, or the sidewalls meet first and ' +
          'the trench is voided.',
        SectionSolvedBesideTheFormulaIt: 'Solved, beside the formula it replaces',
        LabelCopperLate: 'copper, late',
        LabelCopperEarly: 'copper, early',
        SectionCellState: 'Cell state',
        SectionBathAndRecipe: 'Bath and recipe',
        TextPlateTheWaferAndPass: 'Plate the wafer and pass it to CMP-11',
        LabelBottomUpGain: 'Bottom-up gain',
        LabelOverburden: 'Overburden',
        NoteEveryNanometreIsCmp11: 'every nanometre is CMP-11 dishing time',
        LabelTerminalEffect: 'Terminal effect',
        Note4AcrossTheWafer: '< 4% across the wafer',
        LabelChargePassed: 'Charge passed',
        AssistantLead: 'ECD-12 inquiry assistant',
        AssistantBlurb:
          'Ask why a trench voided, why the edge plates thicker, or how long to plate ' +
          'for. Every answer is a fill run against this bath.',
        reading: {
          bathCopper: {
            Limit: '> 34 g/L',
            Label: 'Bath copper against the make-up target',
          },
          houseutilities: {
            Limit: 'all systems nominal',
            Label: 'Call facilities for the utility panel (0.5 h)',
          },
          crossSection: {
            Limit: 'filled',
            Label: 'FIB cross-section of the trench — destructive (3.0 h)',
          },
          thickness: {
            Label: 'Four-point probe thickness map, 49 points (1.0 h)',
          },
          bathTemp: {
            Label: 'Bath temperature',
          },
          contactOhms: {
            Label: 'Contact ring resistance',
          },
          anodeVolts: {
            Label: 'Anode potential above nominal',
          },
          bathAge: {
            Label: 'Bath age since last bleed',
          },
        },
        aspect: {
          house: {
            Action: 'Stop work on the tool and raise it with facilities',
            Label: 'Nothing on this tool — escalate to facilities',
          },
          temperature: {
            Action: 'Re-calibrate the bath thermocouple and re-tune the heater loop',
            Label: 'Bath temperature',
          },
          contact: {
            Action: 'Clean the contact ring and re-check resistance on every finger',
            Label: 'Contact ring',
          },
          anode: {
            Action: 'Strip the anode film and re-activate, or change the anode',
            Label: 'Anode',
          },
          bath: {
            Action: 'Bleed and feed the bath, and re-set the dosing against a CVS analysis',
            Label: 'Additive package',
          },
        },
      },
      thermal: {
        TunnelDrawnMag: 'the picture past the wall is drawn ×{{m}} brighter than it is',
        NoteOnTheMonitor: 'on the monitor',
        LabelJunction: 'Junction depth',
        NextHoldingIsTheEasyHalf: 'Holding is the easy half. Batches that ran earlier on this tube went out ' +
          'against the same bench sheet - what would you have to measure, and on ' +
          'which wafers, to decide whether any of them need to come back?',
        NextOneMonitorInAFiftyWafer: 'One monitor wafer released a batch of fifty. Where in the boat would you ' +
          'put that monitor, and what would a second one somewhere else in the load ' +
          'tell you that this one cannot?',
        LabelActivation: 'Dose activated',
        LabelSheetResistance: 'Sheet resistance',
        LabelGateOxide: 'Gate oxide',
        VerdictWithAWatchItem: 'released, with a watch item',
        VerdictHeld: 'Held',
        VerdictQualified: 'Qualified',
        LabelVerdict: 'Verdict',
        HypAQualificationIsADecision: 'A qualification is a decision, not an explanation. If the monitor wafer ' +
          'comes back with its oxide inside the window and its sheet resistance ' +
          'within tolerance of target, and every bench reading is inside its own ' +
          'limit, the furnace releases. Otherwise it is held - and the qualified ' +
          'recipe on the same tube should say whether the recipe or the tool is ' +
          'responsible.',
        AskedIsThisFurnaceFit: 'Is this furnace fit to run product right now, and what do I do about it?',
        SumRunTheMonitorWafer: 'Run the monitor wafer, read the bench against its limits, and make the call.',
        NotePerNm3: 'silicon itself is 50 atoms per nm³',
        BudgetSpikeCaveat: 'The graph’s two lower panels are this same experiment scored by the two ' +
          'definitions of “thermal budget” in circulation. ∫T dt — temperature times ' +
          'time — calls these flows about 4% apart: its two curves lie on top of each ' +
          'other, and its unit, °C·s, cannot produce a depth at all. ∫D dt, in nm², ' +
          'calls them a hundredfold apart, and it is the one that predicts where the ' +
          'boron went. The experiment is scripted into the scene itself — no knobs, ' +
          'because both wafers must share everything except the spike — and it stops ' +
          'on its own at the end of the flow; Reset runs the whole ninety minutes ' +
          'again.',
        BudgetSpikeNote: 'Two wafers, the same implanted boron dose, the same target: 90 minutes at ' +
          '900 °C. STEADY gets exactly that and lands on target. SPIKED is identical ' +
          'except for ten minutes at 1200 °C, twenty minutes in — one step out of a ' +
          'ninety-minute flow, and it ends several times deeper at the probe, with ' +
          'essentially all of the damage done in those ten minutes. That is what a ' +
          'budget is: the furnace does not average your flow, it integrates D over ' +
          'it, and D is exponential in temperature.',
        SectionBudgetSpike: 'Thermal budget — ten minutes out of ninety',
        BudgetSteady: 'steady',
        BudgetSpiked: 'spiked',
        BudgetSpiking: 'SPIKE — left wafer at 1200 °C',
        BudgetShared: 'same flow — both wafers identical',
        BudgetLabelSpiked: 'SPIKED · 10 min at 1200 °C',
        BudgetLabelSteady: 'STEADY · 900 °C throughout',
        BudgetNowTemp: 'now {{t}} °C',
        BudgetDtNote: 'Each step’s own ∫D·dt in nm² — the quantity the junction actually ' +
          'integrates, and the reconciliation of the two units above: the budget tile ' +
          'divides this same area by D at 1000 °C to quote it as equivalent seconds. ' +
          'Plotted per step, the exponential shows itself — a short hot step can ' +
          'outspend a long cool one.',
        SeriesStepDt: 'per-step D·t',
        ChartDtAxis: 'D·t (nm²)',
        ChartBudgetDtTitle: 'Each step’s D·t — temperature is what spends the budget',
        HopBuilding: 'Building the crystal — placing the interstitial and relaxing 217 atoms ' +
          'onto the force field…',
        HopGlowNote: 'The yellow atom is the interstitial. It is found live, as the atom sitting ' +
          'farthest from every site of the ideal lattice — which is what lets the ' +
          'marker follow the defect through each kick-out, when a different atom ' +
          'inherits the interstice. At temperature the defect sometimes spreads ' +
          'across two atoms sharing one site (a dumbbell); then both glow, because ' +
          'both of them really are the defect.',
        HopMsdNote: 'The graph beside the crystal carries two curves, and the gap between them is the lesson. ' +
          'The solid one is a measurement: where the interstitial has actually got to, sampled ' +
          'continuously, so it trembles with the lattice, climbs when a hop lands, and falls back ' +
          'when the next kick-out runs in reverse. One trajectory of a few jumps is not the mean of ' +
          'anything — three runs at the same temperature reached 9.5, 51 and 3.4 Å², which is ' +
          'simply what a small sample of a random walk looks like, so do not read a slope off it. ' +
          'The dashed one adds up the squared jump lengths instead: the walk those same hops would ' +
          'make if none of them retraced. It only ever climbs, and it is the line D comes from — ' +
          'its slope over six, in the same units the drive-in uses. When the solid curve sags below ' +
          'the dashed one you are watching correlation, the interstitial going back where it came ' +
          'from, which is why a measured diffusivity carries a correlation factor. Read the flat ' +
          'stretches too: hopping is a Poisson process, so even at 1650 K a two-picosecond stretch ' +
          'with no hop at all is perfectly normal, and a run wants ten picoseconds before anything ' +
          'is read off it. The control makes both lines flat for good — no defect, nothing to track ' +
          'and nothing to count, D = 0.',
        HopMsdStatus: 'Interstitial has got {{a}} Å² from where it started · jump sum Σλ² = {{b}} Å² → D ≈ {{c}} Å²/ps (6·D·t)',
        HopChartY: 'displacement² (Å²)',
        HopMeasured: 'Where the interstitial has got to',
        HopJumpSum: 'Random walk from the jumps (Σλ²)',
        HopChartX: 'simulated time (ps)',
        HopArrTitle: 'Arrhenius plot — one point per {{a}} ps run, slope = −Eₐ/k',
        HopArrX: '1000 / T (K⁻¹)',
        HopArrY: 'ln(hops per ps)',
        HopArrPerPs: 'hops/ps',
        HopArrPoints: 'Completed runs',
        HopArrFit: 'Least-squares line',
        HopArrEmpty: 'Run the interstitial for {{a}} ps at a furnace setting: the run stops itself and lands here as a point. Two settings draw the line.',
        HopArrOne: 'One run so far — a second furnace setting draws the line',
        HopArrStatus: '{{n}} runs · the slope gives Eₐ ≈ {{ea}} eV · each point carries a Poisson counting error of ±1/√hops',
        HopArrSkipped: '{{n}} run(s) with no hops sit off the log axis',
        HopArrClear: 'Clear the points',
        HopArrNote: 'This is how an activation energy is measured, in a furnace and here alike. The hop rate goes as exp(−Eₐ/kT), so its logarithm against 1/T is a straight line and the slope is −Eₐ/k. Each {{a}} ps run of the interstitial is one measurement: run it at a few furnace settings and the points line up, with the scatter you would expect of a Poisson count — a run of six events knows its own rate to about 40%. Repeats at one setting show that scatter directly, which is why the line is fitted through all the points rather than drawn between two. The perfect crystal is not plotted: its rate is zero at every temperature, and zero has no logarithm.',
        SectionTunnel: 'Why the gate oxide has a floor — tunnelling',
        TunnelNote: 'THRM-04 grows this oxide, and the recipe above can make it as thin as you like. The transistor cannot use it. Below about two nanometres the electron in the channel is not stopped by the oxide, because an electron is a wave and a wave reaches through a wall it has not the energy to climb. This is the one-dimensional Schrödinger equation solved for a packet meeting a rectangular barrier the height of the silicon–SiO₂ band offset. The fraction that gets through falls by a fixed factor for every tenth of a nanometre added, so taking an oxide from 2 nm to 1.2 nm multiplies the leak by thousands — which is why the industry moved to a thicker film of hafnium oxide rather than a thinner one of silicon dioxide. One effective mass, 0.5 m₀, is used on both sides of the barrier and inside it; the decay inside the oxide is what the lesson rests on, and that is the mass it is right for. The silicon on either side is drawn as its lattice for orientation only: the electron in this model moves through it as a free particle of the effective mass, which is what a crystal does to an electron at these energies, so nothing is solved against those atoms. "Colour by phase" paints the wave by the phase of ψ: the stripes are its wavelength, moving with the packet and shortening where it moves faster; "Blue & green" is plain intensity, green past the wall.',
        TunnelChartTitle: 'Probability past the oxide',
        TunnelChartX: 'time (fs)',
        TunnelChartY: 'log₁₀ fraction',
        TunnelMeasured: 'Propagated packet',
        TunnelFormula: 'formula for this packet',
        TunnelFailed: 'The Schrödinger solver failed to load.',
        TunnelLoading: 'Loading the Schrödinger solver…',
        TunnelStatus: '{{a}} fs · past the oxide {{b}} ({{c}}) · reflected {{d}}%',
        TunnelOneIn: 'one electron in {{n}}',
        TunnelNone: 'below a part per billion',
        TunnelSum: 'Σ|ψ|² = {{s}} (reflected + inside + through, margins counted)',
        TunnelColourLabel: 'Phase colours',
        TunnelChartShort: 'Transfer matrix: {{a}} gets through · {{b}} decades per nm of oxide',
        TunnelChartHelp: 'What these two numbers are',
        TunnelChartStatus: 'Transfer matrix on this wall, averaged over the packet’s spread of energies: {{a}} (at the mean energy alone {{c}}) · {{b}} decades per nanometre of oxide at this energy',
        TunnelThickTitle: 'Transmission against oxide thickness',
        TunnelThickX: 'oxide thickness (nm)',
        TunnelRuns: 'Completed runs',
        TunnelLineAt: 'formula at {{e}} eV, {{v}} eV barrier',
        TunnelThickEmpty: 'Every completed run lands here as one point at its thickness. Change the thickness and run again until the exponential is drawn.',
        TunnelThickStatus: 'Points on the plot: {{n}} · the dashed line falls {{b}} decades per nanometre at the current energy and barrier',
        TunnelClear: 'Clear the points',
        TunnelThickNote: 'This is the experiment the chart above summarises one run at a time. Each completed run is one point at its thickness; run it at three or four thicknesses and the points fall on a straight line on this log axis — and a straight line on a log axis is an exponential. The slope is 2κ/ln 10 decades per nanometre, where κ is set by how far the electron’s energy sits below the barrier, so a lower barrier gives a shallower line: that is the whole case for high-k, made in one picture. Points made at a different energy or barrier stay on the plot and sit off the current line, which is the honest place for them.',
        TunnelReset: 'Reset',
        TunnelSio2: 'SiO₂ · 3.1 eV',
        TunnelHfo2: 'HfO₂ · 1.5 eV',
        TunnelWidth: 'Oxide thickness',
        TunnelEnergy: 'Electron energy',
        TunnelBarrier: 'Barrier height',
        TunnelPastLabel: 'past the oxide',
        BoxNote: 'The same oxide, seen from above and built from atoms. This is the Quantum Modeler’s two-dimensional box with the wall made of close-packed repulsive cores, 0.32 nm apart — about the spacing of neighbouring atoms in silica — in as many layers as the thickness allows: a 1.2 nm oxide is four. The packet spreads as it travels, piles up against the atoms in standing-wave fringes where the incoming and reflected waves cross, and a faint copy carries on beyond them, drawn magnified in green. The cores are sized so their average over a layer is the band offset, so the slab above is exactly their mean, and the dashed line is the slab formula for a wall this thick. The atoms transmit within tens of percent of that mean at every thickness the knob allows, which is checked; the gap between the curve and the line is the atoms, not an error. "Oxide as a slab" swaps the atoms for their mean. The thin lines are contours of |ψ|² at 2, 9, 20, 36, 56 and 81 percent of the packet’s starting peak, on the magnified scale past the wall.',
        BoxChartTitle: 'Probability past the wall',
        BoxChartStatus: 'Transfer matrix on this wall, averaged over the packet’s spread of energies: {{a}} · {{b}} decades per nanometre of wall at this energy',
        BoxPastLabel: 'past the wall',
        BoxStatus: '{{a}} fs · past the wall {{b}} ({{c}}) · reflected {{d}}%',
        BoxLayers: '{{n}} layers',
        TunnelTab1d: 'Line (1D)',
        TunnelTab2d: 'From above (2D)',
        TunnelTab3d: 'Volume (3D)',
        TunnelTabLeak: 'Gate leakage',
        LeakNote: 'What the oxide actually leaks. The views above answer how much of one electron gets through; a transistor is asked a different question, in amps per square centimetre against a spec, and this answers that one. Transmission here is solved by transfer matrix rather than by propagating a packet, which is exact for this profile, takes microseconds, and is an independent check on the packets — the harness holds all three together. It is then integrated over the electrons that actually arrive, the Tsu–Esaki supply function, which is zero at zero bias: a flat barrier carries no current however thin it is, so this view is also where the barrier tilts. Push the bias past the band offset and the far corner of the oxide drops below the electron, the barrier becomes a triangle, and direct tunnelling gives way to Fowler–Nordheim. Nothing in the model was told about that crossover; the transfer matrix only ever sees the profile. Two masses are in play and they differ: 0.5 m₀ for the decay inside the oxide, 0.19 m₀ for counting states in the silicon. Left out, and worth knowing before quoting a number: image-force lowering, inversion-layer subbands, any trap-assisted path, and temperature, which is fixed at 300 K. The result is a direct-tunnelling estimate good to about an order of magnitude against measured oxides, computed on the slab that the atoms above average to.',
        LeakStatus: '{{j}} A/cm² at {{v}} V · field {{f}} MV/cm · {{r}}',
        LeakDirect: 'direct tunnelling',
        LeakFowler: 'Fowler–Nordheim',
        LeakBreakdown: ' · past breakdown for a real oxide',
        LeakOver: '{{n}}× over the 1 A/cm² spec',
        LeakUnder: '{{n}}× under the 1 A/cm² spec',
        LeakNone: 'No bias, so no current: as many electrons come back from the gate as go to it.',
        LeakJvTitle: 'Gate current against bias',
        LeakJvX: 'gate bias (V)',
        LeakJvY: 'log₁₀ A/cm²',
        LeakSeries: '{{w}} nm oxide',
        LeakBarrierSeries: '{{v}} eV barrier',
        LeakSpec: 'spec, 1 A/cm²',
        LeakHere: 'set here',
        LeakThickTitle: 'Gate current against oxide thickness, at {{v}} V',
        LeakJvCaption: 'The curve climbs {{d}} decades per volt where the bias is set now.',
        LeakThickCaption: 'The curve falls {{d}} decades per nanometre of oxide at this bias. Every tenth of a nanometre of scaling costs a fixed factor of leakage, and that is what ended silicon dioxide.',
        LeakBias: 'Gate bias',
        LeakCathode: 'silicon channel',
        LeakOxide: 'oxide, {{w}} nm',
        LeakAnode: 'gate',
        LeakSpectrum: 'where the current comes from',
        LeakThisWall: 'this wall',
        LeakShort: 'Leaks {{j}} A/cm² at {{v}} V',
        LeakRunShort: 'the run confirms {{p}}% of it',
        LeakHelp: 'How this current is computed',
        LeakBeside: 'This wall at {{v}} V leaks {{j}} A/cm². Solved by transfer matrix on the same oxide rather than measured from the packet on the left — a current needs the transmission at every energy the channel supplies, which is hundreds of runs — and the two agree to a few percent.',
        LeakRunCheck: 'The run on the left has measured {{a}} through this wall so far, against the {{b}} the current above is built on: {{p}}% of the way there. The current is a property of the wall, not of the run — what the run does is confirm it.',
        VolumeNote: 'And in three dimensions. This is the Quantum Explorer’s 3D model — the same Cayley propagator split over x, y and z on a 208 × 48 × 48 grid, the same close-packed cores now stacked as real layers of spheres, the same absorbing margins and the same accounting — solved on a worker thread, a few seconds a run, and drawn as a volume: blue where the packet is, green on its own labelled scale past the wall. Drag to orbit, right-drag to pan, wheel to zoom. The transmitted fraction is the one the 2D box and the 1D line measure, and the slab formula is the same dashed line; it is checked against them.',
        VolumeIdle: 'Press Run: the packet is propagated on a worker thread, a few seconds a run.',
        VolumeRunning: 'solving on a worker, a few seconds a run',
        VolumeStop: 'Stop',
        VolumeUnavailable: 'The 3D solver needs a worker thread and could not start one here.',
        BoxAtoms: 'Oxide as atoms',
        BoxSlab: 'Oxide as a slab',
        BoxWallAtoms: '{{n}} atomic layers · {{w}} nm · {{v}} eV mean',
        BoxFormulaSlab: 'slab of the same mean height',
        HopChartTitle: 'How far the interstitial got, against the walk its jumps imply',
        BudgetCaveat: 'The experiment is scripted into the scene itself, which is why this view ' +
          'has no knobs and stops on its own at the end of the flow: both wafers have ' +
          'to get exactly the same predep for the comparison to mean anything, and a ' +
          'knob that moved one of them would destroy that. Reset runs the whole flow ' +
          'again. The two curves beside the picture are the budget itself — each ' +
          'wafer’s ∫D dt, accumulated live by the scene’s own script rather than ' +
          'computed by this panel. Watch how much LESS they separate than the dopant ' +
          'does: about a third more budget on the left wafer produces three times the ' +
          'concentration at the probe depth, because junction depth goes as √(Dt). A ' +
          'modest change in budget is not a modest change in device.',
        HopControlNote: 'Now switch to the perfect crystal and run it as hot as the slider goes. ' +
          'Nothing moves. Not slowly — at all: a defect-free lattice logs exactly ' +
          'zero hops even with the furnace set within forty degrees of melting, while ' +
          'the same cell with ONE interstitial in it hops every two or three ' +
          'picoseconds. That is the whole reason implant damage and the anneal that ' +
          'heals it belong to the same subject as doping: dopant moves on defects, ' +
          'and the beam next door has just made a great many of them. Do not read the ' +
          'still crystal as a claim that an undamaged wafer never diffuses. A real ' +
          'lattice makes its own defects at temperature — vacancies and interstitials ' +
          'appear and recombine in thermal equilibrium, at a concentration set by ' +
          'their formation energy — and a furnace at 1100 °C has plenty. What this ' +
          'cell cannot do is wait for one: 216 atoms over picoseconds is far too ' +
          'small and far too short a look for an event that rare. So the control ' +
          'shows that the defect is the VEHICLE, not that a hot crystal has none.',
        BudgetABNote: 'Two wafers, same implant, same predep. At the end of the predep the flow ' +
          'splits: the left one goes on through the rest of it — 1000 °C, then 900 °C ' +
          '— and the right one is quenched to room temperature and never heated ' +
          'again. The quenched wafer is the control; it shows what the junction WAS ' +
          'when you finished making it. Watch the left one keep going. By the end of ' +
          'the flow its dopant has reached several times further, entirely from steps ' +
          'that had nothing to do with forming it. That is what the budget tile above ' +
          'is counting, and why a junction is designed against the whole flow rather ' +
          'than against its own anneal.',
        SectionBudgetAB: 'Thermal budget — what later steps do to a finished junction',
        HopReset: 'Reset the count',
        HopPause: 'Pause',
        HopRun: 'Run',
        HopTemp: 'Furnace temperature',
        HopPerfect: 'Perfect crystal (control)',
        HopWithDefect: 'One interstitial',
        HopLoading: 'Loading the molecular-dynamics engine…',
        HopFailed: 'The molecular-dynamics engine failed to load.',
        HopStatus: '{{a}} hops · {{b}}/ps · measured {{c}} K · {{d}} ps',
        HopCaveat: 'What this deliberately does NOT show is a boron atom hopping. Boron’s ' +
          'activation energy is 3.46 eV — the same number the drive-in’s Arrhenius ' +
          'uses — which works out to roughly two hops per second at 1100 °C. This ' +
          'view reaches picoseconds, so it is short by about eight orders of ' +
          'magnitude, and no faster computer closes that gap. The interstitial is ' +
          'watchable only because its barrier is far lower. That gap is exactly why ' +
          'wafer-scale diffusion is a fitted coefficient inside a PDE and not a ' +
          'simulation of atoms — the elementary event is real, and far too rare to ' +
          'watch.',
        HopNote: 'The scene above moves dopant with a diffusivity that follows temperature ' +
          'by Arrhenius. This is what that number is made of: a single silicon atom ' +
          'sitting between lattice sites instead of on one, shouldering its ' +
          'neighbours aside and taking their places. Press Run and watch the count ' +
          'climb — each hop is one term in D. Then push the furnace up and watch the ' +
          'rate climb with it, which is the Arrhenius factor happening rather than ' +
          'being asserted.',
        SectionHop: 'What D(T) is made of — one atom changing places',
        BudgetFlowEnded: 'end of flow',
        BudgetQuenched: 'quenched',
        BudgetFullFlow: 'full flow',
        BudgetPredep: 'shared predep — both wafers identical',
        NoteTheSolverPutsThe: 'The solver puts the junction {{a}}× deeper than the formula. The reason is ' +
          'one line: D depends on how much dopant is already there, so a heavy ' +
          'implant digs its own way down, and this profile spans a {{b}}× range of ' +
          'diffusivity that a single Arrhenius number cannot represent.',
        NoteOnThisRecipeTheTwo: 'On this recipe the two agree to {{a}}%. That is the honest result for a ' +
          'short cycle: little diffuses, the profile is still the one the implanter ' +
          'left, and the formula is a perfectly good way to get it. Lengthen the ' +
          'anneal or add a step below and watch them separate.',
        ValueAllOfIt: 'all of it',
        ValueSolubilityCeiling: '{{a}} % (solubility ceiling)',
        MetricElectricallyActive: 'Electrically active',
        Value100ByConstruction: '100 % by construction',
        MetricDoseLeftInThe: 'Dose left in the wafer',
        ValueNoInterface: 'no interface',
        ValueNmInterfaceMoving: '{{a}} nm, interface moving',
        MetricSiliconConsumed: 'Silicon consumed',
        ValueOneNumberEverywhere: 'one number, everywhere',
        ValueSpreadNodeToNode: '{{a}}× spread, node to node',
        MetricDiffusivityAcrossThe: 'Diffusivity across the profile',
        MetricSurfaceConcentration: 'Surface concentration',
        MetricJunctionDepth: 'Junction depth',
        LegendTheFittedGaussian: 'the fitted Gaussian',
        LegendTheEquationSolvedOn: 'the equation, solved on {{a}} time steps',
        LabelAnneal: 'anneal',
        LabelOxidation: 'oxidation',
        SecondaryLabel: 'Sheet resistance error',
        ToolLabel: 'THRM-04 · Vertical furnace and RTP',
        LabelTryTheSpikeAnneal1100: 'Try the spike anneal (1100 °C, 2 s)',
        NextASpikeAnnealSeparatesActivation: 'A spike anneal separates activation from diffusion by being hot and brief. ' +
          'What limits how brief it can be - and what happens to the wafer if you ' +
          'ramp it that fast?',
        AxisYSheetResistanceSq: 'sheet resistance (Ω/sq)',
        AxisXJunctionDepthNm: 'junction depth (nm)',
        TitleJunctionDepthAgainstSheetResistance: 'Junction depth against sheet resistance — the trade, plotted',
        NoteColdAndShortActivatesNothing: 'cold and short activates nothing',
        LabelShortAnnealActivation: 'Short-anneal activation',
        LabelSpikeAnnealActivation: 'Spike anneal activation',
        LabelItsSheetResistance: 'Its sheet resistance',
        LabelShallowestRoute: 'Shallowest route',
        HypJunctionDepthIsSetBy: 'Junction depth is set by where the implant started plus how far heat has ' +
          'moved it, and sheet resistance by how much active dopant is in that depth. ' +
          'Anything that makes the junction shallower puts the same dopant in less ' +
          'silicon, so resistance should rise - unless the change also improves ' +
          'activation.',
        AskedHowDoIMakeThe: 'How do I make the junction shallower, and what does each route cost?',
        SumTheJunctionDepthAndResistance: 'The junction-depth and resistance trade, and why it has no free side.',
        NextAGateOxideHasTo: 'A gate oxide has to be a few nanometres thick and reproducible to a ' +
          'fraction of that. Given what you just saw, would you grow it hot and ' +
          'short, or cool and long - and which gives you more control over the final ' +
          'number?',
        TitleOxideThicknessAgainstTimeDry: 'Oxide thickness against time, dry and wet',
        NoteSqrt42WouldBe: 'sqrt(4) = 2 would be linear-free',
        LabelGrowthRatio: 'Growth ratio',
        LabelWet120Min: 'Wet, 120 min',
        LabelAt120Min: 'At 120 min',
        LabelAt5Min: 'At 5 min',
        AskedWhyDoesOxideGrowthSlow: 'Why does oxide growth slow down the thicker the oxide gets?',
        SumTheLinearAndParabolicRegimes: 'The linear and parabolic regimes, and why they need different recipes.',
        LateralUnderstood: 'Whether a drive-in moves dopant only downward, or sideways under the mask as well.',
        LateralHypothesis: 'Diffusion has no preferred direction. The mask decides where dopant ENTERS the silicon, not where it ends up, so once it is in, it should spread along the surface as readily as into the bulk - and the sideways reach should track the depth rather than lag further behind it as the anneal goes on.',
        NextLateralBudget: 'The sideways reach is set by the same D·t the depth is, so the only way to narrow it is to spend less budget. Ask me what every downstream hot step costs this junction - that experiment runs in 1D, and now you know to double its answer sideways.',
        NextIfADownstreamStepIs: 'If a downstream step is fixed and you cannot remove it, the only lever ' +
          'left is the implant. Ask me what happens to sheet resistance if you make ' +
          'the junction shallow enough to survive that step.',
        LabelAfter1050C: 'after 1050 °C',
        LabelJunctionDepth: 'junction depth',
        AxisYJunctionDepthNm: 'junction depth (nm)',
        AxisXStep0None: 'step (0 = none)',
        TitleJunctionDepthAfterEachAdded: 'Junction depth after each added downstream step',
        NoteEquivalentAt1000C: 'equivalent at 1000 °C',
        LabelBudgetWorstCase: 'Budget, worst case',
        NoteAlmostFree: 'almost free',
        LabelAfterA450CStep: 'After a 450 °C step',
        LabelAfterA1050CStep: 'After a 1050 °C step',
        LabelJunctionAlone: 'Junction, alone',
        HypDiffusionDependsOnlyOnThe: 'Diffusion depends only on the product of diffusivity and time, summed over ' +
          'every step the wafer has seen. If that is true, a step added downstream - ' +
          'one with nothing to do with this junction - will still move it, and a ' +
          'hotter step will move it far more than a longer one, because diffusivity ' +
          'is exponential in temperature and only linear in time.',
        AskedTheImplantDidNotChange: 'The implant did not change, but the junction did. Where did the heat come ' +
          'from?',
        SumShowThatEveryHotStep: 'Show that every hot step spends from one shared account.',
        fault: {
          wetHouseHeader: {
            Evidence: [
              'Oxide is thick with the junction untouched - the moisture signature, not a ' +
                'hot tube.',
              'The dew point on the tool line is high AND a new trap did not fix it. ' +
                'Together those two facts point upstream and nowhere else.',
              'The house utility check finds the header wet.',
              'The ALD tool is complaining at the same time. Its symptom sounds nothing ' +
                'like this one, because water does something completely different there - ' +
                'but it is the same water.',
            ],
            Truth: 'The house dry-gas dryer failed wet over the weekend and the header is ' +
              'carrying about 2% water - carryover, not the ppm-level creep a saturated ' +
              'dryer gives. It reaches every tool that breathes house nitrogen or oxygen, ' +
              'and what it does when it gets there depends entirely on what that tool was ' +
              'using the gas for. Note how far apart the two thresholds are: ALD is ' +
              'ruined by single-figure ppm, because water is its co-reactant, while a ' +
              'furnace growing oxide in steam does not notice anything below about a ' +
              'percent. It takes a gross failure for both tools to complain at once, and ' +
              'that is exactly why the two groups rarely connect their problems. At this ' +
              'tool it looks exactly like a saturated moisture trap, because it is the ' +
              'same water arriving by a different route - upstream of the trap instead of ' +
              'in it. That is why changing the trap achieved nothing, and why the dew ' +
              'point on the tool line still reads wet after it was changed.',
            Complaint: 'Gate oxide thick again, like the moisture trap last quarter. The trap was ' +
              'changed on Monday and it made no difference. DEP-02 says their growth has ' +
              'gone high too.',
            Label: 'Water in the house oxygen header',
          },
          lowDose: {
            Evidence: [
              'Sheet resistance is far high while the gate oxide, the junction and the ' +
                'thermal budget are all nominal. Nothing thermal is involved at all.',
              'Peak concentration is down by half, which no anneal error can do - an ' +
                'anneal spreads dopant, it does not remove it.',
              'The dose monitor wafer disagrees with the implanter log.',
            ],
            Truth: 'Beam current integration is off and the wafers received about half the ' +
              'dose the implanter logged. Half the dopant is half the carriers, so sheet ' +
              'resistance rises steeply. The thermal steps are all perfectly normal, ' +
              'which is why the junction barely moves - there is simply less dopant ' +
              'sitting in the same diffused profile.',
            Complaint: 'Sheet resistance has gone way up. Gate oxide is fine.',
            Label: 'Implanter under-dosing',
          },
          hotAnneal: {
            Evidence: [
              'Sheet resistance is low and the junction is deep, with the gate oxide ' +
                'exactly on target - so whatever happened, it happened after oxidation.',
              'Thermal budget is up by about 90%, which is where to look first when a ' +
                'junction moves.',
              'A low implanted dose would also move sheet resistance, but UPWARD, and ' +
                'would not move the junction. This one moves it down and deepens the ' +
                'junction.',
              'A calibration wafer reads about 50 C above what the pyrometer reports.',
            ],
            Truth: 'The RTP pyrometer has drifted, so the controller drives the lamps to about ' +
              '1100 C while reporting 1050 C. Diffusivity is Arrhenius, so fifty degrees ' +
              'nearly doubles the Dt spent in the anneal: the dopant spreads further, the ' +
              'junction deepens and sheet resistance falls.',
            Complaint: 'Sheet resistance is running low and the junction is deeper than the model ' +
              'says.',
            Label: 'RTP pyrometer reading low',
          },
          wetLine: {
            Evidence: [
              'The oxide is thick and the junction has NOT moved. That is decisive - a ' +
                'hot tube could not leave the junction alone.',
              'Sheet resistance is exactly nominal, so nothing has changed about the ' +
                'thermal budget.',
              'A dew point check on the O2 line reads far above its limit.',
            ],
            Truth: 'The moisture trap on the dry O2 line is saturated and about 5% of the ' +
              'oxidant reaching the tube is water. Wet oxidation is an order of magnitude ' +
              'faster than dry, so even a few percent thickens the oxide badly. Nothing ' +
              'else in the process notices: water does not diffuse dopant, so the ' +
              'junction and the sheet resistance are untouched.',
            Complaint: 'Gate oxide thick again, same as the furnace problem last quarter.',
            Label: 'Moisture in the dry oxygen line',
          },
          hotFurnace: {
            Evidence: [
              'The oxide is thick AND the junction has moved deeper. Only temperature ' +
                'does both, because only temperature is spent on every step.',
              'Moisture in the gas line would thicken exactly the same oxide and leave ' +
                'the junction exactly where it was. That comparison is the diagnosis.',
              'A profile wafer reads about 30 C above setpoint.',
            ],
            Truth: 'The zone thermocouples have drifted and the tube is really at about 930 C. ' +
              'Oxide growth is Arrhenius, so 30 C buys 40% more oxide - but the wafer is ' +
              'also sitting 30 C hot for twenty minutes, and that time is spent out of ' +
              'the same thermal budget the anneal draws on. The junction moves too.',
            Complaint: 'Gate oxide came out about 40% thick. Recipe untouched, setpoint reads 900 C.',
            Label: 'Oxidation furnace running hot',
          },
          healthy: {
            Evidence: [
              'Gate oxide and sheet resistance are both at their qualified values.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Monitor wafers from this morning are on target. Nothing reported.',
            Label: 'Furnace qualified',
          },
        },
        NoteXA: 'x/A = {{a}}',
        SectionSolvedBesideTheFormulaIt: 'Solved, beside the formula it replaces',
        NoteEverythingAboveWidensAGaussian: 'Everything above widens a Gaussian by the square root of two D t, with D ' +
          'read off an Arrhenius line at the anneal temperature. Below, the diffusion ' +
          'equation is SOLVED on a grid instead — a few hundred nodes stepped through ' +
          'the thermal cycle, with D evaluated from the local concentration at every ' +
          'node and the oxide interface moving into the wafer as it grows. Where the ' +
          'anneal is short the two land on top of each other. Where it is long they ' +
          'do not, and the gap is the argument for building solvers.',
        AxisYLogCm: 'log₁₀ nm⁻³',
        reading: {
          implantlog: {
            Label: 'Dose monitor wafer against the implanter log (6 h)',
          },
          pyrometer: {
            Label: 'RTP pyrometer against a calibration wafer (3 h)',
          },
          houseutilities: {
            Limit: 'header tight, PCW at supply, dry gas < 50 ppm',
            Label: 'House utility check with facilities (2 h)',
          },
          moisture: {
            Label: 'Dew point on the dry O2 line (2 h)',
          },
          profilewafer: {
            Label: 'Furnace profile wafer, all zones (4 h)',
          },
          setpoints: {
            Limit: 'as recipe',
            Label: 'Furnace / anneal setpoints',
          },
          budget: {
            Label: 'Thermal budget spent',
          },
          junction: {
            Label: 'Junction depth (SIMS)',
          },
          rs: {
            Label: 'Sheet resistance (four-point probe)',
          },
          oxide: {
            Label: 'Gate oxide (ellipsometer)',
          },
        },
        aspect: {
          house: {
            Action: 'Stop work on the tool and raise it with facilities',
            Label: 'Nothing on this tool — escalate to facilities',
          },
          implanter: {
            Action: 'Re-calibrate beam current integration, re-run the dose monitor',
            Label: 'Implanter dose control',
          },
          rtp: {
            Action: 'Calibrate the pyrometer against an instrumented wafer',
            Label: 'RTP pyrometer',
          },
          gasline: {
            Action: 'Change the moisture trap, purge and re-certify the dry O2 line',
            Label: 'Oxidation gas line',
          },
          furnace: {
            Action: 'Run a profile wafer and re-calibrate the zone thermocouples',
            Label: 'Oxidation furnace temperature',
          },
        },
        SectionLot: 'The lot in the furnace',
        LotNote:
          'The panel above anneals whatever the knobs describe. The furnace below ' +
          'anneals THE WAFER — the implant it actually received at IMP-10, on top of ' +
          'every thermal step it has already been through. Run the same cycle twice ' +
          'and the junction keeps moving, because the wafer remembers and the knobs ' +
          'do not.',
        LotEmpty: 'No lot at the tube. Send a wafer here from an upstream tool to anneal it.',
        LotIngot:
          'This lot is still an ingot. WPR-08 has to slice it before a furnace can ' +
          'touch it.',
        LotCard: 'Travel card',
        LotImplant: 'Carrying an implant: ',
        LotNoImplant: 'No implant recorded on this wafer — the recipe knobs stand in.',
        LotPrior: 'thermal step(s) already on the books — this cycle adds to them',
        LotFresh: 'No thermal history yet. This is its first cycle.',
        LotRun: 'Run this cycle on the lot and pass it on',
        SectionResult: 'Result',
        SectionProfile: 'Dopant profile',
        SectionDriveIn: 'Drive-in — live 2D diffusion',
        SumShowThatADriveIn: 'Show that a drive-in widens every feature as much as it deepens it.',
        LabelJunctionDepth2D: 'Junction depth',
        LabelUnderTheMask: 'Under the mask',
        NoteEachSide: 'each side',
        LabelLateralOverDepth: 'Lateral / depth',
        NoteRoughlyConstant: 'roughly constant in time',
        LabelDopedWidth: 'Doped width',
        NoteFromAWindow: 'from a {{nm}} nm window',
        TitleDepthAndLateralReach: 'Depth and sideways reach, through one drive-in',
        AxisXAnnealMinutes: 'anneal time (min)',
        AxisYNm: 'nm',
        LabelDownDepth: 'down (junction depth)',
        LabelSidewaysUnderMask: 'sideways (under the mask)',
        TitleLateralAsAFraction: 'Sideways reach as a fraction of depth',
        AxisYFraction: 'lateral / depth',
        LabelRatio: 'ratio',
        DriveInNote:
          'A live simulation of boron diffusing through a 0.6 µm window in an SiO₂ ' +
          'mask, at true scale (the frame is 2 µm across). Boron’s diffusivity follows the ' +
          'anneal temperature by Arrhenius — a hundred degrees is an order of magnitude in ' +
          'D — so try the same clock at 1000 °C and 1100 °C. And watch the mask edge: the ' +
          'junction spreads sideways under it at roughly 0.8× its depth, which no 1D ' +
          'profile can show.',
        DiLoading: 'Loading the diffusion engine…',
        DiFailed: 'The diffusion engine failed to load.',
        DiPause: 'Pause',
        DiRun: 'Run',
        DiReset: 'Reset',
        DiAnnealLabel: 'Anneal',
        DiAnneal: 'anneal {{c}} °C',
        DiJunction: 'junction {{um}} µm',
        DiLateral: 'under mask {{um}} µm',
        DiClock: 't = {{min}} min',
        DiOpenInEnergy2D: 'Open this scene in Energy2D ↗',
        DiOpenInEnergy2DTitle: 'Opens this scene in the Energy2D editor, in a new tab, where its geometry and materials can be changed and its AI assistant can rebuild it.',
        DiGraphTitle: 'Concentration at the probes',
        DiGraphX: 'anneal time (min)',
        ProfileNote:
          'Where the dopant ended up. The implant put it at one depth; every hot step ' +
          'since has spread it. The junction is where the profile falls to the ' +
          'substrate doping.',
        SectionSteps: 'Downstream thermal steps',
        StepsNote:
          'Steps that come AFTER this junction is formed, and have nothing to do with ' +
          'it. Add one and watch the junction move anyway. Diffusion only sees the ' +
          'total D·t, and the silicon does not know which step was whose.',
        SectionOxide: 'Oxide growth — linear, then parabolic',
        OxideNote:
          'A thin oxide is limited by the reaction at the silicon interface and grows ' +
          'in step with time. A thick one is limited by oxygen diffusing through the ' +
          'oxide already there, so growth slows to the square root of time. Same ' +
          'equation, both regimes.',
        SectionBudget: 'Where the budget goes',
        BudgetNote:
          'Each step converted to the time at 1000 °C that would have done the same ' +
          'diffusion. Comparable across steps, unlike a number in cm².',
        SectionAmbient: 'Ambient',
        AmbientDry: 'Dry O₂',
        AmbientWet: 'Wet (steam)',
        AmbientDryShort: 'dry',
        AmbientWetShort: 'wet',
        AmbientNote: 'Steam supplies far more oxidant, so wet oxidation runs several times faster.',
        SectionDopant: 'Dopant',
        DopantBoron: 'Boron',
        DopantPhosphorus: 'Phosphorus',
        DopantArsenic: 'Arsenic',
        DopantNote:
          'A heavier atom diffuses more slowly for the same heat, which is why ' +
          'arsenic is used where a junction has to stay shallow and boron where it ' +
          'does not.',
        SectionRecipe: 'Recipe',
        SectionStored: 'Stored recipes',
        SectionShift: 'Shift',
        NoSteps:
          'No downstream steps yet. The junction below is what the implant and its ' +
          'own anneal produced.',
        Remove: 'remove',
        StepContact: 'Contact anneal, 450 °C, 30 min',
        StepInterlayer: 'Interlayer oxide, 700 °C, 60 min',
        StepReflow: 'Reflow, 900 °C, 20 min',
        StepSecondGate: 'Second gate oxide, 1050 °C, 30 min',
        StepOxidation: 'oxidation',
        StepAnneal: 'anneal',
        TileOxide: 'Oxide thickness',
        TileRegime: 'Growth regime',
        RegimeLinear: 'linear',
        RegimeMixed: 'mixed',
        RegimeParabolic: 'parabolic',
        TileJunction: 'Junction depth',
        NoteBefore: '{{nm}} nm before downstream steps',
        NoteNoSteps: 'no downstream steps',
        TileSheetR: 'Sheet resistance',
        TileActive: 'Active fraction',
        NoteLattice: 'dopant on lattice sites',
        TilePeak: 'Peak concentration',
        TileBudget: 'Thermal budget',
        NoteEquivalent: 'equivalent seconds at 1000 °C',
        TileDt: 'Total D·t',
        TileDownstream: 'Downstream steps',
        ChartProfileTitle: 'Dopant concentration against depth (log scale)',
        ChartDepth: 'depth (nm)',
        SeriesBefore: 'before downstream steps',
        SeriesAsProcessed: 'as processed',
        AnnSubstrate: 'substrate doping',
        AnnJunction: 'junction',
        ChartOxideTitle: 'Oxide thickness against time at {{temp}} °C ({{ambient}})',
        ChartTime: 'time (min)',
        ChartThickness: 'thickness (nm)',
        SeriesOxide: 'oxide',
        AnnYourTime: 'your time',
        ChartBudgetTitle: 'Step duration — but temperature is what really spends the budget',
        ChartStepIndex: 'step index',
        ChartSeconds: 'seconds',
        SeriesDuration: 'duration',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'Every hot step in the flow spends from one account. Ask me what a step ' +
          'costs, or why a junction moved when nobody touched the implant.',
      },
      gowning: {
        AirflowWafer: 'at the wafer',
        AirflowWall: 'wall monitor',
        AirflowRatio: 'product/wall',
        SectionAirflow: 'The two rooms, seen (airflow and dispersion)',
        AirflowNote:
          'A slice of the bay: filters across the ceiling pushing air down at the design velocity, a ' +
          'gowned operator standing in it, open product on the tool beside them, and the raised floor ' +
          'taking the air away. The colour is what the operator is shedding. Watch where it goes. There ' +
          'is no thermal plume here, and there should not be — a warm body can raise air at about a ' +
          'quarter of a metre per second, and the filters run at nearly twice that. Being fast enough ' +
          'to overwhelm the people working under it is exactly what 0.45 m/s is for. What the operator ' +
          'does instead is stand in the way: the supply has to get around a body, and the wake of that ' +
          'obstacle is what carries shedding to the product. If you see the cloud climb above the head, ' +
          'that is the wake turning over, not heat rising: the flow directly above a body in a downflow ' +
          'reverses every so often as vortices shed off it — measured here at 0.28 m/s upward one ' +
          'moment and 0.19 m/s downward the next. Nothing in this bay is warmer than anything else, so ' +
          'there is nothing to lift it. Now read the two probes together. The wall monitor reads almost ' +
          'nothing while the product beside the operator is being contaminated — that is the module’s ' +
          'whole objection to it. And turning the downflow UP makes the product read more, not less, ' +
          'because the supply is what delivers the shedding downward and the tool deflects it sideways ' +
          'across the open wafer. The remedy is standing somewhere else, not more air.',
        SecondaryLabel: 'Recovery after an upset',
        ToolLabel: 'GOWN-00 Gowning Airlock and Protocol Bay',
        fault: {
          starvedMakeup: {
            Evidence: [
              'Bay pressure negative against a +5 Pa floor.',
              'Work-zone count out of class.',
              'Filters, garments, interlock and recovery time are all nominal — the air is ' +
                'turning over; it is the corridor’s air that is arriving.',
              'Every bay on the floor reads the same, and the utility panel at facilities ' +
                'shows make-up air at 55% of design with the room negative.',
            ],
            Truth:
              'The make-up air unit in the sub-fab has loaded pre-filters and is delivering ' +
              'about 55% of design outside air. This bay is turning its air over at full rate — ' +
              'the handlers, the ceiling filters and the garments are all fine — but make-up air ' +
              'is what the exhaust fans are balanced against, and with the balance gone the ' +
              'whole cleanroom has dropped below the corridor. Every bay reads negative together ' +
              'and breathes corridor air in through every gap. Chasing this bay’s own exhaust ' +
              'finds nothing, because nothing in this bay changed; the cascade is the ' +
              'building’s, and only the building can give it back.',
            Complaint:
              'Counts up in every bay at once, and every bay reads negative against the ' +
              'corridor. Nothing was commissioned, nobody touched an exhaust.',
            Label: 'Whole cleanroom gone negative — make-up air starved',
          },
          starvedAir: {
            Evidence: [
              'Recovery time far past the 0.85 minute limit.',
              'Filter bank pressure drop is comfortably inside its limit — the air is not ' +
                'being blocked, it is not arriving.',
              'The utility panel at facilities shows delivered air at 55% of design.',
            ],
            Truth:
              'Two of the four recirculation air handlers have slipping belts and the bay is ' +
              'getting about 55% of design airflow. Nothing in this room is broken — the ' +
              'filters are mid-life, the pressure cascade holds, the garments are fresh. The ' +
              'number that gives it away is RECOVERY: how long the bay takes to swallow an ' +
              'upset is set by air changes and nothing else, so a slow recovery with a clean ' +
              'filter bank is the building, not the room. Changing the ceiling filters costs ' +
              'twelve hours and recovers almost nothing.',
            Complaint:
              'The bay reads dirtier at the same headcount and takes visibly longer to come ' +
              'back after a door event. The ceiling filters were changed last month.',
            Label: 'Bay starved of air by the house handlers',
          },
          defeatedInterlock: {
            Evidence: [
              'Interlock reads DEFEATED.',
              'Work-zone count out of class.',
              'Every other reading is nominal, and the counts are episodic rather than ' +
                'sustained.',
            ],
            Truth: 'Somebody has taped the airlock interlock so both doors can be open at ' +
              'once, because the lock was slowing down a tool install. Every time they do ' +
              'it the bay is briefly connected to the corridor. The bursts ARE the ' +
              'signature: a continuous fault does not come and go, and no amount of ' +
              'ventilation fixes a door that is being held open.',
            Complaint: 'Counts spike in bursts through the day rather than sitting high. ' +
              'Everything measurable is in specification between the spikes.',
            Label: 'Airlock interlock defeated',
          },
          wornGarments: {
            Evidence: [
              'Garment cycles past the 30 limit.',
              'Work-zone count out of class.',
              'Filter pressure drop, bay pressure and recovery time are all nominal.',
            ],
            Truth: 'This bay is drawing from a garment lot on its 51st laundering cycle. ' +
              'Fabric opens up as it is washed, and a garment that has opened up stops ' +
              'being a filter — it passes several times what a fresh one does. Everything ' +
              'mechanical about the bay is fine.',
            Complaint: 'Counts up in one bay only, and the ventilation checks out completely.',
            Label: 'Garment lot past its laundering cycles',
          },
          negativePressure: {
            Evidence: [
              'Bay pressure at −14 Pa against a +5 Pa floor.',
              'Work-zone count out of class.',
              'Filters, garments and interlock are all nominal — this is not a bay ' +
                'problem, it is a building problem.',
            ],
            Truth: 'The new exhaust has pulled the bay to −14 Pa, so it is drawing corridor ' +
              'air in through every gap and cable penetration instead of pushing clean ' +
              'air out. The bay is no longer the cleanest room on its own boundary, which ' +
              'is the entire principle a cleanroom is built on.',
            Complaint: 'Particle counts up across the whole bay since a new tool exhaust was ' +
              'commissioned next door.',
            Label: 'Bay gone to negative pressure',
          },
          loadedFilters: {
            Evidence: [
              'Filter pressure drop past the 220 Pa limit.',
              'Recovery time well over the 0.85 min limit.',
              'Pressurisation, garments and the interlock are all fine.',
            ],
            Truth: 'The filter banks are loaded and the fans cannot push the air they used to, ' +
              'so the bay is running at well under its design air changes. The STEADY ' +
              'state barely moves — generation over ventilation is not very sensitive ' +
              'when generation is low — and the RECOVERY time moves a great deal, because ' +
              'that is set by ventilation alone. A bay that recovers slowly is a bay that ' +
              'is dirty for longer after every single upset.',
            Complaint: 'The bay takes an age to settle after a tool is opened. The wall monitor is ' +
              'still in class and nobody can point at anything wrong.',
            Label: 'Filter banks loaded',
          },
          gownHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'The work-zone count is inside the class limit.',
            ],
            Truth: 'Nothing is wrong. The bay is holding class at the product as well as at ' +
              'the wall.',
            Complaint: 'Routine walkthrough. The wall monitor is reading in class.',
            Label: 'Nothing wrong',
          },
        },
        garment: {
          mask: 'Mask',
          gloves: 'Gloves',
          boots: 'Boots',
          hood: 'Hood',
          coverall: 'Coverall',
        },
        SectionGowning: 'Gowning',
        NoteOverMinOpenCln09: 'over {{a}} min open — CLN-09 has to remove these',
        NoteEffectiveAirChangesH: '{{a}} effective air changes/h',
        NotePeopleAtActivity: '{{a}} people at {{b}}% activity',
        NoteOf5Garments: '{{a}} of 5 garments{{b}}',
        NoteIsoAtMLimit: 'ISO {{a}} at {{b}} µm — limit {{c}}',
        SectionAChainDoesNotAverage: 'A chain does not average',
        NoteTheGarmentsMultiplyLeavingThe: 'The garments multiply. Leaving the hood off does not cost a fifth of the ' +
          'protection, it costs the entire factor the hood was contributing — and the ' +
          'room monitor on the wall will barely notice, because what the WAFER sees ' +
          'is dominated by the nearest person, not by the bay average. You cannot ' +
          'ventilate your way out of a gowning failure.',
        SectionStoredScenarios: 'Stored scenarios',
        SectionBayState: 'Bay state',
        SectionBayAndProtocol: 'Bay and protocol',
        TextExposeTheLotToThis: 'Expose the lot to this bay',
        LabelCorrectOrder: 'Correct order',
        LabelOutOfOrder: 'Out of order',
        LabelAtTheWafer: 'At the wafer',
        LabelRoomMonitor: 'Room monitor',
        NoteWhatTheWallReadsAnd: 'what the wall reads, and it is not what the product sees',
        LabelGarmentPenetration: 'Garment penetration',
        LabelShedIntoTheBay: 'Shed into the bay',
        LabelRecoveryAfterUpset: 'Recovery after upset',
        LabelOntoTheWafer: 'Onto the wafer',
        AssistantLead: 'GOWN-00 inquiry assistant',
        AssistantBlurb:
          'Ask why a bay that reads in class can still be contaminating wafers, or ' +
          'what one garment is worth in particles. Every answer is a run against this ' +
          'bay as it stands.',
        reading: {
          houseutilities: {
            Label: 'Call facilities for the utility panel (0.5 h)',
          },
          recovery: {
            Label: 'Smoke test and recovery timing, bay down (2.0 h)',
          },
          workZone: {
            Label: 'Portable counter at the work zone, one hour (1.0 h)',
          },
          interlock: {
            ValueBad: 'DEFEATED',
            ValueOk: 'functioning',
            Limit: 'functioning',
            Label: 'Airlock interlock',
          },
          garmentCycles: {
            Label: 'Garment lot laundering cycles',
          },
          pressure: {
            Label: 'Bay pressure against the corridor',
          },
          filterDp: {
            Label: 'Filter bank pressure drop',
          },
        },
        aspect: {
          house: {
            Action: 'Stop changing things in the bay and raise it with facilities',
            Label: 'Nothing in this room — escalate to facilities',
          },
          interlock: {
            Action: 'Repair the interlock, and find out who defeated it and why',
            Label: 'Airlock interlock',
          },
          garments: {
            Action: 'Pull the worn garment lot and re-issue from a fresh laundering cycle',
            Label: 'Garment stock',
          },
          pressurisation: {
            Action: 'Find what is exhausting the bay and restore the pressure cascade',
            Label: 'Bay pressurisation',
          },
          filters: {
            Action: 'Change the loaded filter banks and re-balance the ceiling',
            Label: 'Ceiling filters',
          },
        },
      },
      gas: {
        ValueNotNeeded: 'not needed',
        NoteTheAlarmArrivesAfter: 'the alarm arrives after the bay is already dangerous',
        NoteThePurgeAsSpecifiedLeaves: 'The purge as specified leaves {{a}} ppm — {{b}} cycles are needed at this ' +
          'ratio, not {{c}}. Do not open the panel.',
        NoteThePurgeAsSpecifiedReaches: 'The purge as specified reaches {{a}} ppm, which is below the limit. The ' +
          'panel can be opened.',
        VerdictImmediatelyDangerous: 'Immediately dangerous to life or health',
        VerdictAboveTheExposureLimit: 'Above the exposure limit',
        VerdictBelowTheExposureLimit: 'Below the exposure limit',
        NoteAlarmsAtPpmToRespond: 'alarms at {{a}} ppm, {{b}} s to respond',
        ValueNoWarning: 'none',
        NoteStaysBelowTheDangerous: 'stays below the dangerous level',
        NoteImmediatelyDangerousAt: 'immediately dangerous at {{a}} s',
        NoteEveryNumberOnThis: 'Every number on this panel is answerable on paper before a cylinder is ' +
          'connected: how many purge cycles a line needs, what a leak reaches at ' +
          'steady state, and how much time a detector actually buys. The purge is a ' +
          'geometric series, so cycle QUALITY beats cycle count — ten cycles at ' +
          'ten-to-one and ten at two-to-one take the same time and land seven orders ' +
          'of magnitude apart.',
        ValueNever: 'never',
        gasNote: {
          ammonia: 'The one on this list you would smell long before it hurt you — which ' +
            'builds bad habits.',
          chlorine: 'Heavier than air, so it pools at floor level where a ceiling detector will ' +
            'not see it.',
          hydrogenFluoride: 'The fluoride ion chases calcium into bone. The systemic effect arrives ' +
            'hours later.',
          phosphine: 'Pyrophoric and acutely toxic at once, so a leak is both a fire and a ' +
            'poisoning.',
          arsine: 'Five parts per billion. Destroys red blood cells; the damage is done ' +
            'before symptoms.',
          silane: 'Ignites in air, sometimes after a delay — which lets a leak accumulate ' +
            'first.',
        },
        gasLabel: {
          ammonia: 'Ammonia (NH₃)',
          chlorine: 'Chlorine (Cl₂)',
          hydrogenFluoride: 'Hydrogen fluoride (HF)',
          phosphine: 'Phosphine (PH₃)',
          arsine: 'Arsine (AsH₃)',
          silane: 'Silane (SiH₄)',
        },
        NotePyrophoricIgnites: 'Pyrophoric: this ignites in air without an ignition source.',
        SectionSpecies: 'Species',
        SecondaryLabel: 'Reaching the stack',
        ToolLabel: 'GAS-19 Gas Bunker and Chemical Distribution',
        NextFac06SolvesTheSame: 'FAC-06 solves the same conductance physics for vacuum rather than for ' +
          'delivery. The equation is identical and the consequence of getting it ' +
          'wrong is not - ask it what a restricted foreline costs a process chamber.',
        TitleLineConductanceAgainstDiameter: 'Line conductance against diameter',
        LabelPyrophoric: 'Pyrophoric',
        LabelConductance: 'Conductance',
        HypMolecularFlowConductanceGoesAs: 'Molecular-flow conductance goes as the cube of diameter and inversely with ' +
          'length, so line geometry should dominate delivery far more than intuition ' +
          'suggests - a modest diameter change should be a large conductance change. ' +
          'What reaches the stack should depend on the scrubber and not on the line ' +
          'at all.',
        AskedWhatDoesTheDeliveryLine: 'What does the delivery line do, and what reaches the stack?',
        SumLineConductanceScrubberLoadingAnd: 'Line conductance, scrubber loading, and what leaves the building.',
        NextTheBayVentilationIsWhat: 'The bay ventilation is what sets the steady state. If a fan is running at ' +
          'half speed and nobody has noticed, which of these numbers changes first - ' +
          'and would anything on the panel show it before a leak did?',
        LabelNoWarning: 'no warning',
        AxisYSecondsFromAlarmToIdlh: 'seconds from alarm to IDLH',
        AxisXLeakRateSccm: 'leak rate (sccm)',
        TitleWarningTimeAgainstLeakRate: 'Warning time against leak rate',
        LabelSeverity: 'Severity',
        LabelSteadyState: 'Steady state',
        LabelAlarmsAt: 'Alarms at',
        NoteAlarmToIdlh: 'alarm to IDLH',
        HypADetectorAlarmsAtA: 'A detector alarms at a concentration, and the bay keeps filling while it ' +
          'responds. The useful quantity is not the alarm level but the SECONDS ' +
          'between alarming and the atmosphere becoming immediately dangerous. If the ' +
          'leak is fast enough, that interval shrinks to nothing and the detector ' +
          'becomes a record of what happened rather than a warning.',
        AskedDoesTheDetectorGiveEnough: 'Does the detector give enough warning to be worth having?',
        SumAlarmThresholdResponseTimeAnd: 'Alarm threshold, response time, and the seconds between alarm and IDLH.',
        NextThePurgeNitrogenCarriesIts: 'The purge nitrogen carries its own moisture, and a wet purge makes solids ' +
          'in the line rather than cleaning it. What would that do to the curve above ' +
          '- and would the cycle count on the procedure still be the right one?',
        TitleResidualConcentrationAgainstPurgeCycles: 'Residual concentration against purge cycles',
        LabelPurgeTime: 'Purge time',
        LabelSetTo: 'Set to',
        LabelCyclesNeeded: 'Cycles needed',
        LabelResidual: 'Residual',
        HypEachPurgeCycleReplacesA: 'Each purge cycle replaces a fixed FRACTION of what is in the line, not a ' +
          'fixed amount. That makes residual concentration exponential in cycle count ' +
          '- so getting from 100% to 1% takes about as many cycles as getting from 1% ' +
          'to 0.01%, and intuition about "a few purges should do it" will be wrong by ' +
          'orders of magnitude.',
        AskedHowManyPurgeCyclesDoes: 'How many purge cycles does this line actually need?',
        SumCyclePurgingIsExponentialAnd: 'Cycle purging is exponential - and the last decade of concentration is the ' +
          'slow one.',
        fault: {
          weakExtract: {
            Evidence: [
              'Extract flow at 35% against a 90% floor.',
              'Modelled release steady state far higher than design.',
              'The purge, the valve and the scrubber are all nominal.',
            ],
            Truth: 'The extract fan is delivering 35% of design flow. Steady-state ' +
              'concentration is release rate over ventilation, so a third of the flow is ' +
              'three times the concentration for any leak the bunker has — and it also ' +
              'lengthens the time to clear one, which is the half nobody calculates.',
            Complaint: 'The bay smells faintly of solvent on warm days.',
            Label: 'Bay extract below design',
          },
          spentScrubber: {
            Evidence: [
              'Media loading at 95% against a 60% limit.',
              'Stack sample above the limit.',
              'The bay release model is completely unchanged.',
            ],
            Truth: 'The scrubber media is at 95% loaded. Abatement does not fail closed — it ' +
              'degrades, so the destruction efficiency falls and what is not destroyed ' +
              'goes up the stack. Every reading inside the bay is unchanged, because ' +
              'nothing inside the bay changed: this fault is entirely downstream of the ' +
              'people it is protecting, and only a stack sample sees it.',
            Complaint: 'Environmental has queried a stack reading. Everything inside the bay is ' +
              'normal and always has been.',
            Label: 'Abatement media spent',
          },
          seizedEfv: {
            Evidence: [
              'The valve function test reads FAILED TO CLOSE.',
              'The modelled release goes straight past the IDLH.',
              'Moisture, media and extract are all nominal — nothing is wrong with the ' +
                'gas or the bay.',
            ],
            Truth: 'The excess-flow valve failed its last function test and was signed off as ' +
              'passed. It is the mechanical stop that turns a severed line into a ' +
              'manageable leak instead of a full cylinder release — so nothing is wrong ' +
              'today, and the size of the accident this bunker can have has gone up ' +
              'eightfold. This is the fault that is only ever discovered on the day it ' +
              'matters.',
            Complaint: 'Routine review before a cylinder change. Nothing is wrong and nothing has ' +
              'happened.',
            Label: 'Excess-flow valve seized open',
          },
          wetHeader: {
            Evidence: [
              'Header moisture at 24 ppm against a 1 ppm limit.',
              'Line analysis after the purge is far above the exposure limit.',
              'The excess-flow valve, the scrubber and the extract are all fine — this is ' +
                'the purge, and only the purge.',
            ],
            Truth: 'The nitrogen header is at 24 ppm moisture. Water adsorbs on the line wall ' +
              'and desorbs slowly, so each pump-down no longer reaches the vacuum a dry ' +
              'one would and the effective cycle ratio collapses — and the ratio is what ' +
              'the purge multiplies by, ten times over. Ten cycles at the setpoint left ' +
              'four orders more arsine in the line than the procedure assumes. The panel ' +
              'counted cycles, which is not the same as counting molecules.',
            Complaint: 'A technician reported a smell of something at the last arsine cylinder ' +
              'change. The panel logged ten completed purge cycles, which is the ' +
              'qualified procedure.',
            Label: 'Purge nitrogen wet',
          },
          gasHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'The purge verifies below the exposure limit.',
            ],
            Truth: 'Nothing is wrong. The correct call is to proceed with the change.',
            Complaint: 'Pre-cylinder-change review. Everything on the panel is green.',
            Label: 'Nothing wrong',
          },
        },
        TextPpmAgainstAPpmLimit: '{{a}} ppm against a {{b}} ppm limit',
        NoteSccmIntoMAtAch: '{{a}} sccm into {{b}} m³ at {{c}} ACH',
        NoteCyclesAreNeeded: '{{a}} cycles — {{b}} are needed',
        NoteImmediatelyDangerousAtPpm: 'immediately dangerous at {{a}} ppm',
        SectionArithmeticYouDoBeforeYou: 'Arithmetic you do before you open anything',
        SectionStoredScenarios: 'Stored scenarios',
        SectionBunkerState: 'Bunker state',
        SectionAssessment: 'Assessment',
        SectionPanelAndBay: 'Panel and bay',
        LabelExposureLimit: 'Exposure limit',
        LabelResidualAfterPurge: 'Residual after purge',
        LabelLeakSteadyState: 'Leak, steady state',
        LabelToExposureLimit: 'To exposure limit',
        LabelWarningTheDetectorBuys: 'Warning the detector buys',
        LabelReachingTheStack: 'Reaching the stack',
        NoteAfterAbatementAndAbatementDegrades: 'after abatement — and abatement degrades quietly',
        TextSignOffThisBayAs: 'Sign off this bay as safe to open',
        AssistantLead: 'GAS-19 inquiry assistant',
        AssistantBlurb:
          'Ask how many purge cycles are actually needed, whether the detector warns ' +
          'in time, or what reaches the stack. Every answer is an assessment run ' +
          'against this panel.',
        reading: {
          stackSample: {
            Label: 'Stack sample and analysis (5.0 h)',
          },
          releaseModel: {
            Limit: '< 3 ppm, warning > 0 s',
            Label: 'Tracer-gas release test in the bay, bay evacuated (4.0 h)',
          },
          purgeVerify: {
            Label: 'Analyse the line after the purge, portable analyser (3.0 h)',
          },
          extractFlow: {
            Label: 'Bay extract flow',
          },
          mediaHours: {
            Label: 'Scrubber media loading',
          },
          efvTest: {
            ValueBad: 'FAILED TO CLOSE',
            ValueOk: 'passed',
            Limit: 'passed',
            Label: 'Excess-flow valve, last function test',
          },
          moisture: {
            Label: 'Purge nitrogen moisture',
          },
        },
        aspect: {
          ventilation: {
            Action: 'Restore the bay extract to design flow',
            Label: 'Bay ventilation',
          },
          scrubber: {
            Action: 'Change the scrubber media and re-certify destruction efficiency at the stack',
            Label: 'Abatement media',
          },
          excessFlow: {
            Action:
              'Replace the excess-flow valve and function-test it before the cylinder ' +
              'goes back on',
            Label: 'Excess-flow valve',
          },
          purgeGas: {
            Action: 'Change the header dryer, purge the distribution and re-certify to moisture',
            Label: 'Purge nitrogen quality',
          },
        },
      },
      crystal: {
        LatticeStatus: '{{a}} atoms · measured {{b}} K · {{c}} ps',
        LatticeTemp: 'Crystal temperature',
        TraceTitle: 'Distance from lattice site — thermal vibration',
        TraceX: 'simulated time (ps)',
        TraceY: 'displacement (Å)',
        TraceHost: 'One silicon atom',
        TraceSiRef: 'Si rms at 300 K, X-ray Debye–Waller',
        TraceStatus: 'rms from site over the last {{a}} ps: {{b}} Å · bond 2.35 Å',
        TraceIdle: 'Press Run: rms distance from site is measured over the last {{a}} ps of the run',
        LatticeReset: 'Reset',
        LatticePause: 'Pause',
        LatticeRun: 'Run',
        LatticeFailed: 'The molecular-dynamics engine failed to load.',
        LatticeLoading: 'Loading the molecular-dynamics engine…',
        NoteLattice: 'The boule, atom by atom: a diamond-cubic silicon lattice — every atom ' +
          'covalently bonded to four neighbours — vibrating live under a ' +
          'Stillinger–Weber force field. Nothing pins the atoms in place; the crystal ' +
          'holds its shape because this arrangement is the ground state of the ' +
          'potential. Drag to rotate, scroll to zoom. Warm it toward the melting ' +
          'point (1687 K) and watch thermal vibration blur the order the puller ' +
          'worked so hard to win.',
        NoteBands: 'What that arrangement does to electrons, from AIMS’s tight-binding model — not a remembered curve. The shaded strip is the gap: energies no electron in this crystal can have. Silicon’s conduction minimum does not sit above its valence maximum, so the dashed line leans — crossing the gap needs a change of momentum as well as energy, which a photon cannot supply alone. That is why silicon makes superb detectors and hopeless lasers.',
        BandsAxisEnergy: 'energy above the valence band (eV)',
        BandsLoading: 'Solving the tight-binding model…',
        BandsFailed: 'The band-structure model is unavailable for this crystal.',
        BandsGap: 'gap {{ev}} eV',
        BandsDirect: 'direct at {{at}}',
        BandsIndirect: 'indirect, {{from}} → {{to}}',
        BandsMeasured: 'measured {{ev}} eV',
        SectionLattice: 'The product — a silicon crystal lattice (molecular dynamics)',
        NoteSpecificationBand: 'Specification band {{a}} – {{b}} Ω·cm. {{c}} of {{d}} slices are inside it.',
        LabelGrowthTime: 'Growth time',
        LabelPullRate: 'Pull rate',
        SecondaryLabel: 'Pull rate against the ceiling',
        ToolLabel: 'CZ-07 Czochralski Crystal Puller',
        NextTheFurnaceAtThrm04: 'The furnace at THRM-04 precipitates this oxygen into the bulk on purpose. ' +
          'Ask it what happens to a wafer that arrived with too little.',
        AxisYOxygenCm: 'oxygen (nm⁻³)',
        AxisXCrucibleRotationRpm: 'crucible rotation (rpm)',
        TitleOxygenAgainstCrucibleRotation: 'Oxygen against crucible rotation',
        NoteAcrossTheSweep: 'across the sweep',
        LabelRotationRange: 'Rotation range',
        LabelWith03TMagnet: 'With 0.3 T magnet',
        LabelOxygenAsSet: 'Oxygen as set',
        HypTheMeltSitsInA: 'The melt sits in a silica crucible, so the crucible is dissolving into it ' +
          'the whole time. If that is the source, then anything that stirs the melt ' +
          'harder should carry more oxygen to the interface - and anything that damps ' +
          'convection should carry less.',
        AskedWhatPutsOxygenInThe: 'What puts oxygen in the crystal, and what can move it?',
        SumCrucibleDissolutionMeltConvectionAnd: 'Crucible dissolution, melt convection, and why oxygen is specified rather ' +
          'than removed.',
        NextVGHasTwoHalves: 'v/G has two halves and you have only moved one. What happens to the window ' +
          'if the hot zone changes the gradient instead - and which of the two is ' +
          'easier to hold steady for thirty hours?',
        LabelPerfectSiliconBoundary: 'perfect-silicon boundary',
        AxisYVGCmMinK: 'v/G  cm²/(min·K)',
        AxisXPullRateMmMin: 'pull rate (mm/min)',
        TitleVoronkovRatioAgainstPullRate: 'Voronkov ratio against pull rate',
        NoteLatentHeat: 'latent heat',
        LabelRateCeiling: 'Rate ceiling',
        LabelVGAsSet: 'v/G as set',
        HypIfPullRateMatteredOnly: 'If pull rate mattered only for throughput, every rate here would give the ' +
          'same crystal more or less quickly. If Voronkov is right, the ratio v/G ' +
          'crosses a boundary somewhere in this range and the dominant point defect ' +
          'changes species - which is a change in what the crystal IS, not in how ' +
          'fast it was made.',
        AskedWhatDoesPullRateActually: 'What does pull rate actually set, besides how long the growth takes?',
        NextEveryWaferInABox: 'Every wafer in a box came from a different place in this curve. If a ' +
          'process downstream is sensitive to substrate resistivity, what does that ' +
          'make a "lot"?',
        LabelSpecLow: 'spec low',
        AxisXDistanceFromSeedMm: 'distance from seed (mm)',
        NoteSpread: 'Spread',
        DesignOneGrowthAsSetThen: 'One growth as set, then the same growth judged against three different ' +
          'resistivity specifications. Nothing about the crystal changes between ' +
          'those three: only how much of it a customer would accept.',
        HypBoronIsMoreSolubleIn: 'Boron is more soluble in liquid silicon than in solid, so every millimetre ' +
          'that freezes leaves the melt slightly richer than it found it. If that is ' +
          'what is happening, the tail must be more heavily doped than the seed - ' +
          'lower resistivity - and the gap should widen toward the end rather than ' +
          'drifting at random.',
        AskedWhyDoesResistivityChangeDown: 'Why does resistivity change down the ingot, and what does that cost?',
        SumSegregationDownTheIngotAnd: 'Segregation down the ingot, and how much of it a specification lets you ' +
          'sell.',
        fault: {
          shakingLift: {
            Evidence: [
              'Seed lift vibration at 55% against a 10% limit.',
              'Hot-zone probe is fine — the ceiling is where it should be.',
              'Heater calibration is nominal.',
            ],
            Truth: 'A bearing in the seed lift column has gone and the crystal is being shaken ' +
              'as it is withdrawn. Structure is lost well BELOW the thermal ceiling, ' +
              'which is why the hot-zone probe comes back clean and the run still fails.',
            Complaint: 'Lost structure twice this month, both times in the body, and the hot zone ' +
              'is new.',
            Label: 'Seed lift vibrating',
          },
          heaterDrift: {
            Evidence: [
              'Heater calibration at 78% against a ±2% window.',
              'Hot-zone probe puts the ceiling below the recipe pull rate.',
              'Seed vibration is nominal, which rules out the mechanical way to lose ' +
                'structure.',
            ],
            Truth: 'The heater is delivering 78% of its calibrated power, so the axial ' +
              'gradient at the interface is far shallower than the recipe assumes. The ' +
              'gradient is what carries latent heat away, so the maximum pull rate has ' +
              'fallen underneath an unchanged setpoint — the recipe is now asking for ' +
              'more than the hot zone can give.',
            Complaint: 'Two of the last four runs lost structure around the shoulder. The recipe ' +
              'has not been touched in a year.',
            Label: 'Heater out of calibration',
          },
          restrictedArgon: {
            Evidence: [
              'Argon flow at 45% against a 90% floor.',
              'FTIR oxygen high.',
              'The crucible is on its first run — this is not a consumable problem.',
            ],
            Truth: 'The argon line is restricted to 45% of flow. Argon sweeps silicon monoxide ' +
              'off the melt surface before it can dissolve back in; without it the oxygen ' +
              'returns. It produces exactly the FTIR number a spent crucible does, and ' +
              'the crucible reading is what tells them apart.',
            Complaint: 'Same high oxygen, and the crucible was changed last week.',
            Label: 'Argon sweep restricted',
          },
          spentCrucible: {
            Evidence: [
              'Runs on this crucible past the limit.',
              'FTIR oxygen above the 12 ×10¹⁷ band.',
              'Argon flow is nominal, which rules out the other way oxygen climbs.',
            ],
            Truth: 'The crucible is on its fifth run. Silica dissolves into the melt for the ' +
              'whole of every pull, and a tired crucible sheds nearly twice what a fresh ' +
              'one does — so the oxygen goes up while every setpoint on the tool stays ' +
              'exactly where it was. Nothing was changed, and that is precisely why ' +
              'nobody looked here.',
            Complaint: 'Wafers from the last two ingots are precipitating oxygen in the furnace. ' +
              'Device is asking whether the puller changed. It did not.',
            Label: 'Crucible past its runs',
          },
          czHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Oxygen on the last ingot was in band.',
            ],
            Truth: 'Nothing is wrong. The correct call is to start the run.',
            Complaint: 'Pre-run review before a three-day pull. Everything reads normal.',
            Label: 'Nothing wrong',
          },
        },
        TextCm: '{{a}} Ω·cm',
        NoteLatentHeatCeilingMmMin: 'latent-heat ceiling {{a}} mm/min',
        NoteVG: 'v/G = {{a}}',
        NoteCmSeedToTail: '{{a}} → {{b}} Ω·cm, seed to tail',
        NoteOfMmGrown: 'of {{a}} mm grown — {{b}}%',
        SectionWhyAnIngotIsNot: 'Why an ingot is not one material',
        SegregationLead: 'The crystal takes only a fraction k of the dopant the melt holds, so it gets more doped along its length.',
        SegregationHelp: 'Why that makes the ingot shorter, not better',
        NoteDopantDoesNotPartitionEqually: 'Dopant does not partition equally between solid and liquid. The crystal ' +
          'takes a fraction k of what the melt holds and rejects the rest back, so ' +
          'the melt gets richer as the ingot grows and the crystal gets more doped ' +
          'along its length. Resistivity is a specification, and the length of ingot ' +
          'that meets it is this tool’s yield — which is why narrowing the window ' +
          'does not give a customer a better ingot, it gives them a shorter one.',
        SectionPullerState: 'Puller state',
        SectionHotZone: 'Hot zone — live heat & flow',
        NoteHotZone:
          'A live heat-transfer simulation of the puller’s cross-section: the crucible held at ' +
          'heater temperature, molten silicon convecting under buoyancy, and the boule pulling ' +
          'heat up to the cooled seed chuck. The arrows are the melt flow — the circulation ' +
          'that carries heat to the growth interface.',
        NoteHotZoneMagnet:
          'The magnetic field knob acts here too. Molten silicon conducts, so a field across it ' +
          'brakes any parcel of melt that moves — turn it up and watch the arrows die down. ' +
          'That is the whole of magnetic Czochralski: the circulation is what ferries oxygen ' +
          'off the quartz crucible to the growth interface, so damping it is how low-oxygen ' +
          'silicon is grown. A braked melt also stops swinging in temperature, which is what ' +
          'keeps growth striations out of the crystal.',
        CzMagnet: 'magnet',
        CzLoading: 'Loading the heat-transfer engine…',
        CzFailed: 'The heat-transfer engine failed to load.',
        CzPause: 'Pause',
        CzRun: 'Run',
        CzReset: 'Reset',
        CzClock: 't = {{s}} s',
        CzProbeMelt: 'melt',
        CzProbeInterface: 'interface',
        CzProbeBoule: 'boule',
        SectionResistivityDownTheIngot: 'Resistivity down the ingot',
        TextGrowTheIngotAndSend: 'Grow the ingot and send it to WPR-08',
        BandsAbout: 'The band structure beside it: what that arrangement does to electrons',
        LabelUsableIngot: 'Usable ingot',
        LabelResistivitySpread: 'Resistivity spread',
        LabelDefectRegime: 'Defect regime',
        LabelInterstitialOxygen: 'Interstitial oxygen',
        NoteStrengthAndGetteringOrPrecipitates: 'strength and gettering, or precipitates',
        NoteOneCrystalStartToFinish: 'one crystal, start to finish',
        AssistantLead: 'CZ-07 inquiry assistant',
        AssistantBlurb:
          'Ask about segregation down the ingot, how fast this crystal can be pulled, ' +
          'or where the oxygen comes from. Every answer is a growth run against this ' +
          'puller.',
        reading: {
          gradientProbe: {
            Label: 'Thermocouple probe of the hot zone, hot zone open (5.0 h)',
          },
          oxygenCheck: {
            Label: 'FTIR interstitial oxygen on the previous ingot (3.0 h)',
          },
          vibration: {
            Label: 'Seed lift vibration',
          },
          argonFlow: {
            Label: 'Argon flow',
          },
          heaterCal: {
            Label: 'Heater power calibration',
          },
          crucibleRuns: {
            Label: 'Runs on this crucible',
          },
        },
        aspect: {
          seedLift: {
            Action: 'Re-bearing the lift column and re-balance it',
            Label: 'Seed lift mechanism',
          },
          argon: {
            Action: 'Clear the argon line restriction and re-set the flow',
            Label: 'Argon sweep',
          },
          heater: {
            Action:
              'Re-calibrate the heater against a reference pyrometer and re-map the hot ' +
              'zone',
            Label: 'Heater calibration',
          },
          crucible: {
            Action:
              'Scrap the crucible and re-charge. Three days of production, and it is ' +
              'unavoidable',
            Label: 'Silica crucible',
          },
        },
      },
      waferprep: {
        NoteSurfaceCaveats: 'Three honest caveats. This is the CLEAN surface, the one a vacuum tool ' +
          'sees after an HF dip or an in-situ bake — a wafer sitting in air is ' +
          'covered in native oxide within minutes. The reconstruction is not ' +
          'something wafers spend picoseconds doing; it is simply where a clean ' +
          'surface sits, and starting from the ideal cut is how you see what gets ' +
          'traded. And real dimers BUCKLE, one atom of each pair riding higher than ' +
          'the other, because charge shifts between them — that is electronic ' +
          'structure, and a classical force field carries no electrons, so the pairs ' +
          'here stay symmetric. The bottom of the slab is held still: those atoms ' +
          'stand in for the 775 µm of wafer below that is not in the box.',
        NoteSurfaceDimers: 'An ideal cut is not what a surface looks like. Slice the lattice and every ' +
          'atom in the top layer loses two of its four bonds — those are the red ' +
          'ones, and each is a broken bond with nowhere to go. Press Run and watch: ' +
          'neighbours lean toward each other and pair up, trading a dangling bond ' +
          'each for a new bond between them. The pairs are dimers, they line up into ' +
          'rows, and that 2×1 reconstruction is what every clean Si(100) wafer is ' +
          'actually wearing. Nothing here scripts it — the Stillinger–Weber potential ' +
          'says the paired surface is lower in energy, and the atoms go there. It ' +
          'never quite finishes: pairing is a local decision, so a few atoms end up ' +
          'stranded when both of their would-be partners have already paired off, and ' +
          'a real Si(100) surface carries the same defect. Cool the wafer to 150 K, ' +
          'reset, and it stalls far short of even that — the atoms no longer have the ' +
          'energy to find their partners.',
        SurfaceReset: 'Back to the ideal cut',
        SurfacePause: 'Pause',
        SurfaceRun: 'Run',
        SurfaceTemp: 'Wafer temperature',
        SurfaceLoading: 'Loading the molecular-dynamics engine…',
        SurfaceFailed: 'The molecular-dynamics engine failed to load.',
        SurfaceChartTitle: 'Dimers formed against time',
        SurfaceChartX: 'simulated time (ps)',
        SurfaceChartY: 'dimers',
        SurfaceChartSeries: 'Dimers',
        SurfaceChartAllSites: 'every site paired',
        SurfaceStatus: '{{a}}/{{b}} dimers · {{c}} atoms still unpaired · measured {{d}} K · {{e}} ' +
          'ps',
        NoteSurface: 'The saw cuts the boule perpendicular to the seed, so the wafer\'s notch is ' +
          'a statement about which crystal plane the surface is — and for CMOS logic ' +
          'that plane is (100), because the (100)/SiO₂ interface carries the fewest ' +
          'electrical traps and gives the highest electron mobility in an inversion ' +
          'layer. This is that face, cut out of the same diamond-cubic silicon the ' +
          'puller grew. Left-drag rotates, the wheel zooms, right-drag pans.',
        SectionSurface: 'The face it presents — a Si(100) surface (molecular dynamics)',
        NoteFrontOnly: 'FRONT ONLY — the back is still sawn',
        NoteBothFacesBalanced: 'both faces balanced',
        LabelBow: 'Bow',
        SecondaryLabel: 'Scratches',
        ToolLabel: 'WPR-08 Slice, Lap and Polish',
        NextCmp11FlattensEachLayer: 'CMP-11 flattens each layer as it is built, and hands its own focus budget ' +
          'to the same scanner. Which of the two matters more by the tenth metal ' +
          'layer?',
        AxisYTtvM: 'TTV (µm)',
        AxisXPolishTimeMin: 'polish time (min)',
        TitleTtvAndFlatnessYieldAgainst: 'TTV and flatness yield against polish time',
        LabelRoughness: 'Roughness',
        LabelFlatnessYield: 'Flatness yield',
        HypTtvIsAThicknessVariation: 'TTV is a thickness variation and polishing should reduce it. Bow is not - ' +
          'it is a shape, and a shape comes from an imbalance between the two faces. ' +
          'If that is right, polishing longer should flatten TTV and do very little ' +
          'for bow, and working one face only should make bow dramatically worse ' +
          'while TTV barely notices.',
        AskedWhatSetsTheFlatnessOf: 'What sets the flatness of the finished wafer?',
        SumTtvBowAndWarpAnd: 'TTV, bow and warp, and the focus budget the wafer hands to lithography.',
        NextDice15GrindsThisWafer: 'DICE-15 grinds this wafer down to a tenth of its thickness after the ' +
          'devices are built, with the same physics and none of the margin. Ask it ' +
          'what damage costs there.',
        AxisYDamageM: 'damage (µm)',
        AxisXLapRemovalPerSideM: 'lap removal per side (µm)',
        TitleResidualDamageAndStrengthAgainst: 'Residual damage and strength against lap removal',
        NotePerWafer: 'per wafer',
        LabelScratches: 'Scratches',
        LabelStrength: 'Strength',
        LabelResidual: 'Residual',
        LabelSawDamage: 'Saw damage',
        HypAnAbrasiveCutLeavesCracks: 'An abrasive cut leaves cracks below the surface it made, roughly in ' +
          'proportion to the grit size. If so, a wafer that looks perfect can still ' +
          'be weak - and the removal steps after the saw are not cosmetic, they are ' +
          'structural.',
        AskedHowDeepDoesTheSaw: 'How deep does the saw damage go, and what does removing it cost?',
        SumSubsurfaceDamageAndWhyThree: 'Subsurface damage, and why three steps follow the saw instead of one.',
        NextTheGapBetweenCutAnd: 'The gap between "cut" and "shipped" on that chart is flatness and ' +
          'breakage. Ask me what sets it.',
        AxisXWireDiameterM: 'wire diameter (µm)',
        TitleWafersFromOneIngotAgainst: 'Wafers from one ingot, against wire diameter',
        NoteAfterFlatnessAndBreakage: 'after flatness and breakage',
        LabelIngotLost: 'Ingot lost',
        HypEveryCutRemovesASlot: 'Every cut removes a slot as wide as the wire plus the abrasive on it, and ' +
          'that silicon is gone. If kerf is comparable to wafer thickness, the loss ' +
          'should be enormous - not a rounding error but a large fraction of a ' +
          'crystal that took thirty hours to grow.',
        AskedHowMuchOfTheIngot: 'How much of the ingot leaves as wafer, and what decides it?',
        SumKerfLossWaferCountAnd: 'Kerf loss, wafer count, and the silicon that leaves as slurry.',
        fault: {
          oversizeSlurry: {
            Evidence: [
              'Slurry size at 240% of batch specification.',
              'Scratch count far above the limit while the flatness map is clean.',
              'Every saw and pad reading is nominal.',
            ],
            Truth: 'The slurry batch has agglomerated and is running 2.4× its specified ' +
              'particle size. Oversize particles do not affect flatness at all — they ' +
              'plough single deep scratches, and a scratch is a killer defect on a wafer ' +
              'that is otherwise faultless. It is the cheapest repair on the tool and it ' +
              'is invisible on the axis the other three faults compete on.',
            Complaint: 'Inspection is finding long scratches on finished wafers. Flatness is ' +
              'perfect on every one of them.',
            Label: 'Slurry batch out of size specification',
          },
          glazedPad: {
            Evidence: [
              'Pad hours past the 45 h limit.',
              'Removal on the monitor wafer below the 0.28 µm floor.',
              'Kerf and tension are both nominal.',
            ],
            Truth: 'The pad is glazed at 76 hours. A glazed pad stops transporting slurry and ' +
              'its removal rate collapses — so the polish neither takes the thickness off ' +
              'nor does the flattening the process was relying on it for. The saw is ' +
              'entirely innocent, and both saw readings say so.',
            Complaint: 'Flatness marginal and the wafers are coming through slightly thick.',
            Label: 'Polish pad glazed',
          },
          slackWire: {
            Evidence: [
              'Tension at 19 N against a 26 – 30 N window.',
              'Flatness map far out, and kerf width perfectly normal.',
              'Wire kilometres inside limit — this is not a consumable.',
            ],
            Truth: 'A tension arm is reading low and the wire is running at 19 N instead of ' +
              '28. A slack wire wanders in the cut, so the slot is wavy — the same ' +
              'rejected flatness a worn wire produces, from a completely different cause. ' +
              'The kerf is NORMAL here, because the wire is sharp; it is simply not being ' +
              'held straight.',
            Complaint: 'Same flatness rejects, and the wire was changed on Monday.',
            Label: 'Wire tension low',
          },
          wornWire: {
            Evidence: [
              'Wire kilometres past the 450 km limit.',
              'Kerf width above 185 µm.',
              'Tension is on target and the pad is inside its hours.',
            ],
            Truth: 'The wire is at 810 km. A worn wire has lost its abrasive and ploughs ' +
              'rather than cuts, which both widens the kerf and leaves a wavier slot — ' +
              'and the waviness survives lapping and polishing as thickness variation. ' +
              'The kerf measurement is what distinguishes it from the other two ways to ' +
              'lose flatness.',
            Complaint: 'Flatness has drifted out on the last three ingots and the customer has ' +
              'rejected two.',
            Label: 'Saw wire past its length',
          },
          wprHealthy: {
            Evidence: [
              'Every consumable counter is inside limit.',
              'Flatness map is well under 1.5 µm.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the ingot.',
            Complaint: 'Routine ingot. Wafer count and flatness both normal.',
            Label: 'Nothing wrong',
          },
        },
        NoteTtvTheScannerHasTo: 'TTV — the scanner has to print on this',
        NoteFromMTheSawLeft: 'from {{a}} µm the saw left',
        NoteMKerfKgOfCrystal: '{{a}} µm kerf — {{b}} kg of crystal',
        NotePassFlatnessAndHandling: '{{a}} pass flatness and handling',
        SectionTheOneThatIsGenuinely: 'The one that is genuinely counter-intuitive',
        BowLead: 'A wafer with MORE damage can be FLATTER than a cleaner one, if the damage is on both faces.',
        BowHelp: 'Why bow is not a damage measurement',
        NoteADamagedLayerIsA: 'A damaged layer is a stressed layer, and stress balanced on both faces ' +
          'bends nothing. Work only the front and the back keeps everything the saw ' +
          'left, so the wafer bows — which means a wafer with MORE damage on it can ' +
          'be FLATTER than a cleaner one, if the damage is symmetric. Bow is not a ' +
          'damage measurement. That is why lapping and polishing are double-sided.',
        FacesBoth: 'Both faces worked',
        FacesFront: 'Front face only',
        HistoryBareWafer: 'Bare polished wafer, no films',
        HistoryStockBlank: 'Stock blank — no ingot from CZ-07 on the saw.',
        SectionDoubleSidedProcessing: 'Double-sided processing',
        SectionLineState: 'Line state',
        TextSliceTheIngotAndStart: 'Slice the ingot and start a wafer down the line',
        LabelWafersFromTheIngot: 'Wafers from the ingot',
        LabelLostAsSlurry: 'Lost as slurry',
        LabelResidualDamage: 'Residual damage',
        LabelBreakingStrength: 'Breaking strength',
        NoteSetByTheWorstFlaw: 'set by the worst flaw left in the surface',
        LabelFlatness: 'Flatness',
        AssistantLead: 'WPR-08 inquiry assistant',
        AssistantBlurb:
          'Ask what the saw costs in silicon, how deep the damage goes, or what makes ' +
          'a wafer bow. Every answer is a run against this line.',
        reading: {
          scratchCount: {
            Label: 'Dark-field scan for scratches, 25 wafers (2.0 h)',
          },
          removalRate: {
            Label: 'Polish removal on a monitor wafer (1.0 h)',
          },
          kerfCheck: {
            Label: 'Kerf width on a scrap section (2.0 h)',
          },
          ttvMap: {
            Label: 'Capacitive flatness map, 10 wafers (1.5 h)',
          },
          slurrySize: {
            Label: 'Slurry particle size, against batch spec',
          },
          padHours: {
            Label: 'Polish pad hours',
          },
          tension: {
            Label: 'Wire tension',
          },
          wireKm: {
            Label: 'Wire kilometres run',
          },
        },
        aspect: {
          slurry: {
            Action: 'Change the slurry batch and check the particle size distribution',
            Label: 'Polish slurry',
          },
          pad: {
            Action: 'Condition the pad, or change it if it is glazed past dressing',
            Label: 'Polish pad',
          },
          tension: {
            Action: 'Re-set the tension arms and re-zero the load cells',
            Label: 'Wire tension',
          },
          wire: {
            Action: 'Change the wire spool and re-qualify kerf on a scrap ingot section',
            Label: 'Saw wire',
          },
        },
      },
      inspection: {
        LeadTopOfDistribution: 'The tool reports the top of the size distribution. It is not broken; it is optics.',
        NoteThisScanWouldReport: 'This scan would report {{a}}/cm². Whether that is close to what the wafer ' +
          'carries is a question only the prober can answer.',
        SecondaryLabel: 'Nuisance rate',
        ToolLabel: 'INSP-13 Patterned Wafer Inspection',
        NextAYieldMapAtPrb: 'A yield map at PRB-14 covers every die by definition. What does that make ' +
          'sort - an inspection step, or the only full-coverage inspection in the ' +
          'line?',
        AxisXWaferCoverage: 'wafer coverage',
        TitleScanTimeAgainstCoverage: 'Scan time against coverage',
        LabelTimeSaved: 'Time saved',
        LabelAtFullCoverage: 'At full coverage',
        LabelAt10Coverage: 'At 10% coverage',
        HypADensityIsAPer: 'A density is a per-area number, so a partial scan should estimate it about ' +
          'as well as a full one - sampling works for averages. What sampling cannot ' +
          'do is find a LOCALISED signature, because a cluster in an unscanned region ' +
          'contributes nothing to the estimate and nothing to the map.',
        AskedWhatDoesScanningLessOf: 'What does scanning less of the wafer cost?',
        SumSampledCoverageScanTimeAnd: 'Sampled coverage, scan time, and what a partial scan can and cannot tell ' +
          'you.',
        NextPrb14MeasuresTheYield: 'PRB-14 measures the yield this defect density predicts. If the two ' +
          'disagree, which one is wrong - and which one would you bet a lot on?',
        LabelDesignRule: 'design rule',
        AxisYDetectionLimitNm: 'detection limit (nm)',
        AxisXPixelNm: 'pixel (nm)',
        TitleDetectionLimitAndScanTime: 'Detection limit and scan time against pixel size',
        HypScatteredIntensityFallsExtremelyFast: 'Scattered intensity falls extremely fast with particle size, so below some ' +
          'diameter a defect returns less light than the noise floor no matter how ' +
          'long you look. Defect size distributions are power laws, meaning the small ' +
          'ones are the numerous ones. If both hold, the invisible population should ' +
          'be far larger than the visible one - and some of it should still be big ' +
          'enough to kill a die.',
        AskedWhatCanThisRecipeNot: 'What can this recipe not see, and does any of it matter?',
        SumTheDetectionLimitAndWhy: 'The detection limit, and why every inspection number is an underestimate.',
        NextNeitherEndOfThatCurve: 'Neither end of that curve gets capture to 100%. Ask me why - the answer is ' +
          'not the threshold, and it is the more important limit of the two.',
        AxisXThreshold: 'threshold (σ)',
        TitleCaptureAndNuisanceAgainstThreshold: 'Capture and nuisance against threshold',
        NoteWhatIsActuallyThere: 'what is actually there',
        LabelReported: 'Reported',
        LabelNuisance: 'Nuisance',
        HypTheThresholdIsALevel: 'The threshold is a level above a noise floor, and real defects and surface ' +
          'noise are on the same axis. Lowering it must therefore catch more of both. ' +
          'If so there is no setting that is simply "more sensitive" - only a choice ' +
          'about which error costs more, and the cost of the false one is paid in SEM ' +
          'review hours.',
        AskedWhatDoesTurningTheDetection: 'What does turning the detection threshold down actually buy?',
        SumCaptureRateAgainstNuisanceRate: 'Capture rate against nuisance rate, and the review time that pays for it.',
        fault: {
          roughIncoming: {
            Evidence: [
              'Incoming roughness at 1.6 nm against a 0.5 nm limit.',
              'Haze, laser power and stage vibration are all nominal.',
              'The effect is on one product only — a tool fault would be on all of them.',
            ],
            Truth: 'Nothing is wrong with the inspector. The wafers are arriving at 1.6 nm RMS ' +
              'instead of 0.3 — an over-aggressive SC-1 at CLN-09 — and surface roughness ' +
              'is the floor the nuisance population grows out of. Cleaning the optics of ' +
              'a tool whose optics are clean costs four hours and fixes nothing.',
            Complaint: 'Nuisance counts have tripled on one product. The inspector is being blamed.',
            Label: 'The wafer is rough, and the tool is fine',
          },
          stageShake: {
            Evidence: [
              'Stage vibration at 60% against a 10% limit.',
              'Capture rate is down while laser power and haze are both normal.',
            ],
            Truth: 'An air isolator has lost pressure, so the stage carries floor vibration ' +
              'into every scan. The image smears, which costs sensitivity the same way a ' +
              'weak source does — but the vibration reading names it directly, and the ' +
              'laser is fine.',
            Complaint: 'Scans take longer and the maps look smeared along the scan direction.',
            Label: 'Stage isolation degraded',
          },
          hazedOptics: {
            Evidence: [
              'Haze reads 45% against a 10% limit.',
              'Nuisance rate is far above the 40% review limit.',
              'Laser power monitor is perfectly normal.',
            ],
            Truth: 'The collection path is hazed. It scatters light into the detector, which ' +
              'raises the noise floor AND costs sensitivity — so the tool is ' +
              'simultaneously blinder and noisier. That combination is the signature: a ' +
              'weak laser does the first without the second.',
            Complaint: 'Review is drowning. Half the sites they look at have nothing there, and ' +
              'the queue is two days long.',
            Label: 'Collection optics contaminated',
          },
          tiredLaser: {
            Evidence: [
              'Power monitor reads 55% against an 85% floor.',
              'Capture rate on the standard wafer has collapsed.',
              'Nuisance rate is NORMAL — a weak source costs sensitivity without adding ' +
                'noise, which is what separates this from hazed optics.',
            ],
            Truth: 'The laser is at 55% of rated power. Scattered signal goes as illumination ' +
              'intensity, so the detection limit has climbed and the smallest defects ' +
              'have simply stopped being reported. The counts fell because the tool went ' +
              'blind, not because the fab got clean.',
            Complaint: 'Defect counts across every product have been falling for a fortnight. The ' +
              'line thinks the fab got cleaner.',
            Label: 'Illumination source failing',
          },
          inspHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Capture rate is where the standard says it should be.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine shift start. The tool has been quiet.',
            Label: 'Nothing wrong',
          },
        },
        NoteOfTheWafer: '{{a}}% of the wafer',
        NoteOfReviewsWasted: '{{a}} of {{b}} reviews wasted',
        NoteRealNuisance: '{{a}} real + {{b}} nuisance',
        NoteNmLight: '{{a}} nm light, {{b}}σ',
        SectionWhyTheNumberIsAlways: 'Why the number is always low',
        NoteScatteringFromASmallParticle: 'Scattering from a small particle goes as the sixth power of its diameter, ' +
          'so there is a size below which this tool sees nothing at all — and defect ' +
          'sizes follow a power law, with far more small ones than large. The tool ' +
          'reports the top of the distribution. It is not broken; it is optics.',
        SectionScanResult: 'Scan result',
        LabelDetectionLimit: 'Detection limit',
        LabelCaptureRate: 'Capture rate',
        NoteOfTheDefectsActuallyPresent: 'of the defects actually present',
        LabelReportedDensity: 'Reported density',
        LabelNuisanceRate: 'Nuisance rate',
        LabelKillerDensity: 'Killer density',
        NoteBigEnoughAndOnPattern: 'big enough, and on pattern',
        LabelScanTime: 'Scan time',
        NoteNoLotOnTheStage: 'No lot on the stage. Bring a wafer here to scan it.',
        TextScanTheWaferAndPass: 'Scan the wafer and pass it on',
        AssistantLead: 'INSP-13 inquiry assistant',
        AssistantBlurb:
          'Ask what a lower threshold buys, what this recipe cannot see at all, or ' +
          'whether a partial scan is enough. Every answer is a scan run against this ' +
          'tool.',
        reading: {
          nuisance: {
            Label: 'SEM review of 50 flagged sites (2.0 h)',
          },
          sensitivity: {
            Limit: '> 8% (qualified: 10%)',
            Label: 'Standard defect wafer, capture rate (1.0 h)',
          },
          roughness: {
            Label: 'Incoming surface roughness',
          },
          vibration: {
            Label: 'Stage vibration',
          },
          haze: {
            Label: 'Optics haze',
          },
          laserPower: {
            Label: 'Laser power monitor',
          },
        },
        aspect: {
          incoming: {
            Action: 'Not a repair — the wafer is rough, and the bench upstream owns it',
            Label: 'Incoming surface',
          },
          stage: {
            Action: 'Re-level the air isolators and re-tune the stage servo',
            Label: 'Stage and isolation',
          },
          optics: {
            Action: 'Strip and clean the collection path, then re-qualify on a standard',
            Label: 'Collection optics',
          },
          laser: {
            Action: 'Replace the laser head and re-calibrate the power monitor',
            Label: 'Illumination source',
          },
        },
      },
      probe: {
        ValueDie: '{{a}} die',
        signature: {
          clustered: 'clustered',
          radial: 'radial',
          centre: 'centre',
          edge: 'edge',
          uniform: 'uniform',
        },
        LotDefectsShort: 'This lot: {{a}} defects/cm², {{b}}/cm² of them killers',
        LeadMeanHidesTheMap: 'One mean yield can be three different problems with three different owners.',
        NoteThisLotCarriesDefects: 'This lot carries {{a}} defects/cm², of which {{b}}/cm² are killers. ' +
          'Compare that with what INSP-13 put on the travel card — the two will not ' +
          'agree, and the difference is what the inspector could not see.',
        NoteNoLotOnTheProber: 'No lot on the prober. It is running against the line\'s assumed {{a}}/cm² ' +
          'until a wafer arrives.',
        LabelEdgeExclusion: 'Edge exclusion',
        SecondaryLabel: 'Measurement uncertainty',
        ToolLabel: 'PRB-14 Wafer Probe and Sort',
        NextFt18AppliesAGuard: 'FT-18 applies a guard band of its own to the packaged part, on a ' +
          'measurement with its own gauge error. Ask it what two guard bands in ' +
          'series cost - and whether the second one is buying anything the first did ' +
          'not.',
        LabelParametric: 'Parametric',
        LabelSignature: 'Signature',
        HypSortYieldIsAProduct: 'Sort yield is a product of three independent terms and only one of them is ' +
          'about the wafer. If the loss is defects, cleaning the probe card will not ' +
          'move it; if it is contact, it will move a lot and the map will look like ' +
          'the card rather than like the process. The guard band is a third thing ' +
          'entirely - a decision, not a failure.',
        AskedWhereIsThisYieldActually: 'Where is this yield actually going?',
        SumSeparatingAProcessSignatureFrom: 'Separating a process signature from a contact problem and a guard band.',
        LabelNegativeBinomial: 'negative binomial',
        AxisXClusteringLowHeavilyClustered: 'clustering α  (low = heavily clustered)',
        TitleTwoModelsOneWafer: 'Two models, one wafer',
        LabelImpliedDensity: 'Implied density',
        NotePoisson: 'Poisson',
        LabelNegativeBinomialL196: 'Negative binomial',
        HypPoissonAssumesEveryDefectLands: 'Poisson assumes every defect lands independently of every other. Real ' +
          'defects arrive in scratches, in coating streaks, in edge rings - so they ' +
          'overlap, and several defects land on die that were already dead. If so, ' +
          'clustering should RAISE yield above Poisson for the same total defect ' +
          'count, and Poisson should be pessimistic exactly when a line has a ' +
          'signature.',
        AskedWhyDoTheYieldModels: 'Why do the yield models disagree, and which one should the line use?',
        SumClusteringAndWhyTheSimplest: 'Clustering, and why the simplest yield model is always pessimistic.',
        NextThatCurveAssumedDefectsLand: 'That curve assumed defects land independently. They do not - they cluster. ' +
          'Ask me what clustering does to the same numbers, because it is the ' +
          'difference between a model that predicts your line and one that does not.',
        LabelGoodDie: 'good die',
        TitleGoodDieOut: 'Good die out',
        AxisXDieAreaCm: 'die area (cm²)',
        TitleDefectYieldAgainstDieArea: 'Defect yield against die area',
        LabelTestTime: 'Test time',
        LabelEdgeLoss: 'Edge loss',
        HypADieFailsIfAny: 'A die fails if any killer defect lands on it, so the probability of ' +
          'survival falls exponentially with area. If that is right, doubling the die ' +
          'should cost far more than half the yield, and no process improvement of ' +
          'realistic size will compensate for a design decision made before the mask ' +
          'was cut.',
        AskedHowMuchDoesDieArea: 'How much does die area matter to yield, compared with everything else?',
        SumYieldIsExponentialInDie: 'Yield is exponential in die area, so area is the biggest lever and design ' +
          'owns it.',
        fault: {
          chuckStarved: {
            Evidence: [
              'Chuck vacuum 21 kPa against a 60 kPa floor.',
              'Map signature reads edge, with card planarity inside its 6 µm limit.',
              'Contact loss on the short standard well past 2%, tip wear nominal.',
              'The utility panel at facilities shows the PRB-14 dry pump down, every other ' +
                'pump pulling.',
            ],
            Truth:
              'The dry pump under this tool, down in the sub-fab, has seized: its foreline is ' +
              'sitting near atmosphere and the chuck is holding the wafer with a fifth of the ' +
              'vacuum it should. A wafer that is not held flat lifts at its edge, and a lifted ' +
              'edge lands light under the card exactly as an out-of-plane card does — which is ' +
              'why the map says EDGE while the planarity gauge says the card is fine. ' +
              'Re-shimming the card, cleaning the tips: nothing on this tool restores a vacuum ' +
              'this tool does not make. No other tool on the floor has moved, because no other ' +
              'tool shares this pump.',
            Complaint:
              'The maps have gone edge-heavy overnight and contact loss is up on every product. ' +
              'The card was re-shimmed last week and its planarity checks out.',
            Label: 'Chuck starved of vacuum by its dry pump',
          },
          noisyGauge: {
            Evidence: [
              'Gauge sigma 14% of spec against a 5% limit.',
              'Map signature is uniform — noise has no shape.',
              'Chuck, planarity and tip wear are all nominal.',
            ],
            Truth: 'The parametric measurement unit is noisy — gauge sigma is 14% of the spec ' +
              'width against a 5% limit. Good die near a limit fail on one touchdown and ' +
              'pass on the next. The AVERAGE yield is barely moved, which is why it went ' +
              'unnoticed, and the re-test disagreement is the only symptom that could ' +
              'have caught it.',
            Complaint: 'Re-testing the same wafer gives a different bin split each time. Yield is ' +
              'roughly right on average.',
            Label: 'Parametric measurement chain noisy',
          },
          chuckOffset: {
            Evidence: [
              'Chuck offset +9 °C against a ±2 °C limit.',
              'Map signature reads radial, which follows the chuck, not the process.',
              'Sort yield is UNCHANGED — this fault does not scrap die, it moves where ' +
                'the limits fall relative to the population.',
              'Contact loss is normal — this is not a touchdown problem.',
            ],
            Truth: 'The chuck thermocouple has drifted and the wafer is 9 °C above the ' +
              'temperature the log reports. Parametrics are temperature-sensitive, so the ' +
              'whole population has shifted against limits that were set at the right ' +
              'temperature. The die are fine.',
            Complaint: 'Parametric bins have moved. Nothing is failing continuity, and the die ' +
              'that fail are scattered radially.',
            Label: 'Chuck running hot',
          },
          cardOutOfPlane: {
            Evidence: [
              'Planarity 14 µm against a 6 µm limit.',
              'Map signature reads edge.',
              'Tip wear is inside limits, so this is not a contact-cleanliness problem.',
            ],
            Truth: 'The card is 14 µm out of plane, so one side of it lands light. Those die ' +
              'never make contact and are binned as failures. The signature is the ' +
              'giveaway: an out-of-plane card fails the EDGE, and an edge map is not what ' +
              'a defect population looks like.',
            Complaint: 'The maps have gone edge-heavy. Everyone is blaming the edge exclusion.',
            Label: 'Probe card out of plane',
          },
          wornTips: {
            Evidence: [
              'Touchdowns since last clean well past the 150,000 limit.',
              'Contact loss on the short standard is above 2%.',
              'The loss is on every product, which a wafer defect would not be.',
            ],
            Truth: 'The tips are past their touchdown limit and no longer breaking through pad ' +
              'oxide reliably. Die that are electrically fine are failing continuity — ' +
              'the loss is at the CONTACT, not on the wafer, and a contact-resistance ' +
              'standard says so directly.',
            Complaint: 'Yield down four points across every product on this cell since Tuesday.',
            Label: 'Probe tips worn',
          },
          probeHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'The map signature is uniform.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. Yield is where the model says it should be.',
            Label: 'Nothing wrong',
          },
        },
        NoteMmRingHToProbe: '{{a}} mm ring, {{b}} h to probe',
        NotePoissonWouldSay: 'Poisson would say {{a}}%',
        NoteCentreEdge: 'centre {{a}}%, edge {{b}}%',
        NoteGoodOfProbed: '{{a}} good of {{b}} probed',
        SectionReadTheMapNotThe: 'Read the map, not the number',
        NoteAMeanYieldOf78: 'A mean yield of 78% can be a uniform 78%, or 95% in the middle and dead at ' +
          'the edge, or a perfect wafer with a scratch across it. Three different ' +
          'problems with three different owners — and the mean cannot tell them ' +
          'apart.',
        InvBandEdge: 'Conduction band edge',
        InvFermi: 'Fermi level',
        InvInterface: 'oxide | silicon',
        InvLevel: '{{ladder}} level {{n}}',
        InvShape: 'Where each electron is (|ψ|²)',
        InvBandTitle: 'The well the gate digs, and its levels',
        InvBandX: 'depth from the oxide (nm)',
        InvBandY: 'electron energy (eV, bulk band edge = 0)',
        InvDensityTitle: 'Where the channel charge sits',
        InvDensityX: 'depth from the oxide (nm)',
        InvDensityY: 'electron density (10¹⁹ cm⁻³)',
        InvDensityQ: 'Quantum',
        InvDensityCl: 'Classical (Fermi–Dirac)',
        InvCentroid: 'centroid',
        InvCvTitle: 'What the probe card measures: gate capacitance',
        InvCvX: 'gate voltage (V)',
        InvCvY: 'capacitance (µF/cm²)',
        InvCvQ: 'Quantum',
        InvCvCl: 'Classical (Fermi–Dirac)',
        InvCox: 'the oxide alone',
        InvHere: 'this gate voltage',
        InvFailed: 'The channel solver failed to load.',
        InvStatus: 'φ_s = {{phis}} V · N_inv = {{ninv}} cm⁻² · charge centroid {{zavg}} nm off the oxide · effective oxide +{{dt}} nm · threshold shift +{{dvt}} mV',
        InvLoading: 'Loading the channel solver…',
        InvStatusDepleted: 'φ_s = {{phis}} V · no inversion yet: the gate has not pulled a channel to the surface · threshold shift +{{dvt}} mV',
        InvComputing: 'Solving the capacitance sweep and the threshold…',
        InvSweepFailed: 'The capacitance sweep could not be computed.',
        InvRight: 'Right chart',
        InvRightCv: 'Capacitance vs gate voltage',
        InvRightDensity: 'Charge vs depth',
        InvVg: 'Gate voltage',
        InvTox: 'Oxide thickness',
        InvNa: 'Substrate doping',
        SectionInversion: 'Why the threshold is not where the formula says — the channel as a quantum well (Schrödinger–Poisson)',
        InversionNote: 'Every threshold voltage this card measures is set a nanometre under the oxide by a fact the classical charge-sheet formula leaves out: the electrons a gate pulls to the surface cannot sit at the surface. The gate field bends the conduction band into a triangular well against the oxide, and a well that narrow allows only discrete levels, each with a wavefunction that is zero at the oxide and peaks about a nanometre in. This is that well solved for real: Poisson\'s equation across gate, oxide and substrate, Schrödinger\'s equation for the two valley ladders of (100) silicon, iterated until each agrees with the other. Left, the band diagram with its levels and the shape of each electron; right, what the probe card sees — the gate capacitance, quantum against a classical solve given the same Fermi–Dirac statistics, so the gap between the curves is quantisation alone. Neither reaches the oxide\'s own value, because a dense electron gas has a capacitance of its own; the quantum curve sits lower still because the charge each extra volt brings lands a few ångströms further from the oxide, and that standoff gives the size of the gap to within a third. The threshold shift in the status line is the difference between the two solves at the same sheet charge. The gate is n⁺ polysilicon treated as a metal; poly depletion, interface traps and the drain bias are left out, and holes are classical, so the accumulation side is not quantised here.',
        SectionShortChannel: 'Why the gate stopped shrinking — the barrier between source and drain, and the electrons that tunnel through it (transfer matrix, propagated packet)',
        ShortChannelNote: 'A transistor turns off by raising a barrier between its source and its channel, and the current that leaks while it is off is the tail of electrons with enough thermal energy to climb over: sixty millivolts of gate for each decade of current, and no thermal device does better. A long fin gets there: its body is fully depleted, so nothing shares the gate’s swing, and the model’s long gate swings at the thermal limit. This is what a short gate does to that barrier. The source and drain are electrodes too, and their influence reaches into the channel over a scale length set by the body and the oxide, so once the gate is only a few of those long it loses part of its grip and the drain lowers the barrier itself — the swing degrades past sixty and the threshold falls with drain bias. That threshold is read on a criterion corrected for the drain’s own back-injection, so a long gate’s DIBL is the fraction of a millivolt per volt it should be. The barrier also gets thin, and one two nanometres wide is transparent to electrons that cannot climb it at all. Both follow from one calculation: the electrostatics are the scale-length model (Young; Yan, Ourmazd and Lee for the double gate), and the transmission is the same transfer matrix THRM-04 runs over a gate oxide, close to one above the barrier and falling exponentially below it, so thermionic emission and tunnelling are one curve rather than two models. The wave on the left is that transmission propagated instead of computed. It is a long packet on purpose: a packet is a spread of energies, and only a long one is narrow enough to sit under the barrier top, so what comes out the far side — drawn again past the gate, magnified — is tunnelling, and falls by hundreds of times from a 10 nm gate to a 20 nm one. It still is not one energy, which is why three numbers are printed: what the packet delivered, the transfer matrix averaged over the packet’s own energies, and T at its mean energy alone. The first two agree to within one per cent up to a 20 nm gate and three at 40; the third can be a million times smaller. Pull the gate length down and watch the tunnelled share of the leak climb past half, the on/off ratio collapse, and the length past which the gate cannot turn the device off at all. Doping, in a fin this thin, moves the threshold by its body charge and leaves the swing alone, which is why real fins are left undoped. Scattering is left out, so the on-current is a ballistic upper bound; confinement across the body is the section above; and the absolute current rests on an assumed channel thickness, so the ratios are the lesson rather than the amps.',
        ScBandTitle: 'The barrier along the channel, and an electron meeting it',
        ScBandX: 'position along the channel (nm)',
        ScBandY: 'electron energy (eV, source band edge = 0)',
        ScBand: 'Conduction band edge',
        ScWave: 'Where the electron is (|ψ|²)',
        ScGate: 'gate',
        ScTop: 'barrier top',
        ScLaunch: 'launched at',
        ScTransferTitle: 'What the prober measures: current against gate voltage',
        ScTransferX: 'gate voltage (V)',
        ScTransferY: 'log₁₀ drain current (µA/µm)',
        ScTransferHigh: 'Drain at {{vd}} V',
        ScTransferLow: 'Drain at {{vd}} V',
        ScCriterion: 'threshold criterion',
        ScHere: 'this gate voltage',
        ScVt: 'threshold',
        ScScaleOffTitle: 'What scaling costs: off-current against gate length',
        ScScaleTunTitle: 'How much of the leak tunnelled',
        ScScaleX: 'gate length (nm)',
        ScScaleOffY: 'log₁₀ off-current (µA/µm)',
        ScScaleTunY: 'share below the barrier top (%)',
        ScScaleOff: 'Gate grounded',
        ScScaleTun: 'Tunnelled',
        ScThisGate: 'this gate',
        ScStatusShort: 'Barrier {{top}} meV · {{tun}}% of the current tunnels under it · {{current}} µA/µm',
        ScNeverOnShort: 'Never on: the threshold is above the {{max}} V the gate can reach · swing {{s}} mV/dec',
        ScPacketShort: '{{fs}} fs · through the gate {{through}} · transfer matrix {{matrix}}',
        ScStatus: 'Barrier {{top}} meV and {{width}} nm wide halfway up · scale length {{lambda}} nm · {{grip}}% of the gate’s swing reaches the barrier top · {{tun}}% of this current is under the top · {{current}} µA/µm here',
        ScMeasured: 'Swing {{s}} mV/dec · DIBL {{dibl}} mV/V · threshold {{vt}} V · off-current {{off}} µA/µm · on/off {{ratio}}',
        ScPunch: 'Punched through: the gate cannot turn this device off. It passes {{off}} µA/µm with the gate grounded, so it has no threshold, no swing and no threshold shift to report.',
        ScPacket: '{{fs}} fs · through the gate {{through}} · the transfer matrix over this packet’s energies gives {{matrix}}, and at its mean energy alone {{single}} · reflected {{back}} · still arriving {{coming}} · probability accounted {{norm}}',
        ScComputing: 'Solving the transfer curves, the swing and the threshold shift…',
        ScSweepFailed: 'The transfer curves could not be computed.',
        ScFailed: 'The short-channel solver failed to load.',
        ScLoading: 'Loading the short-channel solver…',
        ScNone: 'none',
        ScRun: 'Launch an electron',
        ScPause: 'Pause',
        ScReset: 'Reset',
        ScRight: 'Scaling chart',
        ScRightOff: 'Off-current',
        ScRightTun: 'Tunnelled share',
        ScLength: 'Gate length',
        ScVg: 'Gate voltage',
        ScVd: 'Drain voltage',
        ScTox: 'Gate oxide',
        ScBody: 'Body thickness',
        ScNa: 'Body doping',
        ScEnergy: 'Launch energy',
        ScEnergyValue: '{{ev}} ± {{spread}} meV, {{pct}}% of the barrier',
        ScEnergyNone: 'no barrier left to aim at — launched at {{ev}} meV',
        ScWavePast: 'Past the gate, magnified ×{{gain}}',
        ScStale: 'A knob moved: the dimmed curves are the previous device’s while this one’s are solved…',
        ScNeverOn: 'Never on: the threshold is above the {{max}} V the gate can reach. It passes only {{on}} µA/µm at full gate · swing {{s}} mV/dec, measured at the top of the range · off-current {{off}} µA/µm',
        ScNaValue: '{{na}} cm⁻³ · +{{shift}} mV of threshold',
        ScBenchDevice: 'The device',
        ScBenchBias: 'The bias, and the electron',
        SectionSortResult: 'Sort result',
        LabelSortYield: 'Sort yield',
        LabelMapSignature: 'Map signature',
        LabelDefectYield: 'Defect yield',
        LabelImpliedKillerDensity: 'Implied killer density',
        NoteInvertedFromTheYield: 'inverted from the yield',
        LabelContactYield: 'Contact yield',
        NoteLostToTheCardNot: 'lost to the card, not the wafer',
        TextProbeTheWaferAndPass: 'Probe the wafer and pass the good die on',
        AssistantLead: 'PRB-14 inquiry assistant',
        AssistantBlurb:
          'Ask why a bigger die yields worse, which yield model this line needs, or ' +
          'whether the loss is the wafer or the probe card. Every answer is a sort ' +
          'run against this lot.',
        reading: {
          chuckVacuum: {
            Limit: '> 60 kPa',
            Label: 'Chuck vacuum',
          },
          houseutilities: {
            Limit: 'all systems nominal',
            Label: 'Call facilities for the utility panel (0.5 h)',
          },
          contactResistance: {
            Label: 'Contact resistance check on a short standard (0.5 h)',
          },
          mapShape: {
            Limit: 'uniform or clustered',
            Label: 'Full wafer map, read the signature (1.5 h)',
          },
          gauge: {
            Label: 'Parametric gauge sigma',
          },
          chuckTemp: {
            Label: 'Chuck temperature offset',
          },
          planarity: {
            Label: 'Card planarity',
          },
          tipWear: {
            Label: 'Touchdowns since last clean',
          },
        },
        aspect: {
          house: {
            Action: 'Stop work on the tool and raise it with facilities',
            Label: 'Nothing on this tool — escalate to facilities',
          },
          gauge: {
            Action: 'Re-calibrate the parametric measurement unit and its cabling',
            Label: 'Parametric measurement chain',
          },
          chuck: {
            Action: 'Re-calibrate the chuck thermocouple and re-tune the loop',
            Label: 'Chuck temperature',
          },
          planarity: {
            Action: 'Re-shim the card and re-measure planarity across all four quadrants',
            Label: 'Card planarity',
          },
          tips: {
            Action:
              'Clean the tips on an abrasive plate, or replace the card if they are past ' +
              'dressing',
            Label: 'Probe tips',
          },
        },
      },
      litho: {
        ExposeLot: 'Expose the lot and send it to etch',
        NextHoldingIsTheEasyHalf: 'Holding is the easy half. Lots printed earlier this shift went through the ' +
          'same optics - what measurement would tell you whether any of them need to ' +
          'come back, and is that measurement still possible after the resist is ' +
          'developed?',
        NextASendAheadIsOneField: 'A send-ahead is one field on one wafer. What would you have to expose, and ' +
          'how many of them, before you would believe the number you just released ' +
          'the tool on?',
        PatternResolved: 'resolved',
        PatternUnresolved: 'DID NOT PRINT',
        LabelResolved: 'Pattern',
        LabelRoughness: 'Roughness, 3σ',
        LabelPrintedCd: 'Printed CD',
        VerdictWithAWatchItem: 'released, with a watch item',
        VerdictHeld: 'Held',
        VerdictQualified: 'Qualified',
        LabelVerdict: 'Verdict',
        HypAQualificationIsADecision: 'A qualification is a decision, not an explanation. If the send-ahead ' +
          'resolves, lands inside the CD window with roughness under its limit, and ' +
          'every bench reading is inside its own limit, the scanner releases. ' +
          'Otherwise it is held - and the qualified recipe on the same optics should ' +
          'say whether the recipe or the tool is responsible.',
        AskedIsThisScannerFit: 'Is this scanner fit to print product right now, and what do I do about it?',
        SumExposeTheSendAhead: 'Expose the send-ahead, read the bench against its limits, and make the call.',
        UnitNmCd: 'nm CD',
        NoteResolutionImprovesLinearly: 'Resolution improves linearly with NA and the focus budget falls at ' +
          'least as fast as its SQUARE — faster, once the angles stop being small, ' +
          'which at these apertures they have. So High-NA buys 1.7× in half-pitch ' +
          'and gives back 2.9× in depth of focus, on a tool that had 72 nm to begin ' +
          'with. That is why CMP-11 exists. And the anamorphic optics that make NA ' +
          '0.55 possible at all halve the field along the scan, so a 26×32 mm die ' +
          'now takes two exposures and a stitch. The photon energy does not change, ' +
          'so the stochastics that were already the limiter are exactly as bad.',
        ValueFieldsStitched: '{{a}} — stitched',
        ValueFields: '{{a}} field',
        HeadPerDie: 'per die',
        HeadField: 'field',
        HeadFocusBudget: 'focus budget',
        HeadHalfPitch: 'half-pitch',
        NotePresetEuvFine: 'Switch the source to EUV as well. Prints, but on a fraction of the photons.',
        NotePresetTightPitch: 'Past what conventional illumination handles well: contrast 0.57, and the ' +
          'window shrinks.',
        NotePresetUnderdosed: '20 mJ/cm². The line prints far too wide — less light removes less resist.',
        NotePresetFocusExcursion: '90 nm of defocus. Contrast falls to 0.42 and the line goes badly off size.',
        NotePresetBaseline: 'Dosed to size at 180 nm pitch, best focus. Contrast 0.97, NILS 7.1, LER ' +
          '2.4 nm.',
        LabelUnderdosed: 'UNDERDOSED',
        SecondaryLabel: 'LER 3 sigma',
        ToolLabel: 'LITH-03 · Immersion scanner and track',
        NextIfDoseCannotFixRoughness: 'If dose cannot fix roughness on its own, what else is left? Consider what ' +
          'the post-exposure bake does to an edge, and what it costs you at the same ' +
          'time.',
        AxisYLerNm3: 'LER (nm, 3σ)',
        AxisXDoseMjCm: 'dose (mJ/cm²)',
        TitleRoughnessAgainstDoseAndThe: 'Roughness against dose — and the floor it cannot cross',
        LabelFailuresIn300: 'Failures in 300',
        NoteDoseOnlyBuysBackThe: 'dose only buys back the photon term',
        LabelAtTwiceTheDose: 'At twice the dose',
        LabelCdSpread3: 'CD spread (3σ)',
        HypDoseIsDeliveredAsA: 'Dose is delivered as a finite number of photons. If the edge position is ' +
          'decided by counting them, the count fluctuates as the square root of ' +
          'itself, and the edge should move by an amount that falls as dose rises - ' +
          'but never to zero, because the resist itself is also made of a finite ' +
          'number of molecules.',
        AskedWhyDoesTheSameExposure: 'Why does the same exposure, repeated, not print the same line twice?',
        SumPhotonStatisticsLineEdgeRoughness: 'Photon statistics, line edge roughness, and why EUV changed the rules.',
        NextEverythingAboveUsedConventionalIllumination: 'Everything above used conventional illumination. Off-axis illumination ' +
          'throws away the centre of the source and keeps only the angles that still ' +
          'form an image at tight pitch. What would that do to the numbers above, and ' +
          'what would it cost?',
        LabelProductionMinimum: 'production minimum',
        AxisYDepthOfFocusNm: 'depth of focus (nm)',
        AxisXPitchNm: 'pitch (nm)',
        TitleDepthOfFocusAgainstPitch: 'Depth of focus against pitch',
        LabelManufacturable: 'Manufacturable',
        LabelDoseLatitude: 'Dose latitude',
        LabelDepthOfFocus: 'Depth of focus',
        DesignFocusSwept140NmAgainst: 'Focus swept ±140 nm against dose swept ±40%, at three pitches. Counted ' +
          'where CD stays inside ±10% of target.',
        HypAProcessWindowIsThe: 'A process window is the overlap of two tolerances, not one. If the window ' +
          'is wide, the feature will survive an imperfectly flat wafer and a drifting ' +
          'source; if it is narrow, the feature can print beautifully in a test and ' +
          'still fail in production.',
        AskedHowMuchFocusAndDose: 'How much focus and dose error can this feature absorb before it goes out ' +
          'of spec?',
        SumMeasureDepthOfFocusAnd: 'Measure depth of focus and dose latitude, and say whether it is ' +
          'manufacturable.',
        LabelReturnToBestFocus: 'Return to best focus',
        TitleCdAgainstFocusAtThree: 'CD against focus at three doses',
        LabelNilsAsSet: 'NILS as set',
        LabelDiagnosis: 'Diagnosis',
        LabelDoseSensitivity: 'Dose sensitivity',
        HypDoseAndFocusLeaveDifferent: 'Dose and focus leave different fingerprints. Dose moves CD monotonically - ' +
          'more light always thins a line - while focus is symmetric about best ' +
          'focus, so it moves CD the same way whichever side you are on. If CD ' +
          'responds asymmetrically to focus, the tool is not at best focus; if it ' +
          'responds only to dose, it is a dose error.',
        AskedTheCdIsNotOn: 'The CD is not on target. Which of the two knobs is responsible?',
        SumSeparateADoseErrorFrom: 'Separate a dose error from a focus error, which look alike on a CD reading.',
        fault: {
          warmPcwLitho: {
            Evidence: [
              'CD is wide with contrast, NILS and LER all nominal - the dose signature.',
              'The dose meter cross-check is CLEAN and so is the resist contrast curve. ' +
                'Both of the usual suspects have been ruled out, which is the point at ' +
                'which most people start guessing.',
              'The house utility check shows cooling water well above supply temperature.',
              'The etcher is slow at the same time, for what looks like a completely ' +
                'different reason. It is not a different reason.',
            ],
            Truth: 'The chilled water plant is delivering supply water about 22 C above ' +
              'nominal. Nothing on any tool is broken; every tool that rejects heat into ' +
              'that loop has simply stopped being able to hold its own setpoint. The ' +
              'scanner feels it through its laser: an excimer source is water-cooled and ' +
              'derates as its coolant warms, so it delivers about three quarters of the ' +
              'dose the meter believes it delivered. The meter is not lying - it is ' +
              'measuring the pulse energy it was told to expect, and the tool has no way ' +
              'to know its own water is warm.',
            Complaint: 'Lines printing wide, about 7%. Looks like the source problem in the ' +
              'spring, but the dose meter cross-check came back clean and so did the ' +
              'resist contrast curve. ETCH-01 is running slow at the same time and nobody ' +
              'thinks the two are connected.',
            Label: 'Cooling water running warm',
          },
          hotPlate: {
            Evidence: [
              'LER is BELOW its band. That is not good news, it is a signal - nothing ' +
                'else on this tool makes an edge smoother than the qualified process.',
              'Image contrast and NILS are untouched, so the optics are fine. Blur ' +
                'happens after the exposure, not during it.',
              'The plate thermocouple survey reads well above setpoint.',
            ],
            Truth: 'The post-exposure bake plate is running hot, so acid diffuses four times ' +
              'as far during the bake. Blur cuts both ways and that is the whole lesson: ' +
              'it smooths the acid profile, which genuinely improves line edge roughness, ' +
              'and it also washes out the profile, which shrinks the line. The tool is ' +
              'producing better-looking edges on out-of-spec features.',
            Complaint: 'CD has been trending narrow for a week. Odd thing: the line edges look ' +
              'BETTER than usual.',
            Label: 'Bake plate running hot',
          },
          focusDrift: {
            Evidence: [
              'Image contrast and NILS are both well down. No dose or resist fault can do ' +
                'that - they change where the threshold is crossed, not how sharply the ' +
                'image crosses it.',
              'LER is UP, which follows from the lost NILS.',
              'A through-focus exposure finds best focus about 35 nm from where the tool ' +
                'thinks it is.',
            ],
            Truth: 'The levelling sensor has drifted, so the wafer sits about 35 nm off the ' +
              'focal plane while the tool believes it is at best focus. Defocus destroys ' +
              'the aerial image before it touches anything else: contrast falls from 0.97 ' +
              'to 0.80 and NILS with it, and a soft image prints a narrow, rough line.',
            Complaint: 'CD is running narrow and the SEM images look soft at the line edges.',
            Label: 'Levelling sensor offset',
          },
          weakResist: {
            Evidence: [
              'CD is wide with contrast, NILS and LER all nominal - indistinguishable at ' +
                'the tool from a source that has lost output.',
              'The dose meter cross-check comes back CLEAN. That is the reading that ' +
                'decides it, and it decides it by ruling the other one out rather than by ' +
                'finding anything.',
              'A contrast curve on a monitor wafer shows gamma about half of the ' +
                'qualified value.',
            ],
            Truth: 'The resist lot has lost about half its contrast - it has been on the shelf ' +
              'too long. A lower-contrast resist dissolves more gradually through the ' +
              'acid gradient, so the edge lands further out and the line prints wide. The ' +
              'image is perfect; the material is not.',
            Complaint: 'Lines printing wide again, just like the source problem in the spring. ' +
              'Dose was re-calibrated last week.',
            Label: 'Resist lot out of contrast spec',
          },
          sourceDrift: {
            Evidence: [
              'CD is wide and everything else at the tool is nominal. Contrast, NILS and ' +
                'LER are untouched, so the optics and the bake are not involved.',
              'A degraded resist lot looks EXACTLY the same on every reading available at ' +
                'the tool. Separating them takes the dose meter cross-check, and nothing ' +
                'else will do it.',
              'The cross-check against a reference detector shows the delivered dose well ' +
                'below setpoint.',
            ],
            Truth: 'The excimer source has aged and now delivers about 80% of the dose the ' +
              'meter believes it is delivering. Less acid is generated, the development ' +
              'threshold is crossed further out along the image, and the line prints ' +
              'wide. Nothing about the IMAGE has changed - contrast, NILS and roughness ' +
              'are all exactly nominal - which is what makes this so hard to see.',
            Complaint: 'Lines are printing about 6% wide across the whole wafer. Same recipe, same ' +
              'reticle, and the tool says it delivered exactly the dose we asked for.',
            Label: 'Source output fallen',
          },
          healthy: {
            Evidence: [
              'CD is on target, contrast and roughness are both at the qualified values.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Tool passed its daily CD check. Nothing reported.',
            Label: 'Scanner qualified',
          },
        },
        TextNmOfIncomingTopographyCd: '{{a}} nm of incoming topography → CD spreads ',
        LabelBaseline193i: 'BASELINE-193i',
        LabelFocusExcursion: 'FOCUS EXCURSION',
        LabelTightPitch110Nm: 'TIGHT PITCH 110 nm',
        LabelEuv36NmPitch: 'EUV 36 nm PITCH',
        LabelHighNaEuv: 'High-NA EUV',
        reading: {
          resistlot: {
            Label: 'Resist contrast curve on a monitor (4 h)',
          },
          plateprofile: {
            Label: 'Bake plate thermocouple survey (90 min)',
          },
          focusprobe: {
            Label: 'Through-focus exposure on a monitor wafer (3 h)',
          },
          houseutilities: {
            Limit: 'header tight, PCW at supply, dry gas < 50 ppm',
            Label: 'House utility check with facilities (2 h)',
          },
          dosemeter: {
            Label: 'Dose meter cross-check, reference detector (2 h)',
          },
          setpoint: {
            Limit: 'as recipe',
            Label: 'Dose setpoint / focus setpoint',
          },
          nils: {
            Label: 'Normalised image log slope',
          },
          contrast: {
            Label: 'Aerial image contrast',
          },
          ler: {
            Label: 'Line edge roughness, 3 sigma',
          },
          cd: {
            Label: 'Printed CD (SEM)',
          },
        },
        aspect: {
          house: {
            Action: 'Stop work on the tool and raise it with facilities',
            Label: 'Nothing on this tool — escalate to facilities',
          },
          resist: {
            Action: 'Pull the lot, run a contrast curve, dispense from a fresh bottle',
            Label: 'Resist lot',
          },
          bakeplate: {
            Action: 'Survey the plate thermocouples and re-level the proximity pins',
            Label: 'Post-exposure bake plate',
          },
          stage: {
            Action: 'Re-qualify the levelling sensor against a through-focus wafer',
            Label: 'Levelling and focus',
          },
          source: {
            Action: 'Cross-check the dose meter, re-calibrate the source output',
            Label: 'Exposure source and dose control',
          },
        },
        SectionLot: 'The lot on the chuck',
        LotNote:
          'A scanner focuses on one plane, and the wafer brings its own surface with ' +
          'it. Whatever CMP-11 failed to flatten is spent here, directly out of the ' +
          'CD budget — on a scanner that is working perfectly. This is where a CMP ' +
          'problem is first noticed, two tools later.',
        LotEmpty: 'No lot on the chuck. The knobs above expose an ideally flat wafer.',
        LotIngot: 'This lot is still an ingot. WPR-08 has to slice it before it can be exposed.',
        LotCard: 'Travel card',
        SectionPrinted: 'Printed result',
        SectionSource: 'Source',
        SourceDuv: '193 nm immersion (NA 1.35)',
        SourceEuv: 'EUV 13.5 nm (NA 0.33)',
        SourceNote:
          'Changing the source changes both what can be resolved and how many photons ' +
          'a given dose delivers. Those two consequences pull in opposite directions, ' +
          'which is the whole argument about EUV.',
        SectionAerial: 'Aerial image and resist response',
        SectionAerialWave: 'Where the image comes from — two orders interfering (wave optics)',
        AerialWaveNote:
          'The chart above is built from a formula. This is the thing the formula is about. Two ' +
          'coherent beams — the +1 and −1 diffraction orders the lens has caught — cross at the ' +
          'wafer plane, and the brightness is the time-averaged square of the wave field, which ' +
          'is what a resist integrates. Nothing here computes an image: two line sources radiate, ' +
          'the solver propagates, and the stripes are where the two beams add and cancel. Their ' +
          'spacing is the pitch being printed. Press Run and watch the beams arrive and the ' +
          'fringes assemble; the picture is stationary from the moment they meet, because a ' +
          'standing pattern is what two crossing waves make. Then switch the source above. A ' +
          'bigger numerical aperture is nothing but a steeper angle between the two orders, and ' +
          'a steeper angle is a finer fringe — which is the whole of λ / 2NA, happening rather ' +
          'than being asserted.',
        AerialWaveImmersion:
          'And this is what the water is for. The index of the medium enters as the wave speed, ' +
          'with the frequency left alone, because that is what happens to light crossing into a ' +
          'liquid: the frequency is kept and the wavelength shortens. So the same angle prints ' +
          'finer lines under water. But the real prize is the one the arithmetic gives away — ' +
          'NA is n sin θ, and a sine cannot exceed one, so a dry lens can never reach NA 1, ' +
          'however it is built. An immersion scanner at 1.35 is not a better lens. It is the ' +
          'same geometry in a medium where the angle exists.',
        AerialWaveCaveat:
          'Three honest limits. The box is six wavelengths across whatever the wavelength is, ' +
          'because 193 nm and 13.5 nm cannot share a grid — so the picture is in wavelengths and ' +
          'the readouts convert to nanometres using the source you selected. The sources are six ' +
          'wavelengths long rather than infinite, so the beams diffract at their own edges and ' +
          'the measured period lands within about three per cent rather than exactly; at the ' +
          'lowest numerical aperture the box only holds a couple of fringes to measure. And this ' +
          'is deliberately the TWO-beam case — the finest pitch an aperture can print, which is ' +
          'the k₁ = 0.25 limit the panel’s resolution number assumes. A production image at a ' +
          'relaxed pitch carries the zeroth order too, and that third beam is what the chart ' +
          'above computes and this one leaves out.',
        ImageWaferPlane: 'wafer',
        ImageAngle: 'orders at',
        ImageHalfPitch: 'half-pitch',
        ImageVersus: 'vs',
        ImageContrast: 'contrast',
        ImageAxisX: 'across the wafer (nm)',
        ImageAxisY: 'relative intensity',
        ImageLabelSolved: 'what the wave solver printed',
        ImageLabelIdeal: 'λ / 2NA, the period two beams owe',
        ImageVerdict:
          'The solver printed a {{measured}} nm half-pitch from an angle alone. The tool’s ' +
          'resolution limit, 0.25 λ / NA, says {{limit}} nm. {{dry}}',
        ImageDryImpossible:
          'In air there is no angle that would do this at all: sin θ would have to exceed one. That is what the water is for.',
        ImageDryAngle: 'In air the same aperture would need ±{{angle}}°.',
        SectionStack: 'What happens when it lands — standing waves in the resist',
        StackNote:
          'A wafer is not a screen. Light that reaches it goes INTO the resist, crosses it, ' +
          'reflects off whatever is underneath, and comes back up through resist it has ' +
          'already exposed. The wave going down and the wave coming back are two halves of ' +
          'the same beam, so they are coherent, and they interfere: the resist is developed ' +
          'not by a uniform beam but by a standing wave, bright and dark planes stacked ' +
          'through its depth. Those planes are why a developed resist sidewall is scalloped ' +
          'rather than smooth, and you can see them in the canvas — the vertical stack of ' +
          'blobs that the aerial image alone would have drawn as clean stripes.',
        StackNoteSwing:
          'Now drag the thickness. The resist is an optical cavity, so whether the round trip ' +
          'comes back in phase depends on how thick it is, and the dose the film absorbs ' +
          'swings up and down as you go — which means the line width does too. That is the ' +
          'swing curve, and it is why resist thickness is a specification held to a few ' +
          'nanometres rather than a preference, and why a coat track cares about spin speed ' +
          'to a few rpm. Then switch the anti-reflective coating in. The ripples collapse and ' +
          'the swing curve goes flat, because there is no longer a reflected wave for the ' +
          'incoming one to interfere with. That is the entire job of the layer, and it is the ' +
          'same trick as the coating on a camera lens.',
        StackNoteCaveat:
          'Three honest limits. This section is pinned to DUV immersion rather than following ' +
          'the source selector, because the story is about a resist a fraction of a wavelength ' +
          'thick and 193 nm and 13.5 nm cannot both be that. The substrate is not silicon: a ' +
          'scalar wave model cannot hold an index like silicon’s 0.88 − 2.78i at 193 nm, so ' +
          'it is a high-index absorbing stand-in with a comparable reflectance, and the exact ' +
          'stack drawn beside the solver uses the same stand-in so that the two are answering ' +
          'the same question. And the solved pattern sits within one grid cell — about six ' +
          'nanometres — of where the exact solution puts it, which is what a sharp interface ' +
          'costs on a hundred-cell grid; the period, the contrast and the shape are what the ' +
          'comparison is about, so the exact curve is drawn anchored to the measured one.',
        StackResistTop: 'resist',
        StackBarc: 'coating',
        StackSubstrate: 'substrate',
        StackResist: 'resist',
        StackRipple: 'ripple',
        StackCoated: 'coated',
        StackAxisDepth: 'depth into the resist (nm)',
        StackLabelExact: 'the exact film stack',
        StackAxisThickness: 'resist thickness (nm)',
        StackAxisDose: 'mean dose in the resist',
        StackLabelBare: 'on bare substrate',
        StackLabelCoated: 'with the anti-reflective coating',
        StackLabelHere: 'the solver, at this thickness',
        StackThickness: 'Resist thickness',
        StackBare: 'Bare substrate',
        StackWithBarc: 'With anti-reflective coating',
        StackVerdict:
          'The solver laid down bright and dark planes {{measured}} nm apart. Two waves ' +
          'crossing a film beat at half the wavelength they have in it, measured down the ' +
          'depth axis, which for this resist at this angle is {{predicted}} nm. Note that it ' +
          'is not {{normal}} nm: the orders arrive seventy degrees off vertical, and the ' +
          'cosine of that is worth sixty per cent of the answer. The same period is the one ' +
          'the swing curve repeats at, because they are the same physics — a cavity that ' +
          'comes back into phase every time you add half an internal wavelength of resist.',
        StackThin:
          'This film is too thin to hold a ripple worth measuring — there is barely one dark ' +
          'plane in it. Drag the thickness up and the period becomes something the solver ' +
          'can be asked about.',
        SectionFocus: 'Where depth of focus comes from — the third beam',
        FocusNote:
          'The scene two sections up crosses two beams, and two beams have no focus dependence ' +
          'at all: move the wafer along the axis and both orders pick up exactly the same ' +
          'phase, so the pattern they make is the same pattern everywhere. Every Bossung curve ' +
          'on this panel therefore comes from somewhere else, and this is it — the ZEROTH ' +
          'order, the undiffracted light that goes straight through. It travels down the axis ' +
          'while the first orders travel at an angle, so the three of them drift out of step ' +
          'as they go, and the image they make rises, falls to nothing, and returns with the ' +
          'lines and the spaces swapped. Watch the stripes in the canvas pinch to half their ' +
          'spacing partway down: that is the moment the zeroth order has fallen a quarter ' +
          'cycle behind and what is left is the two first orders alone.',
        FocusNoteDipole:
          'Then take the zeroth order out. The curve goes flat — not small, flat — and so does ' +
          'the picture. That is the entire case for off-axis and dipole illumination: an ' +
          'image built from two beams at the edge of the pupil has no depth-of-focus limit, ' +
          'because there is no third beam to fall out of step with. What it costs is ' +
          'generality, since the two beams are placed for one pitch and everything else on ' +
          'the mask prints worse.',
        FocusBest: 'best focus',
        FocusGone: 'image gone',
        FocusPitch: 'pitch',
        FocusThree: 'three orders',
        FocusTwo: 'two orders',
        FocusVanishes: 'image gone by',
        FocusNoDof: 'no focus dependence at all',
        FocusAxisDepth: 'depth below best focus (nm)',
        FocusAxisImage: 'image at the printed pitch',
        FocusLabelExact: 'cos of the exact defocus phase',
        FocusLabelParaxial: 'the textbook paraxial phase',
        FocusLabelHalf: 'the half-pitch component',
        FocusAllThree: 'All three orders',
        FocusJustTwo: 'Zeroth order removed (dipole)',
        FocusVerdictThree:
          'The image falls to nothing {{null}} nm below best focus and comes back inverted — ' +
          'lines where the spaces were. That is depth of focus: not a blurring, but a ' +
          'contrast that oscillates, and it is the Bossung curve the panel above draws from ' +
          'a formula. Note how far the dashed paraxial curve has drifted from the solved ' +
          'one by the bottom of the box; at seventy degrees in water, π z λ / P² is not the ' +
          'defocus phase.',
        FocusVerdictTwo:
          'With the zeroth order gone the curve is a flat line at zero and the half-pitch ' +
          'component below it does not move — the image is the same image at every depth in ' +
          'the box. Two beams have no focus dependence at all, because moving the wafer ' +
          'gives both of them exactly the same extra phase. This is the whole argument for ' +
          'dipole and other off-axis illumination: throw away the order that travels ' +
          'differently and the depth of focus stops being a constraint, at the price of ' +
          'printing one pitch well and the rest badly.',
        SectionMask: 'Where the lines stop — the image in two dimensions',
        MaskNote:
          'Every optical scene above images a line/space grating, which is a one-dimensional ' +
          'object: it varies across the lines and is the same forever along them. That is the ' +
          'right object for the resolution limit and the wrong one for the question a mask ' +
          'designer actually spends the day on, which is what happens where the lines STOP. ' +
          'Line ends pull back. Corners refuse to print square. Contact holes never gather the ' +
          'light a large opening does. None of it appears in a cross-section, because none of ' +
          'it happens in one dimension — so this one is a surface, and you can turn it.',
        MaskNoteWhy:
          'A printed edge is a contour of this surface at the dose the resist clears at, which ' +
          'is the sheet cut through it, and the flat outline underneath is the mask as drawn. ' +
          'The gap between the two is what optical proximity correction is paid to close, and ' +
          'the reason it cannot be closed with dose is visible in the shape: dose raises or ' +
          'lowers the sheet, which moves every edge at once, while the corner and the line end ' +
          'are wrong by different amounts and in different directions. Drag the focus offset ' +
          'and watch the surface slump — the line end goes first, because it was already the ' +
          'part of the image with the least contrast to spare.',
        MaskDrag:
          'Drag to turn it. The flat outline is the mask as drawn; the sheet is where the resist clears.',
        MaskEnds: 'Two line ends',
        MaskHoles: 'Contact holes',
        MaskElbow: 'A corner',
        MaskDefocus: 'Focus offset',
        MaskSigma: 'Partial coherence σ',
        MaskReadEnds:
          'Drawn gap {{drawn}} nm; each end pulls back {{pull}} nm, so the gap prints ' +
          '{{printed}} nm — {{pct}}% wider than drawn.',
        MaskReadHoles:
          'Drawn 110 nm across; the centre hole peaks at {{peak}} of clear field and prints ' +
          '{{width}} nm. A hole never gathers the light a large opening does, which is why ' +
          'contact layers are dosed on their own.',
        MaskReadElbow:
          'The arm prints {{width}} nm wide against 100 nm drawn, and the corner does not print ' +
          'square at all: the outside rounds off and the inside fills in. No change of dose ' +
          'fixes that, because dose moves every edge together.',
        SectionPeb: 'The bake in between — what a blur length does to a shape (acid diffusion)',
        PebNote:
          'The recipe above carries a post-exposure bake as one number, a diffusion length, and the ' +
          'aerial image model convolves with it in a single line. Here is the bake that number ' +
          'stands for: photoacid, made where the light landed, spreading through the resist while ' +
          'the plate is hot. The picture is a PLAN view — looking down at the wafer rather than ' +
          'through it — because that is the only way to see what a blur length cannot tell you. ' +
          'The yellow line is where the developer will stop, and it is drawn at the level where a ' +
          'long straight edge prints exactly on size. Watch that edge: it does not move, however ' +
          'long the bake runs. Now watch everything else. The outer corner of the elbow rounds ' +
          'inward, the inner corner fills outward, and the two line ends pull back from each ' +
          'other until the gap between them is visibly wider than it was drawn.',
        PebNoteWhy:
          'Why those three and not the straight edge: a blurred edge is a balance between acid ' +
          'leaving and acid arriving, and along a straight edge the two match exactly. At a ' +
          'convex corner the exposed side is missing a quadrant, so more leaves than arrives and ' +
          'the contour retreats. At a concave corner the reverse, so it advances. At the end of a ' +
          'line, acid leaks in three directions at once, and once the blur is comparable to the ' +
          'linewidth the end gives up and walks backwards — which is why the gap here barely ' +
          'moves at a few nanometres of blur and then opens quickly. None of it is a dose error. ' +
          'Dose moves every edge the same way, so it can trade a line against a space and never ' +
          'put a corner back. That is the entire reason optical proximity correction exists, and ' +
          'why its serifs go on outside corners and its cuts on inside ones.',
        PebNoteCaveats:
          'Three honest limits. The acid starts as the mask itself, with edges no lens would ' +
          'deliver, so what you are watching is the bake’s contribution alone rather than the ' +
          'bake stacked on the optics — the section above owns that half. Nothing here develops: ' +
          'the contour is where the developer would stop, not resist dissolving. And the frame is ' +
          '300 nm across a hundred cells, so a blur below about three nanometres is finer than the ' +
          'grid and the picture stops resolving it, which is roughly where a real bake’s ' +
          'contribution stops mattering beside the optics anyway.',
        PebBlur: 'blur',
        PebBake: 'a',
        PebBakeAt: 's bake at',
        PebCorner: 'corner rounds in',
        PebGap: 'gap opens',
        PebOver: 'OVER-BAKED past the recipe',
        PebVerdict:
          'At {{blur}} nm of blur the straight edge has not moved at all, and everything else has: the ' +
          'outer corner has rounded {{corner}} nm inward, the inner corner has filled {{notch}} nm ' +
          'outward, and the gap between the two line ends has opened by {{gap}} nm. A dose change ' +
          'moves every edge together and cannot undo any of it. That is the whole case for optical ' +
          'proximity correction — and, on the plate itself, the reason a bake is specified to a ' +
          'fraction of a degree: near an electronvolt of activation energy, one degree is about ' +
          '{{perDegree}}% of the blur.',
        AerialNote:
          'The upper trace is the light landing on the wafer, built from the ' +
          'diffraction orders the lens could catch. The lower one is the acid left ' +
          'after the post-exposure bake. The line is whatever survives development, ' +
          'and its width is the CD.',
        SectionBossung: 'Bossung curves — CD against focus, one line per dose',
        BossungNote:
          'The measurement a lithographer lives by. Flat curves mean focus does not ' +
          'matter much; curves that fan apart mean dose and focus interact, and the ' +
          'usable window is where they all stay inside the CD tolerance at once.',
        MeasureBossung: 'Measure Bossung curves',
        SectionWindow: 'Focus-exposure window',
        WindowNote:
          'For each focus, the range of dose that still prints in spec. The wafer is ' +
          'never perfectly flat and the scanner never perfectly stable, so a process ' +
          'is only usable if this region is wide enough to absorb both.',
        MeasureWindow: 'Measure the window',
        SectionStochastics: 'Stochastics — print the same thing 300 times',
        PhotonCurveEuv: 'EUV scanner, 92 eV photons',
        PhotonCurveArf: 'ArF scanner, 6.4 eV photons',
        PhotonCurveSame: 'The EUV image made of ArF photons',
        PhotonCurveHere: 'This wafer',
        PhotonChartTitle: 'Edge roughness against dose',
        PhotonChartX: 'dose (mJ/cm²)',
        PhotonChartY: 'line-edge roughness, 3σ (nm)',
        PhotonStatus: '{{energy}} eV photons · {{perNm2}} per nm², {{perPixel}} per pixel · LER {{ler}} nm · line {{width}} nm · {{defects}} defect rows',
        PhotonPicImage: 'What the lens makes',
        PhotonPicCounts: 'The photons that arrive',
        PhotonPicPrint: 'What develops, with the drawn edges',
        PhotonSource: 'Whose photons',
        PhotonChoiceEuv: 'EUV scanner',
        PhotonChoiceArf: 'ArF immersion scanner',
        PhotonChoiceSame: 'The EUV image, ArF photons',
        PhotonDose: 'Dose',
        PhotonBlur: 'Resist reach (acid, secondary electrons)',
        PhotonReroll: 'Expose another wafer',
        SectionPhotons: 'What the image is made of — photons, counted (Poisson statistics on the aerial image)',
        PhotonsNote: 'Every image on this panel is an average. A resist never gets the average: it gets a finite number of photons, each absorbed here or not here, and the count that lands in the few nanometres deciding an edge is a Poisson draw with mean N and spread √N. Dose is energy, and a 92 eV EUV photon carries fourteen times the energy of a 6.4 eV ArF photon, so the same dose in mJ/cm² arrives as fourteen times fewer quanta. Here the panel\'s own Fourier imager makes the aerial image of 60 nm lines on a 120 nm pitch, the image is delivered pixel by pixel as a seeded Poisson count, the count is blurred by the resist\'s own reach and developed at the usual threshold, and the printed edges are measured row by row. The roughness falls as the square root of the dose. Scanner against scanner the ArF edge is the rougher one at this pitch, because its lens gives the shallower edge and slope buys back what photons lose — so the dashed curve isolates the photon: the same EUV image made of ArF quanta, √14 smoother. The photons counted are the incident ones; absorption and acid yield cut the effective count further, so the real EUV case is worse than shown.',
        StochasticsNote:
          'Every exposure below uses identical settings. Dose is energy, but printing ' +
          'is counting: a finite number of photons lands in each feature, and at ' +
          'small dimensions that count fluctuates enough to move the edge. The spread ' +
          'is the yield limit.',
        RunExposures: 'Run 300 exposures',
        SectionRecipe: 'Exposure recipe',
        DoseToSize: 'Dose to size',
        SectionStored: 'Stored recipes',
        SectionResist: 'Why the resist answers this wavelength — a photon lifting an electron (PPP-CIS)',
        ResistChartTitle: 'What the molecule absorbs, against the lines a fab exposes with',
        ResistChartX: 'wavelength (nm)',
        ResistChartY: 'absorbance, relative',
        ResistStatus: '{{formula}}, {{bonds}} double bonds · absorbs strongest at {{lambda}} nm · answers {{lines}}',
        ResistBlind: 'none of them — this molecule is transparent to every one',
        ResistConjugation: 'Conjugation length',
        ResistBonds: '{{n}} double bonds · {{formula}}',
        ResistFailed: 'The molecular spectroscopy engine failed to load.',
        ResistLoading: 'Loading the molecular spectroscopy engine…',
        ResistNote: 'Everything above is optics: orders collected, an image formed, a dose delivered. All of it assumes the one step that is not optics at all - that when the photon arrives, the resist does something. What it does is lift an electron out of the molecule\'s highest filled orbital into its lowest empty one, and a molecule can only absorb a photon whose energy matches that gap. Too red and the light goes straight through a film that might as well be glass. The gap is set by how far the electron can spread: a π electron in a conjugated chain is a particle in a box whose length is the conjugation, and a longer box has closer levels. Drag the chain out and watch the band march from the deep ultraviolet into the visible, passing each exposure line in turn. That is why a sensitizer built for the i-line at 365 nm is transparent at 193 nm and useless there, and why every step down the spectrum meant new resist chemistry rather than a new lamp.',
        ResistWhatNote: 'THESE ARE NOT PHOTORESISTS. They are all-trans polyenes - a plain carbon chain with alternating double and single bonds - and a real resist is a novolac resin with a diazonaphthoquinone sensitizer, or an acrylate with a photoacid generator: large molecules whose chemistry this model does not carry. What the polyene series does carry exactly is the relationship the panel needs, conjugation length against absorption wavelength, in the cleanest form it comes in. Note that no chain here lands on ArF at 193 nm: the short ones absorb below it and the long ones above, which is a fair picture of why 193 nm needed a different family of chromophore rather than a shorter version of the old one. The engine is AIMS\'s PPP-CIS, and it puts ethene at 161 nm against a measured 165.',
        SectionShift: 'Shift',
        TilePrintedCd: 'Printed CD',
        DidNotPrint: 'did not print',
        NoteTarget: 'target {{nm}} nm',
        TileCdError: 'CD error',
        NoteSpec: '±10% spec',
        K1BelowLimit: 'below the physical limit',
        K1Aggressive: 'aggressive',
        K1Comfortable: 'comfortable',
        TileContrast: 'Image contrast',
        NoteNils: 'image log slope at the edge',
        TileLer: 'Line edge roughness',
        TilePhotons: 'Photons delivered',
        TilePhotonEnergy: 'Photon energy',
        TileWavelengthNa: 'Wavelength / NA',
        NotMeasured: 'Not measured yet',
        ChartAerialTitle: 'Light on the wafer, and the acid it leaves behind',
        ChartPosition: 'position across one pitch (nm)',
        ChartNormalised: 'normalised',
        SeriesAerial: 'aerial image',
        SeriesAcid: 'acid after bake',
        AnnDevelop: 'develop threshold',
        ChartBossungTitle: 'CD against focus at several doses',
        ChartFocusOffset: 'focus offset (nm)',
        ChartCd: 'CD (nm)',
        AnnTarget: 'target',
        ChartWindowTitle: 'Dose latitude against focus — DOF {{dof}} nm',
        ChartDoseLatitude: 'dose latitude (%)',
        SeriesDoseLatitude: 'dose latitude',
        AnnNeeded: '5% needed in production',
        ChartStochTitle: '300 identical exposures — sigma {{sigma}} nm',
        ChartStochFailures: ', {{n}} failed outright',
        ChartPrintedCd: 'printed CD (nm)',
        ChartCount: 'count',
        SeriesExposures: 'exposures',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'Two numbers reach the wafer here: dose and focus. Neither one sets the CD ' +
          'directly. Ask me how they get there.',
      },
      clean: {
        TransportSurface: 'at the wafer',
        TransportStarved: 'starved by',
        SectionTransport: 'What a bath can run short of — two resistances in series (mass transport)',
        TransportNote: 'The chemistry has to reach the wafer before it can do anything, and here the wafer consumes ' +
          'it faster than a still bath resupplies it — so a layer over the surface runs short, and the ' +
          'colour is how much oxidant is actually present. Stir the bath and the layer thins ' +
          'and the surface refills. Now try the two knobs against each other. In a still bath, raising ' +
          'the temperature from 70 to 85 °C more than doubles the reaction rate but buys only about a ' +
          'quarter more removal, because the surface simply starves further. Do it with the bath moving ' +
          'and the same step pays three times over. And on a cold bath, agitation buys nothing at all — ' +
          'the chemistry is the bottleneck, and stirring it is fixing the wrong thing. Two resistances ' +
          'in series, and only the larger one is ever worth attacking.',
        TransportCaveat: 'THIS IS THE GENERAL CASE AND NOT THIS TANK, and the difference is worth being exact ' +
          'about. SC-1 is roughly molar in peroxide and hydroxide while the surface consumes a few ' +
          'nanometres a minute, so supply beats demand by orders of magnitude and a real SC-1 bath does ' +
          'not starve. The regime above comes from a rate constant chosen to put reaction and transport ' +
          'in the same range, which is where the lesson lives — and it is a regime real wet processing ' +
          'does reach, in a dilute or spent bath, in a fast etch, and at the bottom of a deep feature. ' +
          'It is also NOT why the transducers are there. Megasonics take particles off MECHANICALLY: ' +
          'acoustic streaming drags at a particle until the drag beats the van der Waals force holding ' +
          'it down, with cavitation helping. That is a detachment mechanism, not a delivery one. Which ' +
          'leaves the recipe’s arithmetic above still wrong to multiply a thermal factor by a megasonic ' +
          'one — heat and sound are not two settings of the same knob — but wrong for this reason ' +
          'rather than the one the picture shows.',
        ActionRunTheCleanAnd: 'Run the clean and pass the wafer on',
        NoteNoLotInTheBench: 'No lot in the bench. Bring a wafer here to clean it.',
        SecondaryLabel: 'Surface metals',
        ToolLabel: 'CLN-09 RCA Wet Clean Bench',
        NextTheRinseWaterIsA: 'The rinse water is a source too, below about 15 MΩ·cm. If the bath ' +
          'chemistry is right and the wafer still comes out dirty, where would you ' +
          'look first?',
        AxisY10AtomsCm: '×10¹⁰ atoms/cm²',
        AxisXSc2TimeMin: 'SC-2 time (min)',
        TitleMetalsAndParticlesAgainstSc: 'Metals and particles against SC-2 time',
        NoteSc1SJob: 'SC-1\'s job',
        LabelMetalsAsSet: 'Metals as set',
        LabelMetalsNoSc2: 'Metals, no SC-2',
        HypTheTwoBathsHaveOpposite: 'The two baths have opposite chemistry for opposite reasons. SC-1 is ' +
          'alkaline and lifts particles by undercutting them; SC-2 is acidic and ' +
          'dissolves metals that alkaline chemistry leaves plated on the surface. If ' +
          'that is right, SC-2 time should move the metal number and barely touch the ' +
          'particle number, and dropping SC-1 should do the reverse.',
        AskedWhatDoesTheSecondBath: 'What does the second bath remove that the first one does not?',
        SumMetalsVersusParticlesAndWhy: 'Metals versus particles, and why one bath cannot do both.',
        NextThrm04GrowsAGate: 'THRM-04 grows a gate oxide on this surface. What does an extra ' +
          'half-nanometre of native oxide, grown in air on a dirty bench, do to a ' +
          'Deal-Grove growth that assumes it is starting from bare silicon?',
        AxisYOxideNm: 'oxide (nm)',
        AxisXMinutesAfterHf: 'minutes after HF',
        TitleNativeOxideRegrowthAgainstQueue: 'Native oxide regrowth against queue time',
        LabelOxideImmediate: 'Oxide, immediate',
        HypHfStripsTheNativeOxide: 'HF strips the native oxide and leaves the surface hydrogen-terminated. Air ' +
          'starts putting the oxide back immediately, and logarithmically - fast ' +
          'while the surface is reactive, then crawling. If so, most of what a queue ' +
          'costs is spent in its first half hour, and a four-hour wait is barely ' +
          'worse than a one-hour one.',
        AskedWhatDoesTheWaitBetween: 'What does the wait between this bench and the next tool cost?',
        SumNativeOxideRegrowthAgainstThe: 'Native oxide regrowth against the queue clock, and why HF starts one.',
        NextTheFloorThisCurveFlattens: 'The floor this curve flattens onto is not zero - it is what the rinse and ' +
          'the dry put back. Ask me what happens after the HF dip, which is where ' +
          'that gets much worse.',
        AxisYNmRms: 'nm RMS',
        TitleAndTheRoughnessItCost: 'And the roughness it cost',
        AxisYParticlesCm: 'particles/cm²',
        AxisXSc1TimeMin: 'SC-1 time (min)',
        TitleParticlesRemainingAgainstSc1: 'Particles remaining against SC-1 time',
        LabelRoughness: 'Roughness',
        LabelParticles: 'Particles',
        AskedDoesMoreBathTimeKeep: 'Does more bath time keep making the wafer cleaner?',
        SumDiminishingReturnsOnBathTime: 'Diminishing returns on bath time, and the roughness it buys them with.',
        fault: {
          poorRinse: {
            Evidence: [
              'Rinse resistivity at 11 MΩ·cm against a 17 floor.',
              'Both the particle check and the metals check are out.',
              'Both bath ages and the transducer are nominal.',
            ],
            Truth: 'The polishing mixed bed on the UPW plant is exhausted and the rinse water is at 11 ' +
              'MΩ·cm. The rinse is the LAST thing the wafer sees, so whatever it is ' +
              'carrying goes back on after both chemistries have done their jobs ' +
              'perfectly. Failing at both particles and metals at once is a signature no ' +
              'single bath can produce — and the repair is not on this tool at all.',
            Complaint: 'Particles AND metals are both out on this bench. Both baths are fresh and ' +
              'the megasonics check out.',
            Label: 'Rinse water off specification',
          },
          deadTransducer: {
            Evidence: [
              'Delivered megasonic power at 30% against an 85% floor.',
              'Removal below 98% on the monitor wafer.',
              'Both bath ages are nominal — this is not chemistry.',
            ],
            Truth: 'The megasonic transducer is delivering 30% of commanded power. Megasonics ' +
              'accelerate the transport of a lifted particle away from the surface, so ' +
              'without them the chemistry works and the particles settle back. Fresh ' +
              'baths and poor removal together is the signature.',
            Complaint: 'Particle removal has degraded, and both baths were remade this morning.',
            Label: 'Megasonic transducer failing',
          },
          spentSc2: {
            Evidence: [
              'SC-2 bath age past its life.',
              'VPD-ICPMS surface metals far above the 5 ×10¹⁰ limit.',
              'Particle removal is completely normal.',
            ],
            Truth: 'The SC-2 bath is spent, so metals are not being removed. The wafers are ' +
              'visually clean — an optical inspector cannot see a monolayer of copper — ' +
              'and they carry it straight into a furnace, where it diffuses and kills the ' +
              'oxide. This is the failure that is invisible to every cheap measurement in ' +
              'the fab.',
            Complaint: 'Gate oxide integrity has fallen off on one product. Particle counts are ' +
              'perfectly normal and nobody can find a defect.',
            Label: 'SC-2 bath spent',
          },
          spentSc1: {
            Evidence: [
              'SC-1 bath age past its life.',
              'Monitor wafer shows removal well below 98%.',
              'Surface metals are normal, so SC-2 is doing its job — this is one ' +
                'chemistry, not both.',
            ],
            Truth: 'The SC-1 peroxide has decomposed — the bath is 34 hours old against a 16 ' +
              'hour life. Removal is first-order in an active bath and a spent one barely ' +
              'works, so the particles go straight through. Nothing about the tool looks ' +
              'different, because nothing about a transparent bath ever does.',
            Complaint: 'Defect density at INSP-13 has doubled on everything that goes through this ' +
              'bench. The bench itself has not alarmed and looks exactly as it always ' +
              'does.',
            Label: 'SC-1 bath spent',
          },
          clnHealthy: {
            Evidence: [
              'Both baths are fresh.',
              'Particle removal and surface metals are both in band.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine shift. Nothing has been reported against this bench.',
            Label: 'Nothing wrong',
          },
        },
        NoteMinAtC: '{{a}} min at {{b}} °C',
        NoteFromCmIncoming: 'from {{a}}/cm² incoming',
        SectionTwoThingsThisBenchIs: 'Two things this bench is trading',
        NoteParticleRemovalIsFirstOrder: 'Particle removal is first-order, so the last particles cost the most time ' +
          '— and every extra minute of hot SC-1 etches silicon and roughens the ' +
          'surface. Then, if you take the oxide off with HF, a clock starts: the ' +
          'native oxide comes back fastest in the first twenty minutes, so queue time ' +
          'is a specification and not a courtesy.',
        NoteThisLotIsStillAn:
          'This lot is still an ingot. WPR-08 has to slice it before this tool can ' +
          'touch it.',
        SectionBenchState: 'Bench state',
        LabelParticlesLeft: 'Particles left',
        LabelRemovalEfficiency: 'Removal efficiency',
        LabelMetalRemaining: 'Metal remaining',
        Note5BeforeAFurnace: '< 5 before a furnace',
        LabelSurfaceRoughness: 'Surface roughness',
        NoteSc1EtchesAndEtching: 'SC-1 etches, and etching roughens',
        LabelOxideAtTheNextStep: 'Oxide at the next step',
        LabelHfBenefitLost: 'HF benefit lost',
        AssistantLead: 'CLN-09 inquiry assistant',
        AssistantBlurb:
          'Ask whether a longer bath helps, what the queue after HF costs, or what ' +
          'the second bath is for. Every answer is a clean run against this bench.',
        reading: {
          houseutilities: {
            Label: 'Call facilities for the utility panel (0.5 h)',
          },
          metalCheck: {
            Label: 'VPD-ICPMS surface metals — destructive (4.0 h)',
          },
          particleCheck: {
            Limit: '> 98% removed',
            Label: 'Particle count on a monitor wafer before and after (1.5 h)',
          },
          resistivity: {
            Label: 'Rinse water resistivity',
          },
          transducer: {
            Label: 'Megasonic power delivered',
          },
          sc2Age: {
            Label: 'SC-2 bath age',
          },
          sc1Age: {
            Label: 'SC-1 bath age',
          },
        },
        aspect: {
          house: {
            Action:
              'Escalate to facilities — the polishing mixed bed on the UPW plant needs ' +
              'regenerating',
            Label: 'Rinse water supply — not this tool',
          },
          megasonic: {
            Action: 'Replace the transducer and re-map the acoustic field across the tank',
            Label: 'Megasonic transducer',
          },
          sc2: {
            Action: 'Dump and remake the SC-2 bath',
            Label: 'SC-2 bath',
          },
          sc1: {
            Action: 'Dump and remake the SC-1 bath, and shorten the bath-life interval',
            Label: 'SC-1 bath',
          },
        },
      },
      atomView: {
        Atom: 'Atom',
        Coordinates: 'Coordinates',
        AtomicMass: 'Atomic Mass',
        AtomicRadius: 'Atomic Radius',
        ElectricCharge: 'Electric Charge',
        AutoRotate: 'Auto Rotate',
        Axes: 'Axes',
        Container: 'Container',
        Fog: 'Fog',
        ViewDirection: 'View Direction',
        PositiveX: 'Positive X Direction',
        NegativeX: 'Negative X Direction',
        PositiveY: 'Positive Y Direction',
        NegativeY: 'Negative Y Direction',
        PositiveZ: 'Positive Z Direction',
        NegativeZ: 'Negative Z Direction',
        Mechanics: 'Mechanics',
        KineticEnergyColors: 'Kinetic Energy Colors',
        MomentumVectors: 'Momentum Vectors',
        ForceVectors: 'Force Vectors',
        GlobalStyle: 'Global Style',
        BallAndStick: 'Ball-and-Stick',
        Wireframe: 'Wireframe',
        Stick: 'Stick',
        SpaceFilling: 'Space-Filling',
        Material: 'Material',
        Diffuse: 'Diffuse',
        Soft: 'Soft',
        Glossy: 'Glossy',
        Metal: 'Metal',
        Translucent: 'Translucent',
        Glass: 'Glass',
        Toon: 'Toon',
        Flat: 'Flat',
        BackgroundColor: 'Background Color',
        TakeScreenshot: 'Take a Screenshot',
      },
      e2d: {
        ViewProperties: 'View properties…',
        Properties: 'properties…',
        ThisPart: 'this part',
        ModelProperties: 'Model properties…',
        Loading: 'Loading the simulation engine…',
        Failed: 'The simulation engine failed to load.',
        Run: 'Run',
        Pause: 'Pause',
        Reset: 'Reset',
      },
      implant: {
        BeamWafer: 'wafer',
        BeamSpot: 'hottest',
        BeamClamp: 'clamp',
        SectionBeamHeat: 'What the beam heats — the wafer on its clamp (heat transfer)',
        BeamHeatNote: 'Beam power is just volts times amps: 30 keV at 10 mA is 300 W landing on one wafer. This is ' +
          'that power going in, the ribbon rastering across at a metre a second, and the electrostatic ' +
          'clamp pulling heat out through the helium behind the wafer. Three things are worth watching. ' +
          'The wafer settles a few degrees above the platen and no more — the clamp, not the beam, is ' +
          'what sets the temperature, and a wafer that loses its backside gas runs past 300 °C on the ' +
          'same beam and reticulates its resist. Beam current buys throughput and heat together. And ' +
          'the ribbon leaves no scorch line: silicon conducts too well for that, which is exactly why ' +
          'the panel above can honestly carry a single wafer temperature instead of a map.',
        DamageFurnace: 'Anneal furnace',
        DamageReset: 'Reset',
        DamagePause: 'Pause',
        DamageRun: 'Run',
        DamageFailed: 'The molecular-dynamics engine failed to load.',
        DamageLoading: 'Loading the molecular-dynamics engine…',
        DamageHighlight: 'Highlight defects',
        DamageChartTitle: 'Surviving defects against time — the anneal',
        DamageChartX: 'simulated time (ps)',
        DamageChartY: 'defects (vacancies + interstitials)',
        DamageChartSeries: 'Surviving defects',
        DamageChartAsImplanted: 'as implanted',
        RdfTitle: 'Radial distribution function g(r) — the crystal as a curve',
        RdfX: 'pair distance r (Å)',
        RdfY: 'g(r)',
        RdfSeries: 'g(r) now',
        RdfUncorrelated: 'g = 1, no structure',
        RdfStatus: 'peaks at {{a}} Å and {{b}} Å (ideal 2.35 and 3.84) · between them g(3.0 Å) = {{c}} — zero in a crystal, filled in rubble',
        RdfNote: 'The same atoms as a histogram of every pair distance, normalised so an uncorrelated gas reads 1. A crystal is peaks on the ruled shells and nothing between; heat widens the peaks, and damage does something different — it fills the gap and washes the second and third shells into each other. Drag the dose upstairs from zero to the ceiling and watch the curve go from comb to hump: that is the difference between crystalline and amorphous, as a diffractometer would see it. It updates live, so an anneal that re-sharpens a peak shows here too.',
        DamageStatus: '{{a}} vacancies · {{b}} interstitials · beam damage {{c}}% of the ' +
          'amorphisation dose · {{d}} K',
        DamageNote: 'Every ion that stops in the wafer knocks silicon atoms off their sites on ' +
          'the way in. Each one leaves a vacancy — the red cage, an empty site — and ' +
          'comes to rest between sites as an interstitial, in yellow. That pair is a ' +
          'Frenkel pair, and enough of them is what "amorphised" means: the tidy ' +
          'diamond order of the crystal replaced by rubble. This lattice is driven by ' +
          'the recipe above, so raise the dose or switch to a heavier ion and watch ' +
          'the damage climb — then raise the wafer temperature and watch it fall, ' +
          'because a hot implant repairs itself as it goes. The cell is 216 atoms, so ' +
          'the damage is scaled to be visible rather than literal.',
        CarriersTitle: 'Carriers vs temperature',
        CarriersX: 'temperature (K)',
        CarriersY: 'log₁₀ nm⁻³',
        CarriersSeries: 'carriers',
        CarriersIntrinsic: 'intrinsic silicon',
        CarriersKnee: 'all delivering',
        CarriersCrossover: 'silicon takes over',
        CarriersFailed: 'The carrier model does not cover this doping.',
        CarriersLoading: 'Solving carrier statistics…',
        CarriersAtRoom: '{{pct}}% delivering at 300 K',
        CarriersKneeAt: 'all delivering by {{k}} K',
        CarriersCrossAt: 'silicon takes over at {{k}} K',
        CarriersDoping: 'doping 1e{{exp}} nm⁻³',
        NoteCarriers: 'A dopant atom on a site is not yet a carrier. The curve beside the lattice is AIMS solving charge neutrality with the dopant levels occupied by Fermi–Dirac statistics: cold, the carriers fall back onto their dopants and the concentration collapses — which is why a wafer measured in liquid nitrogen reads nothing like its room-temperature spec. Warm, every dopant delivers and the plateau is the doping you put in; that flat middle is the only reason doping is a usable design knob. Hotter still, silicon lifts its own electrons across the gap and stops caring what you implanted — the ceiling on junction temperature. The slider is the well or substrate doping, NOT this implant’s peak, which lands about two orders of magnitude higher. Above 10¹⁸ cm⁻³ the dopant level has merged into the band and the model’s non-degenerate statistics stop applying, so the slider ends there rather than drawing a confident wrong curve — and the implant this panel just made is past it.',
        SectionDamage: 'What the beam breaks — lattice damage (molecular dynamics)',
        LatticeHighlight: 'Highlight dopants',
        LatticeStatus: '{{a}}: {{b}} dopant atoms among {{c}} · measured {{d}} K',
        LatticeConc: 'Dopant concentration (exaggerated)',
        TraceTitle: 'Distance from lattice site — thermal vibration',
        TraceX: 'simulated time (ps)',
        TraceY: 'displacement (Å)',
        TraceDopant: '{{a}} on its site',
        TraceHost: 'Si host atom',
        TraceSiRef: 'Si rms at 300 K, X-ray Debye–Waller',
        TraceStatus: 'rms from site over the last {{a}} ps: {{b}} {{c}} Å · Si {{d}} Å · bond 2.35 Å',
        TraceIdle: 'Press Run: rms distance from site is measured over the last {{a}} ps of the run',
        LatticeReset: 'Reset',
        LatticePause: 'Pause',
        LatticeRun: 'Run',
        LatticeFailed: 'The molecular-dynamics engine failed to load.',
        LatticeLoading: 'Loading the molecular-dynamics engine…',
        DopantBohr: 'a*',
        DopantMeanR: '⟨r⟩',
        DopantHalfSpacing: 'halfway to the next dopant',
        DopantOrbitTitle: 'Where the extra electron is',
        DopantOrbitX: 'distance from the dopant (nm)',
        DopantOrbitY: 'radial density 4πr²|ψ|², peak = 1',
        DopantOrbitSeries: 'The bound electron',
        DopantOrbitNone: 'No bound state - the electron is free',
        DopantModelNc: 'model: the atom is gone',
        DopantMeasuredNc: 'measured Si:P transition',
        DopantMeasuredE: 'measured for {{dopant}}',
        DopantBindingTitle: 'How tightly it is held, against doping',
        DopantBindingX: 'log₁₀ dopant concentration (cm⁻³)',
        DopantBindingY: 'binding energy (meV)',
        DopantBindingSeries: 'Computed, with screening',
        DopantBindingHere: 'This recipe',
        DopantFailed: 'The dopant atom solver failed to load.',
        DopantShort: 'a* = {{bohr}} nm · bound by {{bind}} meV here · gone at {{nc}} cm⁻³',
        DopantHelp: 'Every number for this atom',
        DopantStatus: 'a* = {{bohr}} nm · Ry* = {{ry}} meV (measured for {{dopant}}: {{measured}}) · bound by {{bind}} meV here · Debye length {{debye}} nm, next dopant {{spacing}} nm away · gone at {{nc}} cm⁻³',
        DopantStatusGone: 'a* = {{bohr}} nm · no bound state: the Debye length is {{debye}} nm, the next dopant {{spacing}} nm away · the atom was lost at {{nc}} cm⁻³',
        DopantLoading: 'Loading the dopant atom solver…',
        DopantConc: 'Dopant concentration',
        DopantHost: 'Host and carrier',
        DopantHostSiDonor: 'Si, donor (P)',
        DopantHostSiAcceptor: 'Si, acceptor (B)',
        DopantHostGe: 'Ge, donor',
        DopantHostGaAs: 'GaAs, donor',
        SectionDopant: 'What a dopant is — a hydrogen atom inside silicon (Schrödinger, radial)',
        DopantNote: 'The phosphorus atom on a silicon site above has one electron more than the site wants, and that electron is not free: the core is one charge more positive than its neighbours and the electron orbits it as an electron orbits a proton. A hydrogen atom, with the attraction divided by the crystal\'s dielectric constant and the electron carrying its effective mass - so the orbit grows to about 2.4 nm and the binding shrinks to tens of meV, which is why a dopant is ionised at room temperature and frozen out in liquid nitrogen. The radial Schrödinger equation is solved on a grid; the closed form only checks it. The right-hand chart has no closed form: every ionised dopant leaves a free carrier, free carriers screen, and past a few 10¹⁸ per cm³ the well is too shallow to hold a bound state at all. The silicon is then a metal - the Mott transition, within about 10% of the measured Si:P value. The model is isotropic, so silicon\'s donor comes out at 26 meV against the measured 45; the central-cell correction supplies the rest. Screening is Debye\'s, at 300 K.',
        Orbital3dHostSi: 'silicon',
        Orbital3dHostGe: 'germanium',
        Orbital3dHostGaAs: 'gallium arsenide',
        Orbital3dFailed: 'The orbital builder failed to load.',
        Orbital3dLoading: 'Building the orbital…',
        Orbital3dLegend: 'The bound electron’s |ψ|² · brightness ∝ √density against its own centre · the cloud fades as screening lowers that centre, never below 30% · amber where two dopants’ electrons overlap',
        Orbital3dClipped: 'Only {{inside}}% of the electron lies inside the largest box drawn (±{{half}} nm; ⟨r⟩ = {{mean}} nm), so the cloud is shown clipped at its edges.',
        DopantWallHeld: 'Barely bound: the binding is now weaker than the confinement of the solver’s own {{box}} a* box, and ⟨r⟩ has spread to {{mean}} a*. The edge of that box holds this electron as much as the dopant does, so read this binding energy as the model at its limit, not as a property of the atom.',
        Orbital3dUnbound: 'No bound state',
        Orbital3dCaption: 'a* = {{bohr}} nm ({{bohrCells}} unit cells) · ⟨r⟩ = {{mean}} nm ({{meanCells}} cells) · mean dopant spacing {{spacing}} nm ({{spacingCells}} cells)',
        Orbital3dCaptionGone: 'a* = {{bohr}} nm · mean dopant spacing {{spacing}} nm ({{spacingCells}} cells) · no bound state: the electron belongs to no single dopant',
        Orbital3dLattice: 'The {{host}} crystal is drawn as two adjacent atomic planes through the dopant, {{count}} atoms out to ±{{half}} nm: a slab, not the whole crystal, which in this box would be {{full}} atoms. The green bar along the bottom edge is one a*.',
        Orbital3dNeighbours: '{{k}} neighbouring dopants sit inside the box at the mean spacing, {{spacing}} nm. Where their clouds meet the central one the picture turns amber: an electron there can hop from dopant to dopant, and that overlap is the Mott transition.',
        Orbital3dNoNeighbours: 'The next dopant is {{spacing}} nm away, outside this box: at this doping each electron has its dopant to itself.',
        LatticeNote: 'The implanted wafer at atom scale: a diamond-cubic silicon lattice with ' +
          'the recipe\'s dopant sitting substitutionally on silicon sites, alive under the ' +
          'Stillinger–Weber force field. "n-type" is nothing more mysterious than these atoms: a donor on a ' +
          'site, one extra valence electron. Change the species in the recipe above ' +
          'and the lattice re-dopes (BF₂ implants boron; the fluorine leaves in the ' +
          'anneal). The concentration slider is deliberately exaggerated — real ' +
          'doping is parts per million, invisible in a cell this small. Left-drag ' +
          'rotates, the wheel zooms, right-drag pans.',
        SectionLattice: 'What doping is — the lattice (molecular dynamics)',
        ActionImplantAndPassThe: 'Implant and pass the wafer to the furnace',
        UnitPctOnTarget: '% on target',
        NoteNoLotOnTheEndstation: 'No lot on the endstation. Bring a masked wafer here to implant it.',
        NoteCrystalline: 'crystalline',
        NoteAmorphisedTheAnnealIs: 'amorphised — the anneal is not optional',
        NoteThePanelAboveRuns: 'The panel above runs four fitted formulas: a power law for the range, a ' +
          'fraction of it for the straggle, a Gaussian in tilt for the channelled ' +
          'fraction, and a dose threshold for amorphisation. Below, the same implant ' +
          'is SOLVED — three thousand ions traced through a crystal, one collision at ' +
          'a time. Where the closed form was fitted the two agree; away from it they ' +
          'do not, and that gap is the argument for building solvers.',
        NoteTheCriticalAngleIs: 'The critical angle is {{a}}° for this ion at this energy, and it contains ' +
          'no fitted parameter — it follows from the lattice constant and the two ' +
          'atomic numbers. Set the tilt inside it and the beam channels; set it ' +
          'outside and the beam does not. The formula above has one curve in tilt for ' +
          'every species and every energy, so it cannot tell you that arsenic at ' +
          'seven degrees still channels and boron does not.',
        ValueThresholdAsOneNumber: '{{a}}× threshold, as one number',
        ValueVacanciesPerIon: '{{a}} vacancies/ion, as a profile',
        MetricDamage: 'Damage',
        ValueZeroGaussian: '0 (Gaussian)',
        MetricDistributionSkew: 'Distribution skew',
        ValueNoSuchQuantity: 'no such quantity',
        MetricCriticalAngle: 'Critical angle',
        MetricChannelled: 'Channelled',
        MetricStraggle: 'Straggle',
        MetricProjectedRange: 'Projected range',
        LegendTheFittedFormula: 'the fitted formula',
        LegendIonsTraced: '{{a}} ions traced',
        LabelPeakConcentration: 'Peak concentration',
        SecondaryLabel: 'Dose non-uniformity',
        ToolLabel: 'IMP-10 High-Current Ion Implanter',
        NextBothOfThoseProfilesGet: 'Both of those profiles get annealed. Which one does the furnace change ' +
          'more - and does that make the shallow implant better or worse than it ' +
          'looks here?',
        AxisXEnergyKev: 'energy (keV)',
        TitleProjectedRangeAgainstEnergy: 'Projected range against energy',
        LabelStraggleDeep: 'Straggle, deep',
        LabelStraggleShallow: 'Straggle, shallow',
        HypEnergyBuysDepthButThe: 'Energy buys depth. But the spread of the distribution does not shrink in ' +
          'proportion, so a shallow implant should be RELATIVELY broader than a deep ' +
          'one - the straggle becomes a larger fraction of the range as the range ' +
          'falls. If that holds, "shallower" and "sharper" are not the same request.',
        AskedWhatSetsHowDeepThe: 'What sets how deep the dopant goes, and what does it cost to go shallow?',
        SumEnergySetsDepthAndDose: 'Energy sets depth and dose sets concentration - and they are not ' +
          'interchangeable.',
        NextThrm04HasToAnneal: 'THRM-04 has to anneal this out, and the anneal spends from the same ' +
          'thermal budget the gate oxide does. Ask the furnace what repairing this ' +
          'costs the junction you just placed.',
        LabelDamageRatio: 'damage ratio',
        AxisYDamageAmorphisationThreshold: 'damage / amorphisation threshold',
        AxisXDoseCm: 'dose (nm⁻²)',
        TitleLatticeDamageAgainstDose: 'Lattice damage against dose',
        LabelUniformity: 'Uniformity',
        LabelDamageRatioL226: 'Damage ratio',
        HypEveryIonKnocksLatticeAtoms: 'Every ion knocks lattice atoms off their sites on the way in. Below some ' +
          'accumulated density the crystal repairs itself between cascades; above it ' +
          'the damage overlaps and the layer goes fully amorphous. If so there should ' +
          'be a threshold in dose rather than a gradual change - and running the ' +
          'wafer hot should push it higher, because a warm lattice anneals as it is ' +
          'damaged.',
        AskedHowMuchDamageIsThis: 'How much damage is this implant doing, and when does it go amorphous?',
        SumLatticeDamageAgainstDoseAnd: 'Lattice damage against dose, and what the furnace is being asked to repair.',
        NextTheStageHasATilt: 'The stage has a tilt error of its own, well under a degree. Look at where ' +
          'this curve is steepest - what does half a degree of stage error cost ' +
          'there, and what does it cost at 7°?',
        LabelTailDepth: 'tail depth',
        TitleAndTheDepthItReaches: 'And the depth it reaches',
        AxisYFractionOfDose: 'fraction of dose',
        AxisXTiltDegrees: 'tilt (degrees)',
        TitleChannelledFractionAgainstTilt: 'Channelled fraction against tilt',
        LabelTailAt7: 'Tail at 7°',
        LabelTailOnAxis: 'Tail on axis',
        LabelTableRange: 'Table range',
        HypSiliconIsACrystalNot: 'Silicon is a crystal, not an amorphous solid, and along a low-index ' +
          'direction it is mostly empty space. An ion that enters down a channel ' +
          'loses energy far more slowly than the tables assume. If that is what is ' +
          'happening, the depth should be a strong function of TILT and not of energy ' +
          'at all - and a few degrees should be enough to close it.',
        AskedWhyDoesTheImplantGo: 'Why does the implant go deeper than the projected range predicts?',
        SumChannellingDownTheLatticeAnd: 'Channelling down the lattice, and the seven degrees of tilt that stop it.',
        fault: {
          weakSource: {
            Evidence: [
              'Source pressure at 35% against an 85% floor.',
              'Sheet resistance map out of limit.',
              'Scan uniformity is nominal — the beam is being swept correctly, there just ' +
                'is not a steady beam to sweep.',
            ],
            Truth: 'Source pressure has fallen to 35% of nominal. A starved source strikes an ' +
              'unstable beam, and an unstable beam implants an unevenly dosed wafer — the ' +
              'same non-uniformity a bad scan produces, from the other end of the ' +
              'beamline. The scan reading is what separates them.',
            Complaint: 'Beam is unstable, cycle time has climbed and the maps are noisy.',
            Label: 'Ion source failing',
          },
          scanDrift: {
            Evidence: [
              'Scan uniformity at 30% against a 90% floor.',
              'Sheet resistance map above the 1% non-uniformity limit.',
              'Total dose on the monitor is correct, and the profile has no tail.',
            ],
            Truth: 'The scan system is at 30% of its uniformity factor. The dose is right on ' +
              'average and wrong everywhere in particular — the total charge is correct, ' +
              'so the Faraday cup is content, and the wafer is not uniformly implanted.',
            Complaint: 'Sheet resistance maps have gone striped along the scan direction.',
            Label: 'Scan waveform degraded',
          },
          tiltZeroLost: {
            Evidence: [
              'Tilt readback 0.6° against a 7° setpoint.',
              'SIMS shows 40% of the dose in a deep tail.',
              'Dose on the annealed monitor is correct — the cup is fine.',
            ],
            Truth: 'The tilt encoder has lost its zero and the stage is 6.4° off, so a recipe ' +
              'asking for 7° is implanting at 0.6° — straight down the channel. Forty per ' +
              'cent of the beam goes three times deeper than intended while the PEAK ' +
              'stays put, so the mean is fine and the tail is the junction. A sheet ' +
              'resistance integrates the tail away, which is how this reaches product.',
            Complaint: 'Junction leakage on one product. Sheet resistance is on target, dose is on ' +
              'target, and the profile peak is exactly where it should be.',
            Label: 'Tilt stage zero lost',
          },
          driftedCup: {
            Evidence: [
              'The calibration sticker is in date, and it is in date on a good tool too.',
              'Annealed monitor comes back at 87% of intended dose.',
              'Tilt, scan and source are all nominal.',
            ],
            Truth: 'The dose integrator is reading 13% high, so the tool stops the implant ' +
              'early and reports a full dose. It is not lying — it is measuring the wrong ' +
              'thing accurately, which is worse, because every recipe in the fab is ' +
              'written against that number. The only way to catch it is to stop measuring ' +
              'the beam and measure the WAFER.',
            Complaint: 'Sheet resistance is out on every product through this implanter. The tool ' +
              'insists it delivered exactly the dose the recipe asked for, and it has the ' +
              'integrated charge to prove it.',
            Label: 'Faraday cup reading high',
          },
          impHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Monitor dose and profile both check out.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. The tool reports the dose it was asked for.',
            Label: 'Nothing wrong',
          },
        },
        NoteMaOfBeam: '{{a}} mA of beam',
        NoteTailToNmAtTilt: 'tail to {{a}} nm at {{b}}° tilt',
        NoteCmDelivered: '{{a}}/nm² delivered',
        NoteStraggleNm: 'straggle ±{{a}} nm',
        SectionTheFailureThatHidesIn: 'The failure that hides in the average',
        NoteSiliconIsACrystalAnd: 'Silicon is a crystal, and along a major axis it is mostly empty space. ' +
          'Take the tilt off and a large part of the beam goes straight down a ' +
          'channel and stops three times deeper than intended — while the peak stays ' +
          'exactly where the recipe says. The average is fine. The tail is the ' +
          'junction.',
        SectionSolvedBesideTheFormulaIt: 'Solved, beside the formula it replaces',
        SectionBeamlineState: 'Beamline state',
        LabelProjectedRange: 'Projected range',
        LabelChannelled: 'Channelled',
        LabelLatticeDamage: 'Lattice damage',
        LabelDoseNonUniformity: 'Dose non-uniformity',
        LabelImplantTime: 'Implant time',
        AssistantLead: 'IMP-10 inquiry assistant',
        AssistantBlurb:
          'Ask why a junction came out deeper than the tables say, when the lattice ' +
          'goes amorphous, or what energy actually sets. Every answer is an implant ' +
          'run against this beamline.',
        reading: {
          uniformityMap: {
            Label: 'Sheet resistance map, 49 points on a monitor (3.0 h)',
          },
          sims: {
            Label: 'SIMS depth profile — destructive, outsourced (24 h)',
          },
          sheetRho: {
            Label: 'Anneal a monitor and four-point probe it (6.0 h)',
          },
          sourcePressure: {
            Label: 'Source pressure',
          },
          scanUniformity: {
            Label: 'Scan system uniformity factor',
          },
          tiltReadback: {
            Label: 'Tilt stage readback against setpoint',
          },
          doseCal: {
            Value: 'in date',
            Limit: 'in date',
            Label: 'Faraday cup calibration date',
          },
        },
        aspect: {
          source: {
            Action: 'Change the source and re-strike the beam',
            Label: 'Ion source',
          },
          scan: {
            Action: 'Re-tune the scan waveform and re-map uniformity across the wafer',
            Label: 'Beam scan system',
          },
          tiltStage: {
            Action: 'Re-zero the tilt encoder and verify against a mechanical reference',
            Label: 'Tilt stage',
          },
          faraday: {
            Action:
              'Re-calibrate the dose integrator against a reference cup and re-qualify on ' +
              'monitors',
            Label: 'Faraday cup and dose integrator',
          },
        },
      },
      facilities: {
        SecondaryLabel: 'Pump-down to 10⁻⁵',
        ToolLabel: 'FAC-06 · Process vacuum',
        LabelPureOutgassing: 'pure outgassing',
        LabelPureLeak: 'pure leak',
        LabelThisChamber: 'this chamber',
        LabelReaches1e5: 'Reaches 10⁻⁵',
        LabelBaseAt24H: 'Base at 24 h',
        LabelBaseAt1H: 'Base at 1 h',
        DesignATwentyMinuteRateOf: 'A twenty-minute rate-of-rise on the chamber as found, alongside a pure ' +
          'leak and pure outgassing tuned to the SAME initial slope - so only the ' +
          'shape can tell them apart.',
        HypBothRaiseBasePressureAnd: 'Both raise base pressure and both raise it by the same mechanism - ' +
          'throughput the pump has to remove. But a leak is fed by the atmosphere and ' +
          'never runs out, while outgassing is fed by a finite amount of adsorbed ' +
          'material and does. So close the valve: a leak should give a straight line ' +
          'for ever, and outgassing should curve over.',
        AskedTheChamberWillNotReach: 'The chamber will not reach base. Is air getting in, or are the walls ' +
          'letting go?',
        SumSeparateARealLeakFrom: 'Separate a real leak from a wet chamber by the shape of the rise.',
        NextConductanceSetsHowFastYou: 'Conductance sets how fast you get there. It does not set where you stop. ' +
          'What decides the base pressure a chamber settles at, and why can a perfect ' +
          'pump still not reach it?',
        TitleEffectiveSpeedAgainstLineDiameter: 'Effective speed against line diameter, at this pump',
        LabelTripleThePump: 'Triple the pump',
        AskedPumpingIsSlowerThanExpected: 'Pumping is slower than expected. Is the pump too small, or is something in ' +
          'the way?',
        SumShowThatThePipeNot: 'Show that the pipe, not the pump, usually sets the speed at the chamber.',
        fault: {
          wornPump: {
            Evidence: [
              'Base pressure is in spec. Every wafer would ship. That is the trap.',
              'Pump-down to crossover has gone from seconds to well past the limit.',
              'Measured speed at the pump inlet is far below its rating - decisive, and ' +
                'it costs two hours.',
              'Line conductance is healthy, so the pipe cannot be blamed.',
              'The rate-of-rise is normal in shape, so nothing is feeding the chamber.',
            ],
            Truth: 'Bearings and blade clearances have gone; the turbo delivers about a ' +
              'twentieth of its rated speed. The gauge says nothing, and there is a ' +
              'reason: base pressure is throughput divided by pumping speed, and on a ' +
              'chamber that pumped overnight the throughput is almost nothing, so almost ' +
              'any speed reaches a good base. A dying pump shows up in how long it TAKES, ' +
              'never in where it ends up. That is the only reason this fault is findable ' +
              'at all, and it is why a facilities log that records pump-down times is ' +
              'worth more than one that records base pressures.',
            Complaint: 'Nothing is failing. Pump-down takes noticeably longer than the log says it ' +
              'used to and the pump sounds different. Base pressure is fine.',
            Label: 'Pump stack degraded',
          },
          throttledLine: {
            Evidence: [
              'Effective speed at the chamber is a small fraction of the pump rating - ' +
                'the decisive number.',
              'The rate-of-rise is normal in both slope and shape, so there is no leak ' +
                'and the walls are dry.',
              'Base pressure eventually gets there; it is the TIME that is wrong, not the ' +
                'destination.',
            ],
            Truth: 'The chamber sits behind a 5 cm line two metres long. Conductance goes as ' +
              'the CUBE of diameter, so that pipe passes a fraction of what the pump ' +
              'could take, and the chamber never sees the speed that was paid for. ' +
              'Tripling the pump would change almost nothing, because a pump and the pipe ' +
              'in front of it add like resistors in series.',
            Complaint: 'Pump-down takes forever. Someone has already quoted a pump three times the ' +
              'size.',
            Label: 'Long narrow connecting line',
          },
          wetChamber: {
            Evidence: [
              'The rate-of-rise slope COLLAPSES over twenty minutes as the surfaces ' +
                'empty. That decay is the whole diagnosis; the pressure at one hour is not.',
              'Pump speed, conductance and seals all check out.',
              'Left pumping, base pressure keeps improving. A leak would have stopped ' +
                'improving.',
            ],
            Truth: 'The chamber was opened to atmosphere and put back together damp. The walls ' +
              'are loaded with adsorbed water and are letting it go. Nothing is broken ' +
              'and nothing needs disassembly - it needs time, or a bake. Taking this ' +
              'chamber apart to hunt a leak costs six hours and finds nothing.',
            Complaint: 'Same as the door seal last month: will not get below the low 10⁻⁵s after ' +
              'an hour.',
            Label: 'Chamber reassembled damp',
          },
          realLeak: {
            Evidence: [
              'The rate-of-rise slope does not decay over twenty minutes. Whatever is ' +
                'feeding the chamber is not running out, which no amount of outgassing can ' +
                'do.',
              'The chamber has already pumped overnight, so the walls are dry - which is ' +
                'the only reason this test can decide anything.',
              'Pump speed and line conductance are both normal.',
            ],
            Truth: 'A door seal was pinched at reassembly after the last PM. Air is leaking in ' +
              'continuously. Two things follow, and the second is the one that matters to ' +
              'the fab: base pressure sticks, and the oxygen that comes in with the air ' +
              'scavenges fluorine in the etcher, so a process technician two floors up ' +
              'sees a mystery where a facilities technician sees a seal.',
            Complaint: 'Chamber will not get below the low 10⁻⁵s. It pumped fine last week. The ' +
              'etcher upstairs is also running slow with normal flows and normal RF.',
            Label: 'Pinched door seal',
          },
          healthy: {
            Evidence: [
              'Base pressure is at spec and the rate-of-rise is flat.',
            ],
            Truth: 'Nothing is wrong. The vacuum is not your problem.',
            Complaint: 'System pumped down overnight and is sitting at base. Nothing reported.',
            Label: 'Chamber at base',
          },
        },
        TextH: '{{a}} — {{b}} h.',
        NoteVSEff: 'V / S_eff',
        reading: {
          speedcheck: {
            Label: 'Throughput check at the chamber (1 h)',
          },
          pumpspeed: {
            Limit: '> 70 % of rating',
            Label: 'Speed measurement at the pump inlet (2 h)',
          },
          outgas: {
            Label: 'Overnight pump and re-measure (14 h)',
          },
          riseShape: {
            Limit: 'tight',
            Label: 'Rate of rise, 20 min with the valve shut (25 min)',
          },
          conductance: {
            Label: 'Line conductance',
          },
          throttle: {
            Limit: '> 720 L/s (60% of the pump)',
            Label: 'Speed at the chamber',
          },
          timeto: {
            Label: 'Time to 10⁻⁵ Torr',
          },
          base: {
            Label: 'Base pressure at 1 h',
          },
        },
        aspect: {
          walls: {
            Action: 'Keep pumping, or bake the chamber out',
            Label: 'Chamber surfaces',
          },
          seals: {
            Action: 'Helium leak check, re-seat the door seal',
            Label: 'Seals and flanges',
          },
          line: {
            Action: 'Re-plumb with a shorter, wider line',
            Label: 'Connecting line',
          },
          pump: {
            Action: 'Service or replace the turbo and its backing pump',
            Label: 'Pump stack',
          },
        },
        SectionSystem: 'System',
        SectionPumpdown: 'Pump-down',
        PumpdownNote:
          'From rough vacuum. The fast part is the pump emptying the volume; the long ' +
          'tail is the chamber walls giving up what they absorbed while it was open. ' +
          'Base pressure is where what comes in equals what goes out.',
        SectionRise: 'Rate-of-rise — the decisive test',
        RiseNote:
          'Close the isolation valve and watch. A real leak feeds the chamber at a ' +
          'constant rate, so the trace is a straight line that never bends. ' +
          'Outgassing runs out as the surfaces empty, so the trace curves over. Both ' +
          'start at the same slope — the SHAPE is the whole diagnosis.',
        RunRise: 'Run a rate-of-rise test (20 min)',
        SectionConductance: 'Conductance — why the pipe matters more than the pump',
        ConductanceNote:
          'Effective speed at the chamber against line diameter, with your pump. ' +
          'Conductance goes as the CUBE of diameter, so a slightly narrower line ' +
          'costs far more than it looks like it should — and no pump on the far side ' +
          'of it can make that back.',
        SectionConfig: 'System configuration',
        SectionStored: 'Stored configurations',
        SectionShift: 'Shift',
        SectionUpw: 'Ultrapure water plant',
        UpwNote:
          'The loop every wet step drinks from. Three habits live here. The meter reads ' +
          'temperature before it reads purity — perfectly pure water at 45 °C shows under ' +
          '7 MΩ·cm, which is why every real probe compensates to 25 °C. The mixed bed holds ' +
          '18.2 for almost its whole life and then fails across the last few percent, which ' +
          'is why you regenerate on throughput and never on the reading. And resistivity ' +
          'counts ions only — the loop can carry ten times its TOC limit with the meter ' +
          'serene at 18.2, which is why TOC is its own instrument.',
        TileUpwRaw: 'Probe, uncompensated',
        NoteUpwRaw: 'purity and temperature, mixed',
        TileUpwComp: 'Meter, 25 °C compensated',
        NoteUpwComp: 'what CLN-09 rinses with',
        TileUpwLeakage: 'Ionic contamination',
        TileUpwToc: 'TOC at point of use',
        NoteUpwToc: 'invisible to the resistivity meter',
        ChartUpwTitle: 'Loop resistivity vs mixed-bed life used',
        ChartUpwX: 'bed capacity used (%)',
        SeriesUpwLoop: 'loop at 25 °C',
        AnnUpwBed: 'your bed',
        AnnUpwFloor: 'CLN-09 floor',
        SectionUtilities: 'Utilities — the whole building, live',
        DeskNote:
          'The dispatch desk. Every number here is the building as it stands; the ' +
          'instruments behind them are on the skids in the sub-fab — take the stairs ' +
          'behind this tool and click the equipment. Repairs happen down there too.',
        SectionHouse: 'House systems — the whole fab',
        HouseHealthy: 'House healthy — no event',
        HouseNote:
          'One event in the building, felt at every tool plumbed into it. Arm one, ' +
          'then walk the floor: the symptoms will not resemble each other, and none ' +
          'of them can be repaired at the tool where they show up.',
        TileConductance: 'Line conductance',
        TileSpeed: 'Speed at the chamber',
        NotePercentOfPump: '{{pct}}% of the pump',
        TilePumpRating: 'Pump rating',
        NoteOwnInlet: 'at its own inlet',
        TileBase1h: 'Base after 1 h',
        TileBase24h: 'Base after 24 h',
        NoteLeakFloor: 'a leak sets a floor',
        NoteOutgassed: 'outgassing has decayed',
        TileReaches: 'Reaches 10⁻⁵ Torr',
        Never: 'never',
        TileTimeConstant: 'Pumping time constant',
        TileVerdict: 'Rate-of-rise verdict',
        NotRun: 'not run',
        RunTheTest: 'run the test',
        NoteSlopeRatio: 'slope ratio {{ratio}}',
        VerdictLeak: 'leak',
        VerdictOutgassing: 'outgassing',
        VerdictMixed: 'mixed',
        VerdictTight: 'tight',
        TileThroughput: 'Throughput in',
        NoteAt1h: 'Torr·L/s at 1 h',
        ChartPumpTitle: 'Pump-down from rough vacuum',
        ChartTimeS: 'time (s)',
        ChartLogPressure: 'log₁₀ pressure (Torr)',
        SeriesChamber: 'chamber pressure',
        AnnBase1h: 'base at 1 h',
        NotRunYet: 'Not run yet',
        ChartRiseTitle: 'Rate of rise — reads as "{{verdict}}"',
        ChartTimeAfterValve: 'time after closing the valve (s)',
        ChartPressureU: 'pressure (µTorr)',
        SeriesMeasured: 'measured',
        SeriesPureLeak: 'a pure leak would look like this',
        ChartConductanceTitle: 'Speed at the chamber against line diameter ({{cm}} cm long)',
        ChartDiameter: 'line diameter (cm)',
        ChartEffectiveSpeed: 'effective speed (L/s)',
        SeriesEffectiveSpeed: 'effective speed',
        AnnYourLine: 'your line',
        AnnPumpRating: 'pump rating',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'The etcher upstairs blames this room more often than it knows. Ask me ' +
          'whether the vacuum is the problem, and which part of it.',
      },
      epi: {
        SectionWell: 'Grow only a few nanometres of it — the ladder that appears (Schrödinger, 1D)',
        WellNote: 'A thin SiGe layer between silicon sits lower in energy for a HOLE than the silicon either side of it, so a hole in that layer is in a box — and a particle in a box cannot have any energy it likes. It gets a ladder of allowed levels whose spacing is set by how wide the box is, and the box is exactly the thickness this recipe grows. Narrow the layer and every level climbs, because squeezing a wave shortens its wavelength and a shorter wavelength costs energy. Climb far enough and the top level reaches the wall and is gone: the well that held five holds four. Nothing is drawn in here — the levels are the eigenvalues of the grown profile, and one disappears from the list when the solver stops finding it below the barrier.',
        WellCarrierNote: 'HOLES, NOT ELECTRONS, and the asymmetry is the point. Silicon-germanium grown on silicon puts almost all of its band offset in the valence band: the conduction band barely moves, so an electron sees hardly a well at all while a hole sees a deep one. That is why SiGe is used for the base of a heterojunction bipolar transistor and for the strained source and drain of a p-channel FET, and not for confining electrons. The offset is the standard 0.74 eV per unit germanium fraction for a strained layer on a silicon substrate, and the mass is the heavy hole\'s along the growth direction; the light hole sits in the same well with its own ladder, left out because two interleaved sets of lines taught nothing.',
        WellChartTitle: 'The grown layer as a box, and what it allows',
        WellChartX: 'position through the stack (nm)',
        WellChartY: 'energy for a hole (eV)',
        WellProfile: 'The grown profile',
        WellDensity: 'Where the lowest hole is',
        WellTop: 'top of the well',
        WellLevel: 'E{{n}}',
        WellStatus: '{{n}} bound levels in {{width}} nm of {{ge}}% germanium · well {{depth}} meV deep · lowest at {{ground}} meV',
        WellWidth: 'Thickness grown',
        WellGe: 'Germanium fraction',
        WellGeValue: '{{pct}}% · {{depth}} meV deep',
        WellFailed: 'The quantum well solver failed to load.',
        WellLoading: 'Loading the quantum well solver…',
        StepBake: 'H2 bake',
        StepGrowth: 'Epi growth',
        ReactorDepletion: 'feed gone by the far edge',
        ReactorLayer: 'free stream over surface',
        SectionReactor: 'The same physics inside the reactor (gas transport)',
        ReactorNote: 'Gas enters at the left and crosses a heated susceptor; the colour is how much source gas is ' +
          'left. Two things here are invisible to the curve above. The feed runs out ALONG the flow — ' +
          'about a third of it is gone by the trailing edge, so that end grows slower than the leading ' +
          'edge, which is the whole reason a susceptor rotates and why epi is judged on uniformity ' +
          'rather than rate. And the boundary layer is a real distance: the free stream a few ' +
          'millimetres up stays at full concentration while the surface under it starves, and that gap ' +
          'IS the transport resistance the curve draws as a dashed ceiling. Raise the hydrogen carrier ' +
          'to thin the layer; raise the temperature to make the surface consume harder and starve ' +
          'itself further. One label worth reading twice: the recipe above meters trichlorosilane, ' +
          'and this is Energy2D’s own SILANE reactor, so the sensors are named for a gas the tool ' +
          'does not run. Which molecule pyrolyses changes the numbers and not the picture — a feed ' +
          'that runs out along the flow, and a boundary layer with a thickness, are what every ' +
          'silicon source does over a hot susceptor. Read the shape here and the rate from the ' +
          'curve above.',
        fault: {
          spentSusceptor: {
            Evidence: [
              'Susceptor hours far past the coating interval.',
              'Added defect density up an order of magnitude on the monitor.',
              'A faint thickness mottle on the 49-point map - patchy emissivity - while ' +
                'rate, haze and the kinetic probe stay clean.',
            ],
            Truth: 'The susceptor coating is worn through and shedding into the gas stream. ' +
              'Everything the reactor MEASURES is healthy - rate, uniformity, haze - ' +
              'because the coating does not set any of them. It shows up only as defects ' +
              'on the layer, which is INSP-13 evidence, not reactor evidence.',
            Complaint: 'Particle adders are climbing lot over lot. Nothing else has moved.',
            Label: 'Susceptor coating spent',
          },
          wetHydrogen: {
            Evidence: [
              'Dewpoint at the tool far above the -85 °C limit.',
              'Haze scan an order of magnitude over limit, at NORMAL growth rate.',
              'Extending the bake does not help — which rules out a bake-recipe error.',
            ],
            Truth: 'The H2 purifier bed is breaking through and the carrier is carrying water. ' +
              'The bake strips the native oxide and the wet ambient puts it straight ' +
              'back, so the surface seeds stacking faults no matter how long the bake ' +
              'runs. Growth kinetics barely notice — the rate says nothing is wrong while ' +
              'the surface says everything is.',
            Complaint: 'Haze alarms on every wafer since the weekend. Rate and thickness are dead ' +
              'on recipe.',
            Label: 'Moisture in the hydrogen',
          },
          tiltedLamps: {
            Evidence: [
              'Lamp quadrant current spread far above the 3% limit.',
              '49-point map shows the tilt; mean thickness is nearly nominal.',
              'Slip lines at the wafer edge under decoration etch.',
              'The kinetic probe agrees with the production point — temperature CONTROL ' +
                'is fine, temperature DISTRIBUTION is not.',
            ],
            Truth: 'One lamp quadrant is delivering low and the radial profile is tilted by ' +
              '9%. Mean rate barely moves in the transport plateau — but the stress from ' +
              'the gradient moves dislocations at growth temperature, and the edge slips. ' +
              'This is the fault the plateau CANNOT forgive, because slip is about the ' +
              'gradient, not the mean.',
            Complaint: 'Edge slip on the last two lots, and the thickness map has grown a radial ' +
              'tilt. Rate on average is fine.',
            Label: 'Lamp quadrant failing',
          },
          coldPyrometer: {
            Evidence: [
              'The kinetic probe DISAGREES with the production point: modest deficit at ' +
                '1120 °C, a collapse at 950 °C.',
              'Bubbler level and source-line delivery verify clean.',
              'Slightly worse uniformity, because the colder wafer sits lower in the ' +
                'plateau.',
            ],
            Truth: 'The pyrometer has drifted 60 °C optimistic, so the loop holds the wafer 60 ' +
              '°C colder than everyone believes. At the production point the transport ' +
              'plateau hides most of it; drop to 950 °C and the deficit balloons, because ' +
              'down the Arrhenius curve the rate IS a thermometer. Same symptom as the ' +
              'bubbler, opposite regime signature.',
            Complaint: 'Same complaint as a gas problem: layers are thin. The gas panel checks out ' +
              'and the bubbler was refilled yesterday.',
            Label: 'Pyrometer reading high',
          },
          starvedBubbler: {
            Evidence: [
              'The kinetic probe shows the SAME percentage deficit at 950 °C as at 1120 ' +
                '°C — a gas-side signature no temperature fault can produce.',
              'Bubbler level reads below the dip tube.',
              'Uniformity and haze are unaffected.',
            ],
            Truth: 'The bubbler is below its dip tube and delivering about 55% of commanded ' +
              'TCS. At 1120 °C the process is transport-limited, so the rate follows the ' +
              'gas one-for-one — and the temperature loop is telling the truth, which is ' +
              'exactly why staring at it goes nowhere.',
            Complaint: 'Layers are coming out 40% thin at the production recipe. Temperature ' +
              'readback is rock steady the whole run.',
            Label: 'TCS bubbler starving',
          },
          epiHealthy: {
            Evidence: [
              'Rate, uniformity and haze are all in band.',
              'The kinetic probe agrees with the production point.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine shift. Nothing has been reported against the reactor.',
            Label: 'Nothing wrong',
          },
        },
        reading: {
          hazeCheck: {
            Limit: '< 3 ppm',
            Label: 'Surface haze scan after a bare-silicon bake (1.0 h)',
          },
          uniformityCheck: {
            Value: '{{a}} % half-range',
            Limit: '< 2%',
            Label: '49-point thickness map on a monitor wafer (1.5 h)',
          },
          kineticProbe: {
            Value: '{{a}}% of nominal at {{b}} °C, {{c}}% at 950 °C',
            Limit: 'the two should agree',
            Label: 'Kinetic probe — regrow at 950 °C and compare (2.5 h)',
          },
          susceptorHours: {
            Limit: '< 240 h',
            Label: 'Susceptor hours since coat',
          },
          dewpoint: {
            Limit: '< -85 °C',
            Label: 'H2 dewpoint at the tool',
          },
          bubblerLevel: {
            ValueOk: 'in band',
            ValueLow: 'below the dip tube',
            Limit: 'above dip tube',
            Label: 'TCS bubbler level',
          },
          pyro: {
            Value: 'agrees (closed loop — an offset cannot show here)',
            Limit: 'instrumented wafer to verify',
            Label: 'Pyrometer vs setpoint at idle',
          },
          lampCurrent: {
            Limit: '< 3%',
            Label: 'Lamp quadrant current spread',
          },
        },
        aspect: {
          susceptor: {
            Action: 'Swap the susceptor and run the coating recipe',
            Label: 'Susceptor coating',
          },
          ambient: {
            Action: 'Escalate to the gas pad — the purifier bed on the H2 line is breaking ' +
              'through',
            Label: 'Hydrogen quality',
          },
          gasline: {
            Action: 'Refill the bubbler and verify delivery with a rate-of-rise on the source ' +
              'line',
            Label: 'TCS delivery',
          },
          pyrometer: {
            Action: 'Recalibrate the pyrometer against a thermocouple-instrumented wafer',
            Label: 'Pyrometer calibration',
          },
          lamps: {
            Action: 'Replace the weak lamp quadrant and re-map the radial temperature profile',
            Label: 'Lamp bank balance',
          },
        },
        SecondaryLabel: 'Thickness non-uniformity',
        ToolLabel: 'EPI-20 Reduced-Pressure Epitaxy Reactor',
        NextIfTheLayerMustBe: 'If the layer must be 4 µm and the day needs 20 lots, would you buy the ' +
          'time with TCS or with a second reactor - and what does each cost that the ' +
          'other does not?',
        AxisXTcsTorr: 'TCS (Torr)',
        TitleRateAndItsPrice: 'Rate and its price',
        LabelDefectsAtMax: 'Defects at max',
        LabelRateAtMax: 'Rate at max',
        HypOnTheTransportPlateauThe: 'On the transport plateau the rate should follow delivery one-for-one. But ' +
          'the surface can only consume so fast - push the gas phase hard enough and ' +
          'it nucleates on its own, and every nucleus is a particle that lands on the ' +
          'layer.',
        AskedIsTcsAFreeThroughput: 'Is TCS a free throughput knob?',
        SumRateFollowsTheGasOne: 'Rate follows the gas one-for-one - until the gas phase nucleates on its own.',
        NextTheHazeScanIsClean: 'The haze scan is clean but the bake ran long. Where would the damage show, ' +
          'and which tool down the line would be the first to see it?',
        AxisYHazePpm: 'haze (ppm)',
        AxisXBakeMin: 'bake (min)',
        TitleTheTwoCostsOfThe: 'The two costs of the bake',
        LabelSeamAsSet: 'Seam as set',
        LabelHazeAsSet: 'Haze as set',
        HypTheBakeDoesTwoThings: 'The bake does two things at once and cannot be told to do one. It strips ' +
          'the native oxide - skip it and the oxide seeds stacking faults that read ' +
          'as haze - and it evaporates dopant out of the hot substrate, which the ' +
          'growth front then eats as a smeared seam. If both are real, haze should ' +
          'fall with bake time while the autodoped transition grows.',
        AskedWhatDoesThePreGrowth: 'What does the pre-growth bake actually buy, and what does it cost?',
        SumTheBakeStripsOxideAnd: 'The bake strips oxide and smears the seam - both, always, in proportion.',
        NextLayersAreComingOutThin: 'Layers are coming out thin and the temperature readback is steady. Which ' +
          'regime would you re-measure in, and what would each answer tell you?',
        TitleGrowthRateAgainstTemperature: 'Growth rate against temperature',
        LabelRegimeAsSet: 'Regime as set',
        LabelRatePlateau: 'Rate, plateau',
        LabelRateColdEnd: 'Rate, cold end',
        HypTheGasHasToArrive: 'The gas has to arrive and then react - two rates in series, the slower one ' +
          'ruling. If that is right, the rate should climb an Arrhenius wall at the ' +
          'cold end and then bend onto a plateau the temperature cannot touch, ' +
          'because up there the bubbler is the bottleneck.',
        AskedDoesTemperatureKeepBuyingGrowth: 'Does temperature keep buying growth rate?',
        NoteSurface: '{{a}}% surface',
        TextThisRecipeOnThisSubstrate: 'This recipe on this substrate: {{a}} µm at ',
        LabelGrowthRate: 'growth rate',
        LabelTransportCeiling: 'transport ceiling',
        SectionResult: 'Layer as grown',
        SectionSiGe: 'Silicon-germanium — the alloy’s band gap',
        SiGeNote: 'Open the germanium valve and the layer stops being silicon. AIMS solves a tight-binding model of the alloy at the composition you choose: the gap falls from silicon’s 1.12 eV toward germanium’s 0.66 eV, which is what a SiGe base or a strained channel is bought with. But the curve is a VIRTUAL CRYSTAL — every atom in it is a fictitious average of silicon and germanium — and the real alloy has a feature the average cannot produce: its conduction minimum stays silicon-like until about x = 0.85 and then switches to germanium’s, leaving a kink no smooth curve reproduces. No measured line is drawn beside the computed one for exactly that reason.',
        SiGeCurveTitle: 'Band gap across the composition',
        SiGeCurveX: 'germanium fraction x',
        SiGeCurveY: 'gap (eV)',
        SiGeCurveSeries: 'tight-binding gap',
        SiGeAtX: 'x = {{x}}',
        SiGeCrossover: 'Δ → L in the real alloy',
        SiGeGeFraction: 'germanium fraction {{x}}',
        SectionRegime: 'Two resistances in series',
        RegimeLead: 'Two rates in series, and the slower one rules. The dot is this recipe.',
        LotLead: 'The crystal answers back: substrate dopant smears into the first fraction of a micron of the layer.',
        RegimeNote:
          'The gas has to arrive, and then it has to react — two rates in series, and ' +
          'the slower one rules. Below the crossover the reaction is the bottleneck ' +
          'and the rate is a thermometer; above it the bubbler owns the rate and ' +
          'fifty degrees barely matter. Production runs on the plateau on purpose: it ' +
          'is the regime that forgives the lamps. The dot is this recipe.',
        SectionRecipe: 'Recipe',
        SectionStored: 'Stored recipes',
        SectionLot: 'The lot on the susceptor',
        LotNote:
          'The layer is grown ON the crystal CZ-07 pulled, and the crystal answers ' +
          'back: dopant evaporates from the substrate and smears into the first ' +
          'fraction of a micron. The more heavily doped the handle — and the longer ' +
          'the bake — the wider the smeared seam. Once the layer exists, the devices ' +
          'live in it: every junction downstream forms against the epi doping, not ' +
          'the bulk.',
        LotEmpty:
          'No lot on the susceptor. Send a wafer here from WPR-08 or CLN-09 to grow ' +
          'on it.',
        LotIngot:
          'This lot is still an ingot. WPR-08 has to slice it before a layer can be ' +
          'grown.',
        LotCard: 'Travel card',
        LotBlank: 'Stock blank — substrate assumed at 1.0 × 10⁻⁶/nm³.',
        LotRun: 'Grow the layer and pass the lot on',
        SectionShift: 'Shift',
        TileRate: 'Growth rate',
        NoteSpitting: 'past the gas-phase nucleation limit',
        TileRegime: 'Limiting resistance',
        TileThickness: 'Thickness',
        TileNu: 'Non-uniformity',
        TileSeam: 'Autodoped seam',
        NoteSeam: 'the substrate talking back',
        NoteSeamQuiet: 'light substrate, quiet seam',
        TileHaze: 'Haze',
        TileSlip: 'Slip',
        SlipYes: 'AT THE EDGE',
        SlipNo: 'none',
        TileCycle: 'Cycle',
        ChartHere: 'this recipe',
        ChartSpit: 'spitting',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'Sweep the temperature and watch the rate bend onto the plateau, then ask ' +
          'me why production lives up there.',
      },
      dicing: {
        NoteNoStressRelief: 'NO stress relief',
        NoteAfterStressRelief: 'after stress relief',
        ActionGrindDiceAndPass: 'Grind, dice, and pass the die on',
        NoteNoLotOnTheChuck: 'No lot on the chuck. Send a probed wafer here to grind and singulate it.',
        SecondaryLabel: 'Edge chipping',
        ToolLabel: 'DICE-15 Backgrind and Dicing',
        NextTheseDieGoToBond: 'These die go to BOND-16 next, where a capillary presses on them at some ' +
          'force. A weaker die does not only break here - ask the bonder what ' +
          'cratering risk looks like on thin silicon.',
        TitleDieLostAtSingulation: 'Die lost at singulation',
        LabelResidualDamage: 'residual damage',
        AxisYDamageM: 'damage (µm)',
        AxisXGrit: 'grit (#)',
        TitleDamageAndDieLostAgainst: 'Damage and die lost against wheel grit',
        NoteCoarseToFine: 'coarse to fine',
        LabelDieSaved: 'Die saved',
        LabelWeibullM: 'Weibull m',
        LabelStrength: 'Strength',
        HypDamageDepthScalesWithAbrasive: 'Damage depth scales with abrasive size, and strength goes as damage to a ' +
          'negative power rather than linearly. If that is right, going finer should ' +
          'buy a disproportionate amount of strength - and because breakage is ' +
          'Weibull rather than a hard threshold, the payoff should show up as fewer ' +
          'die lost rather than as a limit that moves.',
        AskedIsAFinerFinishingWheel: 'Is a finer finishing wheel worth what it costs in throughput?',
        SumGritAgainstDamageDepthAnd: 'Grit against damage depth, and the Weibull statistics of what survives.',
        NextNarrowerStreetsMeanMoreDie: 'Narrower streets mean more die per wafer, and only one of those three ' +
          'methods survives them. Given the die count on this recipe, what is a 30 µm ' +
          'street worth?',
        LabelReachesTheDie: 'reaches the die',
        AxisYChipM: 'chip (µm)',
        AxisXFeedRateMmS: 'feed rate (mm/s)',
        TitleChipOutAgainstFeedRate: 'Chip-out against feed rate',
        HypABladeCuttingFasterRemoves: 'A blade cutting faster removes more material per revolution and tears ' +
          'rather than grinds, so chip-out should grow with feed rate. There is a ' +
          'hard limit rather than a soft one: the chip either stays inside the street ' +
          'or it reaches the die, and past that point the die is dead however good ' +
          'the cut looked.',
        AskedWhatDoesCuttingFasterCost: 'What does cutting faster cost, and where is the limit?',
        SumFeedRateAgainstChipOut: 'Feed rate against chip-out, and the street width that has to contain it.',
        NextWpr08RemovesSawDamage: 'WPR-08 removes saw damage with exactly this physics, on a wafer with 775 ' +
          'µm of margin. Here there is none. What does that say about which of the ' +
          'two tools can afford a coarse wheel?',
        LabelBackSide: 'back side',
        AxisXThicknessM: 'thickness (µm)',
        TitleBreakingStrengthAgainstTargetThickness: 'Breaking strength against target thickness',
        LabelBackStrength: 'Back strength',
        HypTheWheelLeavesCracksOf: 'The wheel leaves cracks of a depth set by its grit, and that depth does ' +
          'not shrink when the wafer does. So as the wafer thins, the damaged layer ' +
          'becomes a larger FRACTION of it, and the die gets weaker faster than ' +
          'thickness alone would predict. If so, the limit is not a thickness at all ' +
          '- it is a damage-to-thickness ratio, and removing the damage should move ' +
          'the limit.',
        AskedHowThinCanThisWafer: 'How thin can this wafer be ground before die start breaking?',
        SumGrindingDamageAgainstThicknessAnd: 'Grinding damage against thickness, and why stress relief changes the answer.',
        fault: {
          weakTape: {
            Evidence: [
              'Peel force below the 1.9 N limit.',
              'Chipping high, kerf and blade both normal.',
              'Chuck vacuum is good, which rules out the other way a wafer moves.',
            ],
            Truth: 'The tape lot is under-adhering, so die move as the blade passes and chip ' +
              'on the trailing edge. The saw is fine; the consumable is not, and it is ' +
              'the cheapest repair on the tool.',
            Complaint: 'Die are shifting during the cut on the new tape lot.',
            Label: 'Mounting tape under-adhering',
          },
          chuckLeak: {
            Evidence: [
              'Chuck vacuum below the 70 kPa limit.',
              'Chipping well above limit while kerf width is normal — a worn blade would ' +
                'move both.',
              'Blade metres are inside limit.',
            ],
            Truth: 'The chuck is leaking, so the wafer is not held flat and moves ' +
              'microscopically under the blade. Everything the blade touches chips. It ' +
              'looks like a blade problem and the blade readings are clean.',
            Complaint: 'Chipping has jumped on one saw and the die are hard to pick.',
            Label: 'Chuck losing vacuum',
          },
          wornBlade: {
            Evidence: [
              'Blade metres past the 450 m limit.',
              'Kerf width above the 45 µm limit.',
              'Die strength is normal — this fault is at the edges, not under the surface.',
            ],
            Truth: 'The blade is past its cut limit. A worn blade cuts a wider kerf and chips ' +
              'more at the edges — and unlike the wheel fault, this one shows on the ' +
              'wafer immediately if anyone measures the kerf.',
            Complaint: 'Street width has crept up and two products are close to losing their seal ' +
              'ring.',
            Label: 'Dicing blade worn',
          },
          glazedWheel: {
            Evidence: [
              'Wheel hours past the dress limit.',
              'Three-point bend shows strength far below the 300 MPa floor.',
              'Kerf and chipping are both normal, which is what makes dicing think it is ' +
                'innocent.',
            ],
            Truth: 'The wheel has glazed and is ploughing rather than cutting, so it is ' +
              'leaving three times the subsurface damage. The die COUNT is untouched — ' +
              'every die is there and looks fine — and the strength distribution ' +
              'underneath it has collapsed. The press at PKG-17 applies 183 MPa and finds ' +
              'out.',
            Complaint: 'Assembly is losing die in the mould press two stations downstream. Dicing ' +
              'says its die count is perfect and refuses to take it.',
            Label: 'Grinding wheel glazed',
          },
          diceHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Kerf and chipping are inside limits.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. Die count and visual are both normal.',
            Label: 'Nothing wrong',
          },
        },
        NoteLostMinWafer: '{{a}} lost, {{b}} min/wafer',
        NoteKerfMIntoAM: 'kerf {{a}} µm into a {{b}} µm street',
        NoteAtMpaFromThePackage: 'at {{a}} MPa from the package',
        NoteWeibullM: 'Weibull m = {{a}}',
        SectionWhyThisToolIsDangerous: 'Why this tool is dangerous',
        NoteNothingYouCanMeasureHere: 'Nothing you can measure here tells you whether the die will survive. ' +
          'Grinding damage is invisible, it costs no die at this tool, and it decides ' +
          'how many packages crack in a mould press two tools from now.',
        NoteThisLotIsStillAn:
          'This lot is still an ingot. WPR-08 has to slice it before this tool can ' +
          'touch it.',
        LabelDieThickness: 'Die thickness',
        LabelGrindingDamage: 'Grinding damage',
        LabelBackSideStrength: 'Back-side strength',
        LabelEdgeStrength: 'Edge strength',
        NoteSetByChippingNotBy: 'set by chipping, not by the wheel',
        LabelSurvivesTheMouldPress: 'Survives the mould press',
        LabelChipOut: 'Chip-out',
        LabelGoodDie: 'Good die',
        AssistantLead: 'DICE-15 inquiry assistant',
        AssistantBlurb:
          'Ask how thin these wafers can go, whether a finer wheel pays for itself, ' +
          'or what cutting faster costs. Every answer is a grind and cut run against ' +
          'this recipe.',
        reading: {
          chipping: {
            Label: 'Edge inspection, 50 die (1.5 h)',
          },
          kerf: {
            Label: 'Kerf width on a monitor wafer (1.0 h)',
          },
          strength: {
            Label: 'Three-point bend on 20 die — destructive (2.0 h)',
          },
          tapePeel: {
            Label: 'Tape peel force',
          },
          chuckVacuum: {
            Label: 'Chuck vacuum',
          },
          bladeCuts: {
            Label: 'Blade metres cut',
          },
          wheelHours: {
            Label: 'Grinding wheel hours since dress',
          },
        },
        aspect: {
          tape: {
            Action: 'Switch to a fresh tape lot and re-laminate',
            Label: 'Mounting tape',
          },
          chuck: {
            Action: 'Find and seal the chuck leak, then re-check hold-down across the plate',
            Label: 'Chuck vacuum',
          },
          blade: {
            Action: 'Change the blade and re-qualify kerf width on a monitor wafer',
            Label: 'Dicing blade',
          },
          wheel: {
            Action: 'Dress the wheel, or replace it if it is past dressing',
            Label: 'Grinding wheel',
          },
        },
      },
      bonding: {
        failure: {
          'attach void': 'attach void',
          intermetallic: 'intermetallic',
          'cratered pad': 'cratered pad',
          'lifted ball': 'lifted ball',
          none: 'none',
        },
        ActionAttachBondAndPass: 'Attach, bond, and pass the die on',
        UnitPctWelded: '% welded',
        NoteNoDieOnTheBonder: 'No die on the bonder. Singulate a wafer at DICE-15 and bring the die here.',
        SecondaryLabel: 'Die attach voiding',
        ToolLabel: 'BOND-16 Die Attach and Wire Bond',
        NextFt18AcceleratesExactlyThis: 'FT-18 accelerates exactly this kind of diffusion to find the parts that ' +
          'would have failed in the field. Ask it how many hours of burn-in this ' +
          'intermetallic is worth.',
        LabelImcThickness: 'IMC thickness',
        AxisXCureTemperatureC: 'cure temperature (°C)',
        TitleIntermetallicAgainstCureTemperature: 'Intermetallic against cure temperature',
        LabelFromTheBond: 'From the bond',
        LabelImcTotal: 'IMC total',
        HypGoldAluminiumIntermetallicGrowsBy: 'Gold-aluminium intermetallic grows by diffusion, so its thickness should ' +
          'depend on time and temperature and not on how the bond was made. The bond ' +
          'itself lasts milliseconds and the epoxy cure lasts hours - so if diffusion ' +
          'is the mechanism, almost all of the growth should happen during the CURE, ' +
          'a step nobody thinks of as part of the bond.',
        AskedHowMuchIntermetallicIsThis: 'How much intermetallic is this recipe growing, and where does it grow?',
        SumTimeTemperatureGrowthOfIntermetallic: 'Time-temperature growth of intermetallic, and why the cure is part of the ' +
          'bond.',
        NextPrb14HasTheSame: 'PRB-14 has the same arithmetic with die on a wafer instead of wires on a ' +
          'die. Both are exponentials in a count somebody else chose - which of the ' +
          'two can a process engineer actually do anything about?',
        AxisXWiresPerDie: 'wires per die',
        TitleDieYieldAgainstWireCount: 'Die yield against wire count',
        LabelPerWireYield: 'Per-wire yield',
        HypADieIsGoodOnly: 'A die is good only if every wire on it is good, so die yield should be ' +
          'per-wire yield raised to the number of wires. That is an exponential in ' +
          'pin count, which means a bond quality that is perfectly adequate at low ' +
          'pin counts becomes unusable at high ones - without anything about the ' +
          'bonder changing.',
        AskedWhyDoesAHigherPin: 'Why does a higher pin count yield worse on an unchanged recipe?',
        SumPerWireYieldRaisedTo: 'Per-wire yield raised to the wire count, and what that demands of each bond.',
        NextThatDieYieldIsOne: 'That die yield is one wire raised to the wire count. Ask me what happens ' +
          'to it on a part with six hundred wires instead of this one.',
        TitleDieYieldAcrossTheWindow: 'Die yield across the window',
        LabelCrateringRisk100: 'cratering risk ×100',
        LabelBondedArea: 'bonded area',
        AxisXUltrasonicPowerMw: 'ultrasonic power (mW)',
        TitleTwoFailureModesOneKnob: 'Two failure modes, one knob',
        NotePerBond: 'per bond',
        LabelCratering: 'Cratering',
        LabelBondedAreaL113: 'Bonded area',
        HypUltrasonicEnergyIsWhatScrubs: 'Ultrasonic energy is what scrubs the oxide off the pad and welds the ball ' +
          'to it, so too little should leave an unwelded ball that lifts. The same ' +
          'energy goes somewhere when the weld is already complete - into the silicon ' +
          'under the pad. If so, the two failure modes sit at opposite ends of one ' +
          'knob, and the window is wherever both are simultaneously small rather than ' +
          'wherever either is minimised.',
        AskedWhereIsTheBondingWindow: 'Where is the bonding window, and what fails on either side of it?',
        SumLiftedBallsAtOneEnd: 'Lifted balls at one end, cratered pads at the other, and the window between.',
        fault: {
          starvedDispense: {
            Evidence: [
              'Dispense volume at 45% against a 90% floor.',
              'X-ray void fraction above the 10% limit.',
              'Wire bonds are perfect — this fault is under the die, not on top of it.',
            ],
            Truth: 'The dispense pump is delivering 45% of nominal. There is not enough epoxy ' +
              'to wet the whole die footprint, so the attach is voided — which is a ' +
              'thermal path problem the part will not fail on until it is running hot in ' +
              'the field.',
            Complaint: 'X-ray is showing voids under the die on a lot that bonded perfectly.',
            Label: 'Die attach starved',
          },
          dirtyPads: {
            Evidence: [
              'Pad contamination at 55% against a 15% limit.',
              'Delivered power, ball geometry and bond count are all normal.',
              'One product only — a tool fault would be on all of them.',
            ],
            Truth: 'Nothing is wrong with the bonder. The pads are arriving oxidised — a ' +
              'missed plasma clean upstream — and aluminium oxide is what the ultrasonic ' +
              'energy has to break through. Replacing a healthy transducer costs five ' +
              'hours and fixes nothing.',
            Complaint: 'Non-stick on first bond, on one product only, and the bonder passes every ' +
              'check.',
            Label: 'Pads arriving contaminated',
          },
          wornCapillary: {
            Evidence: [
              'Bonds since change past the 500,000 limit.',
              'Ball diameter above the 62 µm limit.',
              'Delivered ultrasonic power is normal.',
            ],
            Truth: 'The capillary is past its bond count. A worn tip prints a wider, irregular ' +
              'ball, which is a shorting risk at fine pitch — and it grips badly, so the ' +
              'weld is worse too. It is also the cheapest repair in the back end, which ' +
              'is why it is worth telling apart from the transducer.',
            Complaint: 'Balls are coming out oversized and irregular, and two are shorting to a ' +
              'neighbour.',
            Label: 'Capillary worn',
          },
          tiredTransducer: {
            Evidence: [
              'Delivered power at 62% against a 90% floor.',
              'Wire pull shows welded area below the 70% limit.',
              'Pads are clean and the capillary is inside its bond count.',
            ],
            Truth: 'The transducer is delivering 62% of the power it is commanded. The ' +
              'setpoint is right and the energy arriving at the ball is not — which is ' +
              'why the recipe looks innocent. Welded area falls, and a wire pull is the ' +
              'only thing that shows it.',
            Complaint: 'Non-stick failures have climbed on one bonder. The recipe has not been ' +
              'touched and the power setpoint reads correct.',
            Label: 'Transducer delivering less than it reports',
          },
          bondHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Welded area is above the 70% floor.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. Bond yield is where it should be.',
            Label: 'Nothing wrong',
          },
        },
        NoteSPerDie: '{{a}} s per die',
        NoteOverWires: 'over {{a}} wires',
        SectionTheWindowHasAFloor: 'The window has a floor and a ceiling',
        NoteUltrasonicEnergyHasToBreak: 'Ultrasonic energy has to break the aluminium oxide before any metal can ' +
          'weld, so too little leaves the ball lifted. The same energy goes into the ' +
          'silicon once the joint is made, so too much craters the pad. Turn the ' +
          'power up and watch both ends.',
        LabelWeldedArea: 'Welded area',
        NoteOfTheBallFootprint: 'of the ball footprint',
        LabelBallShear: 'Ball shear',
        LabelCrateringRisk: 'Cratering risk',
        NotePerWire: 'per wire',
        LabelIntermetallic: 'Intermetallic',
        NoteVoidingPast15M: 'voiding past 1.5 µm',
        LabelDieYield: 'Die yield',
        LabelFailureMode: 'Failure mode',
        reading: {
          attachVoid: {
            Label: 'Die attach X-ray, 10 units (1.5 h)',
          },
          ballShear: {
            Label: 'Ball shear to destruction, 20 bonds (1.0 h)',
          },
          pullStrength: {
            Label: 'Wire pull to destruction, 30 wires (2.0 h)',
          },
          dispenseVolume: {
            Label: 'Epoxy dispense volume',
          },
          padClean: {
            Label: 'Incoming pad contamination',
          },
          capillaryBonds: {
            Label: 'Bonds since capillary change',
          },
          ultrasonicDelivered: {
            Label: 'Ultrasonic power delivered',
          },
        },
        aspect: {
          epoxy: {
            Action: 'Re-calibrate the dispense pump and purge the syringe',
            Label: 'Die attach dispense',
          },
          pads: {
            Action:
              'Not a repair here — the pads are contaminated and the plasma clean ' +
              'upstream owns it',
            Label: 'Incoming pad condition',
          },
          capillary: {
            Action: 'Change the capillary and re-qualify the ball geometry',
            Label: 'Capillary',
          },
          transducer: {
            Action:
              'Replace the transducer and re-calibrate the power delivered against a ' +
              'reference',
            Label: 'Ultrasonic transducer',
          },
        },
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'Force, ultrasonic power and time make a window, not a setting — and the ' +
          'window moves with every micron of wire. Ask me where its edges are.',
      },
      packaging: {
        failure: {
          'wire sweep': 'wire sweep',
          'mould void': 'mould void',
          popcorn: 'popcorn',
          warpage: 'warpage',
          none: 'none',
        },
        ActionMouldCureReflow: 'Mould, cure, reflow, and pass the packages on',
        NoteNoDieOnThePress: 'No die on the press. Bond a lot at BOND-16 and bring it here.',
        SecondaryLabel: 'Internal steam at reflow',
        ToolLabel: 'PKG-17 Mould and Cure',
        NextEveryPackageThatLeavesHere: 'Every package that leaves here goes to FT-18 and then to a customer. Which ' +
          'of the failures on this panel would burn-in find, and which would only ' +
          'appear on a board?',
        AxisXCureMin: 'cure (min)',
        TitlePackageYieldAgainstPostMould: 'Package yield against post-mould cure',
        HypTransferMouldingFillsACavity: 'Transfer moulding fills a cavity with a viscous compound around wires that ' +
          'can be pushed over. The cure that follows cross-links it. If the cure ' +
          'matters mechanically rather than cosmetically, warpage and yield should ' +
          'keep moving after the part looks finished - and a style with no wires at ' +
          'all should sidestep a whole failure mode.',
        AskedWhatIsTheCureFor: 'What is the cure for, and what does the package style change?',
        SumTransferMouldingVoidsCureAnd: 'Transfer moulding, voids, cure, and what the package style changes.',
        NextTheStressFreeTemperatureIs: 'The stress-free temperature is the mould temperature. What would moulding ' +
          '30 °C cooler do to both of these curves - and what would it cost in cure ' +
          'and in flow?',
        LabelAtRoomTemperature: 'at room temperature',
        LabelAtReflow: 'at reflow',
        AxisXMouldCapM: 'mould cap (µm)',
        TitleWarpageAgainstMouldCapThickness: 'Warpage against mould cap thickness',
        LabelMouldVoids: 'Mould voids',
        NoteTheOneThatMatters: 'the one that matters',
        LabelWarpAtReflow: 'Warp at reflow',
        LabelWarpAtRoom: 'Warp at room',
        HypMouldCompoundSiliconAndLaminate: 'Mould compound, silicon and laminate have different expansion coefficients ' +
          'and were bonded together at the mould temperature. That temperature is the ' +
          'only one at which the package is stress-free; everywhere else it curls. If ' +
          'so, warpage should depend on the THICKNESS RATIO of the layers rather than ' +
          'on any one of them, and there should be a balance point.',
        AskedWhatMakesThePackageWarp: 'What makes the package warp, and which warpage is the one that matters?',
        SumABimetallicStripMadeOf: 'A bimetallic strip made of three materials, and the warpage that decides ' +
          'the balls.',
        NextReflowPeakIsTheOther: 'Reflow peak is the other half of this. If the assembly house runs 5 °C ' +
          'hotter than the profile says, which of these numbers moves - and would ' +
          'anyone find out before the parts were on boards?',
        AxisYFractionOfSaturation: 'fraction of saturation',
        LabelSteamPressure: 'steam pressure',
        AxisXHoursOnTheFloor: 'hours on the floor',
        TitleSteamPressureAgainstFloorLife: 'Steam pressure against floor life',
        LabelSteamPressureL116: 'Steam pressure',
        LabelMoisture: 'Moisture',
        HypMouldCompoundAbsorbsWaterFrom: 'Mould compound absorbs water from the air, slowly and reversibly. At ' +
          'reflow the part goes past 250 °C in seconds and that water becomes steam ' +
          'inside a sealed cavity. If that is the mechanism, the failure should ' +
          'depend on how long the part SAT rather than on anything that was done to ' +
          'it - a defect created by storage.',
        AskedWhyDoesAPackageThat: 'Why does a package that passed everything fail when it is reflowed?',
        SumAbsorbedMoistureSteamPressureAt: 'Absorbed moisture, steam pressure at the die interface, and the floor-life ' +
          'clock.',
        fault: {
          wetParts: {
            Evidence: [
              'Out-of-bag time far past the 48 h limit.',
              'Reflow cross-section shows delamination above the 2% limit.',
              'Oven profile is on target — this is not an over-temperature failure.',
            ],
            Truth: 'The dry-pack was opened and the parts sat out for a week. Absorbed ' +
              'moisture flashes to steam at reflow and blows the package apart from the ' +
              'inside — the classic popcorn crack. Nothing at PKG-17 is broken; the ' +
              'handling discipline is, and the only fix is a 24 hour bake.',
            Complaint: 'Delamination and cracked packages after reflow, on a lot that moulded and ' +
              'tested clean.',
            Label: 'Parts have absorbed moisture',
          },
          staleCompound: {
            Evidence: [
              'Compound age at 85% of shelf life against a 30% limit.',
              'Flow length short.',
              'Transfer pressure nominal — the press is doing its job.',
            ],
            Truth: 'The compound has advanced in the bag and gels earlier than it should. It ' +
              'produces exactly the same short shot as a weak press — and the press ' +
              'readings are perfectly normal, which is the only thing that separates them ' +
              'at the bench.',
            Complaint: 'Same incomplete fill, and the press was re-calibrated last week.',
            Label: 'Mould compound past shelf life',
          },
          lowTransfer: {
            Evidence: [
              'Transfer pressure at 45% against a 90% floor.',
              'Flow length below the 55 mm limit.',
              'Compound age is well inside shelf life, which rules out the lookalike.',
            ],
            Truth: 'The transfer ram is delivering 45% of nominal pressure. The compound does ' +
              'not reach the end of the cavity before it gels, so the far corners are ' +
              'unfilled. It is a press problem and the compound is in date.',
            Complaint: 'Incomplete fill at the far corners of the larger body size.',
            Label: 'Transfer pressure low',
          },
          pkgHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Flow length and reflow survival are both good.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the lot.',
            Complaint: 'Routine lot. Warpage and visual are both normal.',
            Label: 'Nothing wrong',
          },
        },
        NoteHAtRh: '{{a}} h at {{b}}% RH',
        NoteMmBody: '{{a}} mm body',
        SectionWarpageIsGeometryNotProcess: 'Warpage is geometry, not process',
        NoteThePackageIsFlatAt: 'The package is flat at the mould temperature and nowhere else. Everything ' +
          'after that is a temperature difference acting on a mismatch in expansion, ' +
          'and it goes as the SQUARE of the body size — so it is fixed with a ' +
          'material or a thickness, never at the press.',
        LabelWarpageAtRoom: 'Warpage at room',
        LabelWarpageAtReflow: 'Warpage at reflow',
        Note100MToSeatThe: '< 100 µm to seat the balls',
        LabelMoistureAbsorbed: 'Moisture absorbed',
        LabelSteamAtTheInterface: 'Steam at the interface',
        NoteInterfaceHolds26Bar: 'interface holds 26 bar',
        LabelWireSweep: 'Wire sweep',
        NoteWiresTouchPast04: 'wires touch past 0.4',
        LabelPackageYield: 'Package yield',
        AssistantLead: 'PKG-17 inquiry assistant',
        AssistantBlurb:
          'Ask why a good die failed at reflow, what makes the package warp, or what ' +
          'the post-mould cure is for. Every answer is a mould and reflow run against ' +
          'this recipe.',
        reading: {
          popcorn: {
            Limit: 'no crack, < 2 bar',
            Label: 'Reflow 20 units and cross-section (4.0 h)',
          },
          mouldVoid: {
            Label: 'Cross-section 12 units for voids — destructive (2.5 h)',
          },
          ovenPeak: {
            Label: 'Reflow peak offset',
          },
          moisture: {
            Label: 'Dry-pack exposure',
          },
          compoundAge: {
            Label: 'Compound age against shelf life',
          },
          transferPressure: {
            Label: 'Transfer pressure',
          },
        },
        aspect: {
          oven: {
            Action: 'Re-profile the oven against an instrumented board',
            Label: 'Reflow oven profile',
          },
          drypack: {
            Action: 'Bake the lot out at 125 °C and restore the dry-pack discipline',
            Label: 'Dry-pack handling',
          },
          compound: {
            Action: 'Scrap the open pellets and load a fresh, in-date lot',
            Label: 'Mould compound',
          },
          press: {
            Action: 'Re-calibrate the transfer ram pressure and re-check the plunger seals',
            Label: 'Mould press',
          },
        },
      },
      finaltest: {
        ValueNo: 'no',
        ValueYes: 'yes',
        ValueDays: '{{a}} days',
        ActionBurnInTestAndBin: 'Burn in, test, and bin',
        UnitPoints: 'points',
        NoteNoLotOnTheHandler: 'No lot on the handler. Package a lot at PKG-17 and bring it here.',
        NoteThisPopulationIsNot: 'this population is not failing early',
        NoteInfantMortalityScreening: 'infant mortality — screening removes it',
        LabelEscapes: 'Escapes',
        SecondaryLabel: 'Burn-in fallout drift',
        ToolLabel: 'FT-18 Final Test and Burn-in',
        LabelLotHours: 'lot hours',
        TitleAndWhatItCostsThe: 'And what it costs the floor',
        AxisYPartsEscaping: 'parts escaping',
        AxisXTestSecondsPerPart: 'test seconds per part',
        TitleEscapesAgainstTestTime: 'Escapes against test time',
        LabelRoomOnlyEscapes: 'Room-only escapes',
        LabelLotTime: 'Lot time',
        HypMoreTestTimeMeansMore: 'More test time means more patterns run and more of the part exercised, so ' +
          'escapes should fall - with diminishing returns, since the easy failures ' +
          'are found first. Test time is multiplied by the lot size, so the marginal ' +
          'second is expensive in a way that is easy to underestimate on a per-part ' +
          'basis.',
        AskedHowMuchTestTimeDoes: 'How much test time does this part need, and what buys the most?',
        SumTestTimeAgainstEscapesAnd: 'Test time against escapes, and the temperature corners that cost the most.',
        NextBond16GrowsIntermetallicThat: 'BOND-16 grows intermetallic that keeps growing at operating temperature. ' +
          'Is that a mechanism burn-in would catch, or one it would accelerate into a ' +
          'part that was fine?',
        LabelLotTimeL241: 'lot time',
        AxisYLotHours: 'lot hours',
        TitleWhatItCosts: 'What it costs',
        AxisYFractionScreened: 'fraction screened',
        AxisXBurnInHours: 'burn-in hours',
        TitleScreenedFractionAndLotTime: 'Screened fraction and lot time against burn-in',
        LabelJustified: 'Justified',
        LabelScreened: 'Screened',
        LabelFieldHours: 'Field hours',
        LabelAcceleration: 'Acceleration',
        HypBurnInWorksByRunning: 'Burn-in works by running the part hot so that failure mechanisms proceed ' +
          'faster than they would in service. It only screens anything if the ' +
          'population has infant mortality to screen - a Weibull shape below one. On ' +
          'a healthy population every hour of burn-in is consuming useful life from ' +
          'parts that were going to be fine.',
        AskedIsBurnInEarningThe: 'Is burn-in earning the hours it takes on this population?',
        SumInfantMortalityTheAccelerationFactor: 'Infant mortality, the acceleration factor, and when burn-in is only ' +
          'consuming life.',
        NextPrb14AlreadyAppliedA: 'PRB-14 already applied a guard band to the die inside these parts. Two ' +
          'guard bands in series scrap more than either alone - is the second one ' +
          'finding anything the first did not?',
        LabelGoodPartsScrapped: 'good parts scrapped',
        LabelBadPartsShipped: 'bad parts shipped',
        AxisXGuardBandFractionOfSpec: 'guard band (fraction of spec width)',
        TitleTwoErrorsOneKnob: 'Two errors, one knob',
        HypTheTesterMeasuresWithAn: 'The tester measures with an uncertainty of its own, so a part near the ' +
          'limit may read on either side of it. Pulling the limit inward catches more ' +
          'of the marginal bad parts and necessarily also rejects more of the ' +
          'marginal good ones. If that is right, there is no setting with zero of ' +
          'both - only a choice about which error the business would rather make.',
        AskedWhatDoesMovingTheTest: 'What does moving the test limit inward actually buy and cost?',
        SumEscapesAgainstOverkillAndWhy: 'Escapes against overkill, and why neither can be zero.',
        fault: {
          infantMortality: {
            Evidence: [
              'Weibull shape 0.35 against a 0.45 floor — worse than the population ' +
                'burn-in was sized for.',
              'Burn-in fallout far above limit while re-test agreement is perfect.',
              'Gauge, contactor and handler are all nominal.',
            ],
            Truth: 'Nothing is wrong with the tester. The incoming Weibull shape has fallen to ' +
              '0.35 — a a weak tail, which is what infant mortality IS — and burn-in is ' +
              'doing exactly its job by finding it. The problem is at the front end, and ' +
              'burn-in is the only reason it is not at the customer.',
            Complaint: 'Burn-in fallout has tripled. Test engineering wants the tester ' +
              're-calibrated.',
            Label: 'Incoming population has infant mortality',
          },
          coldHandler: {
            Evidence: [
              'Handler soak offset −18 °C against a ±3 °C window.',
              'Re-test agreement is normal, because the tester is fine.',
              'Field returns rather than yield loss — an escape, not overkill.',
            ],
            Truth: 'The handler is 18 °C below the temperature the log claims. The parts are ' +
              'being hot-tested cold, so the hot limits are not really being exercised — ' +
              'the test is passing units it should fail, and that is an ESCAPE, which is ' +
              'the expensive direction to be wrong in.',
            Complaint: 'Hot tests pass a little too easily, and field returns have started ' +
              'arriving from one customer.',
            Label: 'Handler not reaching soak temperature',
          },
          wornContactor: {
            Evidence: [
              'Insertions past the 300,000 limit.',
              'Re-test disagreement above limit.',
              'Gauge sigma is inside limit, so the measurement chain itself is sound.',
            ],
            Truth: 'The contactor is past its insertion limit and adding resistance. It ' +
              'produces the same re-test disagreement a noisy tester does — and the ' +
              'insertion count names it, while the gauge calibration comes back clean.',
            Complaint: 'Continuity failures on one site of a four-site handler.',
            Label: 'Contactor worn',
          },
          noisyTester: {
            Evidence: [
              'Gauge sigma 16% against a 6% limit.',
              'Re-test disagreement far above 0.5% — the same units bin differently twice.',
              'Contactor insertions and handler temperature are both nominal.',
            ],
            Truth: 'The tester gauge sigma is 16% of the spec width against a 6% limit. Good ' +
              'units near a limit are being failed — the loss is OVERKILL, not defects, ' +
              'and it is invisible unless somebody re-tests the rejects. Two points of ' +
              'yield are being thrown away every shift.',
            Complaint: 'Bin 1 yield has dropped two points and the customer has not returned a ' +
              'single failure.',
            Label: 'Tester measurement chain noisy',
          },
          ftHealthy: {
            Evidence: [
              'Every bench reading is nominal.',
              'Re-test agreement is inside limit.',
            ],
            Truth: 'Nothing is wrong. The correct call is to ship the lot.',
            Complaint: 'Routine lot. Bin split matches the forecast.',
            Label: 'Nothing wrong',
          },
        },
        NotePartsAtS: '{{a}} parts at {{b}} s',
        NoteGoodPartsScrapped: '{{a}} good parts scrapped',
        NotePartsEachOneAReturn: '{{a}} parts — each one a return',
        NoteFromHOfBurnIn: 'from {{a}} h of burn-in',
        NoteCAgainstCUse: '{{a}} C against {{b}} C use',
        SectionTheGuardBandIsThe: 'The guard band is the whole decision',
        NoteATesterMeasuresWithError: 'A tester measures with error, so some bad parts read good and some good ' +
          'parts read bad. Moving the trip point inward buys fewer escapes and pays ' +
          'for them in overkill, one for one. There is no setting that avoids both, ' +
          'and where you sit on that trade is a calculation about what a return costs ' +
          'you.',
        LabelAccelerationFactor: 'Acceleration factor',
        LabelEquivalentFieldLife: 'Equivalent field life',
        LabelBurnInJustified: 'Burn-in justified',
        LabelOverkill: 'Overkill',
        LabelTestFloorTime: 'Test floor time',
        AssistantBlurb:
          'Ask what a guard band costs, whether burn-in is earning its hours, or how ' +
          'much test time is enough. Every answer is a lot run against this tester.',
        AssistantLead: 'FT-18 inquiry assistant',
        reading: {
          burnInFallout: {
            Label: 'Burn-in fallout on 500 units (12 h)',
          },
          retest: {
            Label: 'Re-test 200 units and compare bins (1.0 h)',
          },
          weibull: {
            Limit: '> 0.45 (qualified: 0.55)',
            Label: 'Incoming Weibull shape',
          },
          handlerTemp: {
            Label: 'Handler soak offset',
          },
          contactorInsertions: {
            Label: 'Contactor insertions',
          },
          gaugeSigma: {
            Label: 'Tester gauge sigma',
          },
        },
        aspect: {
          population: {
            Action: 'Not a repair here — the die have infant mortality and the front end owns it',
            Label: 'Incoming population',
          },
          handler: {
            Action: 'Re-calibrate the handler soak thermocouples',
            Label: 'Handler temperature',
          },
          contactor: {
            Action: 'Change the contactor and re-verify continuity on a golden unit',
            Label: 'Contactor',
          },
          gauge: {
            Action: 'Re-calibrate the tester and verify against a golden unit',
            Label: 'Tester measurement chain',
          },
        },
      },
      common: {
        StartProcess: 'Start process',
        HoldProcess: 'Hold process',
        LoadWafer: 'Load fresh wafer',
        Measuring: 'Measuring…',
        SessionRecord: 'SimFab — session record',
        ExportRecord: 'Export session record',
      },
      scope: {
        Pending:
          'This station is placed and enterable but not yet simulated. Thirteen ' +
          'stations run their full physics today, from lithography and etch through ' +
          'planarisation and sort to packaging and final test — and the architecture ' +
          'proven across those is what the rest are built on.',
        NothingFaked:
          'Nothing is faked in the meantime. Below is the scope {{tag}} has to cover: ' +
          'it is the same page each of the live stations was built from.',
        WhereItSits: '{{zone}} — where this sits in the flow',
        Takes: 'Takes',
        Makes: 'Makes',
        Macro: 'Macroscopic — protocols and processes',
        Micro: 'Microscopic — the science beneath',
        Engines: 'Planned engines',
        Competency: 'Competency this station serves',
        BackToFloor: 'Back to the floor',
      },
      deposition: {
        MdChartTitle: 'Atoms in the film against time',
        MdChartX: 'simulated time (ps)',
        MdChartY: 'atoms in the film',
        MdChartSeries: 'In the film',
        MdChartAllVapor: 'every vapor atom',
        MdStatus: '{{a}} of {{b}} precursor atoms in the film · wafer {{c}} K · {{d}} ps',
        MdTemp: 'Wafer temperature',
        MdReset: 'Reset',
        MdPause: 'Pause',
        MdRun: 'Run',
        MdFailed: 'The molecular-dynamics engine failed to load.',
        MdLoading: 'Loading the molecular-dynamics engine…',
        MdNote: 'A live molecular-dynamics simulation, not an animation: vapor ' +
          'atoms over a fixed wafer slab, Lennard-Jones forces, a thermostat on ' +
          'everything that moves, and a gentle downward flux standing in for the ' +
          'delivery flow. Nothing tells an atom to stick - a cold wafer captures the ' +
          'vapor into a growing film and a hot wafer boils it back off. Drag the ' +
          'temperature across a few hundred kelvin and watch condensation and ' +
          'desorption trade places.',
        RxStatus: '{{a}} of {{b}} precursor Si grown into the wafer · {{c}} H on the surface · wafer {{d}} K · {{e}} ps',
        RxGas: 'In the gas:',
        RxGasEmpty: 'nothing left in the gas',
        RxChartTitle: 'Grown silicon and surface hydrogen against time',
        RxChartY: 'atoms',
        RxChartGrown: 'Si grown into the wafer',
        RxChartSurfaceH: 'H on the surface',
        RxChartAllSi: 'every precursor Si',
        RxChartAllSites: 'every dangling bond',
        RxPrecursor: 'Precursor',
        RxSilane: 'Silane (SiH₄, thermal CVD)',
        RxSilyl: 'Silyl radicals (SiH₃, plasma CVD)',
        RxSurface: 'Wafer surface',
        RxBare: 'Bare silicon',
        RxHydrogen: 'Hydrogen-terminated',
        SectionRx: 'The other half — a precursor that reacts: bonds forming and breaking (reactive molecular dynamics)',
        RxNote: 'The same engine with a reactive force field: every atom pair carries a live bond order, and a stick is drawn wherever it clears the threshold this step. A silane molecule arrives at the wafer, one of its Si-H bonds breaks against a dangling bond, a Si-Si bond forms, and the silicon is part of the film - chemisorption, watched, with nothing scripting it. The precursor is silane on silicon rather than this recipe\'s trimethylaluminium: the force field carries silicon and not aluminium, and silane on silicon is the CVD chemistry the window chart above competes ALD against, so it is the right thing to watch here, not a stand-in. Two toggles are chemistry: silane needs the surface to break a bond before it sticks, a plasma\'s silyl radicals do not - which is why plasma CVD runs hundreds of kelvin cooler - and a hydrogen-terminated wafer, the state an HF-last clean leaves it in, takes nothing until hydrogen leaves as H₂.',
        RxTimeNote: 'READ THE MECHANISM AND THE ORDERING, NOT A RATE. An event that takes microseconds in a 900 K reactor cannot be waited for here, so the thermostat runs the chamber hot enough for the same mechanism to happen in picoseconds, and the silicon parameters are representative rather than a transcription of a published set. What survives that compression is what matters: radicals stick before molecules, bare silicon takes precursor before passivated silicon, and hydrogen has to leave before growth resumes. What does not survive it is a growth rate in nanometres per minute - that is what the reactor solver in the EPI tool is for.',
        SectionMd: 'Below the window — a precursor that condenses instead of reacting (molecular dynamics)',
        MdWindowNote:
          'READ THIS AS THE COLD END OF THE WINDOW, NOT AS HOW THE FILM ABOVE GROWS. The surface model ' +
          'higher up competes four processes, and CONDENSATION is one of them: below the window a ' +
          'precursor stops reacting with the surface and simply physisorbs in multilayers, which is ' +
          'growth that does not stop when the sites are full. That is the failure this shows, and it ' +
          'is worth watching, because a dose that condenses looks like a wonderfully high growth rate ' +
          'right up until the thickness map is measured. What it does NOT show is the half-reaction the ' +
          'flat middle of the window is made of. ALD grows by CHEMISORPTION - a ligand exchange that ' +
          'forms and breaks real bonds and then has nowhere left to go - and bonds are exactly what a ' +
          'Lennard-Jones potential does not have. So the atoms here are a generic condensable vapor ' +
          "rather than this recipe's trimethylaluminium: a physisorption well is honest about sticking " +
          'and silent about chemistry, and pretending otherwise would be the one thing this panel spends ' +
          'three instruments proving is not true of ALD.',
        presetLabel: {
          highAspect: 'HIGH ASPECT RATIO',
          tooHot: 'ABOVE WINDOW',
          tooCold: 'BELOW WINDOW',
          shortPurge: 'SHORT PURGE',
          starvedDose: 'SHORT DOSE',
          baseline: 'BASELINE-AL2O3',
        },
        SecondaryLabel: 'Non-uniformity',
        ToolLabel: 'ALD-01 · Al2O3 deposition',
        fault: {
          wetHouseGas: {
            Evidence: [
              'Growth per cycle is high with a large parasitic CVD fraction - the ' +
                'signature of a purge failure, which is what makes this so convincing.',
              'The foreline pumping speed check is CLEAN. That is the reading that turns ' +
                'it from a repair into an escalation.',
              'Saturation is still 100% and the instrumented wafer is at setpoint, so ' +
                'neither delivery nor temperature is involved.',
              'The furnace is complaining at the same time, about something that sounds ' +
                'unrelated. It is not unrelated.',
            ],
            Truth: 'The house dry-gas dryer failed wet over the weekend and the header is ' +
              'carrying about 2% water - carryover, not the ppm-level creep a saturated ' +
              'dryer gives. It reaches every tool that breathes house nitrogen or oxygen, ' +
              'and what it does when it gets there depends entirely on what that tool was ' +
              'using the gas for. Note how far apart the two thresholds are: ALD is ' +
              'ruined by single-figure ppm, because water is its co-reactant, while a ' +
              'furnace growing oxide in steam does not notice anything below about a ' +
              'percent. It takes a gross failure for both tools to complain at once, and ' +
              'that is exactly why the two groups rarely connect their problems. At this ' +
              'tool it is worse than anywhere else in the building, because water is what ' +
              'this process uses as its co-reactant. Wet purge gas puts water on the ' +
              'surface during the TMA dose, which is the one thing the purge exists to ' +
              'prevent - so the film sees exactly what it would see from a purge that was ' +
              'too short, and no amount of purging fixes it.',
            Complaint: 'Growth is running high and the film etches back unevenly - same as the ' +
              'foreline problem. The foreline was cleared last month and the pumping ' +
              'speed check was fine. THRM-04 is also reporting thick oxide.',
            Label: 'Water in the house purge gas',
          },
          heaterReadsLow: {
            Evidence: [
              'Growth per cycle is far above nominal with a very large parasitic fraction.',
              'The instrumented wafer reads well ABOVE setpoint - the opposite sign to ' +
                'the other heater fault, and the reason the same repair fixes both.',
              'Purge length makes almost no difference here, which rules out the foreline.',
            ],
            Truth: 'The thermocouple has drifted the other way: the controller pushes the ' +
              'heater to hold a reading of 200 C while the wafer is nearer 350 C. That is ' +
              'above the ALD window, where TMA decomposes on its own without waiting for ' +
              'the co-reactant. The result is CVD wearing an ALD recipe: fast, thick, and ' +
              'conformal only by luck.',
            Complaint: 'Growth is way high and step coverage failed on the high-aspect lot.',
            Label: 'Heater thermocouple reading low',
          },
          heaterReadsHigh: {
            Evidence: [
              'Growth per cycle is high and the parasitic fraction is high - identical in ' +
                'shape to a restricted foreline.',
              'The instrumented wafer is the only reading that separates them, and it ' +
                'reads about 85 C below setpoint.',
              'Saturation is still 100%: this is not a supply problem.',
            ],
            Truth: 'The thermocouple has drifted, so the controller believes it is at 200 C ' +
              'while the wafer sits near 115 C. That is below the ALD window, where TMA ' +
              'condenses on the surface instead of chemisorbing to a monolayer. Condensed ' +
              'precursor is not self-limiting, so growth per cycle goes UP - which is why ' +
              'a cold wafer looks like a purge failure and not like a cold ampoule.',
            Complaint: 'Same as the foreline problem last quarter: growth high, film hazy, HF ' +
              'etch-back uneven.',
            Label: 'Heater thermocouple reading high',
          },
          restrictedExhaust: {
            Evidence: [
              'Growth per cycle is ABOVE nominal, with a parasitic CVD fraction well ' +
                'above zero.',
              'Saturation is still 100% - the dose itself is healthy, which rules out a ' +
                'delivery fault.',
              'An instrumented wafer reads at setpoint, which is what separates this from ' +
                'a heater fault - the other way to get high growth and high CVD on this ' +
                'tool.',
              'Lengthening the purge in the recipe recovers the rate, which is a ' +
                'diagnosis by itself.',
            ],
            Truth: 'Deposit build-up in the foreline has cut the effective pumping speed to ' +
              'about a third, so the chamber no longer clears during the purge. TMA is ' +
              'still present when the water arrives, and part of the growth becomes CVD: ' +
              'faster, not self-limiting, and not uniform.',
            Complaint: 'Growth per cycle is running HIGH and the film etches back unevenly in HF.',
            Label: 'Restricted foreline',
          },
          coldAmpoule: {
            Evidence: [
              'Saturation has fallen below 95% - the dose is no longer finishing. This is ' +
                'the reading that matters; no other fault on this tool touches it.',
              'Non-uniformity is far out of spec while the mean has barely moved, which ' +
                'is the signature of falling off saturation rather than of a hot or cold ' +
                'wafer.',
              'A LONGER dose recovers the film. A temperature fault would not respond to ' +
                'that.',
              'Ampoule temperature reads well under its setpoint.',
            ],
            Truth: 'The ampoule heater has drifted low, so the TMA vapour pressure - and with ' +
              'it the precursor partial pressure in the chamber - is under half of ' +
              'nominal. The dose no longer saturates. That is what makes this serious: ' +
              'while the dose saturates, the film does not care how the precursor was ' +
              'distributed, and the moment it stops saturating, the film cares about ' +
              'nothing else. Uniformity goes before the mean does.',
            Complaint: 'Wafer maps have gone bullseye over the last three lots and the mean has ' +
              'slipped a little low. Recipe has not been touched.',
            Label: 'Cold TMA ampoule',
          },
          healthy: {
            Evidence: [
              'Growth per cycle is at the qualified value and the dose is saturating.',
              'Every reading at the bench is inside its limit.',
            ],
            Truth: 'Nothing is wrong. The correct call is to run the wafer.',
            Complaint: 'Tool passed qualification this morning. Nothing reported.',
            Label: 'Qualified tool',
          },
        },
        reading: {
          flowcheck: {
            Label: 'Showerhead flow check (60 min)',
          },
          houseutilities: {
            Limit: 'header tight, PCW at supply, dry gas < 50 ppm',
            Label: 'House utility check with facilities (2 h)',
          },
          foreline: {
            Label: 'Foreline pumping speed check (40 min)',
          },
          parasitic: {
            Label: 'HF etch-back on a monitor (45 min)',
          },
          instrumented: {
            Label: 'Instrumented wafer (90 min)',
          },
          ampoule: {
            Label: 'Ampoule temperature check (20 min)',
          },
          saturation: {
            Label: 'Dose saturation curve (25 min)',
          },
          setpoint: {
            Limit: 'as recipe',
            Label: 'Heater setpoint',
          },
          step: {
            Label: 'Step coverage at 10:1',
          },
          edge: {
            Label: 'Edge minus centre',
          },
          nu: {
            Label: 'Non-uniformity',
          },
          gpc: {
            Label: 'Growth per cycle',
          },
        },
        aspect: {
          house: {
            Action: 'Stop work on the tool and raise it with facilities',
            Label: 'Nothing on this tool — escalate to facilities',
          },
          showerhead: {
            Action: 'Remove and clean the showerhead, replace the gasket',
            Label: 'Showerhead',
          },
          heater: {
            Action: 'Re-calibrate the heater against an instrumented wafer',
            Label: 'Substrate heater',
          },
          purge: {
            Action: 'Clear the foreline restriction and re-verify pumping speed',
            Label: 'Purge / exhaust path',
          },
          precursor: {
            Action: 'Refill and re-heat the ampoule, verify delivery against the flow meter',
            Label: 'TMA delivery',
          },
        },
        knob: {
          doseMs: 'TMA dose',
          purgeMs: 'Purge after TMA',
          coReactantMs: 'H2O dose',
          coPurgeMs: 'Purge after H2O',
          pressureMtorr: 'Chamber pressure',
          carrierSccm: 'Carrier flow',
          substrateTempC: 'Substrate temperature',
          cycles: 'Cycles',
          aspectRatio: 'Feature aspect ratio',
        },
        preset: {
          baseline: 'Qualified recipe inside the ALD window: saturated dose, adequate purge.',
          starvedDose:
            'TMA dose cut to 15 ms. Below saturation, so growth per cycle falls and - ' +
            'worse - it now depends on how much precursor reached each part of the ' +
            'wafer, so uniformity goes with it.',
          shortPurge:
            'Purge cut to 200 ms. Precursor is still in the chamber when the water ' +
            'arrives, so part of the growth becomes parasitic CVD: faster, but not ' +
            'self-limiting and not uniform.',
          tooCold: 'Substrate at 100 C. Too cold for the surface reaction to complete.',
          tooHot:
            'Substrate at 360 C. The precursor decomposes on its own; growth stops ' +
            'being ALD.',
          highAspect:
            'Same recipe on a 60:1 feature. The trench bottom needs far more exposure ' +
            'than the top, and this dose does not supply it.',
        },
        SectionProcessState: 'Process state',
        SectionRunControl: 'Run control',
        RunControlNote:
          'ALD is counted in cycles, not seconds. There is no rate to set — you ' +
          'choose how many times to repeat the cycle, and the surface decides how ' +
          'much it takes each time.',
        StartCycling: 'Start cycling',
        Hold: 'Hold',
        RunToCompletion: 'Run to completion',
        LoadBareWafer: 'Load bare wafer',
        SectionWaferMap: 'Wafer map — deposited thickness',
        ModeThickness: 'Thickness',
        ModeDeviation: 'Deviation from mean',
        SectionRadialProfile: 'Radial profile',
        SectionSaturation: 'Saturation curve — is this dose self-limiting?',
        SaturationNote:
          'Growth per cycle against dose time, everything else held. A true ALD ' +
          'process rises and then goes FLAT: once every surface site is taken, more ' +
          'precursor does nothing. If this curve keeps climbing, the process is not ' +
          'self-limiting and it is not ALD.',
        MeasureSaturation: 'Measure saturation curve',
        SectionWindow: 'ALD window — the temperature band where this works',
        WindowNote:
          'Below the window the precursor condenses in multilayers; above it, it ' +
          'decomposes on its own without waiting for the co-reactant. Both grow film ' +
          'faster and neither is self-limiting. The window is the flat stretch left ' +
          'in between.',
        MeasureWindow: 'Measure the window',
        SectionRecipe: 'Recipe — {{code}}',
        SectionStoredRecipes: 'Stored recipes',
        TileGpc: 'Growth per cycle',
        TileThickness: 'Thickness',
        TileNonUniformity: 'Non-uniformity',
        TileSaturation: 'Saturation',
        TileParasitic: 'Parasitic CVD',
        TileStepCoverage: 'Step coverage',
        TileCycles: 'Cycles',
        TileCycleTime: 'Cycle time',
        TileRunTime: 'Run time',
        SelfLimiting: 'self-limiting',
        DoseStarved: 'dose is starved',
        TrueAld: 'true ALD',
        NotSelfLimiting: 'not self-limiting',
        AtAspect: 'at {{ratio}}:1',
        PurgeResidence: 'purge is {{factor}}x residence',
        CaptionNonUniformity: '{{percent}}% non-uniformity',
        CaptionNoFilm: 'growth per cycle — no film deposited yet',
        ChartProfileThickness: 'Deposited thickness across the radius',
        ChartProfileGpc: 'Growth per cycle across the radius',
        ChartProfileX: 'r / R  (0 = centre)',
        ChartSeriesFilm: 'film',
        NotMeasured: 'Not measured yet',
        ChartSaturationTitle: 'Growth per cycle versus dose — flat means self-limiting',
        ChartSaturationX: 'TMA dose (ms)',
        ChartSeriesGpc: 'growth per cycle',
        AnnYourDose: 'your dose',
        ChartWindowTitle: 'Growth per cycle versus temperature',
        ChartWindowX: 'substrate temperature (°C)',
        AnnYourSetpoint: 'your setpoint',
        ChartParasiticTitle: 'How much of that growth is NOT self-limiting',
        ChartParasiticY: '% parasitic',
        ChartSeriesParasitic: 'parasitic CVD',
        AnnAldLimit: '5% — practical ALD limit',
        SectionOnChuck: 'On the chuck',
        SendToMet: 'Send the wafer to MET-05',
        SectionAtoms: 'Atomic scale — one cycle, molecule by molecule',
        AtomsNote:
          'Not molecular dynamics — real MD lives at femtoseconds and this is a whole ' +
          'cycle. A lattice of surface sites with scripted reaction rules, slaved to ' +
          'the same rate equations the charts above are drawn from, and driven by the ' +
          'live recipe on this panel. Shorten a purge, or leave the window, and watch ' +
          'what the chemistry does.',
        AtomsRun: 'Resume',
        AtomsPause: 'Pause',
        AtomsReset: 'Reset',
        AtomsPhasePurge: 'purge',
        AtomsLegendOh: 'OH site — accepts TMA',
        AtomsLegendCh3: 'CH₃-terminated — full',
        AtomsLegendCh4: 'CH₄ leaving',
        AtomsLegendCvd: 'gas-phase CVD particle',
        AtomsCycle: 'cycle {{n}}',
        AtomsCoverage: 'coverage',
        AtomsFilm: 'film',
        AtomsCvdCount: 'CVD events',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'The question that matters here is not how fast the film grows, but whether ' +
          'growth still stops when the surface fills. Ask me to check.',
      },
      metrology: {
        D3Note: 'The box above this one sends the wave at a ROW of atoms and its screen is ' +
          'a line. A real sample is a sheet, periodic in two directions, and its ' +
          'screen is a plane of spots, which is what a diffraction instrument ' +
          'actually hands a technician. Read it as the lattice turned inside out: ' +
          'spots far apart mean atoms close together, and stretching the mesh along ' +
          'one direction closes the spots up along that direction and no other. Drag ' +
          'the energy down and the dashed circle shrinks until it holds only the ' +
          'centre cross; below 1.5 eV the wavelength is longer than the mesh and the ' +
          'sheet sends the wave on as one undivided beam, with almost nothing in it to say ' +
          'the atoms were there. Almost, because a packet this short is not one energy: its fast tail is still above the threshold and raises a faint first ring, a fifth as bright, until the knob is well below it. ' +
          'The spots are the Fourier transform of the wave the ' +
          'solver propagated, not a drawing: the crosses and the circle are the ' +
          'prediction, and they were put on the screen afterwards. The mesh is 1 nm, ' +
          'wider than a real surface net, so that both rings open inside the knob’s ' +
          'range; the geometry does not care about the scale. The cores are smooth ' +
          'repulsive bumps, there is one layer of them, and nothing scatters ' +
          'inelastically.',
        D3Energy: 'Electron energy',
        D3Rect: 'Stretched along z',
        D3Square: 'Square mesh',
        D3ScreenCaption: 'The screen, far away: where the wave past the sheet is going. Crosses are ' +
          'the mesh’s reciprocal lattice, 2π over the spacing; the dashed circle is ' +
          'the electron’s own wavenumber. A spot can only light on a cross inside the ' +
          'circle.',
        D3Idle: 'Press Run: the packet is propagated on a worker thread, five to ten ' +
          'seconds a run.',
        D3Gain: 'the wave is drawn ×{{m}} brighter than it is',
        D3Status: '{{fs}} of {{run}} fs · {{fwd}} % past the sheet · Σ|ψ|² = {{norm}}, ' +
          'margins counted',
        D3NoOrders: 'no diffracted beam exists: the wavelength is longer than the mesh',
        D3FirstOrder: 'a first-order spot holds {{p}} %, {{deg}}° off the beam',
        D3Straight: 'On the straight-through spot',
        D3NoNextRing: 'every ring in reach of this knob is open',
        D3NextRing: 'the next ring of spots opens at {{e}} eV',
        D3Beams: 'Beams the sheet can make',
        D3MeshRect: 'the mesh is {{a}} nm by {{b}} nm',
        D3MeshSquare: 'the mesh is {{a}} nm square',
        D3Wavelength: 'Wavelength against the mesh',
        D3Circle: 'a beam must fit inside k = {{k}} nm⁻¹',
        D3ScreenX: 'sideways wavevector k_y (nm⁻¹)',
        D3About: 'What this shows, and what the model leaves out',
        SectionDiffraction3d: 'A sheet of atoms, and the spots it leaves — electron diffraction in three ' +
          'dimensions (Schrödinger, split Cayley step)',
        UnitNmReported: 'nm reported',
        LabelFitResidual: 'Fit residual',
        SecondaryLabel: 'Gauge R&R',
        ToolLabel: 'METR-05 · Spectroscopic reflectometer',
        fault: {
          wrongIndex: {
            Evidence: [
              'The RESIDUAL is up five-fold with normal detector noise. That combination ' +
                'is the whole diagnosis: noisy data raise the residual AND the R&R, a wrong ' +
                'model raises only the residual.',
              'Gauge R&R is normal, so the measurement is repeatable - it is repeatably ' +
                'fitting the wrong material.',
              'An ellipsometer, which solves for n and thickness together, returns an ' +
                'index well above the oxide value.',
            ],
            Truth: 'The deposition tool has drifted nitrogen-rich, so the film is an ' +
              'oxynitride with an index near 1.54 while the measurement recipe still ' +
              'assumes 1.46 for pure oxide. The fit is being asked to reproduce fringes ' +
              'from the wrong material, and it compensates the only way it can - by ' +
              'reporting a thicker film. This is the one fault where the metrology tool ' +
              'is innocent and is reporting a real change upstream.',
            Complaint: 'Oxide thickness reads 4% high and the fits look slightly worse than usual.',
            Label: 'Film is not what the recipe thinks it is',
          },
          badStandard: {
            Evidence: [
              'Gauge R&R and residual are both normal - again nothing looks like a bad ' +
                'measurement.',
              'The error is a CONSTANT number of nanometres, not a percentage. On the ' +
                'two-point check it is the same 3 nm on the thin standard and on the thick ' +
                'one, which is exactly what a wavelength error would not do.',
              'Re-certifying the standard finds it thicker than its paperwork.',
            ],
            Truth: 'The certified standard the tool is zeroed against has been sitting in a ' +
              'drawer and has grown native oxide, so its true thickness is no longer what ' +
              'its certificate says. The tool was zeroed to a wrong number and now adds ' +
              'that error to everything it measures.',
            Complaint: 'Everything is reading about 3 nm thick, same as the grating problem last ' +
              'year.',
            Label: 'Calibration standard has grown native oxide',
          },
          gratingDrift: {
            Evidence: [
              'Gauge R&R is normal and the residual is normal. Nothing here looks like a ' +
                'bad measurement.',
              'The error is PROPORTIONAL to thickness - 3% on every film, thick or thin. ' +
                'A two-point check against certified standards at two different thicknesses ' +
                'shows the error growing with the film, which is what separates this from a ' +
                'bad standard.',
              'The tool is precise and wrong, which is the failure mode no control chart ' +
                'will ever catch.',
            ],
            Truth: 'The monochromator grating has slipped. Every wavelength is reported about ' +
              '3% high, so the fringes appear to sit at longer wavelengths than they do, ' +
              'and the fit returns a thickness about 3% thick. The measurement is ' +
              'perfectly repeatable and the residual is exactly nominal, because nothing ' +
              'is wrong with the DATA - only with the number written beside it. Two weeks ' +
              'of a process group chasing a tool that was never broken.',
            Complaint: 'Deposition has been reading 3% thick for two weeks. The deposition tool ' +
              'has been recalibrated twice and the etch group has started compensating ' +
              'for it.',
            Label: 'Monochromator out of calibration',
          },
          dimLamp: {
            Evidence: [
              'Gauge R&R is above 30%, so most of the variation being charted is the ' +
                'gauge itself.',
              'The MEAN of the repeats is still on the certified value - accuracy is ' +
                'untouched. Only precision is gone.',
              'Residual is up in proportion to the noise, as it should be when the data ' +
                'are noisy rather than when the model is wrong.',
            ],
            Truth: 'The lamp has aged and the collection optics are dirty, so detector noise ' +
              'is five times nominal. The fit is being asked to find a thickness in a ' +
              'noisier spectrum and it wanders. Nothing is wrong with the deposition tool ' +
              'and nothing is wrong with the ANSWER - the tool still scatters around the ' +
              'right value. What is gone is the ability to tell a real process shift from ' +
              'measurement scatter, which is the only thing this tool exists to do.',
            Complaint: 'Measurements are all over the place. Same wafer measured five times gives ' +
              'five different numbers. The engineer thinks the deposition tool has gone ' +
              'unstable.',
            Label: 'Lamp aged, detector noise up',
          },
          healthy: {
            Evidence: [
              'Reported thickness is on the certified value and gauge R&R is under 10%.',
            ],
            Truth: 'Nothing is wrong. The tool is telling the truth and telling it repeatably.',
            Complaint: 'Daily standard check passed. Nothing reported.',
            Label: 'Gauge qualified',
          },
        },
        reading: {
          lampcheck: {
            Label: 'Lamp output and dark-current check (1 h)',
          },
          ellipsometer: {
            Label: 'Cross-check on the ellipsometer, solving for n (4 h)',
          },
          certcheck: {
            Label: 'Re-certify the calibration standard elsewhere (5 days)',
          },
          twopoint: {
            Limit: 'both within +/- 1 nm',
            Label: 'Two-point check, 20 nm and 400 nm standards (3 h)',
          },
          model: {
            Limit: 'as recipe',
            Label: 'Assumed film index',
          },
          repeat: {
            Label: 'Repeatability, 1 sigma',
          },
          residual: {
            Label: 'Fit residual (RMS)',
          },
          grr: {
            Label: 'Gauge R&R',
          },
          reported: {
            Label: 'Certified standard, reported',
          },
        },
        aspect: {
          filmmodel: {
            Action: 'Re-measure n and k for this film, update the recipe model',
            Label: 'Optical model for the film',
          },
          standard: {
            Action: 'Recall the standard, re-certify it, re-zero the tool',
            Label: 'Calibration standard',
          },
          grating: {
            Action: 'Re-calibrate the grating against an emission line source',
            Label: 'Monochromator wavelength scale',
          },
          lamp: {
            Action: 'Replace the lamp, clean the collection optics',
            Label: 'Lamp and detector',
          },
        },
        SectionMeasurement: 'Measurement',
        SectionSpectrum: 'Reflectance spectrum and the fit',
        SpectrumNote:
          'The tool does not measure thickness. It measures reflected light against ' +
          'wavelength, and then fits a film stack to the fringes. The number it ' +
          'reports is whatever that fit converged on.',
        SectionTruth: 'What is really on the wafer',
        TruthNote: 'The film itself. A student would not normally know this.',
        SectionAssumed: 'What the tool recipe assumes',
        AssumedNote:
          'Set these two to different films and the tool will still report a ' +
          'thickness, to two decimal places, and be badly wrong. A measurement is a ' +
          'model, and a model can be confidently mistaken.',
        SectionGauge: 'Gauge capability',
        GaugeNote:
          'Measure the same unchanging wafer many times. The spread is the tool, not ' +
          'the process. What matters is that spread compared with the variation the ' +
          'process actually has — a gauge is never good or bad on its own, only good ' +
          'or bad for a job.',
        RunGauge: 'Run a gauge study (25 repeats)',
        SectionControl: 'Control chart — 60 lots',
        ControlNote:
          'Every point is the true process value plus measurement noise. The rules ' +
          'fire on both, and the chart cannot tell you which it reacted to. Deciding ' +
          'that is the job.',
        RunStable: 'Run a stable process',
        RunDrift: 'Run with a slow drift',
        RunNoisy: 'Run with a noisy gauge',
        ResetGauge: 'Reset the gauge',
        SectionAbsorption: 'Where the index comes from — the substrate absorbing light (tight-binding bands)',
        AbsNote: 'The fit above needs a refractive index for everything in the stack, and an index is not a constant of a material: it is what the material\'s electrons do when light of that energy arrives. Below the gap no photon can lift an electron across, the crystal is transparent and n barely moves; above it the crystal absorbs, k rises from zero and n swings with it. This is that spectrum computed from the same tight-binding bands CZ-07 draws - every vertical jump from a filled band to an empty one, over the whole zone, weighted by its strength. Silicon\'s onset is at its DIRECT gap, around 3 eV, far above its 1.1 eV fundamental gap, because a photon alone cannot supply the momentum an indirect jump needs: so the wafer is transparent across the reflectometer\'s red end and only absorbs hard in the blue. Germanium and gallium arsenide start low and absorb the whole sweep. The shape and the onset are the calculation; the scale is relative, since the model carries no excitonic or local-field corrections.',
        AbsChartTitle: 'Absorption against photon energy',
        AbsChartX: 'photon energy (eV)',
        AbsChartY: 'ε₂, relative',
        AbsOnset: 'onset {{ev}} eV (direct gap)',
        AbsGap: 'gap {{ev}} eV, indirect',
        AbsOnsetGap: 'onset {{ev}} eV, gap {{gap}} eV',
        AbsShort: '{{formula}} absorbs from {{onset}} eV · gap {{gap}} eV, {{kind}}',
        AbsHelp: 'The onset in nanometres, and what the amber rules mark',
        AbsStatus: '{{formula}} starts absorbing at {{onset}} eV ({{nm}} nm) · fundamental gap {{gap}} eV, {{kind}} · amber rules: the reflectometer\'s 800-400 nm sweep',
        AbsIndirect: 'indirect',
        AbsDirect: 'direct',
        AbsCrystal: 'Crystal',
        AbsFailed: 'The band-structure engine failed to load.',
        AbsLoading: 'Loading the band-structure engine…',
        SectionDiffraction: 'What an instrument can resolve — an electron wave meeting a target (Schrödinger, 2D)',
        PhononAcoustic: 'acoustic',
        PhononOptical: 'optical',
        PhononChartTitle: 'Phonon dispersion',
        PhononChartX: 'wavevector along Γ–X–W–K–Γ–L',
        PhononChartY: 'frequency (cm⁻¹)',
        PhononMeasured: 'measured {{cm}} cm⁻¹',
        PhononRamanTitle: 'First-order Raman line',
        PhononRamanX: 'Raman shift (cm⁻¹)',
        PhononRamanY: 'intensity, peak = 1',
        PhononRamanSeries: 'computed: the [001] singlet',
        PhononFailed: 'The crystal engine failed to load.',
        PhononShort: '{{material}}: Raman line {{line}} cm⁻¹ (measured {{measured}})',
        PhononHelp: 'Every number from this solve',
        PhononStatus: '{{material}}: Raman line (the [001] singlet) at {{line}} cm⁻¹ (measured {{measured}}) · in-plane doublet {{doublet}} cm⁻¹ · sound along [100] {{sound}} km/s (measured {{soundMeasured}}) · {{ms}} ms',
        PhononImaginary: 'unstable: an imaginary mode',
        PhononLoading: 'Loading the crystal engine…',
        PhononStrain: 'Biaxial strain',
        PhononCrystal: 'Crystal',
        SectionPhonons: 'What a Raman tool reads — the crystal\'s phonons and the line that moves with strain (Stillinger–Weber lattice dynamics)',
        PhononsNote: 'A Raman spectrometer measures light that comes back from the wafer shifted by exactly one quantum of lattice vibration, and a fab owns one because that quantum moves with strain: compress the lattice and the bonds stiffen, so the line climbs; stretch it and the line falls. Nothing below is a remembered curve. A 3×3×3 supercell of the chosen crystal is built under periodic boundaries on the same Stillinger–Weber force field the IMP-10 lattice runs on, one atom of each sublattice is nudged a hundredth of an ångström along each axis, and the force that lands on every other atom is read back — twelve force evaluations are the whole Hessian a two-atom basis needs. The dynamical matrix assembled from them gives the six branches along Γ–X–W–K–Γ–L, the density of states beside them, and at Γ the Raman line. Expect the honest force field\'s number, not the measured one: SW puts silicon\'s line near 593 cm⁻¹ against the measured 520, because it was fitted to the melting point and the cohesive energy, not to the phonons — as the band view at CZ-07 shows its computed and measured gap. Under biaxial strain the triply degenerate line splits into a singlet polarised along [001] and an in-plane doublet, and a tool looking straight down on a (001) wafer sees only the singlet; the doublet is drawn dashed because that geometry forbids it. SW gets the direction right — down under tension, up under compression — but not the size or the order: measured deformation potentials give silicon\'s singlet about −7 to −8 cm⁻¹ per percent, more than its doublet, while SW gives −4.4 with the doublet moving more; for germanium SW gives −1.4 against a measured −4.1. Omitted: anharmonic linewidth and temperature (the lines are drawn with a fixed 3 cm⁻¹ width) and isotope mass disorder.',
        Mode3dLoading: 'Building the crystal’s modes…',
        Mode3dStatusStill: '{{name}} at Γ · 0 cm⁻¹ · the whole crystal sliding as one: no restoring force, so no frequency',
        Mode3dWaveInfinite: 'infinite',
        Mode3dShort: '{{name}} · {{cm}} cm⁻¹ · wavelength {{wave}}',
        Mode3dHelp: 'More about this mode and this block',
        Mode3dStatus: '{{name}} · {{cm}} cm⁻¹ ({{thz}} THz) · wavelength {{wave}} · one cycle takes {{period}} fs, drawn {{slow}} times slower',
        Mode3dTogether: 'moving together',
        Mode3dAgainst: 'moving against each other',
        Mode3dMixed: 'partly in step',
        Mode3dSublattices: 'The two sublattices are {{how}} ({{phase}})',
        PhononRamanForbidden: 'in-plane doublet, forbidden looking down on (001)',
        PhononDosTitle: 'Density of states',
        PhononDosX: 'modes per cm⁻¹',
        Mode3dPartial: 'This wave is longer than the block, so only part of one wavelength is shown.',
        Mode3dBlock: '{{atoms}} atoms, {{cells}} cells',
        Mode3dLegend: 'Amber and blue are the two interlocking sublattices of the diamond structure.',
        Mode3dAmplitude: 'Drawn amplitude',
        Mode3dAmplitudeValue: '{{a}} Å (real thermal motion at 300 K: about {{thermal}} Å)',
        Mode3dPause: 'Pause',
        Mode3dPlay: 'Play',
        Mode3dChartMark: 'the mode drawn below',
        Mode3dSegment: 'Wavevector along',
        Mode3dFraction: 'Position along it',
        Mode3dBranch: 'Branch',
        DiffNote: 'A wave cannot resolve a feature smaller than its own wavelength, and that one fact sets the limit of every instrument on this floor: the reflectometer\'s light at hundreds of nanometres sees a film\'s thickness and nothing of its texture; the SEM\'s 1 keV electrons, with a wavelength of 0.04 nm, see a 20 nm line. This is that fact at an energy a grid can show. A Gaussian electron wave packet meets two slits or a row of atoms and what leaves the far side is collected on a screen by angle. A periodic target of spacing p sends a wavelength λ into orders at sin θ = mλ/p - the rules on the chart - and no order exists once λ is longer than p. Drag the energy down until it is, and the orders vanish: the wave leaves as one beam and the sample has no structure to it. Nothing is drawn in; the packet meets a potential and the margin of the box, an absorber, counts what arrives. Probability that comes back off the target is counted too, so the accounting sums to one.',
        DiffChartTitle: 'What reaches the screen, by angle',
        DiffChartX: 'angle from the forward direction (°)',
        DiffChartY: 'probability per degree, peak = 1',
        DiffChartSeries: 'On the screen',
        DiffOrder: 'm = {{m}}',
        DiffShort: 'λ = {{lambda}} nm · {{forward}}% forward, {{back}}% back',
        DiffHelp: 'The spacing, the orders and the clock',
        DiffStatus: 'λ = {{lambda}} nm against a spacing of {{spacing}} nm · orders at {{orders}} · {{forward}}% forward, {{back}}% back · {{fs}} fs',
        DiffNoOrders: 'none - the wavelength is longer than the spacing',
        DiffEnergy: 'Electron energy',
        DiffTarget: 'Target',
        DiffSlits: 'Two slits, 1.5 nm apart',
        DiffAtoms: 'A row of atoms, 0.5 nm apart',
        DiffRun: 'Run',
        DiffPause: 'Pause',
        DiffReset: 'Reset',
        DiffFailed: 'The wave solver failed to load.',
        DiffLoading: 'Loading the wave solver…',
        SectionShift: 'Shift',
        TileToolReports: 'Tool reports',
        TileActuallyIs: 'Actually is',
        TileError: 'Error',
        TileFitResidual: 'Fit residual',
        TileRepeatability: 'Repeatability',
        TileGaugeRR: 'Gauge R&R',
        TileCpk: 'Cpk',
        TileChartAlarms: 'Chart alarms',
        TileGaugeNoise: 'Gauge noise',
        NoteAssuming: 'assuming {{material}}',
        NoteWrongModel: 'wrong model assumed',
        NoteModelCorrect: 'model is correct',
        NoteResidualRaised: 'barely raised — it will not warn you',
        NoteResidualFloor: 'at the detector noise floor',
        NotMeasured: 'not measured',
        NoteToolSigma: '1σ of the tool',
        Capable: 'capable',
        NotCapable: 'NOT capable',
        RunAStudy: 'run a study',
        NoteAllFalse: 'process is stable — all false',
        NoteInLots: 'in 60 lots',
        NoteMeasurementSigma: 'measurement σ per lot',
        SectionLot: 'Lot on the stage',
        StageEmpty:
          'The stage is empty. Run a deposition at DEP-02 and send the wafer here, ' +
          'then measure it — the number this tool reports is the first time anyone ' +
          'finds out what that recipe actually made.',
        TravelCard: 'Travel card',
        TileReported: 'Reported thickness',
        TileOffTarget: 'Off target',
        TileNonUniformity: 'Non-uniformity',
        NoteModelMatch: 'how well the model matched',
        TileAssumedIndex: 'Assumed index',
        NoteFromRecipe: 'from the measurement recipe',
        TileVerdict: 'Verdict',
        VerdictShips: 'ships',
        VerdictHold: 'hold',
        NineSites:
          'Nine sites, centre to edge. This tool did not make the film and cannot ' +
          'repair it — if the number is wrong, the answer is upstream, and the gauge ' +
          'check in the shift report is how you rule this tool out first.',
        ChartSpectrumTitle: 'Measured spectrum against the fitted model',
        ChartWavelength: 'wavelength (nm)',
        ChartReflectance: 'reflectance',
        SeriesMeasured: 'measured',
        SeriesFitted: 'fitted model',
        ChartGaugeTitle: '25 measurements of one unchanging wafer — σ {{sigma}} nm',
        ChartGaugeEmpty: 'Not measured yet',
        ChartReadingNumber: 'reading number',
        ChartReportedThickness: 'reported thickness (nm)',
        SeriesReadings: 'readings',
        AnnTrueValue: 'true value',
        ChartSpcTitle: 'Control chart — {{alarms}} alarms, Cpk {{cpk}}',
        ChartSpcEmpty: 'Not run yet',
        ChartLot: 'lot',
        ChartThickness: 'thickness (nm)',
        SeriesTrue: 'true (hidden from the operator)',
        AnnCentre: 'centre',
        AnnSpec: 'spec',
        AssistantBlurb:
          'Ask a question and I will design an experiment, run it against this ' +
          'solver, and report what actually came back — then hand you a sharper ' +
          'question.',
        AssistantLead:
          'This tool only looks. Ask me whether what it is telling you is real — or ' +
          'whether it is the gauge, the model, or luck.',
      },
    },

    house: {

      gowning: {
        AssistantBlurb:
          'Ask why a bay that reads in class can still be contaminating wafers, or what one garment is worth in particles. Every answer is a run against this bay as it stands.',
        AssistantLead: 'GOWN-00 inquiry assistant',
      },

      crystal: {
        AssistantBlurb:
          'Ask about segregation down the ingot, how fast this crystal can be pulled, or where the oxygen comes from. Every answer is a growth run against this puller.',
        AssistantLead: 'CZ-07 inquiry assistant',
      },

      waferprep: {
        AssistantBlurb:
          'Ask what the saw costs in silicon, how deep the damage goes, or what makes a wafer bow. Every answer is a run against this line.',
        AssistantLead: 'WPR-08 inquiry assistant',
      },

      clean: {
        AssistantBlurb:
          'Ask whether a longer bath helps, what the queue after HF costs, or what the second bath is for. Every answer is a clean run against this bench.',
        AssistantLead: 'CLN-09 inquiry assistant',
      },

      implant: {
        AssistantBlurb:
          'Ask why a junction came out deeper than the tables say, when the lattice goes amorphous, or what energy actually sets. Every answer is an implant run against this beamline.',
        AssistantLead: 'IMP-10 inquiry assistant',
      },

      patterning: {
        AssistantBlurb:
          'Ask why the spaces alternate, what really sets the final pitch, or where the route runs out of room. Every answer is the eight-step route run against this recipe.',
        AssistantLead: 'PAT-21 inquiry assistant',
      },

      plating: {
        AssistantBlurb:
          'Ask why a trench voided, why the edge plates thicker, or how long to plate for. Every answer is a fill run against this bath.',
        AssistantLead: 'ECD-12 inquiry assistant',
      },

      inspection: {
        AssistantBlurb:
          'Ask what a lower threshold buys, what this recipe cannot see at all, or whether a partial scan is enough. Every answer is a scan run against this tool.',
        AssistantLead: 'INSP-13 inquiry assistant',
      },

      probe: {
        AssistantBlurb:
          'Ask why a bigger die yields worse, which yield model this line needs, or whether the loss is the wafer or the probe card. Every answer is a sort run against this lot.',
        AssistantLead: 'PRB-14 inquiry assistant',
      },

      dicing: {
        AssistantBlurb:
          'Ask how thin these wafers can go, whether a finer wheel pays for itself, or what cutting faster costs. Every answer is a grind and cut run against this recipe.',
        AssistantLead: 'DICE-15 inquiry assistant',
      },

      bonding: {
        AssistantBlurb:
          'Ask where the bonding window is, why pin count changes the yield, or what the cure is doing to the intermetallic. Every answer is a bond run against this machine.',
        AssistantLead: 'BOND-16 inquiry assistant',
      },

      packaging: {
        AssistantBlurb:
          'Ask why a good die failed at reflow, what makes the package warp, or what the post-mould cure is for. Every answer is a mould and reflow run against this recipe.',
        AssistantLead: 'PKG-17 inquiry assistant',
      },

      finaltest: {
        AssistantBlurb:
          'Ask what a guard band costs, whether burn-in is earning its hours, or how much test time is enough. Every answer is a lot run against this tester.',
        AssistantLead: 'FT-18 inquiry assistant',
      },

      gas: {
        AssistantBlurb:
          'Ask how many purge cycles are actually needed, whether the detector warns in time, or what reaches the stack. Every answer is an assessment run against this panel.',
        AssistantLead: 'GAS-19 inquiry assistant',
      },

      system: {
        vacuum: {
          Label: 'House process vacuum',
          Action: 'Escalate to facilities: leak-check the shared vacuum header',
        },
        pcw: {
          Label: 'Process cooling water',
          Action: 'Escalate to facilities: restore chilled water supply temperature',
        },
        gas: {
          Label: 'House dry-gas header',
          Action: 'Escalate to facilities: change the header dryer, purge and re-certify',
        },
        upw: {
          Label: 'Ultrapure water plant',
          Action: 'Escalate to facilities: regenerate the polishing mixed bed and re-qualify the loop',
        },
        hvac: {
          Label: 'Cleanroom air handling',
          Action: 'Escalate to facilities: restore the recirculation handlers to design flow',
        },
        mau: {
          Label: 'Make-up air unit',
          Action: 'Escalate to facilities: change the make-up air pre-filters and re-balance the cascade',
        },
        pumps: {
          Label: 'Tool dry pumps',
          Action: 'Escalate to facilities: swap the dry pump under the tool and re-qualify its foreline',
        },
        chem: {
          Label: 'Chemical delivery',
          Action: 'Escalate to facilities: isolate the leaking line, replace the inner pipe and re-certify the containment',
        },
      },
      fault: {
        headerLeak: {
          Label: 'Leak in the shared process vacuum header',
          Truth:
            'A flange on the shared process vacuum header was disturbed during the sub-fab work ' +
            'last weekend and is leaking. Every chamber on that header sees it as its own leak — ' +
            'because from inside the chamber, it is one.',
        },
        warmPcw: {
          Label: 'Process cooling water running warm',
          Truth:
            'The chilled water plant is delivering supply water about 22 C above nominal. Nothing ' +
            'on any tool is broken; every tool that rejects heat into that loop has simply stopped ' +
            'being able to hold its own setpoint.',
        },
        wetHeader: {
          Label: 'Moisture in the house dry-gas header',
          Truth:
            'The house dry-gas dryer failed wet over the weekend and the header is carrying about ' +
            '2% water — carryover, not the ppm-level creep a saturated dryer gives. It reaches ' +
            'every tool that breathes house nitrogen or oxygen, and what it does when it gets ' +
            'there depends entirely on what that tool was using the gas for. Note how far apart ' +
            'the two thresholds are: ALD is ruined by single-figure ppm, because water is its ' +
            'co-reactant, while a furnace growing oxide in steam does not notice anything below ' +
            'about a percent. It takes a gross failure for both tools to complain at once, and ' +
            'that is exactly why the two groups rarely connect their problems.',
        },
        spentBed: {
          Label: 'Polishing mixed bed exhausted',
          Truth:
            'The polishing mixed bed on the ultrapure water loop is at 91% of its exchange ' +
            'capacity and sodium is breaking through: the loop that read 18.1 MΩ·cm last week ' +
            'reads 11 today. Nobody watched it coming because there was nothing to watch — an ' +
            'ion-exchange bed holds the meter at 18.2 for almost its whole life and then fails ' +
            'across the last few percent, which is why you regenerate on throughput, not on ' +
            'the reading. The rinse is the LAST thing a wafer sees at the wet bench, so the ' +
            'bench reads as having failed both of its chemistries at once — a signature no ' +
            'bath on that tool can produce.',
        },
        mauFiltersLoaded: {
          Label: 'Make-up air pre-filters loaded',
          Truth:
            'The make-up air unit’s pre-filters have loaded up over a season of pollen and ' +
            'construction dust and the unit is delivering about 55% of design outside air. The ' +
            'recirculation handlers are fine, so every bay still turns its air over at full ' +
            'rate — but make-up air is what the exhaust fans are balanced against, and with the ' +
            'balance gone the whole cleanroom has dropped below the corridor. Every bay reads ' +
            'negative at once and pulls corridor air in through every gap, and nothing any bay ' +
            'can do to its own exhaust restores a cascade the building has lost. Changing the ' +
            'pre-filters downstairs takes four hours.',
        },
        dryPumpFailed: {
          Label: 'Dry pump under PRB-14 failed',
          Truth:
            'The dry pump under PRB-14 has run its bearings and seized: the foreline it used to ' +
            'hold at 50 mTorr is sitting at 600 Torr, a hair under atmosphere. The prober holds ' +
            'every wafer on its chuck by vacuum, and with a fifth of the hold it should have, the ' +
            'wafer sits loose — flat where the chuck’s centre seal still grips, lifting at the ' +
            'edge where it does not. The card lands light on the lifted die and they fail ' +
            'contact, which looks exactly like a card out of plane; but the card is fine, the ' +
            'tips are fine, and no other tool on the floor has moved, because no other tool ' +
            'shares this pump. Swapping the pump downstairs takes three hours.',
        },
        chemLineLeak: {
          Label: 'Plating chemistry line leaking under ECD-12',
          Truth:
            'The inner pipe of the plating chemistry line under ECD-12 has split inside its ' +
            'containment sleeve. The sleeve did its job: the chemistry ran down to the tray under ' +
            'the cabinet, the tray sensor tripped at four litres, and the leak interlock shut the ' +
            'delivery pump — which is exactly what it is for, because plating chemistry on a ' +
            'sub-fab floor is an acid spill. Upstairs the bath has not been made up all shift, and ' +
            'plating pulls copper out of it with every wafer, so the cell is running on a third of ' +
            'the copper its recipe assumes. No other tool has moved: each line is its own. ' +
            'Isolating the line, replacing the inner pipe and re-certifying the containment takes ' +
            'five hours.',
        },
        ahuBeltSlip: {
          Label: 'Recirculation air handlers under-delivering',
          Truth:
            'Two of the four recirculation air handlers have slipping belts and the bays are ' +
            'getting about 55% of design airflow. Every filter in the ceiling is fine, every ' +
            'fan up there is fine — the air simply is not arriving. The room reads dirtier at ' +
            'the same headcount, and the number that gives it away is RECOVERY: how long the ' +
            'bay takes to swallow an upset is set by air changes and nothing else, so a slow ' +
            'recovery with clean filters is the building, not the room. Changing the ceiling ' +
            'filters up here costs twelve hours and recovers nothing.',
        },
      },
    },

    footer: {
      Built: 'Built {{date}}.',
    },

    menu: {
      Menu: 'Menu',
      Settings: 'Settings',
      SessionNote: 'Your shift — journal, lot and tool settings — is saved on this machine and survives a reload.',
      SessionClear: 'Start a new session',
      Entrance: 'Back to entrance',
      EntranceNote: 'Return to the entry screen. Your journal, lot and tool settings are kept.',
      Model: 'AI model',
      Auto: 'Automatic',
      AutoNote: 'by region',
      LocalNote:
        'No hosted model is configured, so questions are matched on this device.',
      Lighting: 'Lighting',
      LightBright: 'White',
      LightBrightNote: 'bright and cool — the working fab',
      LightBlue: 'Blue',
      LightBlueNote: 'the automated bay after hours',
      LightYellow: 'Amber',
      LightYellowNote: 'the safelight — no UV to expose resist',
      Graphics: 'Graphics',
      QualityAuto: 'Automatic',
      QualityAutoNote: 'default',
      QualityHigh: 'More lights',
      QualityHighNote: 'more realistic',
      QualityLow: 'Less lights',
      QualityLowNote: 'less realistic',
      Loading: 'Loading',
      HallBayChase: 'Fab A',
      HallBayChaseNote: 'bay-and-chase layout — tool faces in a wall, the chase behind',
      HallBallroom: 'Fab B',
      HallBallroomNote: 'ballroom layout — open floor, every mainframe in view',
      Sound: 'Sound',
      SoundOn: 'On',
      SoundOnNote: 'the hall as it sounds',
      SoundOff: 'Muted',
      SoundOffNote: 'silent — M toggles',
      DesignFab: 'Design a fab',
      DesignFabNote: 'build your own floor from the twenty-five stations',
      YourFabs: 'Fabs you built',
      CustomFabNote: '{{n}} stations, {{hall}} layout',
      Fabs: 'My fabs',
      FabsNote: 'Your own floors: open one, save this one, or design a new one',
      OpenFab: 'Open…',
      OpenFabNote: 'the fabs saved to your account',
      SaveFab: 'Save',
      SaveFabNote: 'keep the floor you are standing in, under its name',
      SaveNothing: 'Fab A and Fab B are built in; design a fab to have one to save',
      SaveFabAs: 'Save as…',
      SaveFabAsNote: 'a copy under a new name, and you stand in the copy',
      SaveAsName: 'Name for the copy',
      SaveAsGo: 'Save the copy',
      NoSavedFabs: 'No fabs saved to this account yet.',
      LibrarySignedOut: 'Sign in (the account button, top right) to keep fabs with your account. A guest sign-in works too.',
      LibraryDenied: 'This account is not allowed to read that library.',
      LibraryOffline: 'The fab library is out of reach - check the network and try again.',
      LibraryLoading: 'Fetching your fabs…',
      FabSaved: 'Saved {{name}}.',
      DeleteFab: 'Delete from your fabs',
      DeleteFabConfirm: 'Delete “{{name}}” from your account? The floor you are standing in is not affected.',
      SavedOn: 'saved {{when}}',
    },

    routing: {
      Via: 'Answered by {{label}} ({{model}})',
      OnDevice: 'Answered on this device by pattern matching',
      Unreachable: 'Answered on this device — the hosted model was unreachable',
      Assumed: 'assumed {{region}}',
      RegionUs: 'US',
      RegionIntl: 'international',
    },

    shift: {
      Debrief: 'Shift debrief',
      SessionTallyRuns:
        'This session: {{runs}} runs at {{tools}} stations · {{scrapped}} out of spec.',
      Performance: 'Station performance',
      KpiDowntime: 'Tool downtime',
      KpiDowntimeNote: 'this shift',
      KpiOutOfSpec: 'Out of spec',
      KpiOutOfSpecNote: 'of {{runs}} runs',
      /*
       * What the student called before the tool reported (learn/shift.ts
       * `callsMade`). The note says plainly that nothing scored it, because
       * a screen laying two sentences side by side looks like it is about to.
       */
      CallsHeading: 'What you called',
      CallsNote: 'You said these before the tool reported. Nothing here marked them right or wrong — read them against each other.',
      CallsSaid: 'You said',
      CallsMeasured: 'Measured',
      CallsExplained: 'When asked why',
      Hours: '{{hours}} h',
      CostTotal: 'Total',
      NothingYet: 'Nothing measured at this station yet.',
      CostEscalation: 'Escalation — {{label}}',
      CostRepair: 'Repair — {{label}}',
      CostRepairBoth: 'Repair — {{wrong}}, then {{actual}}',
      SessionTally:
        'This session: {{shifts}} shifts · {{correct}} of {{called}} called right · ' +
        '{{hours}} h down · {{scrapped}} runs out of spec.',

      Title: 'SHIFT REPORT — {{tool}}',
      AsFoundInSpec:
        'As found this tool is IN SPEC — {{primary}}, {{secondary}}. Running the lot is a ' +
        'defensible call.',
      AsFoundOutOfSpec:
        'As found this tool is OUT OF SPEC — {{primary}}, {{secondary}}. Every wafer you run is ' +
        'scrap.',
      AtTheTool: 'AT THE TOOL — free',
      Investigate: 'INVESTIGATE — costs tool time',
      TakeIt: 'take it',
      SpentSoFar:
        '{{hours}} h spent so far. Every hour here is an hour the tool is not making wafers — ' +
        'which is exactly the trade a real shift makes.',
      InvestigateNote:
        'Each of these takes the tool, or a technician, out of production. Take the ones that ' +
        'would change your mind; taking all of them is a decision too.',
      CallIt: 'CALL IT',
      CallItNote:
        'Name the one thing you would repair. The answer stays hidden until you commit, and a ' +
        'wrong call costs the downtime twice.',
      Correct: 'Correct — {{label}}.',
      Wrong: 'Wrong — you called {{label}}.',
      RepairRight: '{{action}}. {{hours}} h of downtime, and the tool comes back right.',
      RepairWrong:
        '{{action}} costs {{hours}} h and the tool comes back still broken. Then {{realAction}} ' +
        'costs another {{realHours}} h. {{total}} h total.',
      TheRealRepair: 'the real repair',
      ThisShift:
        'This shift: {{investigating}} h investigating, {{repairing}} h repairing, {{scrapped}} ' +
        'wafers scrapped while you decided.',
      WhatEachRepair: 'WHAT EACH REPAIR WOULD HAVE RECOVERED',
      WhatEachRepairNote:
        'Each row is the tool run again with that one thing put back to nominal and everything ' +
        'else left as found. This is the method, not a scoreboard.',
      Recovered: '{{pct}}% recovered — {{primary}}, {{secondary}}',
      Decidable:
        'The best repair beat the next by {{points}} points. This one was decidable from the ' +
        'counterfactual alone.',
      TooClose:
        'The best two repairs were only {{points}} points apart, because those parts of the tool ' +
        'affect each other. Trying repairs could not have settled this one — only a reading could.',
      StartShift: 'Start a shift (random fault)',
      RevealCause: 'Reveal the cause',
      HideAnswer: 'Hide the answer',
      Instructor: 'INSTRUCTOR — arm a scenario',
    },

    journal: {
      OrderComplete: 'Work order complete',
      ByStation: 'Performance by station',
      ColTool: 'Tool',
      ColRuns: 'Runs',
      ColOutOfSpec: 'Out of spec',
      ColDowntime: 'Downtime',
      ColCalls: 'Calls',
      CallsRight: '{{correct}} of {{calls}} right',
      NotApplicable: '—',
      Unattributed: '(no tool recorded)',

      Title: '# SimFab — session record',
      NoActivity: '_No activity recorded._',
      ShiftStart: 'shift start',
      TookReading: 'took reading',
      RanWafer: 'ran a wafer',
      InSpec: 'in spec',
      OutOfSpec: '**OUT OF SPEC**',
      Changed: 'changed',
      Asked: 'asked',
      Maintenance: 'performed maintenance',
      CalledIt: 'called it',
      Correct: 'CORRECT',
      Incorrect: 'INCORRECT',
      ReadingsBefore: 'readings taken before the call',
      WafersRun: 'wafers run',
      QuestionsAsked: 'questions asked',
      ActualCause: 'the actual cause was',
      NoFault: 'no fault at all',
    },

    author: {
      Title: 'Design a fab',
      Subtitle:
        'Twenty-two stations to choose from. What you leave out is the decision — and the product route will tell you what it cost.',
      Ask: 'Ask for a change…',
      Send: 'Send',
      Working: 'Working…',
      NewFloor: 'New floor',
      SavedFloors: 'Fabs you built',
      NoSaved: 'Nothing built yet. Start from a fab that exists, or just name the tools you want.',
      Build: 'Build it and walk in',
      Rebuild: 'Rebuild and walk in',
      Standing: 'You are standing in this one',
      Save: 'Save',
      Delete: 'Delete',
      DeleteActive: 'You are standing in this one. Go to Fab A or Fab B first.',
      DeleteConfirm: 'Delete “{{name}}”? The floor plan is forgotten. The tools are not — they are the same twenty-five.',
      Cancel: 'Keep it',
      Undo: 'Undo',
      SortShort: 'Flow order',
      allAlias: 'everything|all of them|all the tools|every station|the lot|all of it',
      renameStop: 'and|then|but',
      NotOnFloor: 'Not on this floor: {{tags}}',
      LineNotOnFloor: 'This is the reference line — the full product route on the stations of Fab A. The floor you are standing in has no {{tags}}, so a lot here would have to go out of the building at every one of those operations. The numbers below are for the line as designed, not for this floor.',
      NamePlaceholder: 'Name this fab',
      HallLabel: 'Hall',
      OnTheFloor: 'On the floor, in the order a wafer meets them',
      Palette: 'Not on this floor',
      AddTool: 'Put on the floor',
      RemoveTool: 'Take off the floor',
      MoveEarlier: 'Earlier in the flow',
      MoveLater: 'Later in the flow',
      RequiredTool: 'The airlock is the way in. Every floor has one.',
      HallSize: '{{length}} m of aisle, {{bays}} bays',
      Suggestions: 'Try asking',
      Local: 'matched on this device',
      CoverageTitle: 'Against the product route',
      CoverageBar: '{{covered}} of {{steps}} operations',
      MaskCount: '{{masksCovered}} of {{masks}} masked steps',
      SegmentRuns: 'runs end to end',
      SegmentPart: '{{covered}} of {{steps}}',
      Empty: 'Nothing on this floor but the airlock.',
      ListSeparator: ', ',
      hall: {
        bayChase: 'bay-and-chase',
        ballroom: 'ballroom',
      },
      hallNote: {
        bayChase: 'tool faces set in a wall, the mainframes in the chase behind',
        ballroom: 'one open floor, every mainframe in view',
      },
      hallAlias: {
        bayChase: 'bay and chase|bay-and-chase|baychase|walled|bulkhead|with a wall|closed bays',
        ballroom: 'ballroom|ball room|open floor|open plan|no wall|without a wall|wall-less',
      },
      zone: {
        entry: 'entry',
        substrate: 'substrate',
        frontend: 'front end',
        metrology: 'metrology',
        interconnect: 'interconnect',
        sort: 'wafer sort',
        assembly: 'assembly and test',
        quantum: 'quantum devices',
        support: 'support',
      },
      zoneAlias: {
        entry: 'entrance|way in',
        substrate: 'wafer supply|ingot to wafer|substrate line',
        frontend: 'frontend|feol|device layers|transistor',
        metrology: 'in-line measurement',
        interconnect: 'beol|back end of line|metal layers|wiring|damascene',
        sort: 'probe floor|wafer testing',
        assembly: 'back end|packaging line|osat floor|assembly line',
        quantum: 'qubit|qubits|quantum chip|quantum chips|quantum computing|quantum line',
        support: 'infrastructure|house services',
      },
      segment: {
        substrate: 'the substrate module',
        gate: 'the gate module',
        metal: 'the metal loop',
        backEnd: 'assembly and test',
      },
      archetype: {
        idm: {
          Name: 'Integrated device manufacturer',
          Purpose: 'Sand in one door, binned parts out of the other. All twenty-five stations.',
        },
        foundry: {
          Name: 'Pure-play foundry',
          Purpose: 'Buys polished wafers, ships finished wafers. No puller, and it never sees a package.',
        },
        substrate: {
          Name: 'Substrate supplier',
          Purpose: 'Where the wafer a fab starts with comes from. Not a wafer fab at all.',
        },
        osat: {
          Name: 'Assembly and test house',
          Purpose: 'A finished wafer in, a binned part out. No lithography anywhere on the floor.',
        },
        power: {
          Name: 'Power discrete line',
          Purpose: 'Thick epi, deep implants, aluminium metal — so no damascene loop, on purpose.',
        },
        teaching: {
          Name: 'Teaching floor',
          Purpose: 'The smallest floor that still makes a patterned layer. Nine stations, one loop.',
        },
      },
      archetypeAlias: {
        idm: 'idm|integrated device|the whole thing|full fab|complete fab|everything',
        foundry: 'foundry|pure play|pure-play|contract fab',
        substrate: 'substrate supplier|wafer supplier|ingot house|wafer maker',
        osat: 'osat|assembly house|test house|packaging house|back-end house',
        power: 'power line|discrete|mosfet|igbt|power device',
        teaching: 'teaching|classroom|training floor|minimal fab|smallest|simple fab',
      },
      alias: {
        gowning: 'gown|gowning|airlock|changing room|cleanroom entry',
        crystal: 'puller|czochralski|ingot|boule|crystal grower',
        waferprep: 'wafer prep|waferprep|slicing|lapping|polished wafer|substrate prep|wire saw',
        epi: 'epi|epitaxy|epitaxial|epi reactor',
        clean: 'wet bench|wet clean|rca clean|clean bench|pre-clean',
        implant: 'implant|implanter|ion implanter|doping|dopant',
        etch: 'etcher|etch|dry etch|icp|plasma etcher',
        deposition: 'cvd|pecvd|ald|depo|film deposition|deposition tool',
        litho: 'litho|lithography|scanner|stepper|photolithography|exposure tool|coat and develop',
        patterning: 'multiple patterning|multi patterning|saqp|spacer|self aligned|double patterning|quad patterning|pitch splitting',
        thermal: 'furnace|anneal|annealing|rtp|oxidation|diffusion|thermal tool',
        metrology: 'metrology|ellipsometer|scatterometry|in-line metrology',
        facilities: 'facilities|house systems|utility panel|facility face|valve panel',
        cmp: 'cmp|polisher|planariser|planarisation|planarization|chemical mechanical',
        plating: 'plating|electroplating|ecd|copper fill|electrochemical deposition',
        inspection: 'inspection|defect inspection|particle scan|defect scanner',
        probe: 'prober|wafer sort|probe card|parametric test|electrical sort',
        dicing: 'dicing|dicer|singulation|grind and dice|dicing saw|backgrind',
        bonding: 'die attach|wire bond|wirebond|bonder|bonding',
        packaging: 'mould|mold|encapsulation|moulding|package|packaging',
        finaltest: 'final test|burn in|burn-in|binning|tester|ate',
        gas: 'bulk gas|gas cabinet|gas farm|gas bunker|specialty gas|nitrogen supply',
        euv: 'euv|euv scanner|extreme ultraviolet|13.5 nm|tin source|euv lithography',
        ebeam: 'cd-sem|sem|e-beam|ebeam|electron beam|e-beam inspection|scanning electron microscope',
        quantum: 'quantum|qubit test|cryostat|dilution refrigerator|dilution fridge|transmon|spin qubit',
      },
      action: {
        add: {
          Summary: 'Put a station, or a whole stage of the flow, on the floor',
          Example: 'add a CMP polisher',
        },
        remove: {
          Summary: 'Take a station off the floor',
          Example: 'take the crystal puller off',
        },
        archetype: {
          Summary: 'Start again from a fab that exists',
          Example: 'build me a pure-play foundry',
        },
        hall: {
          Summary: 'Walled bays, or one open floor',
          Example: 'make it a ballroom',
        },
        rename: {
          Summary: 'Call this fab something',
          Example: 'call it the pilot line',
        },
        sort: {
          Summary: 'Put the aisle back in the order a wafer meets the tools',
          Example: 'put it in flow order',
        },
        clear: {
          Summary: 'Strip the floor back and start again',
          Example: 'clear the floor',
        },
        assess: {
          Summary: 'What can this floor make, and what has it not got?',
          Example: 'what am I missing?',
        },
        catalogue: {
          Summary: 'What stations are there to choose from?',
          Example: 'what tools can I have?',
        },
      },
      trigger: {
        add: 'add|put|install|include|i want|i need|give me|bring in|buy|fit',
        remove: 'remove|delete|take|take out|take off|drop|get rid|without|scrap|sell|lose the|off the floor',
        archetype: 'start from|start with|start again from|build me|make me|based on|template|preset|set it up as',
        hall: 'ballroom|bay and chase|bay-and-chase|open floor|walled|bulkhead|layout|hall style',
        rename: 'call it|name it|rename|call this',
        sort: 'flow order|in order|reorder|rearrange|sort the|tidy up|process order',
        clear: 'clear the|empty the|start over|strip it|nothing on the floor',
        assess: 'what can|can it|can this|what am i missing|what is missing|coverage|how complete|assess|good enough|will this work',
        catalogue: 'what tools|which tools|what stations|which stations|list the|what is available|what can i choose|my options',
      },
      renameLead: 'call it|name it|rename it to|call this fab|name this fab',
      reply: {
        Added: 'On the floor now: {{tags}}.',
        Removed: 'Off the floor: {{tags}}.',
        Refused: '{{tags}} stays. It is the way in.',
        AlreadyThere: 'Already standing: {{tags}}.',
        NotThere: 'Not on this floor to begin with: {{tags}}.',
        Hall: 'Hall style: {{hall}}.',
        Renamed: 'Called “{{name}}”.',
        Sorted: 'The aisle now runs in the order a wafer meets the tools.',
        Archetype: 'Starting again from: {{name}}.',
        NothingToDo: 'I can do that — {{summary}} — but nothing in that sentence told me what to do it to.',
        SameArchetype: 'This floor already is {{name}}.',
        Unplaced: 'I could not turn that into a change to the floor. Here is what I can do:',
        Complete: 'Every operation in the product route has a tool on this floor — all {{steps}} of them, {{masks}} masked.',
        Coverage: 'It has a tool for {{covered}} of the product route’s {{steps}} operations — {{percent}}%.',
        CoverageNone: 'Not one of the product route’s {{steps}} operations has a tool on this floor.',
        SegmentsRun: 'End to end: {{segments}}.',
        SegmentsOpened: 'That closes {{segments}}, which now runs end to end.',
        SegmentsBroken: 'That breaks {{segments}}, which no longer runs end to end.',
        BiggestGap: 'The biggest gap is {{tag}} — {{process}} — with {{count}} operations and nowhere to run them.',
        BiggestGap_one: 'The biggest gap is {{tag}} — {{process}} — with {{count}} operation and nowhere to run it.',
        BiggestGap_other: 'The biggest gap is {{tag}} — {{process}} — with {{count}} operations and nowhere to run them.',
        CatalogueHead: '{{n}} stations in the catalogue, {{chosen}} of them on this floor. A tick means it is standing.',
        CatalogueZone: '{{zone}}: {{tools}}',
        EmptyFloor: 'Nothing on this floor but the airlock. Ask for a fab to start from, or name the tools you want.',
        Floor: '{{n}} stations: {{tools}}.',
        NoHouse: 'No {{tags}} on this floor. Every tool here hangs off process vacuum, chilled water, bulk nitrogen and exhaust — leaving them out leaves out the half of the building that actually fails.',
        Unused: 'The product route never visits {{tags}}. Not a criticism: they are here for what this measurement does not measure.',
      },
    },

  },
}
